import * as i0 from '@angular/core';
import { SecurityContext, Injectable, Optional, Inject, Component, ViewEncapsulation, Input, ContentChild, NgModule, ViewChild, ContentChildren, isDevMode, EventEmitter, ViewChildren, Output, HostListener, Directive, forwardRef, LOCALE_ID, ChangeDetectionStrategy, Host, HostBinding, InjectionToken, PLATFORM_ID, QueryList, TemplateRef, Pipe } from '@angular/core';
import * as i2$1 from '@angular/cdk/a11y';
import { A11yModule } from '@angular/cdk/a11y';
import * as i1$2 from '@angular/common';
import { DOCUMENT, CommonModule, getLocaleDayNames, FormStyle, TranslationWidth, formatDate, isPlatformBrowser } from '@angular/common';
import { of, Subscription, Subject, NEVER, defer, merge, fromEvent } from 'rxjs';
import { map, finalize, share, takeUntil, take, switchMap, filter, delay, debounceTime } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import * as i1$1 from '@angular/platform-browser';
import { HammerGestureConfig, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { aciCloseCircle, aciExclamationCircle, aciInfo, aciCheckCircle, aciArrowRight, aciArrowLeft, aciDoubleArrowLeft, aciDoubleArrowRight, aciCircleNotch, aciArrowDown, aciArrowUp, aciXClose, aciCalendar, aciPointer, aciLogin, aciClock, aciStar, aciCheckSimple } from '@am/common-svg-icons';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, Validators, FormsModule } from '@angular/forms';
import { ComponentPortal, DomPortalOutlet, PortalInjector, TemplatePortal } from '@angular/cdk/portal';
import * as i1$3 from '@angular/cdk/overlay';
import { OverlayModule } from '@angular/cdk/overlay';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i2$2 from '@angular/cdk/layout';
import { LayoutModule } from '@angular/cdk/layout';
import * as i1$4 from '@angular/cdk/platform';
import { PlatformModule } from '@angular/cdk/platform';
import { trigger, state, style, transition, animate, keyframes } from '@angular/animations';
import { ESCAPE, ENTER, UP_ARROW, DOWN_ARROW, TAB } from '@angular/cdk/keycodes';
import Hammer from 'hammerjs';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const DEFAULT_NAMESPACE = 'aci'; // Almundo Commons Icons
class SvgIconSetConfig {
    constructor(url) {
        this.url = url;
    }
}
/**
 * Icon Registry class
 * Register icon data to create a inline svg sprite later
 */
class MbIconRegistry {
    constructor(_httpClient, _sanitizer, document) {
        this._httpClient = _httpClient;
        this._sanitizer = _sanitizer;
        this.svgIconDefinitions = new Map();
        this.iconSetConfigs = new Map();
        /** In-progress icon fetches. Used to coalesce multiple requests to the same URL. */
        this.inProgressUrlFetches = new Map();
        this._document = document;
    }
    /**
     * Registers an icon set by URL in the specified namespace.
     * @param namespace in which to register the icon set.
     * @param url
     */
    addSvgIconSetURLInNamespace(namespace, url) {
        return this._addSvgIconSetUrl(namespace, url);
    }
    /**
     * Register a new icon in the registry
     */
    addSvgIconDefinition(icon) {
        const namespace = DEFAULT_NAMESPACE;
        this.svgIconDefinitions.set(`${namespace}:${icon.iconName}`, icon);
    }
    /**
     * Register a new icon in the registry with a given namespace
     */
    addSvgIconDefinitionInNamespace(icon, namespace) {
        this.svgIconDefinitions.set(`${namespace}:${icon.iconName}`, icon);
    }
    /**
     * Retrieve a SvgIconDefinition from the registry
     */
    getSvgIconDefinition(namespace, iconName) {
        if (!namespace) {
            namespace = DEFAULT_NAMESPACE;
        }
        const key = `${namespace}:${iconName}`;
        return this.svgIconDefinitions.get(key);
    }
    /**
     * Register a new icon with a set of aliases in the registry with a given namespace
     */
    addSvgIconDefinitionInNamespaceWithAliases(icon, namespace, aliases) {
        aliases.forEach(alias => this.svgIconDefinitions.set(`${namespace}:${alias}`, icon));
    }
    /**
     * @deprecated use getSvgIconDefinition method instead
     * @param namespace
     * @param iconName
     */
    getSvgIcon(namespace, iconName) {
        throw new Error('This method is deprecated, use getSvgIconDefinition instead');
    }
    /**
     * Gets a SVGElement from the registered icon sets
     * @param namespace
     * @param name
     */
    getSVGElementFromIconSet(namespace, name) {
        // See if we have any icon sets registered for the namespace.
        const iconSetConfigs = this.iconSetConfigs.get(namespace);
        if (iconSetConfigs) {
            return this._getSvgFromIconSetConfigs(name, iconSetConfigs);
        }
    }
    /**
     * Loads the content of the icon set URL specified in the SvgIconConfig and creates an SVG element
     * from it.
     */
    _loadSvgIconSetFromConfig(config) {
        // If the SVG for this icon set has already been parsed, do nothing.
        if (config.svgElement) {
            return of(config.svgElement);
        }
        return this._fetchUrl(config.url).pipe(map(svgText => {
            // It is possible that the icon set was parsed and cached by an earlier request, so parsing
            // only needs to occur if the cache is yet unset.
            if (!config.svgElement) {
                config.svgElement = this._stringToSVGElement(svgText);
            }
            return config.svgElement;
        }));
    }
    /**
     * Attempts to find an icon with the specified name in any of the SVG icon sets.
     * First searches the available cached icons for a nested element with a matching name, and
     * if found copies the element to a new `<svg>` element. If not found, fetches the icon set
     * that have not been cached, and searches inside of it
     */
    _getSvgFromIconSetConfigs(name, iconSetConfig) {
        const namedIcon = this._extractIconWithNameFromAnySet(name, iconSetConfig);
        if (namedIcon) {
            return of(namedIcon);
        }
        return this._loadSvgIconSetFromConfig(iconSetConfig).pipe(map(() => {
            const foundIcon = this._extractIconWithNameFromAnySet(name, iconSetConfig);
            if (!foundIcon) {
                throw Error(`Unable to find icon with the name "${name}"`);
            }
            return foundIcon;
        }));
    }
    /**
     * Searches the cached SVG element for the given icon sets for a nested icon element whose "id"
     * tag matches the specified name. If found, copies the nested element to a new SVG element and
     * returns it. Returns null if no matching element is found.
     */
    _extractIconWithNameFromAnySet(iconName, iconSetConfigs) {
        if (iconSetConfigs.svgElement) {
            const foundIcon = this._extractSvgIconFromSet(iconSetConfigs.svgElement, iconName);
            if (foundIcon) {
                return foundIcon;
            }
        }
        return null;
    }
    /**
     * @from Angular Material
     * Searches the cached element of the given SvgIconSetConfig for a nested icon element whose "id"
     * tag matches the specified name. If found, copies the nested element to a new SVG element and
     * returns it. Returns null if no matching element is found.
     */
    _extractSvgIconFromSet(iconSet, iconName) {
        // Use the `id="iconName"` syntax in order to escape special
        // characters in the ID (versus using the #iconName syntax).
        const iconSource = iconSet.querySelector(`[id="${iconName}"]`);
        if (!iconSource) {
            return null;
        }
        // Clone the element and remove the ID to prevent multiple elements from being added
        // to the page with the same ID.
        const iconElement = iconSource.cloneNode(true);
        iconElement.removeAttribute('id');
        // If the icon node is itself an <svg> node, clone and return it directly. If not, set it as
        // the content of a new <svg> node.
        if (iconElement.nodeName.toLowerCase() === 'svg') {
            return this._setSvgAttributes(iconElement);
        }
        // If the node is a <symbol>, it won't be rendered so we have to convert it into <svg>. Note
        // that the same could be achieved by referring to it via <use href="#id">, however the <use>
        // tag is problematic on Firefox, because it needs to include the current page path.
        if (iconElement.nodeName.toLowerCase() === 'symbol') {
            return this._setSvgAttributes(this._toSvgElement(iconElement));
        }
        const svg = this._document.createElementNS(SVG_NAMESPACE, 'svg');
        // Clone the node so we don't remove it from the parent icon set element.
        svg.appendChild(iconElement);
        return this._setSvgAttributes(svg);
    }
    /**
     * Converts an element into an SVG node by cloning all of its children.
     *
     */
    _toSvgElement(element) {
        const svg = this._document.createElementNS(SVG_NAMESPACE, 'svg');
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < element.childNodes.length; i++) {
            if (element.childNodes[i].nodeType === this._document.ELEMENT_NODE) {
                svg.appendChild(element.childNodes[i].cloneNode(true));
            }
        }
        const viewBox = element.getAttribute('viewBox');
        if (viewBox) {
            svg.setAttribute('viewBox', viewBox);
        }
        return svg;
    }
    /**
     * Creates a DOM element from the given SVG string.
     */
    _stringToSVGElement(str) {
        const div = this._document.createElement('DIV');
        div.innerHTML = str;
        const svg = div.querySelector('svg');
        if (!svg) {
            throw Error('<svg> tag not found');
        }
        return svg;
    }
    /**
     * Sets the default attributes for an SVG element to be used as an icon.
     */
    _setSvgAttributes(svg) {
        svg.setAttribute('fit', '');
        svg.setAttribute('height', '100%');
        svg.setAttribute('width', '100%');
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        return svg;
    }
    /**
     * Adds an icon set URL
     * @param namespace
     * @param url
     */
    _addSvgIconSetUrl(namespace, url) {
        const iconSetConfigs = this.iconSetConfigs.get(namespace);
        if (!iconSetConfigs) {
            const config = new SvgIconSetConfig(url);
            this.iconSetConfigs.set(namespace, config);
        }
        else {
            throw new Error(`An URL has been already registered for the namespace ${namespace}`);
        }
    }
    /**
     * Fetch an url making sure that multiple fetch for the same URL won't
     * generate several requests
     * @param safeUrl
     */
    _fetchUrl(safeUrl) {
        const url = this._sanitizer.sanitize(SecurityContext.RESOURCE_URL, safeUrl);
        if (!url) {
            throw new Error(`The URL provided to MbIconRegistry was not trusted as a resource URL ` +
                `via Angular's DomSanitizer. Attempted URL was "${safeUrl}".`);
        }
        // Store in-progress fetches to avoid sending a duplicate request for a URL when there is
        // already a request in progress for that URL.
        const inProgressFetch = this.inProgressUrlFetches.get(url);
        if (inProgressFetch) {
            return inProgressFetch;
        }
        const req = this._httpClient.get(url, { responseType: 'text' }).pipe(finalize(() => this.inProgressUrlFetches.delete(url)), 
        // It's necessary to call share() so that multiple subscribers don't cause multiple requests.
        share());
        this.inProgressUrlFetches.set(url, req);
        return req;
    }
}
MbIconRegistry.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconRegistry, deps: [{ token: i1.HttpClient, optional: true }, { token: i1$1.DomSanitizer }, { token: DOCUMENT, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
MbIconRegistry.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconRegistry, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconRegistry, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient, decorators: [{
                    type: Optional
                }] }, { type: i1$1.DomSanitizer }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class MbIcon {
    constructor(registry, elementRef, renderer) {
        this.registry = registry;
        this.elementRef = elementRef;
        this.renderer = renderer;
    }
    ngOnInit() {
        this.renderSVG();
    }
    ngOnChanges(changes) {
        if (changes.icon && !changes.icon.firstChange) {
            this.removeSVG();
            this.renderSVG();
        }
    }
    removeSVG() {
        this.elementRef.nativeElement.querySelector('svg').remove();
    }
    /**
     * Reads the input icon binding and renders the svg according to the usage type
     * There are 2 main usages for this input binding:
     *  1. A string denoting the icon name
     *  2. A direct icon definition
     */
    renderSVG() {
        let svg;
        // It's a SvgDefinition, build a SVGElement from it
        if (this.isInputSvgDefinition()) {
            svg = this.buildSVGElementFromSvgDefinition(this.icon);
        }
        // It's a string look it up on the icon registry
        else if (this.isInputString()) {
            const [namespace, name] = this.splitIconName(this.icon);
            // Search first for the svg definitions of the registry
            const svgDef = this.getSvgDefinitionFromRegistry(namespace, name);
            // If found build the SVGElement
            if (svgDef) {
                svg = this.buildSVGElementFromSvgDefinition(svgDef);
            }
            // look for it inside the icon sets
            else {
                svg = this.getSVGElementFromRegistry(namespace, name);
            }
        }
        else {
            throw Error('Invalid icon input usage');
        }
        if (svg) {
            svg.subscribe(svgElement => {
                this._appendSVG(svgElement);
            });
        }
    }
    /**
     * Append the SVGElement as
     * @param svg
     */
    _appendSVG(svg) {
        this.elementRef.nativeElement.appendChild(svg);
    }
    /**
     * SvgIconDefinition Type Guard
     */
    isSvgIconDefinition(icon) {
        return (icon !== null &&
            typeof icon === 'object' &&
            'iconName' in icon &&
            (this.isSvgBasicDefinition(icon) || this.isSvgComplexDefinition(icon)));
    }
    splitIconName(iconName) {
        const parts = iconName.split(':');
        switch (parts.length) {
            case 1:
                return ['', parts[0]]; // Use default namespace.
            case 2:
                return parts;
            default:
                throw new Error(`Invalid icon name: "${iconName}"`);
        }
    }
    isInputSvgDefinition() {
        return this.isSvgIconDefinition(this.icon);
    }
    /**
     * Determines if a binding icon is a string
     */
    isInputString() {
        return typeof this.icon === 'string';
    }
    /**
     * Searches for an icon definition on the registry
     */
    getSvgDefinitionFromRegistry(namespace, name) {
        return this.registry.getSvgIconDefinition(namespace, name);
    }
    /**
     * Searches for an SVGElement on the registry
     */
    getSVGElementFromRegistry(namespace, name) {
        return this.registry.getSVGElementFromIconSet(namespace, name);
    }
    /**
     * Builds a SVGElement
     */
    buildSVGElementFromSvgDefinition(iconDefinition) {
        let svg;
        if (this.isSvgBasicDefinition(iconDefinition)) {
            svg = this.buildBasicSVGElement(iconDefinition);
        }
        else {
            svg = this.buildComplexSVGElement(iconDefinition);
        }
        return of(svg);
    }
    isSvgBasicDefinition(def) {
        const { iconData } = def;
        if (iconData === undefined) {
            return false;
        }
        const [width, height, svgPathData] = iconData;
        return (typeof width === 'number' &&
            typeof height === 'number' &&
            typeof svgPathData === 'string');
    }
    isSvgComplexDefinition(def) {
        const { svgDef } = def;
        return svgDef !== undefined;
    }
    /**
     * Builds a SVGElement from SvgBasicDefinition
     */
    buildBasicSVGElement(iconDefinition) {
        const { iconName, iconData } = iconDefinition;
        const [width, height, pathData] = iconData;
        const svgRoot = this.renderer.createElement('svg', SVG_NAMESPACE);
        this.renderer.setAttribute(svgRoot, 'id', iconName);
        this.renderer.setAttribute(svgRoot, 'viewBox', `0 0 ${width} ${height}`);
        const path = this.renderer.createElement('path', SVG_NAMESPACE);
        this.renderer.setAttribute(path, 'fill', 'currentColor');
        this.renderer.setAttribute(path, 'd', `${pathData}`);
        this.renderer.appendChild(svgRoot, path);
        return svgRoot;
    }
    /**
     * Builds an Element from a root and a SvgDefinition
     */
    toDOMElement(root, svgDef) {
        const attrMap = svgDef.attr;
        if (attrMap) {
            Object.keys(attrMap).forEach((attr) => this.renderer.setAttribute(root, attr, attrMap[attr]));
        }
        const childrenTags = Object.keys(svgDef).filter((key) => key !== 'attr');
        childrenTags.forEach((tag) => {
            let children;
            const tagDef = svgDef[tag];
            if (Array.isArray(tagDef)) {
                children = tagDef;
            }
            else {
                children = [tagDef];
            }
            const svgChildren = children.map((childDef) => {
                const svgChild = this.renderer.createElement(tag, SVG_NAMESPACE);
                return this.toDOMElement(svgChild, childDef);
            });
            svgChildren.forEach((svgChild) => this.renderer.appendChild(root, svgChild));
        });
        return root;
    }
    /**
     * Builds a SVGElement from SvgComplexDefinition
     */
    buildComplexSVGElement(iconDefinition) {
        const { iconName, svgDef } = iconDefinition;
        const svgRoot = this.renderer.createElement('svg', SVG_NAMESPACE);
        this.renderer.setAttribute(svgRoot, 'id', iconName);
        return this.toDOMElement(svgRoot, svgDef);
    }
}
MbIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIcon, deps: [{ token: MbIconRegistry }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
MbIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbIcon, selector: "mb-icon", inputs: { icon: "icon", horizontalFlip: "horizontalFlip", verticalFlip: "verticalFlip", spin: "spin" }, host: { properties: { "class.mb-icon--horizontal-flip": "horizontalFlip", "class.mb-icon--vertical-flip": "verticalFlip", "class.mb-icon--spin": "spin" }, classAttribute: "mb-icon" }, usesOnChanges: true, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [".mb-icon{display:inline-block;line-height:0;vertical-align:middle}.mb-icon svg{height:1em;width:1em}.mb-icon--horizontal-flip{transform:scaleX(-1)}.mb-icon--vertical-flip{transform:scaleY(-1)}.mb-icon--horizontal-flip.mb-icon--vertical-flip{transform:scale(-1)}.mb-icon--spin svg{animation:spin 1.5s linear infinite}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(1turn)}}\n"], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIcon, decorators: [{
            type: Component,
            args: [{ selector: 'mb-icon', template: '<ng-content></ng-content>', encapsulation: ViewEncapsulation.None, host: {
                        'class': 'mb-icon',
                        '[class.mb-icon--horizontal-flip]': 'horizontalFlip',
                        '[class.mb-icon--vertical-flip]': 'verticalFlip',
                        '[class.mb-icon--spin]': 'spin'
                    }, styles: [".mb-icon{display:inline-block;line-height:0;vertical-align:middle}.mb-icon svg{height:1em;width:1em}.mb-icon--horizontal-flip{transform:scaleX(-1)}.mb-icon--vertical-flip{transform:scaleY(-1)}.mb-icon--horizontal-flip.mb-icon--vertical-flip{transform:scale(-1)}.mb-icon--spin svg{animation:spin 1.5s linear infinite}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(1turn)}}\n"] }]
        }], ctorParameters: function () { return [{ type: MbIconRegistry }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { icon: [{
                type: Input
            }], horizontalFlip: [{
                type: Input
            }], verticalFlip: [{
                type: Input
            }], spin: [{
                type: Input
            }] } });

class MbAdvice {
    constructor() {
        this.color = 'primary';
        this.align = 'left';
        this.size = 'large';
    }
}
MbAdvice.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAdvice, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbAdvice.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbAdvice, selector: "mb-advice", inputs: { color: "color", align: "align", size: "size" }, host: { properties: { "class.mb-advice--primary": "color === \"primary\"", "class.mb-advice--secondary": "color === \"secondary\"", "class.mb-advice--tertiary": "color === \"tertiary\"", "class.mb-advice--success": "color === \"success\"", "class.mb-advice--light": "color === \"light\"", "class.mb-advice--left": "align === \"left\"", "class.mb-advice--right": "align === \"right\"", "class.mb-advice--large": "size === \"large\"", "class.mb-advice--small": "size === \"small\"" }, classAttribute: "mb-advice" }, queries: [{ propertyName: "icon", first: true, predicate: MbIcon, descendants: true, static: true }], ngImport: i0, template: "<div class=\"mb-advice__content\">\n  <span *ngIf=\"icon\" class=\"mb-advice__icon\">\n    <ng-content select=\"mb-icon\"></ng-content>\n  </span>\n  <span><ng-content></ng-content></span>\n</div>\n", styles: [".mb-advice__content{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;align-items:center;display:inline-flex;height:32px;line-height:inherit;padding:0 16px}@media only screen and (max-width: 767px){.mb-advice__content{line-height:1.5}}.mb-advice__icon{display:flex;font-size:20px;line-height:inherit;margin-right:8px}.mb-advice:host{display:block;line-height:1;text-transform:uppercase}.mb-advice:host--primary .mb-advice__content{background:var(--color-primary);color:var(--color-primary-contrast)}.mb-advice:host--secondary .mb-advice__content{background:var(--color-secondary);color:var(--color-secondary-contrast)}.mb-advice:host--tertiary .mb-advice__content{background:var(--color-tertiary);color:var(--color-tertiary-contrast)}.mb-advice:host--success .mb-advice__content{background:var(--color-success);color:var(--color-success-contrast)}.mb-advice:host--light .mb-advice__content{background:var(--color-foreground);color:var(--color-foreground-contrast)}.mb-advice:host--left{text-align:left}.mb-advice:host--left .mb-advice__content{border-bottom-right-radius:20px;border-top-right-radius:20px}.mb-advice:host--right{text-align:right}.mb-advice:host--right .mb-advice__content{border-bottom-left-radius:20px;border-top-left-radius:20px}.mb-advice:host--small .mb-advice__content{font-size:.75rem;font-weight:var(--font-weight-semibold);line-height:1.5;height:24px;line-height:inherit;padding:0 8px}@media only screen and (max-width: 767px){.mb-advice:host--small .mb-advice__content{line-height:1.5}}.mb-advice:host--small .mb-advice__icon{font-size:16px;margin-right:4px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAdvice, decorators: [{
            type: Component,
            args: [{ selector: 'mb-advice', host: {
                        'class': 'mb-advice',
                        '[class.mb-advice--primary]': 'color === "primary"',
                        '[class.mb-advice--secondary]': 'color === "secondary"',
                        '[class.mb-advice--tertiary]': 'color === "tertiary"',
                        '[class.mb-advice--success]': 'color === "success"',
                        '[class.mb-advice--light]': 'color === "light"',
                        '[class.mb-advice--left]': 'align === "left"',
                        '[class.mb-advice--right]': 'align === "right"',
                        '[class.mb-advice--large]': 'size === "large"',
                        '[class.mb-advice--small]': 'size === "small"'
                    }, template: "<div class=\"mb-advice__content\">\n  <span *ngIf=\"icon\" class=\"mb-advice__icon\">\n    <ng-content select=\"mb-icon\"></ng-content>\n  </span>\n  <span><ng-content></ng-content></span>\n</div>\n", styles: [".mb-advice__content{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;align-items:center;display:inline-flex;height:32px;line-height:inherit;padding:0 16px}@media only screen and (max-width: 767px){.mb-advice__content{line-height:1.5}}.mb-advice__icon{display:flex;font-size:20px;line-height:inherit;margin-right:8px}.mb-advice:host{display:block;line-height:1;text-transform:uppercase}.mb-advice:host--primary .mb-advice__content{background:var(--color-primary);color:var(--color-primary-contrast)}.mb-advice:host--secondary .mb-advice__content{background:var(--color-secondary);color:var(--color-secondary-contrast)}.mb-advice:host--tertiary .mb-advice__content{background:var(--color-tertiary);color:var(--color-tertiary-contrast)}.mb-advice:host--success .mb-advice__content{background:var(--color-success);color:var(--color-success-contrast)}.mb-advice:host--light .mb-advice__content{background:var(--color-foreground);color:var(--color-foreground-contrast)}.mb-advice:host--left{text-align:left}.mb-advice:host--left .mb-advice__content{border-bottom-right-radius:20px;border-top-right-radius:20px}.mb-advice:host--right{text-align:right}.mb-advice:host--right .mb-advice__content{border-bottom-left-radius:20px;border-top-left-radius:20px}.mb-advice:host--small .mb-advice__content{font-size:.75rem;font-weight:var(--font-weight-semibold);line-height:1.5;height:24px;line-height:inherit;padding:0 8px}@media only screen and (max-width: 767px){.mb-advice:host--small .mb-advice__content{line-height:1.5}}.mb-advice:host--small .mb-advice__icon{font-size:16px;margin-right:4px}\n"] }]
        }], propDecorators: { color: [{
                type: Input
            }], align: [{
                type: Input
            }], size: [{
                type: Input
            }], icon: [{
                type: ContentChild,
                args: [MbIcon, { static: true }]
            }] } });

class MbAdviceModule {
}
MbAdviceModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAdviceModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbAdviceModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbAdviceModule, declarations: [MbAdvice], imports: [CommonModule], exports: [MbAdvice] });
MbAdviceModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAdviceModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAdviceModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [MbAdvice],
                    exports: [MbAdvice]
                }]
        }] });

class MbProgressBar {
    constructor() {
        this.color = 'primary';
    }
}
MbProgressBar.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbProgressBar, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbProgressBar.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbProgressBar, selector: "mb-progress-bar", inputs: { color: "color" }, host: { properties: { "class.mb-progress-bar--primary": "color === \"primary\"", "class.mb-progress-bar--secondary": "color === \"secondary\"" }, classAttribute: "mb-progress-bar" }, ngImport: i0, template: '<div class="mb-progress-bar__line"></div>', isInline: true, styles: [".mb-progress-bar:host{background:var(--color-background);display:block;height:4px;overflow:hidden;position:relative;width:100%}.mb-progress-bar:host--primary .mb-progress-bar__line{background-color:var(--color-primary)}.mb-progress-bar:host--secondary .mb-progress-bar__line{background-color:var(--color-secondary)}.mb-progress-bar__line{animation:lineal 2s infinite;height:100%;left:-145%;position:absolute;width:100%}@keyframes lineal{0%{animation-timing-function:cubic-bezier(.15,0,.51506,.40969);transform:translate(0)}5%{animation-timing-function:cubic-bezier(.31033,.28406,.8,.73371);transform:translate(0)}50%{animation-timing-function:cubic-bezier(.4,.62704,.6,.90203);transform:translate(83.67142%)}to{transform:translate(250.61106%)}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbProgressBar, decorators: [{
            type: Component,
            args: [{ selector: 'mb-progress-bar', template: '<div class="mb-progress-bar__line"></div>', host: {
                        'class': 'mb-progress-bar',
                        '[class.mb-progress-bar--primary]': 'color === "primary"',
                        '[class.mb-progress-bar--secondary]': 'color === "secondary"'
                    }, styles: [".mb-progress-bar:host{background:var(--color-background);display:block;height:4px;overflow:hidden;position:relative;width:100%}.mb-progress-bar:host--primary .mb-progress-bar__line{background-color:var(--color-primary)}.mb-progress-bar:host--secondary .mb-progress-bar__line{background-color:var(--color-secondary)}.mb-progress-bar__line{animation:lineal 2s infinite;height:100%;left:-145%;position:absolute;width:100%}@keyframes lineal{0%{animation-timing-function:cubic-bezier(.15,0,.51506,.40969);transform:translate(0)}5%{animation-timing-function:cubic-bezier(.31033,.28406,.8,.73371);transform:translate(0)}50%{animation-timing-function:cubic-bezier(.4,.62704,.6,.90203);transform:translate(83.67142%)}to{transform:translate(250.61106%)}}\n"] }]
        }], propDecorators: { color: [{
                type: Input
            }] } });

class MbProgressBarModule {
}
MbProgressBarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbProgressBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbProgressBarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbProgressBarModule, declarations: [MbProgressBar], exports: [MbProgressBar] });
MbProgressBarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbProgressBarModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbProgressBarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbProgressBar],
                    exports: [MbProgressBar]
                }]
        }] });

class MbBreadcrumbItem {
    constructor(vcr) {
        this.vcr = vcr;
    }
    renderSeparator() {
        this.vcr.clear();
        this.vcr.createEmbeddedView(this.separator);
    }
}
MbBreadcrumbItem.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBreadcrumbItem, deps: [{ token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Component });
MbBreadcrumbItem.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBreadcrumbItem, selector: "a[mb-breadcrumb-item]", host: { classAttribute: "mb-breadcrumbs__item" }, viewQueries: [{ propertyName: "separator", first: true, predicate: ["separator"], descendants: true, static: true }], ngImport: i0, template: "<ng-content></ng-content>\n<ng-template #separator>\n  <span class=\"mb-breadcrumb__separator\">></span>\n</ng-template>\n", styles: [":host:visited{color:var(--color-secondary)}:host:hover{color:var(--color-secondary-light)}:host:last-child{color:var(--color-text-dark)}.mb-breadcrumb__separator{color:var(--color-secondary);margin:0 8px;-webkit-user-select:none;user-select:none}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBreadcrumbItem, decorators: [{
            type: Component,
            args: [{ selector: 'a[mb-breadcrumb-item]', host: {
                        class: 'mb-breadcrumbs__item'
                    }, template: "<ng-content></ng-content>\n<ng-template #separator>\n  <span class=\"mb-breadcrumb__separator\">></span>\n</ng-template>\n", styles: [":host:visited{color:var(--color-secondary)}:host:hover{color:var(--color-secondary-light)}:host:last-child{color:var(--color-text-dark)}.mb-breadcrumb__separator{color:var(--color-secondary);margin:0 8px;-webkit-user-select:none;user-select:none}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }]; }, propDecorators: { separator: [{
                type: ViewChild,
                args: ['separator', { static: true }]
            }] } });

class MbBreadcrumbs {
    ngAfterContentInit() {
        this.addSeparators(this.breadcrumbItems);
        this.breadcrumbItems.changes.subscribe(items => this.addSeparators(items));
    }
    addSeparators(items) {
        items.forEach(item => {
            if (items.last !== item) {
                item.renderSeparator();
            }
        });
    }
}
MbBreadcrumbs.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBreadcrumbs, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbBreadcrumbs.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBreadcrumbs, selector: "mb-breadcrumbs", host: { classAttribute: "mb-breadcrumbs" }, queries: [{ propertyName: "breadcrumbItems", predicate: MbBreadcrumbItem }], ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{display:block;font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBreadcrumbs, decorators: [{
            type: Component,
            args: [{ selector: 'mb-breadcrumbs', template: '<ng-content></ng-content>', host: {
                        class: 'mb-breadcrumbs'
                    }, styles: [":host{display:block;font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }], propDecorators: { breadcrumbItems: [{
                type: ContentChildren,
                args: [MbBreadcrumbItem]
            }] } });

class MbBreadcrumbsModule {
}
MbBreadcrumbsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBreadcrumbsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbBreadcrumbsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbBreadcrumbsModule, declarations: [MbBreadcrumbs, MbBreadcrumbItem], exports: [MbBreadcrumbs, MbBreadcrumbItem] });
MbBreadcrumbsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBreadcrumbsModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBreadcrumbsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbBreadcrumbs, MbBreadcrumbItem],
                    exports: [MbBreadcrumbs, MbBreadcrumbItem]
                }]
        }] });

let nextId$7 = 0;
class MbTab {
    constructor() {
        this.id = `mb-tab-${++nextId$7}`;
        this.disabled = false;
        this._active = false;
    }
    set active(value) {
        if (isDevMode()) {
            console.warn('The MbTab `active` input is deprecated. Use the `selectedTab` input in MbTabs instead.');
        }
        this._active = value;
    }
    get active() {
        return this._active;
    }
}
MbTab.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTab, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbTab.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTab, selector: "mb-tab", inputs: { id: "id", disabled: "disabled", active: "active" }, viewQueries: [{ propertyName: "content", first: true, predicate: ["tabContent"], descendants: true, static: true }, { propertyName: "title", first: true, predicate: ["tabTitle"], descendants: true, static: true }], ngImport: i0, template: `
    <ng-template #tabTitle>
      <ng-content select="mb-tab-title"></ng-content>
    </ng-template>
    <ng-template #tabContent>
      <ng-content select="mb-tab-content"></ng-content>
    </ng-template>
  `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTab, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-tab',
                    template: `
    <ng-template #tabTitle>
      <ng-content select="mb-tab-title"></ng-content>
    </ng-template>
    <ng-template #tabContent>
      <ng-content select="mb-tab-content"></ng-content>
    </ng-template>
  `
                }]
        }], propDecorators: { id: [{
                type: Input
            }], disabled: [{
                type: Input
            }], active: [{
                type: Input
            }], content: [{
                type: ViewChild,
                args: ['tabContent', { static: true }]
            }], title: [{
                type: ViewChild,
                args: ['tabTitle', { static: true }]
            }] } });

const EXTRA_PADDING = 16;
class MbTabs {
    constructor(cd) {
        this.cd = cd;
        this.selectedTab = 0;
        this.selectedTabChange = new EventEmitter();
        this.deltas = [];
        this.translate = 0;
        this.position = 0;
        this.overflowLeft = false;
        this.overflowRight = false;
        this.transitionDuration = '300ms';
        this.transform = `translateX(${this.translate}px)`;
        this.tabChanges = Subscription.EMPTY;
    }
    get hasOverflow() {
        return this.overflowLeft || this.overflowRight;
    }
    onResize() {
        this.calculateOverflow();
    }
    ngAfterContentInit() {
        let targetTab;
        if (this.selectedTab && this.selectedTab < this.tabs.length) {
            targetTab = this.tabs.toArray()[this.selectedTab];
        }
        else {
            targetTab = this.tabs.find(tab => tab.active);
        }
        this.activeTab = targetTab || this.tabs.first;
    }
    ngAfterViewInit() {
        this.calculateOverflow();
        this.tabChanges = this.tabElements.changes.subscribe(() => this.calculateOverflow());
    }
    ngOnDestroy() {
        this.tabChanges.unsubscribe();
    }
    selectTab(tab, index) {
        if (!tab.disabled) {
            this.activeTab = tab;
            this.selectedTabChange.emit({ tab, index });
        }
    }
    trackByTab(_, tab) {
        return tab.id;
    }
    slideRight() {
        if (this.position < this.deltas.length) {
            this.translate -= this.deltas[this.position++];
            this.transform = `translateX(${this.translate}px)`;
            this.overflowLeft = true;
            this.overflowRight = this.position < this.deltas.length;
        }
    }
    slideLeft() {
        if (this.position > 0) {
            this.translate += this.deltas[--this.position];
            this.transform = `translateX(${this.translate}px)`;
            this.overflowRight = true;
            this.overflowLeft = this.position > 0;
        }
    }
    calculateOverflow() {
        let size = 0;
        let overflow = false;
        const containerWidth = this.tabsContainer.nativeElement.offsetWidth;
        // pre-calculate deltas for every element outside the container
        this.tabElements.forEach((el, i) => {
            const elWidth = el.nativeElement.offsetWidth;
            size += elWidth;
            if (overflow) {
                // if there's overflow just add the tab width
                this.deltas.push(elWidth);
            }
            else if (size > containerWidth) {
                // first time add the hidden part of the tab + some space for the
                // arrow control
                this.deltas.push(size - containerWidth + EXTRA_PADDING);
                overflow = true;
            }
            // remove the EXTRA_PADDING for the last element since the arrow control
            // disappears
            if (i === this.tabElements.length - 1) {
                this.deltas[this.deltas.length - 1] -= EXTRA_PADDING;
            }
        });
        if (overflow) {
            this.overflowRight = true;
            this.cd.detectChanges();
        }
    }
}
MbTabs.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTabs, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
MbTabs.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTabs, selector: "mb-tabs", inputs: { selectedTab: "selectedTab" }, outputs: { selectedTabChange: "selectedTabChange" }, host: { listeners: { "window:resize": "onResize()" } }, queries: [{ propertyName: "tabs", predicate: MbTab }], viewQueries: [{ propertyName: "tabsContainer", first: true, predicate: ["tabsContainer"], descendants: true, static: true }, { propertyName: "tabElements", predicate: ["tabElement"], descendants: true }], ngImport: i0, template: "<div class=\"mb-tabs__container\" #tabsContainer>\n  <ul\n    class=\"mb-tabs\"\n    [ngClass]=\"{ 'mb-tabs__overflow': hasOverflow }\"\n    [style.transitionDuration]=\"transitionDuration\"\n    [style.transform]=\"transform\"\n  >\n    <li\n      #tabElement\n      *ngFor=\"let tab of tabs; index as i; trackBy: trackByTab\"\n      class=\"mb-tabs__tab\"\n      [id]=\"tab.id\"\n      [class.mb-tabs__tab--active]=\"tab === activeTab\"\n      [class.mb-tabs__tab--disabled]=\"tab.disabled\"\n      (click)=\"selectTab(tab, i)\"\n    >\n      <span class=\"mb-tabs__tab-title\">\n        <ng-container *ngTemplateOutlet=\"tab.title\"></ng-container>\n      </span>\n    </li>\n  </ul>\n  <button *ngIf=\"overflowLeft\" class=\"mb-tabs__prev\" (click)=\"slideLeft()\">\n    <span>&#10095;</span>\n  </button>\n  <button *ngIf=\"overflowRight\" class=\"mb-tabs__next\" (click)=\"slideRight()\">\n    <span>&#10095;</span>\n  </button>\n</div>\n<ng-container *ngTemplateOutlet=\"activeTab.content\"></ng-container>\n", styles: [".mb-tabs{border-bottom:1px solid var(--color-gray-500);display:flex;margin:0;padding:0;white-space:nowrap;width:100%}.mb-tabs__overflow{display:block;float:left;width:auto}.mb-tabs__container{overflow:hidden;position:relative}.mb-tabs__tab-title{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;padding:16px;text-decoration:none;text-transform:uppercase;transition:color .5s;-webkit-user-select:none;user-select:none}@media only screen and (max-width: 767px){.mb-tabs__tab-title{line-height:1.5}}.mb-tabs__tab{display:flex;list-style:none;position:relative}.mb-tabs__overflow .mb-tabs__tab{display:inline-block;height:52px;padding:16px 0}.mb-tabs__tab:after{background:transparent;bottom:0;content:\"\";height:3px;left:50%;position:absolute;transition:all .2s ease;width:0}.mb-tabs__tab--active:after{background:var(--color-secondary);left:0;width:100%}.mb-tabs__tab--active .mb-tabs__tab-title{color:var(--color-secondary)}.mb-tabs__tab:hover:not(.mb-tabs__tab--disabled){cursor:pointer;transition:all .5s}.mb-tabs__tab:hover:not(.mb-tabs__tab--disabled) .mb-tabs__tab-title{color:var(--color-secondary-light)}.mb-tabs__tab--disabled{color:var(--color-disabled)}.mb-tabs__prev,.mb-tabs__next{background:linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.6) 28%,#fff);border:none;bottom:0;cursor:pointer;font-size:20px;outline:none;position:absolute;right:0;top:0}.mb-tabs__prev{left:0;right:unset;transform:rotate(-180deg)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTabs, decorators: [{
            type: Component,
            args: [{ selector: 'mb-tabs', template: "<div class=\"mb-tabs__container\" #tabsContainer>\n  <ul\n    class=\"mb-tabs\"\n    [ngClass]=\"{ 'mb-tabs__overflow': hasOverflow }\"\n    [style.transitionDuration]=\"transitionDuration\"\n    [style.transform]=\"transform\"\n  >\n    <li\n      #tabElement\n      *ngFor=\"let tab of tabs; index as i; trackBy: trackByTab\"\n      class=\"mb-tabs__tab\"\n      [id]=\"tab.id\"\n      [class.mb-tabs__tab--active]=\"tab === activeTab\"\n      [class.mb-tabs__tab--disabled]=\"tab.disabled\"\n      (click)=\"selectTab(tab, i)\"\n    >\n      <span class=\"mb-tabs__tab-title\">\n        <ng-container *ngTemplateOutlet=\"tab.title\"></ng-container>\n      </span>\n    </li>\n  </ul>\n  <button *ngIf=\"overflowLeft\" class=\"mb-tabs__prev\" (click)=\"slideLeft()\">\n    <span>&#10095;</span>\n  </button>\n  <button *ngIf=\"overflowRight\" class=\"mb-tabs__next\" (click)=\"slideRight()\">\n    <span>&#10095;</span>\n  </button>\n</div>\n<ng-container *ngTemplateOutlet=\"activeTab.content\"></ng-container>\n", styles: [".mb-tabs{border-bottom:1px solid var(--color-gray-500);display:flex;margin:0;padding:0;white-space:nowrap;width:100%}.mb-tabs__overflow{display:block;float:left;width:auto}.mb-tabs__container{overflow:hidden;position:relative}.mb-tabs__tab-title{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;padding:16px;text-decoration:none;text-transform:uppercase;transition:color .5s;-webkit-user-select:none;user-select:none}@media only screen and (max-width: 767px){.mb-tabs__tab-title{line-height:1.5}}.mb-tabs__tab{display:flex;list-style:none;position:relative}.mb-tabs__overflow .mb-tabs__tab{display:inline-block;height:52px;padding:16px 0}.mb-tabs__tab:after{background:transparent;bottom:0;content:\"\";height:3px;left:50%;position:absolute;transition:all .2s ease;width:0}.mb-tabs__tab--active:after{background:var(--color-secondary);left:0;width:100%}.mb-tabs__tab--active .mb-tabs__tab-title{color:var(--color-secondary)}.mb-tabs__tab:hover:not(.mb-tabs__tab--disabled){cursor:pointer;transition:all .5s}.mb-tabs__tab:hover:not(.mb-tabs__tab--disabled) .mb-tabs__tab-title{color:var(--color-secondary-light)}.mb-tabs__tab--disabled{color:var(--color-disabled)}.mb-tabs__prev,.mb-tabs__next{background:linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.6) 28%,#fff);border:none;bottom:0;cursor:pointer;font-size:20px;outline:none;position:absolute;right:0;top:0}.mb-tabs__prev{left:0;right:unset;transform:rotate(-180deg)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { tabs: [{
                type: ContentChildren,
                args: [MbTab]
            }], tabsContainer: [{
                type: ViewChild,
                args: ['tabsContainer', { static: true }]
            }], tabElements: [{
                type: ViewChildren,
                args: ['tabElement']
            }], selectedTab: [{
                type: Input
            }], selectedTabChange: [{
                type: Output
            }], onResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

class MbTabSections {
}
MbTabSections.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTabSections, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbTabSections.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTabSections, selector: "mb-tab-title,mb-tab-content", ngImport: i0, template: '<ng-content></ng-content>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTabSections, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-tab-title,mb-tab-content',
                    template: '<ng-content></ng-content>'
                }]
        }] });

class MbTabsModule {
}
MbTabsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbTabsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbTabsModule, declarations: [MbTab, MbTabs, MbTabSections], imports: [CommonModule], exports: [MbTab, MbTabs, MbTabSections] });
MbTabsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTabsModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTabsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [MbTab, MbTabs, MbTabSections],
                    exports: [MbTab, MbTabs, MbTabSections]
                }]
        }] });

class MbIconModule {
}
MbIconModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbIconModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbIconModule, declarations: [MbIcon], exports: [MbIcon] });
MbIconModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbIcon],
                    exports: [MbIcon]
                }]
        }] });

/**
 * State Icons
 * Icons representing the common icon feedback states
 */
const STATE_ICONS = {
    error: aciCloseCircle,
    warning: aciExclamationCircle,
    notification: aciInfo,
    success: aciCheckCircle
};

class MbAlert {
    constructor() {
        this.dismissible = false;
        this.dismiss = new EventEmitter();
    }
    ngOnInit() {
        this.icon = STATE_ICONS[this.type];
        if (this.arrow) {
            this.arrowIconClass = `mb-alert__arrow--${this.arrow}`;
        }
    }
}
MbAlert.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAlert, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbAlert.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbAlert, selector: "mb-alert", inputs: { type: "type", arrow: "arrow", dismissible: "dismissible" }, outputs: { dismiss: "dismiss" }, host: { properties: { "class.mb-alert--error": "type === \"error\"", "class.mb-alert--warning": "type === \"warning\"", "class.mb-alert--notification": "type === \"notification\"", "class.mb-alert--success": "type === \"success\"" }, classAttribute: "mb-alert" }, ngImport: i0, template: "<div class=\"mb-alert__icon\" [ngClass]=\"arrowIconClass\">\n  <mb-icon [icon]=\"icon\"></mb-icon>\n</div>\n\n<div class=\"mb-alert__content\">\n  <ng-content select=\"mb-alert-title\"></ng-content>\n  <ng-content select=\"mb-alert-description\"></ng-content>\n</div>\n\n<button class=\"mb-alert__close\" *ngIf=\"dismissible\" (click)=\"dismiss.emit()\"></button>\n", styles: ["@charset \"UTF-8\";.mb-alert:host{background-color:var(--color-foreground);border-radius:4px;box-shadow:0 2px 2px #0000000d,0 2px 32px #0000001a;display:flex;justify-content:flex-start;min-height:48px}.mb-alert:host--notification{border:solid 1px var(--color-notification)}.mb-alert:host--notification .mb-alert__icon{background-color:var(--color-notification)}.mb-alert:host--notification .mb-alert__icon:before{color:var(--color-notification)}.mb-alert:host--success{border:solid 1px var(--color-success)}.mb-alert:host--success .mb-alert__icon{background-color:var(--color-success)}.mb-alert:host--success .mb-alert__icon:before{color:var(--color-success)}.mb-alert:host--warning{border:solid 1px var(--color-warning)}.mb-alert:host--warning .mb-alert__icon{background-color:var(--color-warning)}.mb-alert:host--warning .mb-alert__icon:before{color:var(--color-warning)}.mb-alert:host--error{border:solid 1px var(--color-error)}.mb-alert:host--error .mb-alert__icon{background-color:var(--color-error)}.mb-alert:host--error .mb-alert__icon:before{color:var(--color-error)}.mb-alert__icon{align-items:center;color:var(--color-white);display:flex;font-size:24px;justify-content:center;min-width:56px;position:relative}.mb-alert__arrow--up:before{border-left-color:transparent;border-right-color:transparent;border-style:solid;border-width:8px;content:\"\";left:50%;margin-left:-8px;position:absolute;border-top-color:transparent;top:-15px}.mb-alert__arrow--down:before{border-left-color:transparent;border-right-color:transparent;border-style:solid;border-width:8px;content:\"\";left:50%;margin-left:-8px;position:absolute;border-bottom-color:transparent;bottom:-15px}.mb-alert__content{display:flex;flex-direction:column;flex-grow:1;justify-content:center;padding:8px 16px}.mb-alert__close{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;font:inherit;outline:none;padding:0;align-self:center;cursor:pointer;margin:0 16px}.mb-alert__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}.mb-alert__close:after{font-weight:400}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAlert, decorators: [{
            type: Component,
            args: [{ selector: 'mb-alert', host: {
                        'class': 'mb-alert',
                        '[class.mb-alert--error]': 'type === "error"',
                        '[class.mb-alert--warning]': 'type === "warning"',
                        '[class.mb-alert--notification]': 'type === "notification"',
                        '[class.mb-alert--success]': 'type === "success"'
                    }, template: "<div class=\"mb-alert__icon\" [ngClass]=\"arrowIconClass\">\n  <mb-icon [icon]=\"icon\"></mb-icon>\n</div>\n\n<div class=\"mb-alert__content\">\n  <ng-content select=\"mb-alert-title\"></ng-content>\n  <ng-content select=\"mb-alert-description\"></ng-content>\n</div>\n\n<button class=\"mb-alert__close\" *ngIf=\"dismissible\" (click)=\"dismiss.emit()\"></button>\n", styles: ["@charset \"UTF-8\";.mb-alert:host{background-color:var(--color-foreground);border-radius:4px;box-shadow:0 2px 2px #0000000d,0 2px 32px #0000001a;display:flex;justify-content:flex-start;min-height:48px}.mb-alert:host--notification{border:solid 1px var(--color-notification)}.mb-alert:host--notification .mb-alert__icon{background-color:var(--color-notification)}.mb-alert:host--notification .mb-alert__icon:before{color:var(--color-notification)}.mb-alert:host--success{border:solid 1px var(--color-success)}.mb-alert:host--success .mb-alert__icon{background-color:var(--color-success)}.mb-alert:host--success .mb-alert__icon:before{color:var(--color-success)}.mb-alert:host--warning{border:solid 1px var(--color-warning)}.mb-alert:host--warning .mb-alert__icon{background-color:var(--color-warning)}.mb-alert:host--warning .mb-alert__icon:before{color:var(--color-warning)}.mb-alert:host--error{border:solid 1px var(--color-error)}.mb-alert:host--error .mb-alert__icon{background-color:var(--color-error)}.mb-alert:host--error .mb-alert__icon:before{color:var(--color-error)}.mb-alert__icon{align-items:center;color:var(--color-white);display:flex;font-size:24px;justify-content:center;min-width:56px;position:relative}.mb-alert__arrow--up:before{border-left-color:transparent;border-right-color:transparent;border-style:solid;border-width:8px;content:\"\";left:50%;margin-left:-8px;position:absolute;border-top-color:transparent;top:-15px}.mb-alert__arrow--down:before{border-left-color:transparent;border-right-color:transparent;border-style:solid;border-width:8px;content:\"\";left:50%;margin-left:-8px;position:absolute;border-bottom-color:transparent;bottom:-15px}.mb-alert__content{display:flex;flex-direction:column;flex-grow:1;justify-content:center;padding:8px 16px}.mb-alert__close{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;font:inherit;outline:none;padding:0;align-self:center;cursor:pointer;margin:0 16px}.mb-alert__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}.mb-alert__close:after{font-weight:400}\n"] }]
        }], propDecorators: { type: [{
                type: Input
            }], arrow: [{
                type: Input
            }], dismissible: [{
                type: Input
            }], dismiss: [{
                type: Output
            }] } });

class MbAlertTitle {
}
MbAlertTitle.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAlertTitle, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbAlertTitle.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbAlertTitle, selector: "mb-alert-title", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{display:block;font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAlertTitle, decorators: [{
            type: Component,
            args: [{ selector: 'mb-alert-title', template: '<ng-content></ng-content>', styles: [":host{display:block;font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }] });

class MbAlertDescription {
}
MbAlertDescription.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAlertDescription, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbAlertDescription.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbAlertDescription, selector: "mb-alert-description", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{display:block;font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAlertDescription, decorators: [{
            type: Component,
            args: [{ selector: 'mb-alert-description', template: '<ng-content></ng-content>', styles: [":host{display:block;font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }] });

class MbAlertModule {
}
MbAlertModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbAlertModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbAlertModule, declarations: [MbAlert, MbAlertTitle, MbAlertDescription], imports: [CommonModule, MbIconModule], exports: [MbAlert, MbAlertTitle, MbAlertDescription] });
MbAlertModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAlertModule, imports: [CommonModule, MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAlertModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MbIconModule],
                    declarations: [MbAlert, MbAlertTitle, MbAlertDescription],
                    exports: [MbAlert, MbAlertTitle, MbAlertDescription]
                }]
        }] });

function getWindow(node) {
    if (node.toString() !== '[object Window]') {
        const ownerDocument = node.ownerDocument;
        return ownerDocument ? ownerDocument.defaultView : window;
    }
    return node;
}

function getComputedStyle(element) {
    return getWindow(element).getComputedStyle(element);
}

const DEFAULT_DURATION = '300ms';
const UNSET = 'unset';
class MbCarouselAbs {
    constructor() {
        this.showDots = true;
        this.showButtons = true;
        this.innerDot = ''; // inner
        this.outerDot = ''; // outer //strough
        this.currentItem = new EventEmitter();
        this.next$ = new Subject();
        this.prev$ = new Subject();
        this.change$ = new Subject();
        this.changeValue = new Change();
        this.isTransforming = false;
        this.translate = 0;
        this.itemQuantity = 0;
        this.foo = '';
        this.bar = '';
        this.arrowRight = aciArrowRight;
        this.arrowLeft = aciArrowLeft;
        this.transitionDuration = UNSET;
        this.transform = `translateX(${this.translate}px)`;
        this.currentIndex = 0;
    }
    onResize() {
        this.resetSlider();
    }
    configTranslate(currentIndex = null, transition = true, translate = null) {
        const size = translate || this.sizeTranslate(currentIndex);
        this.transitionDuration = transition ? DEFAULT_DURATION : '0s';
        this.translate = -size;
        this.transform = `translateX(${this.translate}px)`;
        if (transition) {
            this.lockControlsWhileAnimating();
        }
    }
    sizeTranslate(currentIndex) {
        return (currentIndex !== null ? currentIndex : this.currentIndex) * this.sliderWidth;
    }
    lockControlsWhileAnimating() {
        this.isTransforming = true;
        if (this.lastListenerRef) {
            this.slider.nativeElement.removeEventListener('transitionend', this.lastListenerRef);
        }
        const listenerHandler = () => {
            this.isTransforming = false;
        };
        this.slider.nativeElement.addEventListener('transitionend', listenerHandler);
        this.lastListenerRef = listenerHandler;
        this.emitIndex();
    }
    get sliderWidth() {
        return this.slider.nativeElement.offsetWidth;
    }
    resetSlider() { }
    prevSlide() {
        const prev = -1;
        this.moveSlideRegular(prev, this.currentIndex > 0);
    }
    nextSlide() {
        const next = 1;
        this.moveSlideRegular(next, this.currentIndex < this.itemQuantity - 1);
    }
    moveSlideRegular(delta, validMove) {
        if (!this.isTransforming && validMove) {
            delta > 0 ? this.incrementCurrent() : this.decrementCurrent();
            this.configTranslate();
        }
    }
    incrementCurrent() {
        this.currentIndex = this.getCircularIndex(this.currentIndex + 1);
        this.next$.next(this.currentIndex);
        this.changeValue.setNewValue(this.currentIndex, true);
        this.change$.next(this.changeValue);
    }
    decrementCurrent() {
        this.currentIndex = this.getCircularIndex(this.currentIndex - 1);
        this.prev$.next(this.currentIndex);
        this.changeValue.setNewValue(this.currentIndex, false);
        this.change$.next(this.changeValue);
    }
    getCircularIndex(index) {
        return ((index % this.itemQuantity) + this.itemQuantity) % this.itemQuantity;
    }
    emitIndex() {
        this.currentItem.emit(this.currentIndex);
    }
    get index() {
        return this.currentIndex;
    }
    get next() {
        return this.next$.asObservable();
    }
    get prev() {
        return this.prev$.asObservable();
    }
    get change() {
        return this.change$.asObservable();
    }
    cssVar(name) {
        if ('transparent' === name) {
            return name;
        }
        const color = `--color-${name}`;
        return getComputedStyle(document.body).getPropertyValue(color);
    }
    loadColorDots() {
        this.foo = this.cssVar(this.outerDot);
        this.bar = this.cssVar(this.innerDot);
    }
}
MbCarouselAbs.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarouselAbs, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MbCarouselAbs.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbCarouselAbs, inputs: { showDots: "showDots", showButtons: "showButtons", innerDot: "innerDot", outerDot: "outerDot" }, outputs: { currentItem: "currentItem" }, host: { listeners: { "window:resize": "onResize()" } }, viewQueries: [{ propertyName: "slider", first: true, predicate: ["slider"], descendants: true, static: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarouselAbs, decorators: [{
            type: Directive
        }], propDecorators: { slider: [{
                type: ViewChild,
                args: ['slider', { static: true }]
            }], showDots: [{
                type: Input
            }], showButtons: [{
                type: Input
            }], innerDot: [{
                type: Input
            }], outerDot: [{
                type: Input
            }], currentItem: [{
                type: Output
            }], onResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });
class Change {
    constructor() {
        this.oldValue = 0;
        this.newValue = 0;
    }
    setNewValue(current, next) {
        this.event = next ? 'next' : 'prev';
        this.oldValue = this.newValue;
        this.newValue = current;
    }
    getValue() {
        return {
            oldValue: this.oldValue,
            newValue: this.newValue,
            event: this.event,
        };
    }
}

class MbCarousel extends MbCarouselAbs {
    set images(value) {
        this._images = value ? [...value] : [];
    }
    get images() {
        return this._images;
    }
    constructor() {
        super();
        this._images = [];
        this.infinity = true;
        this.showNumbers = true;
        this.showPhotos = false;
        this.moveNext = false;
        this.height = '100%';
        this.width = '100%';
        this.dots = [];
    }
    ngOnInit() {
        this.dots = this.images.slice();
        if (this.images.length > 0) {
            this.itemQuantity = this.images.length;
            if (this.infinity && this.images.length > 0) {
                this.moveEndToStart();
                this.initInfinityCarousel();
                const intervalId = setInterval(() => {
                    this.configTranslate(1, false);
                }, 50);
                setTimeout(() => {
                    clearInterval(intervalId);
                }, 1000);
            }
        }
        if (this.innerDot === '') {
            this.innerDot = 'secondary-contrast';
        }
        if (this.outerDot === '') {
            this.outerDot = 'transparent';
        }
    }
    ngAfterViewInit() {
        this.loadColorDots();
    }
    sizeTranslate(currentIndex) {
        return (currentIndex !== null ? currentIndex : this.currentIndex) * this.sliderWidth;
    }
    resetSlider() {
        if (this.images.length > 1) {
            this.infinity ? this.initInfinityCarousel() : this.configTranslate(null, false);
        }
    }
    prevSlide() {
        const prev = -1;
        if (this.infinity) {
            this.moveSlideInifity(prev);
        }
        else {
            this.moveSlideRegular(prev, this.currentIndex > 0);
        }
    }
    nextSlide() {
        const next = 1;
        if (this.infinity) {
            this.moveSlideInifity(next);
        }
        else {
            this.moveSlideRegular(next, this.currentIndex < this.itemQuantity - 1);
        }
    }
    moveSlideInifity(delta) {
        if (!this.isTransforming) {
            if (delta > 0) {
                this.incrementCurrent();
                this.moveNext = true;
                this.configTranslate(2);
            }
            else {
                this.decrementCurrent();
                this.moveNext = false;
                this.configTranslate(0);
            }
        }
    }
    lockControlsWhileAnimating() {
        this.isTransforming = true;
        if (this.lastListenerRef) {
            this.slider.nativeElement.removeEventListener('transitionend', this.lastListenerRef);
        }
        const listenerHandler = () => {
            this.isTransforming = false;
            if (this.infinity) {
                this.initInfinityCarousel();
                this.moveNext ? this.moveStartToEnd() : this.moveEndToStart();
            }
        };
        this.slider.nativeElement.addEventListener('transitionend', listenerHandler);
        this.lastListenerRef = listenerHandler;
        this.emitIndex();
    }
    initInfinityCarousel() {
        this.configTranslate(1, false);
    }
    moveEndToStart() {
        const endItem = this.images.pop();
        if (endItem) {
            this.images.unshift(endItem);
        }
    }
    moveStartToEnd() {
        const startItem = this.images.shift();
        if (startItem) {
            this.images.push(startItem);
        }
    }
}
MbCarousel.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarousel, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCarousel.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCarousel, selector: "mb-carousel", inputs: { images: "images", infinity: "infinity", showNumbers: "showNumbers", showPhotos: "showPhotos" }, host: { classAttribute: "mb-carousel" }, providers: [{ provide: MbCarouselAbs, useExisting: MbCarousel }], usesInheritance: true, ngImport: i0, template: "<div class=\"mb-carousel__number-indicator\" *ngIf=\"showNumbers\">\n  {{ index + 1 }} / {{ images.length }}\n</div>\n\n<div\n  class=\"mb-carousel__slider-wrapper\"\n  [style.transitionDuration]=\"transitionDuration\"\n  [style.transform]=\"transform\"\n  #slider\n>\n  <div *ngFor=\"let image of images; index as i\" class=\"mb-carousel__slides\">\n    <div class=\"mb-carousel__ctn-image\">\n      <img class=\"mb-carousel__image\" [src]=\"image\" />\n    </div>\n  </div>\n</div>\n\n<button\n  *ngIf=\"images.length > 1 && showButtons\"\n  class=\"mb-carousel__btn-prev\"\n  (click)=\"prevSlide()\"\n>\n  <div class=\"mb-carousel__prev\">\n    <mb-icon [icon]=\"arrowLeft\"></mb-icon>\n  </div>\n</button>\n<button\n  *ngIf=\"images.length > 1 && showButtons\"\n  class=\"mb-carousel__btn-next\"\n  (click)=\"nextSlide()\"\n>\n  <div class=\"mb-carousel__next\">\n    <mb-icon [icon]=\"arrowRight\"></mb-icon>\n  </div>\n</button>\n\n<div class=\"mb-carousel__dots\" *ngIf=\"images.length > 1 && showDots && images.length < 7\">\n  <span\n    *ngFor=\"let dot of dots; index as i\"\n    class=\"mb-carousel__dot\"\n    [style.border-color]=\"foo\"\n    [class.mb-carousel__dot--active]=\"i === currentIndex\"\n  >\n    <div class=\"mb-carousel__dot-secondary\" [style.background-color]=\"bar\"></div>\n  </span>\n</div>\n\n<div\n  class=\"mb-carousel__swiper\"\n  (swipeleft)=\"nextSlide()\"\n  (swiperight)=\"prevSlide()\"\n></div>\n", styles: [".mb-carousel:host{background-color:var(--color-disabled);display:block;height:100%;max-width:100%;overflow-x:hidden;position:relative;width:100%}.mb-carousel__slider-wrapper{box-sizing:content-box;display:flex;height:100%;position:relative;transition-property:transform;z-index:1}.mb-carousel__swiper{height:100%;inset:0;position:absolute;width:100%;z-index:2}.mb-carousel__number-indicator{font-size:.75rem;font-weight:var(--font-weight-semibold);line-height:1.5;background-color:var(--color-primary-contrast);border-radius:10px;bottom:10px;color:var(--color-text-dark-500);font-size:12px;padding:8px 12px;position:absolute;right:10px;z-index:2}@media only screen and (max-width: 767px){.mb-carousel__number-indicator{line-height:1.5}}.mb-carousel__btn-next,.mb-carousel__btn-prev{align-items:center;background-color:transparent;border:none;border-radius:50%;cursor:none;display:flex;height:48px;justify-content:center;left:8px;margin-top:-12px;opacity:0;outline:none;position:absolute;text-decoration:none;top:50%;transition:box-shadow .2s ease,transform .25s ease!important;-webkit-user-select:none;user-select:none;width:48px;z-index:3}.mb-carousel__btn-next{left:unset;right:8px}.mb-carousel__next,.mb-carousel__prev{align-items:center;background:var(--color-white);border:none;border-radius:50%;color:var(--color-secondary);display:flex;height:24px;justify-content:center;padding:0;width:24px;z-index:4}.mb-carousel__next .mb-icon svg,.mb-carousel__prev .mb-icon svg{height:2em;width:1em}.mb-carousel__next:hover,.mb-carousel__prev:hover{border-color:#ddd;box-shadow:transparent 0 0 0 1px,transparent 0 0 0 4px,2px 0 16px #0000001f!important;transform:scale(1.05)!important}.mb-carousel:host:hover .mb-carousel__btn-next,.mb-carousel:host:hover .mb-carousel__btn-prev{cursor:pointer;opacity:1;transition:opacity .8s ease!important}.mb-carousel__dots{align-items:center;bottom:0;display:flex;justify-content:center;margin-bottom:8px;position:absolute;width:100%;z-index:3}.mb-carousel__dot{background-color:#fff;border-radius:50%;display:inline-block;height:7px;margin:0 4px;width:7px}.mb-carousel__ctn-image{align-items:center;display:flex;height:100%;justify-content:center;overflow:hidden;width:100%}.mb-carousel__image{height:100%;object-fit:contain;vertical-align:middle;width:100%}.mb-carousel__slides{flex-shrink:0;height:100%;position:relative;width:100%}.mb-carousel__show-photos{font-size:.75rem;font-weight:var(--font-weight-semibold);line-height:1.5;background-color:var(--color-primary-contrast);border-radius:10px;color:var(--color-text-dark);font-size:12px;padding:8px 12px;position:absolute;right:10px;top:10px;z-index:2}@media only screen and (max-width: 767px){.mb-carousel__show-photos{line-height:1.5}}.mb-carousel__dot{align-items:center;background-color:transparent;border:2px solid var(--color-gray-700);display:flex;height:13px;justify-content:center;opacity:.5;transition:.3s;width:13px}.mb-carousel__dot--active{opacity:1;transform:scale(1.4)}.mb-carousel__dot-secondary{background-color:var(--color-gray-800);border-radius:50%;height:7px;width:7px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarousel, decorators: [{
            type: Component,
            args: [{ selector: 'mb-carousel', providers: [{ provide: MbCarouselAbs, useExisting: MbCarousel }], host: {
                        class: 'mb-carousel',
                    }, template: "<div class=\"mb-carousel__number-indicator\" *ngIf=\"showNumbers\">\n  {{ index + 1 }} / {{ images.length }}\n</div>\n\n<div\n  class=\"mb-carousel__slider-wrapper\"\n  [style.transitionDuration]=\"transitionDuration\"\n  [style.transform]=\"transform\"\n  #slider\n>\n  <div *ngFor=\"let image of images; index as i\" class=\"mb-carousel__slides\">\n    <div class=\"mb-carousel__ctn-image\">\n      <img class=\"mb-carousel__image\" [src]=\"image\" />\n    </div>\n  </div>\n</div>\n\n<button\n  *ngIf=\"images.length > 1 && showButtons\"\n  class=\"mb-carousel__btn-prev\"\n  (click)=\"prevSlide()\"\n>\n  <div class=\"mb-carousel__prev\">\n    <mb-icon [icon]=\"arrowLeft\"></mb-icon>\n  </div>\n</button>\n<button\n  *ngIf=\"images.length > 1 && showButtons\"\n  class=\"mb-carousel__btn-next\"\n  (click)=\"nextSlide()\"\n>\n  <div class=\"mb-carousel__next\">\n    <mb-icon [icon]=\"arrowRight\"></mb-icon>\n  </div>\n</button>\n\n<div class=\"mb-carousel__dots\" *ngIf=\"images.length > 1 && showDots && images.length < 7\">\n  <span\n    *ngFor=\"let dot of dots; index as i\"\n    class=\"mb-carousel__dot\"\n    [style.border-color]=\"foo\"\n    [class.mb-carousel__dot--active]=\"i === currentIndex\"\n  >\n    <div class=\"mb-carousel__dot-secondary\" [style.background-color]=\"bar\"></div>\n  </span>\n</div>\n\n<div\n  class=\"mb-carousel__swiper\"\n  (swipeleft)=\"nextSlide()\"\n  (swiperight)=\"prevSlide()\"\n></div>\n", styles: [".mb-carousel:host{background-color:var(--color-disabled);display:block;height:100%;max-width:100%;overflow-x:hidden;position:relative;width:100%}.mb-carousel__slider-wrapper{box-sizing:content-box;display:flex;height:100%;position:relative;transition-property:transform;z-index:1}.mb-carousel__swiper{height:100%;inset:0;position:absolute;width:100%;z-index:2}.mb-carousel__number-indicator{font-size:.75rem;font-weight:var(--font-weight-semibold);line-height:1.5;background-color:var(--color-primary-contrast);border-radius:10px;bottom:10px;color:var(--color-text-dark-500);font-size:12px;padding:8px 12px;position:absolute;right:10px;z-index:2}@media only screen and (max-width: 767px){.mb-carousel__number-indicator{line-height:1.5}}.mb-carousel__btn-next,.mb-carousel__btn-prev{align-items:center;background-color:transparent;border:none;border-radius:50%;cursor:none;display:flex;height:48px;justify-content:center;left:8px;margin-top:-12px;opacity:0;outline:none;position:absolute;text-decoration:none;top:50%;transition:box-shadow .2s ease,transform .25s ease!important;-webkit-user-select:none;user-select:none;width:48px;z-index:3}.mb-carousel__btn-next{left:unset;right:8px}.mb-carousel__next,.mb-carousel__prev{align-items:center;background:var(--color-white);border:none;border-radius:50%;color:var(--color-secondary);display:flex;height:24px;justify-content:center;padding:0;width:24px;z-index:4}.mb-carousel__next .mb-icon svg,.mb-carousel__prev .mb-icon svg{height:2em;width:1em}.mb-carousel__next:hover,.mb-carousel__prev:hover{border-color:#ddd;box-shadow:transparent 0 0 0 1px,transparent 0 0 0 4px,2px 0 16px #0000001f!important;transform:scale(1.05)!important}.mb-carousel:host:hover .mb-carousel__btn-next,.mb-carousel:host:hover .mb-carousel__btn-prev{cursor:pointer;opacity:1;transition:opacity .8s ease!important}.mb-carousel__dots{align-items:center;bottom:0;display:flex;justify-content:center;margin-bottom:8px;position:absolute;width:100%;z-index:3}.mb-carousel__dot{background-color:#fff;border-radius:50%;display:inline-block;height:7px;margin:0 4px;width:7px}.mb-carousel__ctn-image{align-items:center;display:flex;height:100%;justify-content:center;overflow:hidden;width:100%}.mb-carousel__image{height:100%;object-fit:contain;vertical-align:middle;width:100%}.mb-carousel__slides{flex-shrink:0;height:100%;position:relative;width:100%}.mb-carousel__show-photos{font-size:.75rem;font-weight:var(--font-weight-semibold);line-height:1.5;background-color:var(--color-primary-contrast);border-radius:10px;color:var(--color-text-dark);font-size:12px;padding:8px 12px;position:absolute;right:10px;top:10px;z-index:2}@media only screen and (max-width: 767px){.mb-carousel__show-photos{line-height:1.5}}.mb-carousel__dot{align-items:center;background-color:transparent;border:2px solid var(--color-gray-700);display:flex;height:13px;justify-content:center;opacity:.5;transition:.3s;width:13px}.mb-carousel__dot--active{opacity:1;transform:scale(1.4)}.mb-carousel__dot-secondary{background-color:var(--color-gray-800);border-radius:50%;height:7px;width:7px}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { images: [{
                type: Input
            }], infinity: [{
                type: Input
            }], showNumbers: [{
                type: Input
            }], showPhotos: [{
                type: Input
            }] } });

class MbCarouselItem {
    constructor() {
        this.scale = false;
    }
    get width() {
        return this.carouselItem.nativeElement.offsetWidth;
    }
}
MbCarouselItem.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarouselItem, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCarouselItem.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCarouselItem, selector: "mb-carousel-item", inputs: { scale: "scale" }, viewQueries: [{ propertyName: "carouselItem", first: true, predicate: ["carouselItem"], descendants: true }], ngImport: i0, template: "<div #carouselItem class=\"ctn-item\" [style.scale]=\"scale ? 1.06 : 1\">\n  <ng-content> </ng-content>\n</div>\n", styles: [":host{height:100%}.ctn-item{transition:.3s}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarouselItem, decorators: [{
            type: Component,
            args: [{ selector: 'mb-carousel-item', template: "<div #carouselItem class=\"ctn-item\" [style.scale]=\"scale ? 1.06 : 1\">\n  <ng-content> </ng-content>\n</div>\n", styles: [":host{height:100%}.ctn-item{transition:.3s}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { carouselItem: [{
                type: ViewChild,
                args: ['carouselItem']
            }], scale: [{
                type: Input
            }] } });

class SlidesService {
    constructor() { }
    createDotsCenter(items, gap, sliderWidth, paddingSides) {
        let incrementalWidth = -this.diffSpacingCenter(items, sliderWidth, paddingSides, 0);
        const slides = [];
        for (let i = 0; i < items.length; i++) {
            const widthCustom = items[i].width + gap;
            slides.push(new SliderPagination(i, i, incrementalWidth));
            incrementalWidth += widthCustom;
        }
        return slides;
    }
    /**
     * Busca el tamaño justo para que la ultima posicion sea coincidente con el tamaño de sliderWidth.
     * Mientras esto no se cumple, el tamaño de los elementos se almacena en el array.
     * El sobrante o coincidente con el tamaño de sliderWidth se alamacena en la ultima posicion del array.
     */
    createDotsRegular(items, gap, sliderWidth, paddingSides) {
        let sliderWidthCustom = 0;
        let incrementalWidth = 0;
        let widthRest = 0;
        let custom = 0;
        const widthCtn = this.getTotalWidthCustoms(items, gap);
        const slides = [];
        if (items.length > 0) {
            for (let i = 0; i < items.length; i++) {
                const widthCustom = items[i].width + gap;
                if (incrementalWidth > widthCtn - sliderWidth) {
                    widthRest += widthCustom;
                }
                else {
                    slides.push(new SliderPagination(i, i, incrementalWidth));
                    incrementalWidth += widthCustom;
                    custom = i + 1;
                }
            }
            if (slides.length > 0) {
                if (widthRest !== 0) {
                    sliderWidthCustom = widthRest - sliderWidth;
                    slides.push(new SliderPagination(custom, items.length - 1, incrementalWidth + sliderWidthCustom + paddingSides));
                }
            }
            else {
                slides.push(new SliderPagination(0, items.length - 1, 0));
            }
        }
        return slides;
    }
    /**
     * Busca armar paginas del mismo tamaño del contenedor
     * Cuando la suma de la cantidad de elementos cumple con este objetivo se almacena en el array.
     * El sobrante se incorpora en la ultima posicion del array.
     */
    createPagination(items, gap, sliderWidth, paddingSides) {
        let paginationWidth = 0;
        let custom = 0;
        let i = 0;
        let incrementalWidth = 0;
        const slides = [];
        for (i = 0; i < items.length; i++) {
            const widthCustom = items[i].width + gap;
            if (paginationWidth + widthCustom < sliderWidth) {
                paginationWidth += widthCustom;
            }
            else {
                slides.push(new SliderPagination(custom, i - 1, incrementalWidth));
                incrementalWidth += paginationWidth;
                custom = i;
                paginationWidth = widthCustom;
            }
        }
        if (paginationWidth !== 0) {
            slides.push(new SliderPagination(custom, i - 1, incrementalWidth - (sliderWidth - paginationWidth) + paddingSides));
        }
        return slides;
    }
    diffSpacingCenter(items, sliderWidth, paddingSides, index) {
        const sliderWidth2 = sliderWidth / 2;
        const widthItem = items[index].width / 2;
        return sliderWidth2 - widthItem - paddingSides / 2;
    }
    getTotalWidthCustoms(items, gap) {
        let widthCtn = 0;
        items.forEach(element => {
            widthCtn += element.width + gap;
        });
        return widthCtn;
    }
}
SlidesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SlidesService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SlidesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SlidesService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SlidesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });
class SliderPagination {
    constructor(firstElement, lastElement, transition) {
        this.firstElement = firstElement;
        this.lastElement = lastElement;
        this.transition = transition;
    }
}

class MbCarouselCustomComponent extends MbCarouselAbs {
    constructor(cd, slidesServices) {
        super();
        this.cd = cd;
        this.slidesServices = slidesServices;
        this.gap = 20;
        this.center = false;
        this.pagination = false;
        this.dots = [];
        this.carouselItems = [];
    }
    ngOnInit() {
        this.transitionDuration = '0s';
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    ngAfterViewInit() {
        this.loadCustomItems();
        this.initCarousel();
        if (this.center || this.dots.length > 1) {
            this.configTranslate(null, false, this.getSizeActuallyTranslate());
        }
        this.configDots();
        this.cd.detectChanges();
        this.subscription = this.itemsCustom.changes.subscribe(() => {
            this.loadCustomItems();
            this.resetSlider();
        });
    }
    loadCustomItems() {
        if (this.itemsCustom !== undefined) {
            if (this.itemsCustom.length > 0) {
                this.carouselItems = this.itemsCustom.toArray();
                this.itemQuantity = this.carouselItems.length;
            }
        }
    }
    configDots() {
        if (this.innerDot === '') {
            this.innerDot = 'gray-800';
        }
        if (this.outerDot === '') {
            this.outerDot = 'gray-700';
        }
        this.loadColorDots();
    }
    moveSlideRegular(delta, validMove) {
        if (!this.isTransforming && validMove && this.validMove(delta)) {
            delta > 0 ? this.incrementCurrent() : this.decrementCurrent();
            if (this.itemsCustom.length > 0) {
                this.configTranslate(null, true, this.getSizeActuallyTranslate());
            }
        }
    }
    resetSlider() {
        this.initCarousel();
        if (this.currentIndex > this.totalPages - 1) {
            this.currentIndex = this.totalPages - 1;
        }
        this.configTranslate(null, false, this.getSizeActuallyTranslate());
    }
    initCarousel() {
        if (this.center) {
            this.dots = this.slidesServices.createDotsCenter(this.carouselItems, this.gap, this.sliderWidth, this.paddingSides);
        }
        else {
            if (this.pagination) {
                this.dots = this.slidesServices.createPagination(this.carouselItems, this.gap, this.sliderWidth, this.paddingSides);
            }
            else {
                this.dots = this.slidesServices.createDotsRegular(this.carouselItems, this.gap, this.sliderWidth, this.paddingSides);
            }
        }
    }
    getSizeActuallyTranslate() {
        return this.dots[this.currentIndex].transition;
    }
    get totalPages() {
        return this.dots.length;
    }
    validMove(delta) {
        if (this.dots.length > 0) {
            return delta < 0 ? this.currentIndex > 0 : this.currentIndex < this.totalPages - 1;
        }
        return true;
    }
    get paddingSides() {
        const ctnSlides = this.slider.nativeElement.querySelector('div');
        const paddingLeft = getComputedStyle(ctnSlides).paddingLeft;
        const paddingRight = getComputedStyle(ctnSlides).paddingRight;
        return (this.changePixelsToNumber(paddingLeft) + this.changePixelsToNumber(paddingRight));
    }
    changePixelsToNumber(pixels) {
        return parseInt(pixels, 10);
    }
}
MbCarouselCustomComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarouselCustomComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: SlidesService }], target: i0.ɵɵFactoryTarget.Component });
MbCarouselCustomComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCarouselCustomComponent, selector: "mb-carousel-custom", inputs: { gap: "gap", center: "center", pagination: "pagination" }, queries: [{ propertyName: "itemsCustom", predicate: MbCarouselItem }], usesInheritance: true, ngImport: i0, template: "<div class=\"mb-carousel\" (swipeleft)=\"nextSlide()\" (swiperight)=\"prevSlide()\">\n  <div\n    class=\"mb-carousel__slider-wrapper\"\n    [style.transitionDuration]=\"transitionDuration\"\n    [style.transform]=\"transform\"\n    #slider\n  >\n    <div class=\"mb-carousel__custom-item\" [style.gap.px]=\"gap\">\n      <ng-content #itemsCustom select=\"mb-carousel-item\"> </ng-content>\n    </div>\n  </div>\n\n  <button\n    *ngIf=\"itemsCustom.length > 0 && showButtons && currentIndex > 0\"\n    class=\"mb-carousel__btn-prev\"\n    (click)=\"prevSlide()\"\n  >\n    <div class=\"mb-carousel__prev\">\n      <mb-icon [icon]=\"arrowLeft\"></mb-icon>\n    </div>\n  </button>\n  <button\n    *ngIf=\"itemsCustom.length > 0 && showButtons && currentIndex < this.totalPages - 1\"\n    class=\"mb-carousel__btn-next\"\n    (click)=\"nextSlide()\"\n  >\n    <div class=\"mb-carousel__next\">\n      <mb-icon [icon]=\"arrowRight\"></mb-icon>\n    </div>\n  </button>\n\n  <div\n    class=\"mb-carousel__dots\"\n    [style.margin-top]=\"itemsCustom.length > 1 ? '20px' : 'none'\"\n    *ngIf=\"itemsCustom.length > 0 && showDots\"\n  >\n    <span\n      *ngFor=\"let dot of dots; index as i\"\n      class=\"mb-carousel__dot\"\n      [style.border-color]=\"foo\"\n      [class.mb-carousel__dot--active]=\"i === currentIndex\"\n    >\n      <div class=\"mb-carousel__dot-secondary\" [style.background-color]=\"bar\"></div>\n    </span>\n  </div>\n</div>\n", styles: [".mb-carousel:host{background-color:var(--color-disabled);display:block;height:100%;max-width:100%;overflow-x:hidden;position:relative;width:100%}.mb-carousel__slider-wrapper{box-sizing:content-box;display:flex;height:100%;position:relative;transition-property:transform;z-index:1}.mb-carousel__swiper{height:100%;inset:0;position:absolute;width:100%;z-index:2}.mb-carousel__number-indicator{font-size:.75rem;font-weight:var(--font-weight-semibold);line-height:1.5;background-color:var(--color-primary-contrast);border-radius:10px;bottom:10px;color:var(--color-text-dark-500);font-size:12px;padding:8px 12px;position:absolute;right:10px;z-index:2}@media only screen and (max-width: 767px){.mb-carousel__number-indicator{line-height:1.5}}.mb-carousel__btn-next,.mb-carousel__btn-prev{align-items:center;background-color:transparent;border:none;border-radius:50%;cursor:none;display:flex;height:48px;justify-content:center;left:8px;margin-top:-12px;opacity:0;outline:none;position:absolute;text-decoration:none;top:50%;transition:box-shadow .2s ease,transform .25s ease!important;-webkit-user-select:none;user-select:none;width:48px;z-index:3}.mb-carousel__btn-next{left:unset;right:8px}.mb-carousel__next,.mb-carousel__prev{align-items:center;background:var(--color-white);border:none;border-radius:50%;color:var(--color-secondary);display:flex;height:24px;justify-content:center;padding:0;width:24px;z-index:4}.mb-carousel__next .mb-icon svg,.mb-carousel__prev .mb-icon svg{height:2em;width:1em}.mb-carousel:host:hover .mb-carousel__btn-next,.mb-carousel:host:hover .mb-carousel__btn-prev{cursor:pointer;opacity:1;transition:opacity .8s ease!important}.mb-carousel__dots{align-items:center;bottom:0;display:flex;justify-content:center;margin-bottom:8px;position:absolute;width:100%;z-index:3}.mb-carousel__dot{background-color:#fff;border-radius:50%;display:inline-block;height:7px;margin:0 4px;width:7px}:host{display:block;height:100%;max-width:100%;overflow-x:hidden;position:relative;width:100%}:host:hover .mb-carousel__btn-prev,:host:hover .mb-carousel__btn-next{cursor:pointer;opacity:1;transition:opacity .8s ease!important}.mb-carousel__custom-item{align-items:center;display:flex;gap:20px;padding:20px 10px}.mb-carousel__dots{position:relative}.mb-carousel__dot{align-items:center;background-color:transparent;border:2px solid var(--color-gray-700);display:flex;height:13px;justify-content:center;opacity:.5;transition:.3s;width:13px}.mb-carousel__dot--active{opacity:1;transform:scale(1.3)}.mb-carousel__dot-secondary{background-color:var(--color-gray-800);border-radius:50%;height:7px;width:7px}.mb-carousel__next:hover,.mb-carousel__prev:hover{border-color:#ddd;box-shadow:transparent 0 0 0 1px,transparent 0 0 0 4px,2px 0 16px #0000001f!important;transform:scale(1.05)!important}\n"], dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarouselCustomComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mb-carousel-custom', template: "<div class=\"mb-carousel\" (swipeleft)=\"nextSlide()\" (swiperight)=\"prevSlide()\">\n  <div\n    class=\"mb-carousel__slider-wrapper\"\n    [style.transitionDuration]=\"transitionDuration\"\n    [style.transform]=\"transform\"\n    #slider\n  >\n    <div class=\"mb-carousel__custom-item\" [style.gap.px]=\"gap\">\n      <ng-content #itemsCustom select=\"mb-carousel-item\"> </ng-content>\n    </div>\n  </div>\n\n  <button\n    *ngIf=\"itemsCustom.length > 0 && showButtons && currentIndex > 0\"\n    class=\"mb-carousel__btn-prev\"\n    (click)=\"prevSlide()\"\n  >\n    <div class=\"mb-carousel__prev\">\n      <mb-icon [icon]=\"arrowLeft\"></mb-icon>\n    </div>\n  </button>\n  <button\n    *ngIf=\"itemsCustom.length > 0 && showButtons && currentIndex < this.totalPages - 1\"\n    class=\"mb-carousel__btn-next\"\n    (click)=\"nextSlide()\"\n  >\n    <div class=\"mb-carousel__next\">\n      <mb-icon [icon]=\"arrowRight\"></mb-icon>\n    </div>\n  </button>\n\n  <div\n    class=\"mb-carousel__dots\"\n    [style.margin-top]=\"itemsCustom.length > 1 ? '20px' : 'none'\"\n    *ngIf=\"itemsCustom.length > 0 && showDots\"\n  >\n    <span\n      *ngFor=\"let dot of dots; index as i\"\n      class=\"mb-carousel__dot\"\n      [style.border-color]=\"foo\"\n      [class.mb-carousel__dot--active]=\"i === currentIndex\"\n    >\n      <div class=\"mb-carousel__dot-secondary\" [style.background-color]=\"bar\"></div>\n    </span>\n  </div>\n</div>\n", styles: [".mb-carousel:host{background-color:var(--color-disabled);display:block;height:100%;max-width:100%;overflow-x:hidden;position:relative;width:100%}.mb-carousel__slider-wrapper{box-sizing:content-box;display:flex;height:100%;position:relative;transition-property:transform;z-index:1}.mb-carousel__swiper{height:100%;inset:0;position:absolute;width:100%;z-index:2}.mb-carousel__number-indicator{font-size:.75rem;font-weight:var(--font-weight-semibold);line-height:1.5;background-color:var(--color-primary-contrast);border-radius:10px;bottom:10px;color:var(--color-text-dark-500);font-size:12px;padding:8px 12px;position:absolute;right:10px;z-index:2}@media only screen and (max-width: 767px){.mb-carousel__number-indicator{line-height:1.5}}.mb-carousel__btn-next,.mb-carousel__btn-prev{align-items:center;background-color:transparent;border:none;border-radius:50%;cursor:none;display:flex;height:48px;justify-content:center;left:8px;margin-top:-12px;opacity:0;outline:none;position:absolute;text-decoration:none;top:50%;transition:box-shadow .2s ease,transform .25s ease!important;-webkit-user-select:none;user-select:none;width:48px;z-index:3}.mb-carousel__btn-next{left:unset;right:8px}.mb-carousel__next,.mb-carousel__prev{align-items:center;background:var(--color-white);border:none;border-radius:50%;color:var(--color-secondary);display:flex;height:24px;justify-content:center;padding:0;width:24px;z-index:4}.mb-carousel__next .mb-icon svg,.mb-carousel__prev .mb-icon svg{height:2em;width:1em}.mb-carousel:host:hover .mb-carousel__btn-next,.mb-carousel:host:hover .mb-carousel__btn-prev{cursor:pointer;opacity:1;transition:opacity .8s ease!important}.mb-carousel__dots{align-items:center;bottom:0;display:flex;justify-content:center;margin-bottom:8px;position:absolute;width:100%;z-index:3}.mb-carousel__dot{background-color:#fff;border-radius:50%;display:inline-block;height:7px;margin:0 4px;width:7px}:host{display:block;height:100%;max-width:100%;overflow-x:hidden;position:relative;width:100%}:host:hover .mb-carousel__btn-prev,:host:hover .mb-carousel__btn-next{cursor:pointer;opacity:1;transition:opacity .8s ease!important}.mb-carousel__custom-item{align-items:center;display:flex;gap:20px;padding:20px 10px}.mb-carousel__dots{position:relative}.mb-carousel__dot{align-items:center;background-color:transparent;border:2px solid var(--color-gray-700);display:flex;height:13px;justify-content:center;opacity:.5;transition:.3s;width:13px}.mb-carousel__dot--active{opacity:1;transform:scale(1.3)}.mb-carousel__dot-secondary{background-color:var(--color-gray-800);border-radius:50%;height:7px;width:7px}.mb-carousel__next:hover,.mb-carousel__prev:hover{border-color:#ddd;box-shadow:transparent 0 0 0 1px,transparent 0 0 0 4px,2px 0 16px #0000001f!important;transform:scale(1.05)!important}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: SlidesService }]; }, propDecorators: { itemsCustom: [{
                type: ContentChildren,
                args: [MbCarouselItem]
            }], gap: [{
                type: Input
            }], center: [{
                type: Input
            }], pagination: [{
                type: Input
            }] } });

class MbCarouselModule {
}
MbCarouselModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarouselModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbCarouselModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbCarouselModule, declarations: [MbCarousel, MbCarouselItem, MbCarouselCustomComponent], imports: [CommonModule, MbIconModule], exports: [MbCarousel, MbCarouselItem, MbCarouselCustomComponent] });
MbCarouselModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarouselModule, imports: [CommonModule, MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCarouselModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbCarousel, MbCarouselItem, MbCarouselCustomComponent],
                    exports: [MbCarousel, MbCarouselItem, MbCarouselCustomComponent],
                    imports: [CommonModule, MbIconModule]
                }]
        }] });

const MAX_PAGES_TO_SHOW = 5;
class MbPaginator {
    constructor() {
        this.currentPage = 1;
        this.pageChange = new EventEmitter();
        this.prevPageIcon = aciArrowLeft;
        this.nextPageIcon = aciArrowRight;
        this.firstPageIcon = aciDoubleArrowLeft;
        this.lastPageIcon = aciDoubleArrowRight;
    }
    ngOnInit() {
        if (!this.totalEntries) {
            throw new Error('The "totalEntries" input is missing');
        }
        if (!this.pageSize) {
            throw new Error('The "pageSize" input is missing');
        }
    }
    ngOnChanges(changes) {
        if (changes.totalEntries && !changes.totalEntries.firstChange) {
            this.goToPage(1);
        }
    }
    /**
     * Get the max page number.
     */
    get maxPage() {
        return Math.ceil(this.totalEntries / this.pageSize);
    }
    /**
     * Return the pages to be rendered.
     */
    get visiblePages() {
        return this.getPages();
    }
    /**
     * Navigate to the given page.
     */
    goToPage(targetPage) {
        if (targetPage < 1) {
            targetPage = 1;
        }
        if (targetPage > this.maxPage) {
            targetPage = this.maxPage;
        }
        if (targetPage !== this.currentPage) {
            this.currentPage = targetPage;
            this.pageChange.emit(targetPage);
        }
    }
    /**
     * Navigate to the first page.
     */
    firstPage() {
        this.goToPage(1);
    }
    /**
     * Navigate to the last page.
     */
    lastPage() {
        this.goToPage(this.maxPage);
    }
    /**
     * Navigate to the previous page.
     */
    prevPage() {
        this.goToPage(this.currentPage - 1);
    }
    /**
     * Navigate to the next page.
     */
    nextPage() {
        this.goToPage(this.currentPage + 1);
    }
    /**
     * Return the rendered pages.
     */
    getPages() {
        let startPage;
        let endPage;
        if (this.maxPage <= MAX_PAGES_TO_SHOW) {
            // Show all pages
            startPage = 1;
            endPage = this.maxPage;
        }
        else {
            // Calculate start and end pages
            const maxPagesBeforeCurrentPage = Math.floor(MAX_PAGES_TO_SHOW / 2);
            const maxPagesAfterCurrentPage = Math.ceil(MAX_PAGES_TO_SHOW / 2) - 1;
            if (this.currentPage <= maxPagesBeforeCurrentPage) {
                // Current page is near the start
                startPage = 1;
                endPage = MAX_PAGES_TO_SHOW;
            }
            else if (this.currentPage + maxPagesAfterCurrentPage >= this.maxPage) {
                // Current page is near the end
                startPage = this.maxPage - MAX_PAGES_TO_SHOW + 1;
                endPage = this.maxPage;
            }
            else {
                // Current page is in the middle
                startPage = this.currentPage - maxPagesBeforeCurrentPage;
                endPage = this.currentPage + maxPagesAfterCurrentPage;
            }
        }
        // Return an array of pages necessary for the ngFor
        return Array.from(Array(endPage + 1 - startPage).keys()).map(i => startPage + i);
    }
    trackByItem(index, item) {
        return item;
    }
}
MbPaginator.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPaginator, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbPaginator.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbPaginator, selector: "mb-paginator", inputs: { currentPage: "currentPage", totalEntries: "totalEntries", pageSize: "pageSize" }, outputs: { pageChange: "pageChange" }, host: { classAttribute: "mb-paginator" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"mb-paginator__content\">\n  <button\n    (click)=\"firstPage()\"\n    [disabled]=\"currentPage === 1\"\n    class=\"mb-paginator__page-btn mb-paginator__first-page\"\n  >\n    <mb-icon [icon]=\"firstPageIcon\"></mb-icon>\n  </button>\n\n  <button\n    (click)=\"prevPage()\"\n    [disabled]=\"currentPage === 1\"\n    class=\"mb-paginator__page-btn mb-paginator__previous-page\"\n  >\n    <mb-icon [icon]=\"prevPageIcon\"></mb-icon>\n  </button>\n\n  <div class=\"mb-paginator__page-indicator\">\n    <strong class=\"mb-paginator__current-page\">{{ currentPage }}</strong>\n    <span [hidden]=\"maxPage === 1\">&nbsp;/&nbsp;{{ maxPage }}</span>\n  </div>\n\n  <button\n    *ngFor=\"let page of visiblePages; trackBy: trackByItem\"\n    class=\"mb-paginator__page-btn mb-paginator__numbered-page\"\n    [class.mb-paginator__page-btn--active]=\"page === currentPage\"\n    (click)=\"goToPage(page)\"\n  >\n    {{ page }}\n  </button>\n\n  <button\n    (click)=\"nextPage()\"\n    [disabled]=\"currentPage === maxPage\"\n    class=\"mb-paginator__page-btn mb-paginator__next-page\"\n  >\n    <mb-icon [icon]=\"nextPageIcon\"></mb-icon>\n  </button>\n\n  <button\n    (click)=\"lastPage()\"\n    [disabled]=\"currentPage === maxPage\"\n    class=\"mb-paginator__page-btn mb-paginator__last-page\"\n  >\n    <mb-icon [icon]=\"lastPageIcon\"></mb-icon>\n  </button>\n</div>\n", styles: [".mb-paginator:host{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5;display:block;line-height:24px}@media only screen and (max-width: 767px){.mb-paginator:host{line-height:1.5}}.mb-paginator__content{background:var(--color-foreground);border-radius:3px;box-sizing:border-box;display:inline-block;height:40px;overflow:hidden;width:auto}.mb-paginator__page-btn{background:#fff;border:1px solid var(--color-border);border-right-style:none;color:var(--color-secondary);cursor:pointer;display:inline-block;font-size:16px;height:40px;line-height:40px;outline:none;padding:0;text-align:center;text-decoration:none;vertical-align:middle;width:40px}.mb-paginator__page-btn:hover{color:var(--color-secondary-light)}@media only screen and (min-width: 1152px){.mb-paginator__page-btn{cursor:pointer;height:40px;width:40px}.mb-paginator__page-btn--active{background-color:var(--color-secondary);border:1px solid var(--color-secondary);color:var(--color-secondary-contrast);font-weight:700;z-index:10}.mb-paginator__page-btn--active:hover{color:var(--color-secondary-contrast)}.mb-paginator__page-btn--active+.mb-paginator__page-btn{border-left:none}.mb-paginator__page-btn:first-child:disabled,.mb-paginator__page-btn:last-child:disabled{color:var(--color-disabled)}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-paginator__page-btn{cursor:pointer;height:40px;width:40px}.mb-paginator__page-btn--active{background-color:var(--color-secondary);border:1px solid var(--color-secondary);color:var(--color-secondary-contrast);font-weight:700;z-index:10}.mb-paginator__page-btn--active:hover{color:var(--color-secondary-contrast)}.mb-paginator__page-btn--active+.mb-paginator__page-btn{border-left:none}.mb-paginator__page-btn:first-child:disabled,.mb-paginator__page-btn:last-child:disabled{color:var(--color-disabled)}}.mb-paginator__first-page{border-bottom-left-radius:3px;border-top-left-radius:3px}.mb-paginator__last-page{border-bottom-right-radius:3px;border-right:1px solid var(--color-border);border-top-right-radius:3px}.mb-paginator__first-page,.mb-paginator__last-page,.mb-paginator__previous-page,.mb-paginator__next-page{font-size:11px;font-weight:300;line-height:40px}.mb-paginator__first-page .mb-icon,.mb-paginator__last-page .mb-icon,.mb-paginator__previous-page .mb-icon,.mb-paginator__next-page .mb-icon{line-height:40px}.mb-paginator__first-page:disabled,.mb-paginator__last-page:disabled,.mb-paginator__previous-page:disabled,.mb-paginator__next-page:disabled{color:var(--color-disabled)}.mb-paginator__numbered-page{display:none}@media only screen and (min-width: 1152px){.mb-paginator__numbered-page{display:inline-block}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-paginator__numbered-page{display:inline-block}}.mb-paginator__page-indicator{border-bottom:1px solid var(--color-border);border-left:1px solid var(--color-border);border-top:1px solid var(--color-border);color:var(--color-secondary);display:inline-block;font-size:16px;height:40px;line-height:40px;text-align:center;vertical-align:middle;width:64px}@media only screen and (min-width: 1152px){.mb-paginator__page-indicator{display:none}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-paginator__page-indicator{display:none}}.mb-paginator__current-page{font-weight:700}\n"], dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPaginator, decorators: [{
            type: Component,
            args: [{ selector: 'mb-paginator', host: {
                        class: 'mb-paginator'
                    }, template: "<div class=\"mb-paginator__content\">\n  <button\n    (click)=\"firstPage()\"\n    [disabled]=\"currentPage === 1\"\n    class=\"mb-paginator__page-btn mb-paginator__first-page\"\n  >\n    <mb-icon [icon]=\"firstPageIcon\"></mb-icon>\n  </button>\n\n  <button\n    (click)=\"prevPage()\"\n    [disabled]=\"currentPage === 1\"\n    class=\"mb-paginator__page-btn mb-paginator__previous-page\"\n  >\n    <mb-icon [icon]=\"prevPageIcon\"></mb-icon>\n  </button>\n\n  <div class=\"mb-paginator__page-indicator\">\n    <strong class=\"mb-paginator__current-page\">{{ currentPage }}</strong>\n    <span [hidden]=\"maxPage === 1\">&nbsp;/&nbsp;{{ maxPage }}</span>\n  </div>\n\n  <button\n    *ngFor=\"let page of visiblePages; trackBy: trackByItem\"\n    class=\"mb-paginator__page-btn mb-paginator__numbered-page\"\n    [class.mb-paginator__page-btn--active]=\"page === currentPage\"\n    (click)=\"goToPage(page)\"\n  >\n    {{ page }}\n  </button>\n\n  <button\n    (click)=\"nextPage()\"\n    [disabled]=\"currentPage === maxPage\"\n    class=\"mb-paginator__page-btn mb-paginator__next-page\"\n  >\n    <mb-icon [icon]=\"nextPageIcon\"></mb-icon>\n  </button>\n\n  <button\n    (click)=\"lastPage()\"\n    [disabled]=\"currentPage === maxPage\"\n    class=\"mb-paginator__page-btn mb-paginator__last-page\"\n  >\n    <mb-icon [icon]=\"lastPageIcon\"></mb-icon>\n  </button>\n</div>\n", styles: [".mb-paginator:host{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5;display:block;line-height:24px}@media only screen and (max-width: 767px){.mb-paginator:host{line-height:1.5}}.mb-paginator__content{background:var(--color-foreground);border-radius:3px;box-sizing:border-box;display:inline-block;height:40px;overflow:hidden;width:auto}.mb-paginator__page-btn{background:#fff;border:1px solid var(--color-border);border-right-style:none;color:var(--color-secondary);cursor:pointer;display:inline-block;font-size:16px;height:40px;line-height:40px;outline:none;padding:0;text-align:center;text-decoration:none;vertical-align:middle;width:40px}.mb-paginator__page-btn:hover{color:var(--color-secondary-light)}@media only screen and (min-width: 1152px){.mb-paginator__page-btn{cursor:pointer;height:40px;width:40px}.mb-paginator__page-btn--active{background-color:var(--color-secondary);border:1px solid var(--color-secondary);color:var(--color-secondary-contrast);font-weight:700;z-index:10}.mb-paginator__page-btn--active:hover{color:var(--color-secondary-contrast)}.mb-paginator__page-btn--active+.mb-paginator__page-btn{border-left:none}.mb-paginator__page-btn:first-child:disabled,.mb-paginator__page-btn:last-child:disabled{color:var(--color-disabled)}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-paginator__page-btn{cursor:pointer;height:40px;width:40px}.mb-paginator__page-btn--active{background-color:var(--color-secondary);border:1px solid var(--color-secondary);color:var(--color-secondary-contrast);font-weight:700;z-index:10}.mb-paginator__page-btn--active:hover{color:var(--color-secondary-contrast)}.mb-paginator__page-btn--active+.mb-paginator__page-btn{border-left:none}.mb-paginator__page-btn:first-child:disabled,.mb-paginator__page-btn:last-child:disabled{color:var(--color-disabled)}}.mb-paginator__first-page{border-bottom-left-radius:3px;border-top-left-radius:3px}.mb-paginator__last-page{border-bottom-right-radius:3px;border-right:1px solid var(--color-border);border-top-right-radius:3px}.mb-paginator__first-page,.mb-paginator__last-page,.mb-paginator__previous-page,.mb-paginator__next-page{font-size:11px;font-weight:300;line-height:40px}.mb-paginator__first-page .mb-icon,.mb-paginator__last-page .mb-icon,.mb-paginator__previous-page .mb-icon,.mb-paginator__next-page .mb-icon{line-height:40px}.mb-paginator__first-page:disabled,.mb-paginator__last-page:disabled,.mb-paginator__previous-page:disabled,.mb-paginator__next-page:disabled{color:var(--color-disabled)}.mb-paginator__numbered-page{display:none}@media only screen and (min-width: 1152px){.mb-paginator__numbered-page{display:inline-block}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-paginator__numbered-page{display:inline-block}}.mb-paginator__page-indicator{border-bottom:1px solid var(--color-border);border-left:1px solid var(--color-border);border-top:1px solid var(--color-border);color:var(--color-secondary);display:inline-block;font-size:16px;height:40px;line-height:40px;text-align:center;vertical-align:middle;width:64px}@media only screen and (min-width: 1152px){.mb-paginator__page-indicator{display:none}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-paginator__page-indicator{display:none}}.mb-paginator__current-page{font-weight:700}\n"] }]
        }], propDecorators: { currentPage: [{
                type: Input
            }], totalEntries: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], pageChange: [{
                type: Output
            }] } });

class MbPaginatorModule {
}
MbPaginatorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPaginatorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbPaginatorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbPaginatorModule, declarations: [MbPaginator], imports: [CommonModule, MbIconModule], exports: [MbPaginator] });
MbPaginatorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPaginatorModule, imports: [CommonModule, MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPaginatorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbPaginator],
                    imports: [CommonModule, MbIconModule],
                    exports: [MbPaginator]
                }]
        }] });

const CHECKBOX_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MbCheckbox),
    multi: true
};
let nextId$6 = 0;
class MbCheckbox {
    constructor() {
        this.disabled = false;
        this.value = false;
        this.id = `mb-checkbox-${++nextId$6}`;
        this.tabIndex = 0;
        this.valueChange = new EventEmitter();
    }
    onChange() {
        this.value = !this.value;
        this.valueChange.emit(this.value);
        if (this.onControlValueChange) {
            this.onControlValueChange(this.value);
        }
    }
    writeValue(checked) {
        this.value = checked;
    }
    registerOnChange(fn) {
        this.onControlValueChange = fn;
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    registerOnTouched() { }
}
MbCheckbox.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCheckbox, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCheckbox.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCheckbox, selector: "mb-checkbox", inputs: { disabled: "disabled", value: "value", id: "id", name: "name", tabIndex: "tabIndex" }, outputs: { valueChange: "valueChange" }, host: { properties: { "attr.tabindex": "-1" }, classAttribute: "mb-checkbox" }, providers: [CHECKBOX_CONTROL_VALUE_ACCESSOR], ngImport: i0, template: "<label [attr.for]=\"id\" class=\"mb-checkbox__container\">\n  <input\n    class=\"mb-checkbox__input\"\n    type=\"checkbox\"\n    [tabIndex]=\"tabIndex\"\n    [id]=\"id\"\n    [checked]=\"value\"\n    [disabled]=\"disabled\"\n    [attr.name]=\"name\"\n    (change)=\"onChange()\"\n  />\n  <span class=\"mb-checkbox__mark\">\n    <span class=\"mb-checkbox__hover\"></span>\n  </span>\n  <span class=\"mb-checkbox__content\">\n    <ng-content></ng-content>\n  </span>\n</label>\n", styles: [".mb-checkbox__hover{transform:scale(0)}@media (hover: none){.mb-checkbox__hover{display:none}}.mb-checkbox:host{display:inline-flex}.mb-checkbox:host:focus-within,.mb-checkbox:host:hover{outline:none}.mb-checkbox:host:focus-within .mb-checkbox__hover,.mb-checkbox:host:hover .mb-checkbox__hover{background-color:var(--color-text-disabled);border-radius:20px;height:36px;left:-10px;opacity:.1;pointer-events:none;position:absolute;top:-10px;transform:scale(1);transition:transform .1s ease-in-out;width:36px}.mb-checkbox:host:focus-within .mb-checkbox__input:checked~.mb-checkbox__mark .mb-checkbox__hover,.mb-checkbox:host:hover .mb-checkbox__input:checked~.mb-checkbox__mark .mb-checkbox__hover{background-color:var(--color-secondary);border-radius:20px;height:36px;left:-8px;opacity:.1;pointer-events:none;position:absolute;top:-8px;transform:scale(1);transition:transform .1s ease-in-out;width:36px}.mb-checkbox:host:focus-within .mb-checkbox__input:disabled~.mb-checkbox__mark .mb-checkbox__hover,.mb-checkbox:host:hover .mb-checkbox__input:disabled~.mb-checkbox__mark .mb-checkbox__hover{display:none}.mb-checkbox__container{-webkit-tap-highlight-color:rgba(0,0,0,0);align-items:center;cursor:pointer;display:inline-flex;-webkit-user-select:none;user-select:none}.mb-checkbox__mark{background-color:var(--color-foreground);border:2px solid var(--color-gray-600);border-radius:2px;height:20px;min-width:20px;position:relative}.mb-checkbox__mark:after{border:solid white;border-width:0 2px 2px 0;content:\"\";display:none;height:10px;left:8px;position:absolute;top:4px;transform:rotate(45deg);width:5px}.mb-checkbox__content{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;margin-left:12px}@media only screen and (max-width: 767px){.mb-checkbox__content{line-height:1.5}}.mb-checkbox__input{height:0;opacity:0;width:0}.mb-checkbox__input:checked~.mb-checkbox__mark{background-color:var(--color-secondary);border:none}.mb-checkbox__input:checked~.mb-checkbox__mark:after{display:block}.mb-checkbox__input:disabled~.mb-checkbox__content{pointer-events:none}.mb-checkbox__input:disabled~.mb-checkbox__mark{background-color:var(--color-gray-500);border-color:var(--color-disabled);pointer-events:none}.mb-checkbox__input:disabled~.mb-checkbox__mark:after{display:none}.mb-checkbox__input:checked:disabled~.mb-checkbox__mark{background-color:var(--color-gray-500);border:2px solid var(--color-disabled)}.mb-checkbox__input:checked:disabled~.mb-checkbox__mark:after{border-color:var(--color-disabled);display:block;left:6px;top:2px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCheckbox, decorators: [{
            type: Component,
            args: [{ selector: 'mb-checkbox', host: {
                        '[attr.tabindex]': '-1',
                        'class': 'mb-checkbox'
                    }, providers: [CHECKBOX_CONTROL_VALUE_ACCESSOR], template: "<label [attr.for]=\"id\" class=\"mb-checkbox__container\">\n  <input\n    class=\"mb-checkbox__input\"\n    type=\"checkbox\"\n    [tabIndex]=\"tabIndex\"\n    [id]=\"id\"\n    [checked]=\"value\"\n    [disabled]=\"disabled\"\n    [attr.name]=\"name\"\n    (change)=\"onChange()\"\n  />\n  <span class=\"mb-checkbox__mark\">\n    <span class=\"mb-checkbox__hover\"></span>\n  </span>\n  <span class=\"mb-checkbox__content\">\n    <ng-content></ng-content>\n  </span>\n</label>\n", styles: [".mb-checkbox__hover{transform:scale(0)}@media (hover: none){.mb-checkbox__hover{display:none}}.mb-checkbox:host{display:inline-flex}.mb-checkbox:host:focus-within,.mb-checkbox:host:hover{outline:none}.mb-checkbox:host:focus-within .mb-checkbox__hover,.mb-checkbox:host:hover .mb-checkbox__hover{background-color:var(--color-text-disabled);border-radius:20px;height:36px;left:-10px;opacity:.1;pointer-events:none;position:absolute;top:-10px;transform:scale(1);transition:transform .1s ease-in-out;width:36px}.mb-checkbox:host:focus-within .mb-checkbox__input:checked~.mb-checkbox__mark .mb-checkbox__hover,.mb-checkbox:host:hover .mb-checkbox__input:checked~.mb-checkbox__mark .mb-checkbox__hover{background-color:var(--color-secondary);border-radius:20px;height:36px;left:-8px;opacity:.1;pointer-events:none;position:absolute;top:-8px;transform:scale(1);transition:transform .1s ease-in-out;width:36px}.mb-checkbox:host:focus-within .mb-checkbox__input:disabled~.mb-checkbox__mark .mb-checkbox__hover,.mb-checkbox:host:hover .mb-checkbox__input:disabled~.mb-checkbox__mark .mb-checkbox__hover{display:none}.mb-checkbox__container{-webkit-tap-highlight-color:rgba(0,0,0,0);align-items:center;cursor:pointer;display:inline-flex;-webkit-user-select:none;user-select:none}.mb-checkbox__mark{background-color:var(--color-foreground);border:2px solid var(--color-gray-600);border-radius:2px;height:20px;min-width:20px;position:relative}.mb-checkbox__mark:after{border:solid white;border-width:0 2px 2px 0;content:\"\";display:none;height:10px;left:8px;position:absolute;top:4px;transform:rotate(45deg);width:5px}.mb-checkbox__content{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;margin-left:12px}@media only screen and (max-width: 767px){.mb-checkbox__content{line-height:1.5}}.mb-checkbox__input{height:0;opacity:0;width:0}.mb-checkbox__input:checked~.mb-checkbox__mark{background-color:var(--color-secondary);border:none}.mb-checkbox__input:checked~.mb-checkbox__mark:after{display:block}.mb-checkbox__input:disabled~.mb-checkbox__content{pointer-events:none}.mb-checkbox__input:disabled~.mb-checkbox__mark{background-color:var(--color-gray-500);border-color:var(--color-disabled);pointer-events:none}.mb-checkbox__input:disabled~.mb-checkbox__mark:after{display:none}.mb-checkbox__input:checked:disabled~.mb-checkbox__mark{background-color:var(--color-gray-500);border:2px solid var(--color-disabled)}.mb-checkbox__input:checked:disabled~.mb-checkbox__mark:after{border-color:var(--color-disabled);display:block;left:6px;top:2px}\n"] }]
        }], propDecorators: { disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], id: [{
                type: Input
            }], name: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class MbCheckboxModule {
}
MbCheckboxModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbCheckboxModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbCheckboxModule, declarations: [MbCheckbox], exports: [MbCheckbox] });
MbCheckboxModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCheckboxModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbCheckbox],
                    exports: [MbCheckbox]
                }]
        }] });

const SWITCH_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MbSwitch),
    multi: true
};
let nextId$5 = 0;
class MbSwitch {
    constructor() {
        this.disabled = false;
        this.value = false;
        this.id = `mb-switch-${++nextId$5}`;
        this.tabIndex = 0;
        this.valueChange = new EventEmitter();
    }
    onChange() {
        this.value = !this.value;
        this.valueChange.emit(this.value);
        if (this.onControlValueChange) {
            this.onControlValueChange(this.value);
        }
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onControlValueChange = fn;
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    registerOnTouched() { }
}
MbSwitch.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSwitch, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbSwitch.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbSwitch, selector: "mb-switch", inputs: { disabled: "disabled", value: "value", id: "id", name: "name", tabIndex: "tabIndex" }, outputs: { valueChange: "valueChange" }, host: { properties: { "attr.tabindex": "-1" } }, providers: [SWITCH_CONTROL_VALUE_ACCESSOR], ngImport: i0, template: "<label [attr.for]=\"id\" class=\"mb-switch\">\n  <input\n    class=\"mb-switch__input\"\n    type=\"checkbox\"\n    [tabIndex]=\"tabIndex\"\n    [id]=\"id\"\n    [checked]=\"value\"\n    [disabled]=\"disabled\"\n    [attr.name]=\"name\"\n    (change)=\"onChange()\"\n  />\n  <span class=\"mb-switch__slider\"></span>\n  <span class=\"mb-switch__hover\"></span>\n  <span class=\"mb-switch__content\">\n    <ng-content></ng-content>\n  </span>\n</label>\n", styles: [":host:focus-within,:host:hover{outline:none}:host:focus-within .mb-switch__hover,:host:hover .mb-switch__hover{background-color:var(--color-text-disabled);border-radius:20px;height:40px;left:-8px;opacity:.1;pointer-events:none;position:absolute;top:-8px;transform:scale(1);transition:transform .1s ease-in-out;width:40px}:host:focus-within .mb-switch__input:checked~.mb-switch__hover,:host:hover .mb-switch__input:checked~.mb-switch__hover{background-color:var(--color-secondary);border-radius:20px;height:40px;left:8px;opacity:.1;pointer-events:none;position:absolute;top:-8px;transform:scale(1);transition:transform .1s ease-in-out;width:40px}.mb-switch{-webkit-tap-highlight-color:rgba(0,0,0,0);cursor:pointer;display:inline-block;height:24px;min-width:40px;position:relative;-webkit-user-select:none;user-select:none}.mb-switch__input{height:0;opacity:0;width:0}.mb-switch__hover{transform:scale(0)}@media (hover: none){.mb-switch__hover{display:none}}.mb-switch__slider{background-color:var(--color-gray-700);border-radius:34px;height:18px;position:absolute;top:2px;transition:.4s;width:40px}.mb-switch__slider:before{background-color:var(--color-gray-500);border-image-source:linear-gradient(to bottom,rgba(255,255,255,.12),rgba(255,255,255,.06) 20%,rgba(255,255,255,0));border-radius:50%;box-shadow:0 1px 1px #0000003d,0 0 1px #0000001f;content:\"\";height:24px;position:absolute;top:-3px;transition:.4s;width:24px}.mb-switch__input:focus~.mb-switch__slider{box-shadow:0 0 1px var(--color-secondary-light)}.mb-switch__input:checked~.mb-switch__slider{background-color:var(--color-secondary-light)}.mb-switch__input:checked~.mb-switch__slider:before{background-color:var(--color-secondary);transform:translate(16px)}.mb-switch__input:checked~.mb-switch__hover{background-color:var(--color-secondary)}.mb-switch__input:disabled~.mb-switch__hover{display:none}.mb-switch__input:disabled~.mb-switch__slider{background-color:#9fa2a226;pointer-events:none}.mb-switch__input:disabled~.mb-switch__slider:before{background-color:var(--color-gray-500)}.mb-switch__input:checked:disabled~.mb-switch__slider{background-color:#9fa2a226}.mb-switch__input:checked:disabled~.mb-switch__slider:before{background-color:var(--color-disabled)}.mb-switch__content{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;left:52px;position:relative}@media only screen and (max-width: 767px){.mb-switch__content{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSwitch, decorators: [{
            type: Component,
            args: [{ selector: 'mb-switch', host: {
                        '[attr.tabindex]': '-1'
                    }, providers: [SWITCH_CONTROL_VALUE_ACCESSOR], template: "<label [attr.for]=\"id\" class=\"mb-switch\">\n  <input\n    class=\"mb-switch__input\"\n    type=\"checkbox\"\n    [tabIndex]=\"tabIndex\"\n    [id]=\"id\"\n    [checked]=\"value\"\n    [disabled]=\"disabled\"\n    [attr.name]=\"name\"\n    (change)=\"onChange()\"\n  />\n  <span class=\"mb-switch__slider\"></span>\n  <span class=\"mb-switch__hover\"></span>\n  <span class=\"mb-switch__content\">\n    <ng-content></ng-content>\n  </span>\n</label>\n", styles: [":host:focus-within,:host:hover{outline:none}:host:focus-within .mb-switch__hover,:host:hover .mb-switch__hover{background-color:var(--color-text-disabled);border-radius:20px;height:40px;left:-8px;opacity:.1;pointer-events:none;position:absolute;top:-8px;transform:scale(1);transition:transform .1s ease-in-out;width:40px}:host:focus-within .mb-switch__input:checked~.mb-switch__hover,:host:hover .mb-switch__input:checked~.mb-switch__hover{background-color:var(--color-secondary);border-radius:20px;height:40px;left:8px;opacity:.1;pointer-events:none;position:absolute;top:-8px;transform:scale(1);transition:transform .1s ease-in-out;width:40px}.mb-switch{-webkit-tap-highlight-color:rgba(0,0,0,0);cursor:pointer;display:inline-block;height:24px;min-width:40px;position:relative;-webkit-user-select:none;user-select:none}.mb-switch__input{height:0;opacity:0;width:0}.mb-switch__hover{transform:scale(0)}@media (hover: none){.mb-switch__hover{display:none}}.mb-switch__slider{background-color:var(--color-gray-700);border-radius:34px;height:18px;position:absolute;top:2px;transition:.4s;width:40px}.mb-switch__slider:before{background-color:var(--color-gray-500);border-image-source:linear-gradient(to bottom,rgba(255,255,255,.12),rgba(255,255,255,.06) 20%,rgba(255,255,255,0));border-radius:50%;box-shadow:0 1px 1px #0000003d,0 0 1px #0000001f;content:\"\";height:24px;position:absolute;top:-3px;transition:.4s;width:24px}.mb-switch__input:focus~.mb-switch__slider{box-shadow:0 0 1px var(--color-secondary-light)}.mb-switch__input:checked~.mb-switch__slider{background-color:var(--color-secondary-light)}.mb-switch__input:checked~.mb-switch__slider:before{background-color:var(--color-secondary);transform:translate(16px)}.mb-switch__input:checked~.mb-switch__hover{background-color:var(--color-secondary)}.mb-switch__input:disabled~.mb-switch__hover{display:none}.mb-switch__input:disabled~.mb-switch__slider{background-color:#9fa2a226;pointer-events:none}.mb-switch__input:disabled~.mb-switch__slider:before{background-color:var(--color-gray-500)}.mb-switch__input:checked:disabled~.mb-switch__slider{background-color:#9fa2a226}.mb-switch__input:checked:disabled~.mb-switch__slider:before{background-color:var(--color-disabled)}.mb-switch__content{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;left:52px;position:relative}@media only screen and (max-width: 767px){.mb-switch__content{line-height:1.5}}\n"] }]
        }], propDecorators: { disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], id: [{
                type: Input
            }], name: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class MbSwitchModule {
}
MbSwitchModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSwitchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbSwitchModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbSwitchModule, declarations: [MbSwitch], exports: [MbSwitch] });
MbSwitchModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSwitchModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSwitchModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbSwitch],
                    exports: [MbSwitch]
                }]
        }] });

class MbCard {
    constructor() {
        this.elevation = 'none';
    }
}
MbCard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCard, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCard.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCard, selector: "mb-card", inputs: { elevation: "elevation" }, host: { properties: { "class.mb-card--elevation-hover": "elevation === \"hover\"", "class.mb-card--elevation-always": "elevation === \"always\"" }, classAttribute: "mb-card" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{background-color:var(--color-foreground);border:solid 1px var(--color-border);border-radius:12px;display:block;overflow:hidden}:host.mb-card--elevation-hover{transition:box-shadow .5s}:host.mb-card--elevation-hover:hover{box-shadow:0 2px 30px 1px #3030301a}:host.mb-card--elevation-always{box-shadow:0 2px 8px 1px #30303026}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCard, decorators: [{
            type: Component,
            args: [{ selector: 'mb-card', template: '<ng-content></ng-content>', host: {
                        'class': 'mb-card',
                        '[class.mb-card--elevation-hover]': 'elevation === "hover"',
                        '[class.mb-card--elevation-always]': 'elevation === "always"'
                    }, styles: [":host{background-color:var(--color-foreground);border:solid 1px var(--color-border);border-radius:12px;display:block;overflow:hidden}:host.mb-card--elevation-hover{transition:box-shadow .5s}:host.mb-card--elevation-hover:hover{box-shadow:0 2px 30px 1px #3030301a}:host.mb-card--elevation-always{box-shadow:0 2px 8px 1px #30303026}\n"] }]
        }], propDecorators: { elevation: [{
                type: Input
            }] } });

class MbCardModule {
}
MbCardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbCardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbCardModule, declarations: [MbCard], exports: [MbCard] });
MbCardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCardModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbCard],
                    exports: [MbCard]
                }]
        }] });

let nextId$4 = 0;
const RADIO_GROUP_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MbRadioGroup),
    multi: true
};
class MbRadioGroup {
    constructor() {
        this.disabled = false;
        this.name = `mb-radio-group-${++nextId$4}`;
        this.valueChange = new EventEmitter();
    }
    get value() {
        return this._value || '';
    }
    set value(value) {
        this._value = value;
        this.valueChange.emit(value);
        if (this._onChange) {
            this._onChange(value);
        }
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched() { }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
}
MbRadioGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRadioGroup, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbRadioGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbRadioGroup, selector: "mb-radio-group", inputs: { disabled: "disabled", name: "name", value: "value" }, outputs: { valueChange: "valueChange" }, providers: [RADIO_GROUP_CONTROL_VALUE_ACCESSOR], ngImport: i0, template: '<ng-content></ng-content>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRadioGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-radio-group',
                    template: '<ng-content></ng-content>',
                    providers: [RADIO_GROUP_CONTROL_VALUE_ACCESSOR]
                }]
        }], propDecorators: { disabled: [{
                type: Input
            }], name: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

let nextId$3 = 0;
class MbRadioButton {
    constructor(radioGroup) {
        this.radioGroup = radioGroup;
        this.id = `mb-radio-${++nextId$3}`;
        this.disabled = false;
        this.tabIndex = 0;
    }
    get name() {
        return this.radioGroup.name;
    }
    get isChecked() {
        return this.value === this.radioGroup.value;
    }
    get isDisabled() {
        return this.disabled || this.radioGroup.disabled;
    }
    ngOnInit() {
        if (!this.value) {
            throw new Error('A value input must be provided to the MbRadioButton');
        }
    }
    onChange() {
        this.radioGroup.value = this.value;
    }
}
MbRadioButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRadioButton, deps: [{ token: MbRadioGroup, optional: true }], target: i0.ɵɵFactoryTarget.Component });
MbRadioButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbRadioButton, selector: "mb-radio", inputs: { id: "id", disabled: "disabled", value: "value", tabIndex: "tabIndex" }, host: { properties: { "attr.tabindex": "-1" } }, ngImport: i0, template: "<label [attr.for]=\"id\" class=\"mb-radio\">\n  <input\n    class=\"mb-radio__input\"\n    type=\"radio\"\n    [id]=\"id\"\n    [tabIndex]=\"tabIndex\"\n    [checked]=\"isChecked\"\n    [disabled]=\"isDisabled\"\n    [attr.name]=\"name\"\n    [attr.value]=\"value\"\n    (click)=\"onChange()\"\n  />\n  <span class=\"mb-radio__mark\">\n    <span class=\"mb-radio__hover\"></span>\n  </span>\n  <span class=\"mb-radio__content\">\n    <ng-content></ng-content>\n  </span>\n</label>\n", styles: [":host{outline:none}:host:focus-within .mb-radio__hover,:host:hover .mb-radio__hover{background-color:var(--color-text-disabled);border-radius:20px;height:36px;left:-10px;opacity:.1;pointer-events:none;position:absolute;top:-10px;transform:scale(1);transition:transform .1s ease-in-out;width:36px}:host:focus-within .mb-radio__input:checked~.mb-radio__mark .mb-radio__hover,:host:hover .mb-radio__input:checked~.mb-radio__mark .mb-radio__hover{background-color:var(--color-secondary);border-radius:20px;height:36px;left:-8px;opacity:.1;pointer-events:none;position:absolute;top:-8px;transform:scale(1);transition:transform .1s ease-in-out;width:36px}:host:focus-within .mb-radio__input:disabled~.mb-radio__mark .mb-radio__hover,:host:hover .mb-radio__input:disabled~.mb-radio__mark .mb-radio__hover{display:none}.mb-radio{-webkit-tap-highlight-color:rgba(0,0,0,0);align-items:center;cursor:pointer;display:inline-flex;height:24px;-webkit-touch-callout:none;-webkit-user-select:none;user-select:none}.mb-radio__input{height:0;opacity:0;width:0}.mb-radio__mark{background-color:var(--color-white);border:2px solid var(--color-gray-600);border-radius:50%;height:20px;margin:2px;min-width:20px;position:relative}.mb-radio__mark:after{border:2px solid white;border-radius:50%;content:\"\";display:none;height:14px;left:3px;position:absolute;top:3px;width:14px}.mb-radio__input:checked~.mb-radio__mark{background-color:var(--color-secondary);border:none}.mb-radio__input:checked~.mb-radio__mark:after{display:block}.mb-radio__input:disabled~.mb-radio__mark{background-color:var(--color-gray-500);border:2px solid var(--color-disabled)}.mb-radio__input:disabled~.mb-radio__mark:after{display:none}.mb-radio__input:checked:disabled~.mb-radio__mark{background-color:var(--color-disabled);border:none}.mb-radio__input:checked:disabled~.mb-radio__mark:after{display:block}.mb-radio__content{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;margin-left:12px}@media only screen and (max-width: 767px){.mb-radio__content{line-height:1.5}}.mb-radio__hover{transform:scale(.3)}@media (hover: none){.mb-radio__hover{display:none}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRadioButton, decorators: [{
            type: Component,
            args: [{ selector: 'mb-radio', host: {
                        '[attr.tabindex]': '-1',
                    }, template: "<label [attr.for]=\"id\" class=\"mb-radio\">\n  <input\n    class=\"mb-radio__input\"\n    type=\"radio\"\n    [id]=\"id\"\n    [tabIndex]=\"tabIndex\"\n    [checked]=\"isChecked\"\n    [disabled]=\"isDisabled\"\n    [attr.name]=\"name\"\n    [attr.value]=\"value\"\n    (click)=\"onChange()\"\n  />\n  <span class=\"mb-radio__mark\">\n    <span class=\"mb-radio__hover\"></span>\n  </span>\n  <span class=\"mb-radio__content\">\n    <ng-content></ng-content>\n  </span>\n</label>\n", styles: [":host{outline:none}:host:focus-within .mb-radio__hover,:host:hover .mb-radio__hover{background-color:var(--color-text-disabled);border-radius:20px;height:36px;left:-10px;opacity:.1;pointer-events:none;position:absolute;top:-10px;transform:scale(1);transition:transform .1s ease-in-out;width:36px}:host:focus-within .mb-radio__input:checked~.mb-radio__mark .mb-radio__hover,:host:hover .mb-radio__input:checked~.mb-radio__mark .mb-radio__hover{background-color:var(--color-secondary);border-radius:20px;height:36px;left:-8px;opacity:.1;pointer-events:none;position:absolute;top:-8px;transform:scale(1);transition:transform .1s ease-in-out;width:36px}:host:focus-within .mb-radio__input:disabled~.mb-radio__mark .mb-radio__hover,:host:hover .mb-radio__input:disabled~.mb-radio__mark .mb-radio__hover{display:none}.mb-radio{-webkit-tap-highlight-color:rgba(0,0,0,0);align-items:center;cursor:pointer;display:inline-flex;height:24px;-webkit-touch-callout:none;-webkit-user-select:none;user-select:none}.mb-radio__input{height:0;opacity:0;width:0}.mb-radio__mark{background-color:var(--color-white);border:2px solid var(--color-gray-600);border-radius:50%;height:20px;margin:2px;min-width:20px;position:relative}.mb-radio__mark:after{border:2px solid white;border-radius:50%;content:\"\";display:none;height:14px;left:3px;position:absolute;top:3px;width:14px}.mb-radio__input:checked~.mb-radio__mark{background-color:var(--color-secondary);border:none}.mb-radio__input:checked~.mb-radio__mark:after{display:block}.mb-radio__input:disabled~.mb-radio__mark{background-color:var(--color-gray-500);border:2px solid var(--color-disabled)}.mb-radio__input:disabled~.mb-radio__mark:after{display:none}.mb-radio__input:checked:disabled~.mb-radio__mark{background-color:var(--color-disabled);border:none}.mb-radio__input:checked:disabled~.mb-radio__mark:after{display:block}.mb-radio__content{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;margin-left:12px}@media only screen and (max-width: 767px){.mb-radio__content{line-height:1.5}}.mb-radio__hover{transform:scale(.3)}@media (hover: none){.mb-radio__hover{display:none}}\n"] }]
        }], ctorParameters: function () { return [{ type: MbRadioGroup, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { id: [{
                type: Input
            }], disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }] } });

class MbRadioModule {
}
MbRadioModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRadioModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbRadioModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbRadioModule, declarations: [MbRadioButton, MbRadioGroup], exports: [MbRadioButton, MbRadioGroup] });
MbRadioModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRadioModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRadioModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbRadioButton, MbRadioGroup],
                    exports: [MbRadioButton, MbRadioGroup]
                }]
        }] });

class MbFormFieldHint {
}
MbFormFieldHint.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldHint, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbFormFieldHint.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbFormFieldHint, selector: "mb-hint", host: { classAttribute: "mb-form-field__hint" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldHint, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-hint',
                    template: '<ng-content></ng-content>',
                    host: {
                        class: 'mb-form-field__hint'
                    }
                }]
        }] });
class MbFormFieldSuccess {
    constructor() {
        this.icon = aciCheckCircle;
    }
}
MbFormFieldSuccess.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldSuccess, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbFormFieldSuccess.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbFormFieldSuccess, selector: "mb-success", host: { classAttribute: "mb-form-field__success" }, ngImport: i0, template: `
    <mb-icon [icon]="icon"></mb-icon>
    <ng-content></ng-content>
  `, isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldSuccess, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-success',
                    template: `
    <mb-icon [icon]="icon"></mb-icon>
    <ng-content></ng-content>
  `,
                    host: {
                        class: 'mb-form-field__success'
                    }
                }]
        }] });
class MbFormFieldError {
    constructor() {
        this.icon = aciCloseCircle;
    }
}
MbFormFieldError.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldError, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbFormFieldError.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbFormFieldError, selector: "mb-error", host: { classAttribute: "mb-form-field__error" }, ngImport: i0, template: `
    <mb-icon [icon]="icon"></mb-icon>
    <ng-content></ng-content>
  `, isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldError, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-error',
                    template: `
    <mb-icon [icon]="icon"></mb-icon>
    <ng-content></ng-content>
  `,
                    host: {
                        class: 'mb-form-field__error'
                    }
                }]
        }] });
class MbFormFieldNotification {
    constructor() {
        this.icon = aciInfo;
    }
}
MbFormFieldNotification.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldNotification, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbFormFieldNotification.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbFormFieldNotification, selector: "mb-notification", host: { classAttribute: "mb-form-field__notification" }, ngImport: i0, template: `
    <mb-icon [icon]="icon"></mb-icon>
    <ng-content></ng-content>
  `, isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldNotification, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-notification',
                    template: `
    <mb-icon [icon]="icon"></mb-icon>
    <ng-content></ng-content>
  `,
                    host: {
                        class: 'mb-form-field__notification'
                    }
                }]
        }] });
class MbFormFieldLoading {
    constructor() {
        this.icon = aciCircleNotch;
    }
}
MbFormFieldLoading.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldLoading, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbFormFieldLoading.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbFormFieldLoading, selector: "mb-loading", host: { classAttribute: "mb-form-field__loading" }, ngImport: i0, template: `
    <mb-icon [icon]="icon" [spin]="true"></mb-icon>
    <ng-content></ng-content>
  `, isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldLoading, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-loading',
                    template: `
    <mb-icon [icon]="icon" [spin]="true"></mb-icon>
    <ng-content></ng-content>
  `,
                    host: {
                        class: 'mb-form-field__loading'
                    }
                }]
        }] });

class MbFormField {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.isFocused = false;
        this.isDisabled = null;
        this.appearance = 'dark';
        this.loading = false;
    }
    setIcon(icon) {
        this.icon = icon;
    }
    get showHint() {
        if (this.isDisabled || this.loading) {
            return false;
        }
        else {
            return !!this.hint || !!this.success || !!this.error || !!this.notification;
        }
    }
    get showLoading() {
        return this.isDisabled ? false : this.loading;
    }
    getConnectedOverlayOrigin() {
        return this.connectionContainerRef;
    }
}
MbFormField.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormField, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
MbFormField.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbFormField, selector: "mb-form-field", inputs: { appearance: "appearance", loading: "loading" }, host: { properties: { "class.mb-form-field--success": "success", "class.mb-form-field--error": "error", "class.mb-form-field--focused": "isFocused", "class.mb-form-field--disabled": "isDisabled" }, classAttribute: "mb-form-field" }, queries: [{ propertyName: "hint", first: true, predicate: MbFormFieldHint, descendants: true }, { propertyName: "success", first: true, predicate: MbFormFieldSuccess, descendants: true }, { propertyName: "error", first: true, predicate: MbFormFieldError, descendants: true }, { propertyName: "notification", first: true, predicate: MbFormFieldNotification, descendants: true }], viewQueries: [{ propertyName: "connectionContainerRef", first: true, predicate: ["connectionContainer"], descendants: true, static: true }], ngImport: i0, template: "<div\n  #connectionContainer\n  class=\"mb-form-field__container\"\n  [class.mb-form-field__container--light]=\"appearance === 'light'\"\n>\n  <ng-content select=\"[mb-form-field-prefix]\"></ng-content>\n  <div class=\"mb-form-field__content\">\n    <ng-content select=\"[mb-form-field-label]\"></ng-content>\n    <ng-content select=\"[mb-form-field-input]\"></ng-content>\n  </div>\n  <ng-content select=\"[mb-form-field-suffix]\"></ng-content>\n</div>\n\n<div class=\"mb-form-field__icon\" *ngIf=\"icon\">\n  <mb-icon [icon]=\"icon\"></mb-icon>\n</div>\n\n<ng-container *ngIf=\"showHint\">\n  <ng-content select=\"mb-hint\"></ng-content>\n  <ng-content select=\"mb-error\"></ng-content>\n  <ng-content select=\"mb-success\"></ng-content>\n  <ng-content select=\"mb-notification\"></ng-content>\n</ng-container>\n\n<ng-content *ngIf=\"showLoading\" select=\"mb-loading\"></ng-content>\n", styles: [".mb-form-field__notification,.mb-form-field__hint,.mb-form-field__loading,.mb-form-field .mb-form-field__success,.mb-form-field .mb-form-field__error{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;align-items:center;display:flex;margin-top:4px}@media only screen and (max-width: 767px){.mb-form-field__notification,.mb-form-field__hint,.mb-form-field__loading,.mb-form-field .mb-form-field__success,.mb-form-field .mb-form-field__error{line-height:1.5}}.mb-form-field__notification .mb-icon,.mb-form-field__hint .mb-icon,.mb-form-field__loading .mb-icon,.mb-form-field .mb-form-field__success .mb-icon,.mb-form-field .mb-form-field__error .mb-icon{font-size:16px;margin-right:4px}.mb-form-field{border:none;display:flex;flex-direction:column;margin:0;padding:0;position:relative}.mb-form-field .mb-form-field__error{color:var(--color-error)}.mb-form-field--error .mb-form-field__container{border-color:var(--color-error)}.mb-form-field--error .mb-form-field__label{color:var(--color-error)}.mb-form-field .mb-form-field__success{color:var(--color-success)}.mb-form-field--success .mb-form-field__container{border-color:var(--color-success)}.mb-form-field--success .mb-form-field__label{color:var(--color-success)}.mb-form-field__container{align-items:center;background-color:var(--color-gray-500);border:1px solid var(--color-gray-500);border-radius:8px;display:flex;min-height:56px;padding:8px}.mb-form-field__container--light{background-color:var(--color-foreground)}.mb-form-field__content{display:flex;flex-direction:column;flex-grow:1;overflow:hidden}.mb-form-field__label{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;line-height:16px}@media only screen and (max-width: 767px){.mb-form-field__label{line-height:1.5}}.mb-form-field__input{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5;appearance:none;background-color:transparent;border:none;color:var(--color-text-dark);height:22px;line-height:22px;outline:none;padding:0;resize:none}@media only screen and (max-width: 767px){.mb-form-field__input{line-height:1.5}}.mb-form-field textarea.mb-form-field__input{height:auto}.mb-form-field__select{cursor:pointer;padding-right:30px}.mb-form-field__prefix,.mb-form-field__suffix{color:var(--color-text-dark);margin:0 8px}.mb-form-field__icon{pointer-events:none;position:absolute;right:16px;top:18px}.mb-form-field__notification .mb-icon{color:var(--color-secondary)}.mb-form-field--focused .mb-form-field__container{border-color:var(--color-secondary);transition:border-color .3s}.mb-form-field--focused .mb-form-field__label,.mb-form-field--focused .mb-form-field__icon,.mb-form-field--focused .mb-form-field__prefix,.mb-form-field--focused .mb-form-field__suffix{color:var(--color-secondary);transition:color .3s}.mb-form-field--disabled{pointer-events:none}.mb-form-field--disabled .mb-form-field__container{background-color:var(--color-disabled);border-color:var(--color-disabled)}.mb-form-field--disabled .mb-form-field__label{color:var(--color-text-dark)}.mb-form-field--disabled .mb-form-field__input{color:var(--color-gray-800);-webkit-text-fill-color:var(--color-gray-800)}.mb-form-field--disabled .mb-form-field__input::placeholder{color:var(--color-text-disabled);-webkit-text-fill-color:var(--color-text-disabled)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormField, decorators: [{
            type: Component,
            args: [{ selector: 'mb-form-field', encapsulation: ViewEncapsulation.None, host: {
                        'class': 'mb-form-field',
                        '[class.mb-form-field--success]': 'success',
                        '[class.mb-form-field--error]': 'error',
                        '[class.mb-form-field--focused]': 'isFocused',
                        '[class.mb-form-field--disabled]': 'isDisabled',
                    }, template: "<div\n  #connectionContainer\n  class=\"mb-form-field__container\"\n  [class.mb-form-field__container--light]=\"appearance === 'light'\"\n>\n  <ng-content select=\"[mb-form-field-prefix]\"></ng-content>\n  <div class=\"mb-form-field__content\">\n    <ng-content select=\"[mb-form-field-label]\"></ng-content>\n    <ng-content select=\"[mb-form-field-input]\"></ng-content>\n  </div>\n  <ng-content select=\"[mb-form-field-suffix]\"></ng-content>\n</div>\n\n<div class=\"mb-form-field__icon\" *ngIf=\"icon\">\n  <mb-icon [icon]=\"icon\"></mb-icon>\n</div>\n\n<ng-container *ngIf=\"showHint\">\n  <ng-content select=\"mb-hint\"></ng-content>\n  <ng-content select=\"mb-error\"></ng-content>\n  <ng-content select=\"mb-success\"></ng-content>\n  <ng-content select=\"mb-notification\"></ng-content>\n</ng-container>\n\n<ng-content *ngIf=\"showLoading\" select=\"mb-loading\"></ng-content>\n", styles: [".mb-form-field__notification,.mb-form-field__hint,.mb-form-field__loading,.mb-form-field .mb-form-field__success,.mb-form-field .mb-form-field__error{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;align-items:center;display:flex;margin-top:4px}@media only screen and (max-width: 767px){.mb-form-field__notification,.mb-form-field__hint,.mb-form-field__loading,.mb-form-field .mb-form-field__success,.mb-form-field .mb-form-field__error{line-height:1.5}}.mb-form-field__notification .mb-icon,.mb-form-field__hint .mb-icon,.mb-form-field__loading .mb-icon,.mb-form-field .mb-form-field__success .mb-icon,.mb-form-field .mb-form-field__error .mb-icon{font-size:16px;margin-right:4px}.mb-form-field{border:none;display:flex;flex-direction:column;margin:0;padding:0;position:relative}.mb-form-field .mb-form-field__error{color:var(--color-error)}.mb-form-field--error .mb-form-field__container{border-color:var(--color-error)}.mb-form-field--error .mb-form-field__label{color:var(--color-error)}.mb-form-field .mb-form-field__success{color:var(--color-success)}.mb-form-field--success .mb-form-field__container{border-color:var(--color-success)}.mb-form-field--success .mb-form-field__label{color:var(--color-success)}.mb-form-field__container{align-items:center;background-color:var(--color-gray-500);border:1px solid var(--color-gray-500);border-radius:8px;display:flex;min-height:56px;padding:8px}.mb-form-field__container--light{background-color:var(--color-foreground)}.mb-form-field__content{display:flex;flex-direction:column;flex-grow:1;overflow:hidden}.mb-form-field__label{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;line-height:16px}@media only screen and (max-width: 767px){.mb-form-field__label{line-height:1.5}}.mb-form-field__input{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5;appearance:none;background-color:transparent;border:none;color:var(--color-text-dark);height:22px;line-height:22px;outline:none;padding:0;resize:none}@media only screen and (max-width: 767px){.mb-form-field__input{line-height:1.5}}.mb-form-field textarea.mb-form-field__input{height:auto}.mb-form-field__select{cursor:pointer;padding-right:30px}.mb-form-field__prefix,.mb-form-field__suffix{color:var(--color-text-dark);margin:0 8px}.mb-form-field__icon{pointer-events:none;position:absolute;right:16px;top:18px}.mb-form-field__notification .mb-icon{color:var(--color-secondary)}.mb-form-field--focused .mb-form-field__container{border-color:var(--color-secondary);transition:border-color .3s}.mb-form-field--focused .mb-form-field__label,.mb-form-field--focused .mb-form-field__icon,.mb-form-field--focused .mb-form-field__prefix,.mb-form-field--focused .mb-form-field__suffix{color:var(--color-secondary);transition:color .3s}.mb-form-field--disabled{pointer-events:none}.mb-form-field--disabled .mb-form-field__container{background-color:var(--color-disabled);border-color:var(--color-disabled)}.mb-form-field--disabled .mb-form-field__label{color:var(--color-text-dark)}.mb-form-field--disabled .mb-form-field__input{color:var(--color-gray-800);-webkit-text-fill-color:var(--color-gray-800)}.mb-form-field--disabled .mb-form-field__input::placeholder{color:var(--color-text-disabled);-webkit-text-fill-color:var(--color-text-disabled)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { connectionContainerRef: [{
                type: ViewChild,
                args: ['connectionContainer', { static: true }]
            }], appearance: [{
                type: Input
            }], loading: [{
                type: Input
            }], hint: [{
                type: ContentChild,
                args: [MbFormFieldHint]
            }], success: [{
                type: ContentChild,
                args: [MbFormFieldSuccess]
            }], error: [{
                type: ContentChild,
                args: [MbFormFieldError]
            }], notification: [{
                type: ContentChild,
                args: [MbFormFieldNotification]
            }] } });

class MbFormFieldLabel {
}
MbFormFieldLabel.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldLabel, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MbFormFieldLabel.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbFormFieldLabel, selector: "label[mb-form-field-label]", host: { classAttribute: "mb-form-field__label" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldLabel, decorators: [{
            type: Directive,
            args: [{
                    selector: 'label[mb-form-field-label]',
                    host: {
                        class: 'mb-form-field__label'
                    }
                }]
        }] });

class MbFormFieldInput {
    constructor(formField, changeDetectorRef, ngControl) {
        this.formField = formField;
        this.changeDetectorRef = changeDetectorRef;
        this.ngControl = ngControl;
    }
    ngOnInit() {
        if (this.ngControl) {
            if (!this.formField.isDisabled) {
                this.formField.isDisabled = this.ngControl.disabled;
            }
        }
    }
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
    set disabled(value) {
        this.formField.isDisabled = value;
        this.changeDetectorRef.detectChanges();
        if (this.ngControl && this.ngControl.control) {
            value
                ? this.ngControl.control.disable({ emitEvent: false })
                : this.ngControl.control.enable({ emitEvent: false });
        }
    }
    get disabled() {
        if (this.ngControl) {
            if (this.formField.isDisabled !== (this.ngControl.status === 'DISABLED')) {
                this.formField.isDisabled = this.ngControl.status === 'DISABLED';
                this.changeDetectorRef.detectChanges();
            }
        }
        return !!this.formField.isDisabled;
    }
    onFocus() {
        this.formField.isFocused = true;
    }
    onBlur() {
        this.formField.isFocused = false;
    }
}
MbFormFieldInput.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldInput, deps: [{ token: MbFormField }, { token: i0.ChangeDetectorRef }, { token: i2.NgControl, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
MbFormFieldInput.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbFormFieldInput, selector: "input[mb-form-field-input],textarea[mb-form-field-input]", inputs: { disabled: "disabled" }, host: { listeners: { "focus": "onFocus()", "blur": "onBlur()" }, properties: { "disabled": "disabled" }, classAttribute: "mb-form-field__input" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldInput, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[mb-form-field-input],textarea[mb-form-field-input]',
                    host: {
                        'class': 'mb-form-field__input',
                        '[disabled]': 'disabled',
                    },
                }]
        }], ctorParameters: function () { return [{ type: MbFormField }, { type: i0.ChangeDetectorRef }, { type: i2.NgControl, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { disabled: [{
                type: Input
            }], onFocus: [{
                type: HostListener,
                args: ['focus']
            }], onBlur: [{
                type: HostListener,
                args: ['blur']
            }] } });
class MbFormFieldSelect extends MbFormFieldInput {
    constructor(formField, changeDetectorRef, ngControl) {
        super(formField, changeDetectorRef, ngControl);
    }
    ngOnInit() {
        this.formField.setIcon(aciArrowDown);
        super.ngOnInit();
    }
}
MbFormFieldSelect.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldSelect, deps: [{ token: MbFormField }, { token: i0.ChangeDetectorRef }, { token: i2.NgControl, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
MbFormFieldSelect.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbFormFieldSelect, selector: "select[mb-form-field-input]", host: { properties: { "disabled": "disabled" }, classAttribute: "mb-form-field__input mb-form-field__select" }, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldSelect, decorators: [{
            type: Directive,
            args: [{
                    selector: 'select[mb-form-field-input]',
                    host: {
                        'class': 'mb-form-field__input mb-form-field__select',
                        '[disabled]': 'disabled',
                    },
                }]
        }], ctorParameters: function () { return [{ type: MbFormField }, { type: i0.ChangeDetectorRef }, { type: i2.NgControl, decorators: [{
                    type: Optional
                }] }]; } });

class MbFormFieldPrefix {
}
MbFormFieldPrefix.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldPrefix, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MbFormFieldPrefix.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbFormFieldPrefix, selector: "[mb-form-field-prefix]", host: { classAttribute: "mb-form-field__prefix" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldPrefix, decorators: [{
            type: Directive,
            args: [{
                    selector: '[mb-form-field-prefix]',
                    host: {
                        class: 'mb-form-field__prefix'
                    }
                }]
        }] });
class MbFormFieldSuffix {
}
MbFormFieldSuffix.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldSuffix, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MbFormFieldSuffix.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbFormFieldSuffix, selector: "[mb-form-field-suffix]", host: { classAttribute: "mb-form-field__suffix" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldSuffix, decorators: [{
            type: Directive,
            args: [{
                    selector: '[mb-form-field-suffix]',
                    host: {
                        class: 'mb-form-field__suffix'
                    }
                }]
        }] });

const components$2 = [
    MbFormField,
    MbFormFieldLabel,
    MbFormFieldInput,
    MbFormFieldSelect,
    MbFormFieldPrefix,
    MbFormFieldSuffix,
    MbFormFieldHint,
    MbFormFieldSuccess,
    MbFormFieldError,
    MbFormFieldNotification,
    MbFormFieldLoading
];
class MbFormFieldModule {
}
MbFormFieldModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbFormFieldModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldModule, declarations: [MbFormField,
        MbFormFieldLabel,
        MbFormFieldInput,
        MbFormFieldSelect,
        MbFormFieldPrefix,
        MbFormFieldSuffix,
        MbFormFieldHint,
        MbFormFieldSuccess,
        MbFormFieldError,
        MbFormFieldNotification,
        MbFormFieldLoading], imports: [CommonModule, MbIconModule], exports: [MbFormField,
        MbFormFieldLabel,
        MbFormFieldInput,
        MbFormFieldSelect,
        MbFormFieldPrefix,
        MbFormFieldSuffix,
        MbFormFieldHint,
        MbFormFieldSuccess,
        MbFormFieldError,
        MbFormFieldNotification,
        MbFormFieldLoading] });
MbFormFieldModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldModule, imports: [CommonModule, MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbFormFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MbIconModule],
                    declarations: [...components$2],
                    exports: [...components$2]
                }]
        }] });

function isDateInstance(obj) {
    return obj instanceof Date;
}
function isValid(date) {
    return !isNaN(date.getTime());
}
function getValidDateOrNull(obj) {
    return isDateInstance(obj) && isValid(obj) ? obj : null;
}
function compareDate(date1, date2) {
    return (date1.getFullYear() - date2.getFullYear() ||
        date1.getMonth() - date2.getMonth() ||
        date1.getDate() - date2.getDate());
}
/**
 * Checks if two dates are equal.
 * @param first The first date to check.
 * @param second The second date to check.
 * @returns Whether the two dates are equal.
 *     Null dates are considered equal to other null dates.
 */
function isSameDate(first, second) {
    if (first && second) {
        const firstValid = isValid(first);
        const secondValid = isValid(second);
        if (firstValid && secondValid) {
            return !compareDate(first, second);
        }
        return firstValid === secondValid;
    }
    return first === second;
}
/**
 * Clamp the given date between min and max dates.
 * @param date The date to clamp.
 * @param min The minimum value to allow. If null or omitted no min is enforced.
 * @param max The maximum value to allow. If null or omitted no max is enforced.
 * @returns `min` if `date` is less than `min`, `max` if date is greater than `max`,
 *     otherwise `date`.
 */
function clampDate(date, min, max) {
    if (min && compareDate(date, min) < 0) {
        return min;
    }
    if (max && compareDate(date, max) > 0) {
        return max;
    }
    return date;
}

class CalendarCellViewModel {
    constructor(date, label, isDisabled, calendarCellData = null) {
        this.date = date;
        this.label = label;
        this.isDisabled = isDisabled;
        this.calendarCellData = calendarCellData;
    }
}
var InputRangeType;
(function (InputRangeType) {
    InputRangeType["START"] = "start";
    InputRangeType["END"] = "end";
})(InputRangeType || (InputRangeType = {}));
var NavigationType;
(function (NavigationType) {
    NavigationType["PREV"] = "previous";
    NavigationType["NEXT"] = "next";
})(NavigationType || (NavigationType = {}));
class ChangeDateEvent {
    get date() {
        return this._date;
    }
    set date(value) {
        this._date = value;
    }
    get clicked() {
        return this._clicked;
    }
    set clicked(value) {
        this._clicked = value;
    }
    constructor(date, clicked) {
        this._date = date;
        this._clicked = clicked;
    }
}
const regexFormatDate = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4,}$/;
const regexValidChars = /(d|\/)+$/;

const WEEK_LENGTH = 7;
class CalendarGridBuilder {
    constructor(year, month) {
        this.year = year;
        this.month = month;
        this.firstDay = new Date(year, month, 1);
        this.lastDay = new Date(year, month + 1, 0);
    }
    setMinDate(minDate) {
        this.minDate = minDate;
        return this;
    }
    setMaxDate(maxDate) {
        this.maxDate = maxDate;
        return this;
    }
    setDateFilter(dateFilter) {
        this.dateFilter = dateFilter;
        return this;
    }
    setCalendarData(calendarData) {
        this.calendarData = calendarData;
        return this;
    }
    build() {
        const firstRowOffset = (this.firstDay.getDay() + WEEK_LENGTH - 1) % WEEK_LENGTH;
        const grid = [[]];
        for (let i = 0, cell = firstRowOffset; i < this.lastDay.getDate(); i++, cell++) {
            if (cell === WEEK_LENGTH) {
                grid.push([]);
                cell = 0;
            }
            const date = new Date(this.year, this.month, i + 1);
            const isDisabled = this._isDateDisabled(date);
            const cellData = this.calendarData ? this.calendarData(date) : null;
            const label = `${i + 1}`;
            const cellViewModel = new CalendarCellViewModel(date, label, isDisabled, cellData);
            grid[grid.length - 1].push(cellViewModel);
        }
        return grid;
    }
    _isDateDisabled(date) {
        return (Boolean(this.dateFilter && !this.dateFilter(date)) ||
            Boolean(this.maxDate && compareDate(date, this.maxDate) > 0) ||
            Boolean(this.minDate && compareDate(date, this.minDate) < 0));
    }
}

class MbMonthViewComponent {
    onNavigateTo(type) {
        const date = {
            [NavigationType.PREV]: this.prevMonthDate(),
            [NavigationType.NEXT]: this.nextMonthDate()
        }[type];
        this.month = date.getMonth();
        this.year = date.getFullYear();
        this.updateGrid();
    }
    prevMonthDate() {
        return new Date(this.year, this.month - 1);
    }
    nextMonthDate() {
        return new Date(this.year, this.month + 1);
    }
    updateHoveredDate(cell) {
        this.hoveredDate = cell && cell.date;
    }
}

const getDayNames = (locale) => {
    return getLocaleDayNames(locale, FormStyle.Standalone, TranslationWidth.Abbreviated);
};
const getShortDayNames = (locale) => {
    const dayNames = getDayNames(locale).map((dayName) => dayName.charAt(0).toUpperCase());
    const sunday = dayNames.shift();
    if (sunday) {
        dayNames.push(sunday);
    }
    return dayNames;
};
const getMonthName = (locale, month) => {
    const monthName = formatDate(new Date(new Date().getFullYear(), month), 'LLLL', locale);
    return monthName.charAt(0).toUpperCase() + monthName.slice(1);
};
const formatInputDate = (date, locale) => {
    return formatDate(date, 'dd/MM/yyyy', locale);
};

class MbCalendarBody {
    constructor(locale) {
        this.locale = locale;
        this.selectedRange = [null, null];
        this.dateSelected = new EventEmitter();
        this.dateHover = new EventEmitter();
    }
    ngOnInit() {
        this.dayNames = getShortDayNames(this.locale);
    }
    ngOnChanges() {
        this.firstRowOffset = 7 - this.monthGrid[0].length;
    }
    isSelectedDate(date) {
        const selections = [];
        if (this.selectedDate) {
            selections.push(this.selectedDate);
        }
        if (this.selectedRange && this.selectedRange[0]) {
            selections.push(this.selectedRange[0]);
        }
        if (this.selectedRange && this.selectedRange[1]) {
            selections.push(this.selectedRange[1]);
        }
        return selections.filter(sel => sel && isSameDate(date, sel)).length > 0;
    }
    hoverText(calendarCellData) {
        return calendarCellData && calendarCellData.description
            ? calendarCellData.description
            : '';
    }
    dayClicked(date, clicked = false) {
        this.dateSelected.emit(new ChangeDateEvent(date, clicked));
    }
    isSelectedRangeStart(date) {
        if (!this.selectedRange) {
            return false;
        }
        if (this.selectedRange[0]) {
            return !compareDate(date, this.selectedRange[0]);
        }
    }
    isSelectedRangeEnd(date) {
        if (!this.selectedRange) {
            return false;
        }
        if (this.selectedRange[1]) {
            return !compareDate(date, this.selectedRange[1]);
        }
    }
    isInSelectedRange(date) {
        if (!this.selectedRange) {
            return false;
        }
        if (this.selectedRange[0] && this.selectedRange[1]) {
            return (compareDate(date, this.selectedRange[0]) >= 0 &&
                compareDate(date, this.selectedRange[1]) <= 0);
        }
        return false;
    }
    isInNewRange(date) {
        if (!this.selectedRange) {
            return false;
        }
        if (this.selectedRange[0] && !this.selectedRange[1]) {
            return (this.hoveredDate &&
                compareDate(date, this.selectedRange[0]) >= 0 &&
                compareDate(date, this.hoveredDate) <= 0);
        }
        return false;
    }
}
MbCalendarBody.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCalendarBody, deps: [{ token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Component });
MbCalendarBody.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCalendarBody, selector: "mb-calendar-body", inputs: { hoveredDate: "hoveredDate", selectedDate: "selectedDate", selectedRange: "selectedRange", monthGrid: "monthGrid" }, outputs: { dateSelected: "dateSelected", dateHover: "dateHover" }, usesOnChanges: true, ngImport: i0, template: "<table class=\"mb-calendar-body\" (mouseleave)=\"dateHover.emit(undefined)\">\n  <thead>\n    <tr>\n      <th *ngFor=\"let dayName of dayNames\" class=\"mb-calendar-body__weekday-cell\">\n        <span class=\"mb-calendar-body__weekday\">{{ dayName }}</span>\n      </th>\n    </tr>\n  </thead>\n\n  <tbody>\n    <tr *ngFor=\"let weeks of monthGrid; let rowIndex = index\">\n      <td\n        class=\"mb-calendar-body__date-cell\"\n        *ngIf=\"rowIndex === 0 && firstRowOffset\"\n        [attr.colspan]=\"firstRowOffset\"\n      ></td>\n\n      <td\n        *ngFor=\"let dayCell of weeks\"\n        class=\"mb-calendar-body__date-cell\"\n        [class.mb-calendar-body__date-cell--range-start]=\"\n          isSelectedRangeStart(dayCell.date)\n        \"\n        [class.mb-calendar-body__date-cell--range-end]=\"isSelectedRangeEnd(dayCell.date)\"\n        [class.mb-calendar-body__date-cell--current]=\"\n          dayCell.date === hoveredDate && !dayCell.isDisabled\n        \"\n        [class.mb-calendar-body__date-cell--range-selected]=\"\n          isInSelectedRange(dayCell.date)\n        \"\n        [class.mb-calendar-body__date-cell--new-range]=\"isInNewRange(dayCell.date)\"\n        [class.mb-calendar-body__date-cell--selected]=\"isSelectedDate(dayCell.date)\"\n        [ngClass]=\"dayCell.calendarCellData?.classes\"\n        (mouseenter)=\"dateHover.emit(dayCell)\"\n      >\n        <button\n          *ngIf=\"dayCell\"\n          class=\"mb-calendar-body__btn\"\n          [disabled]=\"dayCell.isDisabled\"\n          [title]=\"hoverText(dayCell.calendarCellData)\"\n          (click)=\"dayClicked(dayCell.date, true)\"\n        >\n          <span class=\"mb-calendar-body__cell-number\">{{ dayCell.label }}</span>\n          <span\n            class=\"mb-calendar-body__cell-title\"\n            *ngIf=\"dayCell.calendarCellData?.title\"\n          >\n            {{ dayCell.calendarCellData?.title }}\n          </span>\n        </button>\n      </td>\n    </tr>\n    <tr *ngIf=\"monthGrid.length < 6\">\n      <td class=\"mb-calendar-body__date-cell\" colspan=\"7\">\n        <button class=\"mb-calendar-body__btn\">&nbsp;</button>\n      </td>\n    </tr>\n  </tbody>\n</table>\n", styles: [".mb-calendar-body{border-spacing:0 2px}.mb-calendar-body__weekday-cell{text-align:center}.mb-calendar-body__btn,.mb-calendar-body__weekday{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){.mb-calendar-body__btn,.mb-calendar-body__weekday{line-height:1.5}}.mb-calendar-body__weekday{color:var(--color-gray-700)}.mb-calendar-body__btn{background-color:transparent;border:none;color:var(--color-text-dark);cursor:pointer;height:28px;outline:none;width:40px}.mb-calendar-body__btn:disabled{color:var(--color-text-disabled);cursor:auto;text-decoration:none}.mb-calendar-body__cell-number{display:block}.mb-calendar-body__cell-title{color:var(--color-gray-700);display:block;font-size:10px}.mb-calendar-body__date-cell{border:1px solid transparent;padding:0}.mb-calendar-body__date-cell--range-selected{background-color:var(--color-secondary-clear)}.mb-calendar-body__date-cell--new-range{background-color:var(--color-gray-500)}.mb-calendar-body__date-cell--current .mb-calendar-body__btn{color:var(--color-secondary)}.mb-calendar-body__date-cell--range-start{border-bottom-left-radius:16px;border-top-left-radius:16px}.mb-calendar-body__date-cell--range-end{border-bottom-right-radius:16px;border-top-right-radius:16px}.mb-calendar-body__date-cell--selected{background-color:var(--color-secondary)}.mb-calendar-body__date-cell--selected .mb-calendar-body__btn,.mb-calendar-body__date-cell--selected .mb-calendar-body__cell-title{color:var(--color-white)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCalendarBody, decorators: [{
            type: Component,
            args: [{ selector: 'mb-calendar-body', encapsulation: ViewEncapsulation.None, template: "<table class=\"mb-calendar-body\" (mouseleave)=\"dateHover.emit(undefined)\">\n  <thead>\n    <tr>\n      <th *ngFor=\"let dayName of dayNames\" class=\"mb-calendar-body__weekday-cell\">\n        <span class=\"mb-calendar-body__weekday\">{{ dayName }}</span>\n      </th>\n    </tr>\n  </thead>\n\n  <tbody>\n    <tr *ngFor=\"let weeks of monthGrid; let rowIndex = index\">\n      <td\n        class=\"mb-calendar-body__date-cell\"\n        *ngIf=\"rowIndex === 0 && firstRowOffset\"\n        [attr.colspan]=\"firstRowOffset\"\n      ></td>\n\n      <td\n        *ngFor=\"let dayCell of weeks\"\n        class=\"mb-calendar-body__date-cell\"\n        [class.mb-calendar-body__date-cell--range-start]=\"\n          isSelectedRangeStart(dayCell.date)\n        \"\n        [class.mb-calendar-body__date-cell--range-end]=\"isSelectedRangeEnd(dayCell.date)\"\n        [class.mb-calendar-body__date-cell--current]=\"\n          dayCell.date === hoveredDate && !dayCell.isDisabled\n        \"\n        [class.mb-calendar-body__date-cell--range-selected]=\"\n          isInSelectedRange(dayCell.date)\n        \"\n        [class.mb-calendar-body__date-cell--new-range]=\"isInNewRange(dayCell.date)\"\n        [class.mb-calendar-body__date-cell--selected]=\"isSelectedDate(dayCell.date)\"\n        [ngClass]=\"dayCell.calendarCellData?.classes\"\n        (mouseenter)=\"dateHover.emit(dayCell)\"\n      >\n        <button\n          *ngIf=\"dayCell\"\n          class=\"mb-calendar-body__btn\"\n          [disabled]=\"dayCell.isDisabled\"\n          [title]=\"hoverText(dayCell.calendarCellData)\"\n          (click)=\"dayClicked(dayCell.date, true)\"\n        >\n          <span class=\"mb-calendar-body__cell-number\">{{ dayCell.label }}</span>\n          <span\n            class=\"mb-calendar-body__cell-title\"\n            *ngIf=\"dayCell.calendarCellData?.title\"\n          >\n            {{ dayCell.calendarCellData?.title }}\n          </span>\n        </button>\n      </td>\n    </tr>\n    <tr *ngIf=\"monthGrid.length < 6\">\n      <td class=\"mb-calendar-body__date-cell\" colspan=\"7\">\n        <button class=\"mb-calendar-body__btn\">&nbsp;</button>\n      </td>\n    </tr>\n  </tbody>\n</table>\n", styles: [".mb-calendar-body{border-spacing:0 2px}.mb-calendar-body__weekday-cell{text-align:center}.mb-calendar-body__btn,.mb-calendar-body__weekday{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){.mb-calendar-body__btn,.mb-calendar-body__weekday{line-height:1.5}}.mb-calendar-body__weekday{color:var(--color-gray-700)}.mb-calendar-body__btn{background-color:transparent;border:none;color:var(--color-text-dark);cursor:pointer;height:28px;outline:none;width:40px}.mb-calendar-body__btn:disabled{color:var(--color-text-disabled);cursor:auto;text-decoration:none}.mb-calendar-body__cell-number{display:block}.mb-calendar-body__cell-title{color:var(--color-gray-700);display:block;font-size:10px}.mb-calendar-body__date-cell{border:1px solid transparent;padding:0}.mb-calendar-body__date-cell--range-selected{background-color:var(--color-secondary-clear)}.mb-calendar-body__date-cell--new-range{background-color:var(--color-gray-500)}.mb-calendar-body__date-cell--current .mb-calendar-body__btn{color:var(--color-secondary)}.mb-calendar-body__date-cell--range-start{border-bottom-left-radius:16px;border-top-left-radius:16px}.mb-calendar-body__date-cell--range-end{border-bottom-right-radius:16px;border-top-right-radius:16px}.mb-calendar-body__date-cell--selected{background-color:var(--color-secondary)}.mb-calendar-body__date-cell--selected .mb-calendar-body__btn,.mb-calendar-body__date-cell--selected .mb-calendar-body__cell-title{color:var(--color-white)}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; }, propDecorators: { hoveredDate: [{
                type: Input
            }], selectedDate: [{
                type: Input
            }], selectedRange: [{
                type: Input
            }], monthGrid: [{
                type: Input
            }], dateSelected: [{
                type: Output
            }], dateHover: [{
                type: Output
            }] } });

class MbCalendarHeader {
    constructor(locale) {
        this.locale = locale;
        this.navigateTo = new EventEmitter();
        this.prevMonthIcon = aciArrowLeft;
        this.nextMonthIcon = aciArrowRight;
    }
    get monthName() {
        return getMonthName(this.locale, this.month);
    }
    previous() {
        this.navigateTo.emit(NavigationType.PREV);
    }
    next() {
        this.navigateTo.emit(NavigationType.NEXT);
    }
}
MbCalendarHeader.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCalendarHeader, deps: [{ token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Component });
MbCalendarHeader.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCalendarHeader, selector: "mb-calendar-header", inputs: { month: "month", year: "year", showLeftArrow: "showLeftArrow", showRightArrow: "showRightArrow", disableLeftArrow: "disableLeftArrow", disableRightArrow: "disableRightArrow" }, outputs: { navigateTo: "navigateTo" }, ngImport: i0, template: "<button\n  (click)=\"previous()\"\n  *ngIf=\"showLeftArrow\"\n  [disabled]=\"disableLeftArrow\"\n  class=\"mb-calendar-header__prev\"\n>\n  <mb-icon [icon]=\"prevMonthIcon\"></mb-icon>\n</button>\n\n<div class=\"mb-calendar-header__title\">\n  <span class=\"mb-calendar-header__month\">{{ monthName }}</span>\n  <span class=\"mb-calendar-header__year\"> {{ year }}</span>\n</div>\n\n<button\n  (click)=\"next()\"\n  *ngIf=\"showRightArrow\"\n  [disabled]=\"disableRightArrow\"\n  class=\"mb-calendar-header__next\"\n>\n  <mb-icon [icon]=\"nextMonthIcon\"></mb-icon>\n</button>\n", styles: [":host{display:flex;padding:16px 0}.mb-calendar-header__title{flex-grow:1;text-align:center;text-transform:uppercase;font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){.mb-calendar-header__title{line-height:1.5}}.mb-calendar-header__month,.mb-calendar-header__year{display:flex;justify-content:center}.mb-calendar-header__month{margin-bottom:8px}.mb-calendar-header__prev,.mb-calendar-header__next{background-color:transparent;border:none;color:var(--color-text-dark);cursor:pointer;font-size:12px;outline:none}.mb-calendar-header__prev:disabled,.mb-calendar-header__next:disabled{color:var(--color-text-disabled)}.mb-calendar-header__prev .mb-icon,.mb-calendar-header__next .mb-icon{align-items:center;display:flex;justify-content:center}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCalendarHeader, decorators: [{
            type: Component,
            args: [{ selector: 'mb-calendar-header', changeDetection: ChangeDetectionStrategy.OnPush, template: "<button\n  (click)=\"previous()\"\n  *ngIf=\"showLeftArrow\"\n  [disabled]=\"disableLeftArrow\"\n  class=\"mb-calendar-header__prev\"\n>\n  <mb-icon [icon]=\"prevMonthIcon\"></mb-icon>\n</button>\n\n<div class=\"mb-calendar-header__title\">\n  <span class=\"mb-calendar-header__month\">{{ monthName }}</span>\n  <span class=\"mb-calendar-header__year\"> {{ year }}</span>\n</div>\n\n<button\n  (click)=\"next()\"\n  *ngIf=\"showRightArrow\"\n  [disabled]=\"disableRightArrow\"\n  class=\"mb-calendar-header__next\"\n>\n  <mb-icon [icon]=\"nextMonthIcon\"></mb-icon>\n</button>\n", styles: [":host{display:flex;padding:16px 0}.mb-calendar-header__title{flex-grow:1;text-align:center;text-transform:uppercase;font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){.mb-calendar-header__title{line-height:1.5}}.mb-calendar-header__month,.mb-calendar-header__year{display:flex;justify-content:center}.mb-calendar-header__month{margin-bottom:8px}.mb-calendar-header__prev,.mb-calendar-header__next{background-color:transparent;border:none;color:var(--color-text-dark);cursor:pointer;font-size:12px;outline:none}.mb-calendar-header__prev:disabled,.mb-calendar-header__next:disabled{color:var(--color-text-disabled)}.mb-calendar-header__prev .mb-icon,.mb-calendar-header__next .mb-icon{align-items:center;display:flex;justify-content:center}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; }, propDecorators: { month: [{
                type: Input
            }], year: [{
                type: Input
            }], showLeftArrow: [{
                type: Input
            }], showRightArrow: [{
                type: Input
            }], disableLeftArrow: [{
                type: Input
            }], disableRightArrow: [{
                type: Input
            }], navigateTo: [{
                type: Output
            }] } });

class MbSingleMonthView extends MbMonthViewComponent {
    constructor() {
        super(...arguments);
        this.dateChanged = new EventEmitter();
    }
    ngOnInit() {
        this.updateGrid();
    }
    ngOnChanges(changes) {
        const dateFilterUpdated = changes.dateFilter && !changes.dateFilter.firstChange;
        const calendarDataUpdated = changes.calendarData && !changes.calendarData.firstChange;
        if (dateFilterUpdated || calendarDataUpdated) {
            this.updateGrid();
        }
        if (changes.selectedDate && changes.selectedDate.previousValue != changes.selectedDate.currentValue) {
            if (changes.selectedDate.currentValue) {
                this.month = changes.selectedDate.currentValue.getMonth();
                this.year = changes.selectedDate.currentValue.getFullYear();
                this.updateGrid();
            }
        }
    }
    updateGrid() {
        this.monthGrid = new CalendarGridBuilder(this.year, this.month)
            .setMinDate(this.minDate)
            .setMaxDate(this.maxDate)
            .setDateFilter(this.dateFilter)
            .setCalendarData(this.calendarData)
            .build();
    }
    onDateSelected($event) {
        this.dateChanged.emit($event);
    }
    disablePrevious() {
        return !!(this.minDate &&
            this.month === this.minDate.getMonth() &&
            this.year === this.minDate.getFullYear());
    }
    disableNext() {
        return !!(this.maxDate &&
            this.month === this.maxDate.getMonth() &&
            this.year === this.maxDate.getFullYear());
    }
}
MbSingleMonthView.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSingleMonthView, deps: null, target: i0.ɵɵFactoryTarget.Component });
MbSingleMonthView.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbSingleMonthView, selector: "mb-single-month-view", inputs: { month: "month", year: "year", selectedDate: "selectedDate", minDate: "minDate", maxDate: "maxDate", dateFilter: "dateFilter", calendarData: "calendarData" }, outputs: { dateChanged: "dateChanged" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: `
    <mb-calendar-header
      (navigateTo)="onNavigateTo($event)"
      [showLeftArrow]="true"
      [showRightArrow]="true"
      [disableLeftArrow]="disablePrevious()"
      [disableRightArrow]="disableNext()"
      [year]="year"
      [month]="month"
    >
    </mb-calendar-header>

    <mb-calendar-body
      (dateSelected)="onDateSelected($event)"
      (dateHover)="updateHoveredDate($event)"
      [selectedDate]="selectedDate"
      [hoveredDate]="hoveredDate"
      [monthGrid]="monthGrid"
    >
    </mb-calendar-body>
  `, isInline: true, dependencies: [{ kind: "component", type: MbCalendarBody, selector: "mb-calendar-body", inputs: ["hoveredDate", "selectedDate", "selectedRange", "monthGrid"], outputs: ["dateSelected", "dateHover"] }, { kind: "component", type: MbCalendarHeader, selector: "mb-calendar-header", inputs: ["month", "year", "showLeftArrow", "showRightArrow", "disableLeftArrow", "disableRightArrow"], outputs: ["navigateTo"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSingleMonthView, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-single-month-view',
                    template: `
    <mb-calendar-header
      (navigateTo)="onNavigateTo($event)"
      [showLeftArrow]="true"
      [showRightArrow]="true"
      [disableLeftArrow]="disablePrevious()"
      [disableRightArrow]="disableNext()"
      [year]="year"
      [month]="month"
    >
    </mb-calendar-header>

    <mb-calendar-body
      (dateSelected)="onDateSelected($event)"
      (dateHover)="updateHoveredDate($event)"
      [selectedDate]="selectedDate"
      [hoveredDate]="hoveredDate"
      [monthGrid]="monthGrid"
    >
    </mb-calendar-body>
  `
                }]
        }], propDecorators: { month: [{
                type: Input
            }], year: [{
                type: Input
            }], selectedDate: [{
                type: Input
            }], minDate: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], dateFilter: [{
                type: Input
            }], calendarData: [{
                type: Input
            }], dateChanged: [{
                type: Output
            }] } });

class MbDatepickerContent {
    ngOnInit() {
        const date = this.resolveDate();
        this.year = date.getFullYear();
        this.month = date.getMonth();
    }
    onDateChanged(event) {
        this.datepicker.dateChanged$.next(event);
    }
    resolveDate() {
        if (this.datepicker && this.datepicker.selectedDate) {
            return clampDate(this.datepicker.selectedDate, this.datepicker.minDate, this.datepicker.maxDate);
        }
        else {
            return new Date();
        }
    }
}
MbDatepickerContent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerContent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbDatepickerContent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbDatepickerContent, selector: "mb-datepicker-content", ngImport: i0, template: `
    <mb-single-month-view
      [year]="year"
      [month]="month"
      [minDate]="datepicker.minDate"
      [maxDate]="datepicker.maxDate"
      [dateFilter]="datepicker.dateFilter"
      [calendarData]="datepicker.calendarData"
      [selectedDate]="datepicker.selectedDate"
      (dateChanged)="onDateChanged($event)"
    ></mb-single-month-view>
    <ng-container *ngTemplateOutlet="datepicker.footer"></ng-container>
  `, isInline: true, styles: [":host{background:var(--color-white);border:1px solid var(--color-gray-500);border-radius:2px;box-shadow:0 0 8px 4px #0003;padding:8px;text-align:center}\n"], dependencies: [{ kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: MbSingleMonthView, selector: "mb-single-month-view", inputs: ["month", "year", "selectedDate", "minDate", "maxDate", "dateFilter", "calendarData"], outputs: ["dateChanged"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerContent, decorators: [{
            type: Component,
            args: [{ selector: 'mb-datepicker-content', template: `
    <mb-single-month-view
      [year]="year"
      [month]="month"
      [minDate]="datepicker.minDate"
      [maxDate]="datepicker.maxDate"
      [dateFilter]="datepicker.dateFilter"
      [calendarData]="datepicker.calendarData"
      [selectedDate]="datepicker.selectedDate"
      (dateChanged)="onDateChanged($event)"
    ></mb-single-month-view>
    <ng-container *ngTemplateOutlet="datepicker.footer"></ng-container>
  `, styles: [":host{background:var(--color-white);border:1px solid var(--color-gray-500);border-radius:2px;box-shadow:0 0 8px 4px #0003;padding:8px;text-align:center}\n"] }]
        }] });

class MbDatepickerOverlayComponent {
    constructor() {
        this.backdropClickSubscription = Subscription.EMPTY;
    }
    openPortal(elementRef) {
        this.createPortal();
        this.calendarOverlay = this.overlayService.createOverlay(elementRef);
        const componentRef = this.calendarOverlay.attach(this.calendarPortal);
        this.backdropClickSubscription = this.calendarOverlay
            .backdropClick()
            .subscribe(() => {
            this.close();
        });
        return componentRef;
    }
    isOpen() {
        return (this.calendarOverlay && this.calendarOverlay.hasAttached())
            || (this.calendarPortal && this.calendarPortal.isAttached);
    }
    close() {
        if (this.calendarOverlay && this.calendarOverlay.hasAttached()) {
            this.calendarOverlay.detach();
        }
        if (this.calendarPortal && this.calendarPortal.isAttached) {
            this.calendarPortal.detach();
        }
    }
    ngOnDestroy() {
        this.backdropClickSubscription.unsubscribe();
    }
}
MbDatepickerOverlayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerOverlayComponent, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MbDatepickerOverlayComponent.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbDatepickerOverlayComponent, inputs: { positionRelativeTo: "positionRelativeTo" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerOverlayComponent, decorators: [{
            type: Directive
        }], propDecorators: { positionRelativeTo: [{
                type: Input
            }] } });

const TRANSPARENT_BACKDROP_CLASS = 'mb-transparent-backdrop';

class MbDatepickerOverlay {
    constructor(overlay) {
        this.overlay = overlay;
    }
    createOverlay(elementRef) {
        const positionStrategy = this.calendarPositionStrategy(elementRef);
        return this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            hasBackdrop: true,
            backdropClass: TRANSPARENT_BACKDROP_CLASS
        });
    }
    calendarPositionStrategy(elementRef) {
        const isPortrait = window.innerHeight / window.innerWidth > 1;
        const isMobileScreen = window.innerHeight < 1024 && window.innerWidth < 1024;
        if (isPortrait || isMobileScreen) {
            return this.overlay
                .position()
                .global()
                .centerHorizontally()
                .centerVertically();
        }
        else {
            return this.overlay
                .position()
                .flexibleConnectedTo(elementRef)
                .withLockedPosition()
                .withFlexibleDimensions(false)
                .withPush(false)
                .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top'
                }
            ]);
        }
    }
}
MbDatepickerOverlay.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerOverlay, deps: [{ token: i1$3.Overlay }], target: i0.ɵɵFactoryTarget.Injectable });
MbDatepickerOverlay.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerOverlay });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerOverlay, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Overlay }]; } });

class MbDatepicker extends MbDatepickerOverlayComponent {
    constructor(overlayService) {
        super();
        this.inputSubscription = Subscription.EMPTY;
        this.selectedDate = null;
        this.dateChanged$ = new Subject();
        this.overlayService = overlayService;
    }
    registerInput(input) {
        this.inputRef = input;
        this.inputSubscription = this.inputRef.valueChanged$.subscribe((value) => (this.selectedDate = value));
    }
    get dateFilter() {
        return this.inputRef && this.inputRef.dateFilter;
    }
    get minDate() {
        return this.inputRef && this.inputRef.min;
    }
    get maxDate() {
        return this.inputRef && this.inputRef.max;
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this.dateChanged$.complete();
        this.inputSubscription.unsubscribe();
    }
    createPortal() {
        this.calendarPortal = new ComponentPortal(MbDatepickerContent);
    }
    open(elementRef) {
        const ref = super.openPortal(this.positionRelativeTo || elementRef);
        ref.instance.datepicker = this;
    }
}
MbDatepicker.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepicker, deps: [{ token: MbDatepickerOverlay }], target: i0.ɵɵFactoryTarget.Component });
MbDatepicker.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbDatepicker, selector: "mb-datepicker", inputs: { calendarData: "calendarData" }, viewQueries: [{ propertyName: "footer", first: true, predicate: ["datepickerFooter"], descendants: true, static: true }], exportAs: ["mbDatepicker"], usesInheritance: true, ngImport: i0, template: `
    <ng-template #datepickerFooter>
      <ng-content select="mb-datepicker-footer"></ng-content>
    </ng-template>
  `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepicker, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-datepicker',
                    template: `
    <ng-template #datepickerFooter>
      <ng-content select="mb-datepicker-footer"></ng-content>
    </ng-template>
  `,
                    exportAs: 'mbDatepicker'
                }]
        }], ctorParameters: function () { return [{ type: MbDatepickerOverlay }]; }, propDecorators: { calendarData: [{
                type: Input
            }], footer: [{
                type: ViewChild,
                args: ['datepickerFooter', { static: true }]
            }] } });

const MB_DATEPICKER_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MbDatepickerInput),
    multi: true,
};
const MB_DATEPICKER_VALIDATORS = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => MbDatepickerInput),
    multi: true,
};
class MbDatepickerInput {
    constructor(elementRef, formField, locale) {
        this.elementRef = elementRef;
        this.formField = formField;
        this.locale = locale;
        this.valueChanged$ = new Subject();
        this.dateSelected = new EventEmitter();
        this.datepicker$ = Subscription.EMPTY;
        // tslint:disable:variable-name
        this._value = null;
        this._max = null;
        this._min = null;
        this.valueOnChange = (_date) => { };
        this.valueOnTouched = () => { };
        this.validatorOnChange = () => { };
        this.minValidator = (control) => {
            const controlValue = getValidDateOrNull(control.value);
            return !this.min || !controlValue || compareDate(this.min, controlValue) <= 0
                ? null
                : { amDatepickerMin: { min: this.min, actual: controlValue } };
        };
        this.maxValidator = (control) => {
            const controlValue = getValidDateOrNull(control.value);
            return !this.max || !controlValue || compareDate(this.max, controlValue) >= 0
                ? null
                : { amDatepickerMax: { max: this.max, actual: controlValue } };
        };
        this.dateFilterValidator = (control) => {
            const controlValue = getValidDateOrNull(control.value);
            return !this._dateFilter || !controlValue || this._dateFilter(controlValue)
                ? null
                : { amDatepickerFilter: { actual: controlValue } };
        };
        // tslint:disable-next-line:member-ordering
        this.validators = Validators.compose([
            this.minValidator,
            this.maxValidator,
            this.dateFilterValidator,
        ]);
    }
    // mbDatepicker binding
    set datepicker(value) {
        if (!value) {
            return;
        }
        this.datepickerComponent = value;
        this.datepickerComponent.registerInput(this);
        if (!this.datepicker$.closed) {
            this.datepicker$.unsubscribe();
        }
        this.datepicker$ = this.datepickerComponent.dateChanged$.subscribe((event) => {
            this.value = event.date;
            this.valueOnChange(event.date);
            this.valueOnTouched();
            if (event.clicked)
                this.datepickerComponent.close();
        });
    }
    get datepicker() {
        return this.datepickerComponent;
    }
    // value binding
    set value(value) {
        // TODO: Store a reference of the old value so we emit only on changes
        const validated = getValidDateOrNull(value);
        this._value = validated;
        this.valueChanged$.next(validated);
        this.dateSelected.emit(validated);
        this.elementRef.nativeElement.value = validated
            ? formatInputDate(validated, this.locale)
            : '';
    }
    get value() {
        return this._value;
    }
    // dateFilter binding
    set dateFilter(value) {
        this._dateFilter = value;
        this.validatorOnChange();
    }
    get dateFilter() {
        return this._dateFilter;
    }
    // minDate binding
    set min(value) {
        this._min = getValidDateOrNull(value);
        this.validatorOnChange();
    }
    get min() {
        return this._min;
    }
    // maxDate binding
    set max(value) {
        this._max = getValidDateOrNull(value);
        this.validatorOnChange();
    }
    get max() {
        return this._max;
    }
    // disabled binding
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
    }
    get disabled() {
        return this._disabled;
    }
    get hostElement() {
        return this.formField ? this.formField.getConnectedOverlayOrigin() : this.elementRef;
    }
    onKeyup(event) {
        const inputElement = event.target;
        const dateValue = inputElement.value;
        if (dateValue && !regexValidChars.test(dateValue)) {
            if (regexFormatDate.test(dateValue)) {
                const dateValueFomatted = `${dateValue.substring(3, 5)}/${dateValue.substring(0, 2)}/${dateValue.substring(6)}`;
                const date = new Date(dateValueFomatted);
                if (!(this.value?.getTime() === date.getTime()) ||
                    !this.min ||
                    (this.min && date >= this.min && (!this.max || (this.max && date <= this.max)))) {
                    this.writeValue(date);
                }
            }
            else if (dateValue === '') {
                this.writeValue(null);
            }
        }
    }
    onChange(event) {
        const inputElement = event.target;
        if (!this.value) {
            event.target.value = '';
            this.writeValue(null);
        }
        const dateValue = inputElement.value;
        const dateValueFomatted = `${dateValue.substring(3, 5)}/${dateValue.substring(0, 2)}/${dateValue.substring(6)}`;
        const date = new Date(dateValueFomatted);
        if (!regexFormatDate.test(dateValue) || !date.getTime()) {
            inputElement.value = '';
            this.writeValue(null);
        }
        this.datepickerComponent.close();
    }
    ngOnInit() {
        // this.elementRef.nativeElement.setAttribute('readonly', 'readonly');
    }
    ngOnDestroy() {
        this.datepicker$.unsubscribe();
    }
    // focus binding
    open() {
        if (!this.datepickerComponent.isOpen())
            this.datepickerComponent.open(this.hostElement);
    }
    // keydown binding
    close(event) {
        if (event.key === 'Tab')
            this.datepicker.close();
    }
    // ControlValueAccessor methods
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.valueOnChange = fn;
    }
    registerOnTouched(fn) {
        this.valueOnTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    // Validator methods
    validate(control) {
        return this.validators ? this.validators(control) : null;
    }
    registerOnValidatorChange(fn) {
        this.validatorOnChange = fn;
    }
}
MbDatepickerInput.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerInput, deps: [{ token: i0.ElementRef }, { token: MbFormField, host: true, optional: true }, { token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Directive });
MbDatepickerInput.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbDatepickerInput, selector: "input[mb-datepicker]", inputs: { datepicker: ["mb-datepicker", "datepicker"], value: "value", dateFilter: "dateFilter", min: ["minDate", "min"], max: ["maxDate", "max"], disabled: "disabled" }, outputs: { dateSelected: "dateSelected" }, host: { listeners: { "keyup": "onKeyup($event)", "change": "onChange($event)", "focus": "open()", "keydown": "close($event)" }, properties: { "disabled": "this.disabled" } }, providers: [MB_DATEPICKER_VALUE_ACCESSOR, MB_DATEPICKER_VALIDATORS], exportAs: ["mbDatepickerInput"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerInput, decorators: [{
            type: Directive,
            args: [{
                    // tslint:disable-next-line:directive-selector
                    selector: 'input[mb-datepicker]',
                    exportAs: 'mbDatepickerInput',
                    providers: [MB_DATEPICKER_VALUE_ACCESSOR, MB_DATEPICKER_VALIDATORS],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: MbFormField, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; }, propDecorators: { datepicker: [{
                type: Input,
                args: ['mb-datepicker']
            }], value: [{
                type: Input
            }], dateFilter: [{
                type: Input
            }], min: [{
                type: Input,
                args: ['minDate']
            }], max: [{
                type: Input,
                args: ['maxDate']
            }], disabled: [{
                type: HostBinding,
                args: ['disabled']
            }, {
                type: Input
            }], dateSelected: [{
                type: Output
            }], onKeyup: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }], onChange: [{
                type: HostListener,
                args: ['change', ['$event']]
            }], open: [{
                type: HostListener,
                args: ['focus']
            }], close: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class MbRangeMonthView extends MbMonthViewComponent {
    constructor() {
        super(...arguments);
        this.selectedRange = [null, null];
        this.startDateChanged = new EventEmitter();
        this.endDateChanged = new EventEmitter();
    }
    ngOnInit() {
        this.updateGrid();
    }
    ngOnChanges(changes) {
        const dateFilterUpdated = changes.dateFilter && !changes.dateFilter.firstChange;
        const calendarDataUpdated = changes.calendarData && !changes.calendarData.firstChange;
        if (dateFilterUpdated || calendarDataUpdated) {
            this.updateGrid();
        }
        if (changes.selectedRange && changes.selectedRange.previousValue &&
            (changes.selectedRange.previousValue[0] != changes.selectedRange.currentValue[0] ||
                changes.selectedRange.previousValue[1] != changes.selectedRange.currentValue[1])) {
            const dateToday = new Date();
            if (changes.selectedRange.currentValue[0] != changes.selectedRange.previousValue[0]) {
                this.month = changes.selectedRange.currentValue[0] ?
                    changes.selectedRange.currentValue[0].getMonth() : dateToday.getMonth();
                this.year = changes.selectedRange.currentValue[0] ?
                    changes.selectedRange.currentValue[0].getFullYear() : dateToday.getFullYear();
            }
            if (changes.selectedRange.currentValue[1] != changes.selectedRange.previousValue[1]) {
                this.month = changes.selectedRange.currentValue[1] ?
                    changes.selectedRange.currentValue[1].getMonth() : dateToday.getMonth();
                this.year = changes.selectedRange.currentValue[1] ?
                    changes.selectedRange.currentValue[1].getFullYear() : dateToday.getFullYear();
            }
            this.updateGrid();
        }
    }
    updateGrid() {
        this.startMonthGrid = new CalendarGridBuilder(this.year, this.month)
            .setMinDate(this.minDate)
            .setMaxDate(this.maxDate)
            .setDateFilter(this.dateFilter)
            .setCalendarData(this.calendarData)
            .build();
        this.endMonthGrid = new CalendarGridBuilder(this.year, this.month + 1)
            .setMinDate(this.minDate)
            .setMaxDate(this.maxDate)
            .setDateFilter(this.dateFilter)
            .setCalendarData(this.calendarData)
            .build();
    }
    onDateSelected(event) {
        if (this.selectedRange[0] === null && this.selectedRange[1] === null) {
            this.startDateChanged.emit(event);
            return;
        }
        if (this.selectedRange[1] === null) {
            if (this.isSelectedDateBeforeStart(event.date)) {
                this.startDateChanged.emit(event);
            }
            else {
                this.endDateChanged.emit(event);
            }
            return;
        }
        if (this.isSelectedDateBeforeStart(event.date)) {
            this.startDateChanged.emit(event);
            return;
        }
        if (this.isSelectedDateAfterEnd(event.date)) {
            const emitter = {
                [InputRangeType.START]: this.startDateChanged,
                [InputRangeType.END]: this.endDateChanged
            }[this.inputRangeType];
            emitter.emit(event);
            return;
        }
        if (this.isSelectedDateInRange(event.date)) {
            if (this.inputRangeType === InputRangeType.START) {
                this.startDateChanged.emit(event);
            }
            else {
                this.endDateChanged.emit(event);
            }
        }
    }
    get nextMonth() {
        return this.nextMonthDate().getMonth();
    }
    get nextMonthYear() {
        return this.nextMonthDate().getFullYear();
    }
    isSelectedDateBeforeStart(date) {
        return this.selectedRange[0] && compareDate(date, this.selectedRange[0]) < 0;
    }
    isSelectedDateAfterEnd(date) {
        return this.selectedRange[1] && compareDate(date, this.selectedRange[1]) > 0;
    }
    isSelectedDateInRange(date) {
        return (this.selectedRange[0] &&
            this.selectedRange[1] &&
            compareDate(date, this.selectedRange[0]) >= 0 &&
            compareDate(date, this.selectedRange[1]) <= 0);
    }
    disablePrevious() {
        return !!(this.minDate &&
            this.month === this.minDate.getMonth() &&
            this.year === this.minDate.getFullYear());
    }
    disableNext() {
        return !!(this.maxDate &&
            this.month === this.maxDate.getMonth() - 1 &&
            this.year === this.maxDate.getFullYear());
    }
}
MbRangeMonthView.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRangeMonthView, deps: null, target: i0.ɵɵFactoryTarget.Component });
MbRangeMonthView.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbRangeMonthView, selector: "mb-range-month-view", inputs: { year: "year", month: "month", selectedRange: "selectedRange", inputRangeType: "inputRangeType", minDate: "minDate", maxDate: "maxDate", dateFilter: "dateFilter", calendarData: "calendarData" }, outputs: { startDateChanged: "startDateChanged", endDateChanged: "endDateChanged" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"calendar-range-view\">\n  <div class=\"calendar-range-view__page\">\n    <mb-calendar-header\n      (navigateTo)=\"onNavigateTo($event)\"\n      [showLeftArrow]=\"true\"\n      [showRightArrow]=\"false\"\n      [disableLeftArrow]=\"disablePrevious()\"\n      [year]=\"year\"\n      [month]=\"month\"\n    >\n    </mb-calendar-header>\n\n    <mb-calendar-body\n      (dateSelected)=\"onDateSelected($event)\"\n      (dateHover)=\"updateHoveredDate($event)\"\n      [monthGrid]=\"startMonthGrid\"\n      [hoveredDate]=\"hoveredDate\"\n      [selectedRange]=\"selectedRange\"\n    >\n    </mb-calendar-body>\n  </div>\n\n  <div class=\"calendar-range-view__page\">\n    <mb-calendar-header\n      (navigateTo)=\"onNavigateTo($event)\"\n      [showLeftArrow]=\"false\"\n      [showRightArrow]=\"true\"\n      [disableRightArrow]=\"disableNext()\"\n      [year]=\"nextMonthYear\"\n      [month]=\"nextMonth\"\n    >\n    </mb-calendar-header>\n\n    <mb-calendar-body\n      (dateSelected)=\"onDateSelected($event)\"\n      (dateHover)=\"updateHoveredDate($event)\"\n      [monthGrid]=\"endMonthGrid\"\n      [hoveredDate]=\"hoveredDate\"\n      [selectedRange]=\"selectedRange\"\n    >\n    </mb-calendar-body>\n  </div>\n</div>\n", styles: [".calendar-range-view{display:flex;flex-wrap:wrap}@media (orientation: portrait){.calendar-range-view{width:min-content}}.calendar-range-view__page{display:inline-block;margin:0 4px}\n"], dependencies: [{ kind: "component", type: MbCalendarBody, selector: "mb-calendar-body", inputs: ["hoveredDate", "selectedDate", "selectedRange", "monthGrid"], outputs: ["dateSelected", "dateHover"] }, { kind: "component", type: MbCalendarHeader, selector: "mb-calendar-header", inputs: ["month", "year", "showLeftArrow", "showRightArrow", "disableLeftArrow", "disableRightArrow"], outputs: ["navigateTo"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRangeMonthView, decorators: [{
            type: Component,
            args: [{ selector: 'mb-range-month-view', template: "<div class=\"calendar-range-view\">\n  <div class=\"calendar-range-view__page\">\n    <mb-calendar-header\n      (navigateTo)=\"onNavigateTo($event)\"\n      [showLeftArrow]=\"true\"\n      [showRightArrow]=\"false\"\n      [disableLeftArrow]=\"disablePrevious()\"\n      [year]=\"year\"\n      [month]=\"month\"\n    >\n    </mb-calendar-header>\n\n    <mb-calendar-body\n      (dateSelected)=\"onDateSelected($event)\"\n      (dateHover)=\"updateHoveredDate($event)\"\n      [monthGrid]=\"startMonthGrid\"\n      [hoveredDate]=\"hoveredDate\"\n      [selectedRange]=\"selectedRange\"\n    >\n    </mb-calendar-body>\n  </div>\n\n  <div class=\"calendar-range-view__page\">\n    <mb-calendar-header\n      (navigateTo)=\"onNavigateTo($event)\"\n      [showLeftArrow]=\"false\"\n      [showRightArrow]=\"true\"\n      [disableRightArrow]=\"disableNext()\"\n      [year]=\"nextMonthYear\"\n      [month]=\"nextMonth\"\n    >\n    </mb-calendar-header>\n\n    <mb-calendar-body\n      (dateSelected)=\"onDateSelected($event)\"\n      (dateHover)=\"updateHoveredDate($event)\"\n      [monthGrid]=\"endMonthGrid\"\n      [hoveredDate]=\"hoveredDate\"\n      [selectedRange]=\"selectedRange\"\n    >\n    </mb-calendar-body>\n  </div>\n</div>\n", styles: [".calendar-range-view{display:flex;flex-wrap:wrap}@media (orientation: portrait){.calendar-range-view{width:min-content}}.calendar-range-view__page{display:inline-block;margin:0 4px}\n"] }]
        }], propDecorators: { year: [{
                type: Input
            }], month: [{
                type: Input
            }], selectedRange: [{
                type: Input
            }], inputRangeType: [{
                type: Input
            }], minDate: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], dateFilter: [{
                type: Input
            }], calendarData: [{
                type: Input
            }], startDateChanged: [{
                type: Output
            }], endDateChanged: [{
                type: Output
            }] } });

class MbRangeDatepickerContent {
    ngOnInit() {
        const date = this.resolveDate();
        this.year = date.getFullYear();
        this.month = date.getMonth();
    }
    onStartDateChanged(event) {
        this.rangeDatepicker.startDateChanged$.next(event);
    }
    onEndDateChanged(event) {
        this.rangeDatepicker.endDateChanged$.next(event);
    }
    resolveDate() {
        if (this.isStart) {
            return this.startDate || new Date();
        }
        else {
            return this.endDate || this.startDate || new Date();
        }
    }
    get isStart() {
        return this.inputRangeType === InputRangeType.START;
    }
    get isEnd() {
        return this.inputRangeType === InputRangeType.END;
    }
    get startDate() {
        return this.rangeDatepicker.selectedRange[0];
    }
    get endDate() {
        return this.rangeDatepicker.selectedRange[1];
    }
}
MbRangeDatepickerContent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRangeDatepickerContent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbRangeDatepickerContent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbRangeDatepickerContent, selector: "mb-range-datepicker-content", ngImport: i0, template: `
    <mb-range-month-view
      [year]="year"
      [month]="month"
      [minDate]="rangeDatepicker.minDate(inputRangeType)"
      [maxDate]="rangeDatepicker.maxDate(inputRangeType)"
      [dateFilter]="rangeDatepicker.dateFilter(inputRangeType)"
      [calendarData]="rangeDatepicker.calendarData"
      [inputRangeType]="inputRangeType"
      [selectedRange]="rangeDatepicker.selectedRange"
      (startDateChanged)="onStartDateChanged($event)"
      (endDateChanged)="onEndDateChanged($event)"
    ></mb-range-month-view>
    <ng-container *ngTemplateOutlet="rangeDatepicker.footer"></ng-container>
  `, isInline: true, styles: [":host{background:var(--color-white);border:1px solid var(--color-gray-500);border-radius:2px;box-shadow:0 0 8px 4px #0003;padding:8px;text-align:center}\n"], dependencies: [{ kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: MbRangeMonthView, selector: "mb-range-month-view", inputs: ["year", "month", "selectedRange", "inputRangeType", "minDate", "maxDate", "dateFilter", "calendarData"], outputs: ["startDateChanged", "endDateChanged"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRangeDatepickerContent, decorators: [{
            type: Component,
            args: [{ selector: 'mb-range-datepicker-content', template: `
    <mb-range-month-view
      [year]="year"
      [month]="month"
      [minDate]="rangeDatepicker.minDate(inputRangeType)"
      [maxDate]="rangeDatepicker.maxDate(inputRangeType)"
      [dateFilter]="rangeDatepicker.dateFilter(inputRangeType)"
      [calendarData]="rangeDatepicker.calendarData"
      [inputRangeType]="inputRangeType"
      [selectedRange]="rangeDatepicker.selectedRange"
      (startDateChanged)="onStartDateChanged($event)"
      (endDateChanged)="onEndDateChanged($event)"
    ></mb-range-month-view>
    <ng-container *ngTemplateOutlet="rangeDatepicker.footer"></ng-container>
  `, styles: [":host{background:var(--color-white);border:1px solid var(--color-gray-500);border-radius:2px;box-shadow:0 0 8px 4px #0003;padding:8px;text-align:center}\n"] }]
        }] });

class MbRangeDatepicker extends MbDatepickerOverlayComponent {
    constructor(overlayService) {
        super();
        this.startInputSubscription = Subscription.EMPTY;
        this.endInputSubscription = Subscription.EMPTY;
        this.selectedRange = [null, null];
        /**
         * These subjects are observed in the input directive, and the
         * values are emitted in the range datepicker content component
         */
        this.startDateChanged$ = new Subject();
        this.endDateChanged$ = new Subject();
        this.overlayService = overlayService;
    }
    registerStartInput(input) {
        this.startInputRef = input;
        this.startInputSubscription = input.valueChanged$.subscribe((value) => {
            if (this.isValueAfterEndDate(value)) {
                this.selectedRange = [value, null];
                this.endInputRef.value = null;
            }
            else {
                this.selectedRange = [value, this.selectedRange[1]];
            }
            if (this.endInputRef && value) {
                this.endInputRef.min = value;
            }
        });
        this.startDateChanged$.subscribe(() => {
            this.close();
            this.open(this.endInputRef.hostElement);
        });
    }
    registerEndInput(input) {
        this.endInputRef = input;
        this.endInputSubscription = input.valueChanged$.subscribe((value) => {
            this.selectedRange = [this.selectedRange[0], value];
        });
        this.endDateChanged$.subscribe(() => {
            this.close();
        });
    }
    isValueAfterEndDate(value) {
        return (value && this.selectedRange[1] && compareDate(value, this.selectedRange[1]) > 0);
    }
    getInputRef(inputRangeType) {
        return inputRangeType === InputRangeType.START
            ? this.startInputRef
            : this.endInputRef;
    }
    dateFilter(inputRangeType) {
        const inputRef = this.getInputRef(inputRangeType);
        return inputRef && inputRef.dateFilter;
    }
    minDate(inputRangeType) {
        const inputRef = this.getInputRef(inputRangeType);
        return inputRef && inputRef.min;
    }
    maxDate(inputRangeType) {
        const inputRef = this.getInputRef(inputRangeType);
        return inputRef && inputRef.max;
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this.startInputSubscription.unsubscribe();
        this.endInputSubscription.unsubscribe();
    }
    createPortal() {
        this.calendarPortal = new ComponentPortal(MbRangeDatepickerContent);
    }
    open(elementRef) {
        const ref = super.openPortal(this.positionRelativeTo || elementRef);
        ref.instance.rangeDatepicker = this;
        if (elementRef === this.startInputRef.hostElement) {
            ref.instance.inputRangeType = InputRangeType.START;
        }
        else if (elementRef === this.endInputRef.hostElement) {
            ref.instance.inputRangeType = InputRangeType.END;
        }
    }
}
MbRangeDatepicker.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRangeDatepicker, deps: [{ token: MbDatepickerOverlay }], target: i0.ɵɵFactoryTarget.Component });
MbRangeDatepicker.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbRangeDatepicker, selector: "mb-range-datepicker", inputs: { calendarData: "calendarData" }, viewQueries: [{ propertyName: "footer", first: true, predicate: ["rangeDatepickerFooter"], descendants: true, static: true }], exportAs: ["mbRangeDatepicker"], usesInheritance: true, ngImport: i0, template: `
    <ng-template #rangeDatepickerFooter>
      <ng-content select="mb-range-datepicker-footer"></ng-content>
    </ng-template>
  `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRangeDatepicker, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-range-datepicker',
                    template: `
    <ng-template #rangeDatepickerFooter>
      <ng-content select="mb-range-datepicker-footer"></ng-content>
    </ng-template>
  `,
                    exportAs: 'mbRangeDatepicker'
                }]
        }], ctorParameters: function () { return [{ type: MbDatepickerOverlay }]; }, propDecorators: { calendarData: [{
                type: Input
            }], footer: [{
                type: ViewChild,
                args: ['rangeDatepickerFooter', { static: true }]
            }] } });

const MB_RANGE_DATEPICKER_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MbRangeDatepickerInput),
    multi: true,
};
const MB_RANGE_DATEPICKER_VALIDATORS = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => MbRangeDatepickerInput),
    multi: true,
};
class MbRangeDatepickerInput {
    constructor(elementRef, cdr, formField, locale) {
        this.elementRef = elementRef;
        this.cdr = cdr;
        this.formField = formField;
        this.locale = locale;
        this.valueChanged$ = new Subject();
        this.dateSelected = new EventEmitter();
        this.startDatepicker$ = Subscription.EMPTY;
        this.endDatepicker$ = Subscription.EMPTY;
        // tslint:disable:variable-name
        this._value = null;
        this._max = null;
        this._min = null;
        this.valueOnChange = (_date) => { };
        this.valueOnTouched = () => { };
        this.validatorOnChange = () => { };
        this.updateValue = (event) => {
            this.value = event.date;
            this.valueOnChange(event.date);
            this.valueOnTouched();
        };
        this.minValidator = (control) => {
            const controlValue = getValidDateOrNull(control.value);
            return !this.min || !controlValue || compareDate(this.min, controlValue) <= 0
                ? null
                : { amDatepickerMin: { min: this.min, actual: controlValue } };
        };
        this.maxValidator = (control) => {
            const controlValue = getValidDateOrNull(control.value);
            return !this.max || !controlValue || compareDate(this.max, controlValue) >= 0
                ? null
                : { amDatepickerMax: { max: this.max, actual: controlValue } };
        };
        this.dateFilterValidator = (control) => {
            const controlValue = getValidDateOrNull(control.value);
            return !this._dateFilter || !controlValue || this._dateFilter(controlValue)
                ? null
                : { amDatepickerFilter: { actual: controlValue } };
        };
        // tslint:disable-next-line:member-ordering
        this.validators = Validators.compose([
            this.minValidator,
            this.maxValidator,
            this.dateFilterValidator,
        ]);
    }
    // mbRangeDatepickerStart binding
    set rangeDatepickerStart(value) {
        if (!value) {
            return;
        }
        this.rangeDatepickerComponent = value;
        this.rangeDatepickerComponent.registerStartInput(this);
        if (!this.startDatepicker$.closed) {
            this.startDatepicker$.unsubscribe();
        }
        this.startDatepicker$ = this.rangeDatepickerComponent.startDateChanged$.subscribe(this.updateValue);
    }
    get rangeDatepickerStart() {
        return this.rangeDatepickerComponent;
    }
    // mbRangeDatepickerEnd binding
    set rangeDatepickerEnd(value) {
        if (!value) {
            return;
        }
        this.rangeDatepickerComponent = value;
        this.rangeDatepickerComponent.registerEndInput(this);
        if (!this.endDatepicker$.closed) {
            this.endDatepicker$.unsubscribe();
        }
        this.endDatepicker$ = this.rangeDatepickerComponent.endDateChanged$.subscribe(this.updateValue);
    }
    get rangeDatepickerEnd() {
        return this.rangeDatepickerComponent;
    }
    // value binding
    set value(value) {
        const validated = getValidDateOrNull(value);
        this._value = validated;
        this.valueChanged$.next(validated);
        this.dateSelected.emit(validated);
        this.elementRef.nativeElement.value = validated
            ? formatInputDate(validated, this.locale)
            : '';
    }
    get value() {
        return this._value;
    }
    // dateFilter binding
    set dateFilter(value) {
        this._dateFilter = value;
        this.validatorOnChange();
    }
    get dateFilter() {
        return this._dateFilter;
    }
    // minDate binding
    set min(value) {
        this._min = getValidDateOrNull(value);
        this.validatorOnChange();
    }
    get min() {
        return this._min;
    }
    // maxDate binding
    set max(value) {
        this._max = getValidDateOrNull(value);
        this.validatorOnChange();
    }
    get max() {
        return this._max;
    }
    // disabled binding
    set disabled(value) {
        const coercedValue = coerceBooleanProperty(value);
        this._disabled = coercedValue;
    }
    get disabled() {
        return this._disabled;
    }
    get hostElement() {
        return this.formField ? this.formField.getConnectedOverlayOrigin() : this.elementRef;
    }
    onKeyup(event) {
        const inputElement = event.target;
        const dateValue = inputElement.value;
        if (dateValue && !regexValidChars.test(dateValue)) {
            if (regexFormatDate.test(dateValue)) {
                const dateValueFomatted = `${dateValue.substring(3, 5)}/${dateValue.substring(0, 2)}/${dateValue.substring(6)}`;
                const date = new Date(dateValueFomatted);
                if (!(this.value?.getTime() === date.getTime()) ||
                    !this.min ||
                    (this.min && date >= this.min && (!this.max || (this.max && date <= this.max)))) {
                    this.writeValue(date);
                }
            }
            else if (dateValue === '') {
                this.writeValue(null);
            }
        }
    }
    onChange(event) {
        const inputElement = event.target;
        if (!this.value) {
            inputElement.value = '';
            this.writeValue(null);
        }
        const dateValue = inputElement.value;
        const dateValueFomatted = `${dateValue.substring(3, 5)}/${dateValue.substring(0, 2)}/${dateValue.substring(6)}`;
        const date = new Date(dateValueFomatted);
        if (!regexFormatDate.test(dateValue) || !date.getTime()) {
            inputElement.value = '';
            this.writeValue(null);
        }
        this.rangeDatepickerComponent.close();
    }
    ngOnInit() {
        // this.elementRef.nativeElement.setAttribute('readonly', 'readonly');
    }
    ngOnDestroy() {
        this.startDatepicker$.unsubscribe();
        this.endDatepicker$.unsubscribe();
    }
    // focus binding
    open() {
        this.rangeDatepickerComponent.close();
        this.rangeDatepickerComponent.open(this.hostElement);
    }
    // keydown binding
    close(event) {
        if (event.key === 'Tab')
            this.rangeDatepickerComponent.close();
    }
    // ControlValueAccessor methods
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.valueOnChange = fn;
    }
    registerOnTouched(fn) {
        this.valueOnTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    // Validator methods
    validate(control) {
        return this.validators ? this.validators(control) : null;
    }
    registerOnValidatorChange(fn) {
        this.validatorOnChange = fn;
    }
}
MbRangeDatepickerInput.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRangeDatepickerInput, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: MbFormField, host: true, optional: true }, { token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Directive });
MbRangeDatepickerInput.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbRangeDatepickerInput, selector: "input[mb-range-datepicker-start], input[mb-range-datepicker-end]", inputs: { rangeDatepickerStart: ["mb-range-datepicker-start", "rangeDatepickerStart"], rangeDatepickerEnd: ["mb-range-datepicker-end", "rangeDatepickerEnd"], value: "value", dateFilter: "dateFilter", min: ["minDate", "min"], max: ["maxDate", "max"], disabled: "disabled" }, outputs: { dateSelected: "dateSelected" }, host: { listeners: { "keyup": "onKeyup($event)", "change": "onChange($event)", "focus": "open()", "keydown": "close($event)" }, properties: { "disabled": "this.disabled" } }, providers: [MB_RANGE_DATEPICKER_VALUE_ACCESSOR, MB_RANGE_DATEPICKER_VALIDATORS], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRangeDatepickerInput, decorators: [{
            type: Directive,
            args: [{
                    // tslint:disable-next-line:directive-selector
                    selector: 'input[mb-range-datepicker-start], input[mb-range-datepicker-end]',
                    providers: [MB_RANGE_DATEPICKER_VALUE_ACCESSOR, MB_RANGE_DATEPICKER_VALIDATORS],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: MbFormField, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; }, propDecorators: { rangeDatepickerStart: [{
                type: Input,
                args: ['mb-range-datepicker-start']
            }], rangeDatepickerEnd: [{
                type: Input,
                args: ['mb-range-datepicker-end']
            }], value: [{
                type: Input
            }], dateFilter: [{
                type: Input
            }], min: [{
                type: Input,
                args: ['minDate']
            }], max: [{
                type: Input,
                args: ['maxDate']
            }], disabled: [{
                type: HostBinding,
                args: ['disabled']
            }, {
                type: Input
            }], dateSelected: [{
                type: Output
            }], onKeyup: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }], onChange: [{
                type: HostListener,
                args: ['change', ['$event']]
            }], open: [{
                type: HostListener,
                args: ['focus']
            }], close: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class MbDatepickerFooter {
}
MbDatepickerFooter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerFooter, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbDatepickerFooter.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbDatepickerFooter, selector: "mb-datepicker-footer,mb-range-datepicker-footer", ngImport: i0, template: '<ng-content></ng-content>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerFooter, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-datepicker-footer,mb-range-datepicker-footer',
                    template: '<ng-content></ng-content>'
                }]
        }] });

class MbDatepickerModule {
}
MbDatepickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbDatepickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerModule, declarations: [MbDatepicker,
        MbSingleMonthView,
        MbCalendarBody,
        MbCalendarHeader,
        MbDatepickerInput,
        MbDatepickerContent,
        MbRangeDatepickerContent,
        MbRangeDatepicker,
        MbRangeDatepickerInput,
        MbRangeMonthView,
        MbDatepickerFooter], imports: [CommonModule, MbIconModule, OverlayModule, FormsModule], exports: [MbDatepicker,
        MbDatepickerInput,
        MbRangeDatepicker,
        MbRangeDatepickerInput,
        MbDatepickerFooter] });
MbDatepickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerModule, providers: [MbDatepickerOverlay], imports: [CommonModule, MbIconModule, OverlayModule, FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDatepickerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        MbDatepicker,
                        MbSingleMonthView,
                        MbCalendarBody,
                        MbCalendarHeader,
                        MbDatepickerInput,
                        MbDatepickerContent,
                        MbRangeDatepickerContent,
                        MbRangeDatepicker,
                        MbRangeDatepickerInput,
                        MbRangeMonthView,
                        MbDatepickerFooter,
                    ],
                    providers: [MbDatepickerOverlay],
                    imports: [CommonModule, MbIconModule, OverlayModule, FormsModule],
                    exports: [
                        MbDatepicker,
                        MbDatepickerInput,
                        MbRangeDatepicker,
                        MbRangeDatepickerInput,
                        MbDatepickerFooter,
                    ],
                }]
        }] });

/**
 * Returns the result of converting the value to its
 * corresponding pixel value and dividing it over the
 * trackWidth. The result of this function is the
 * percentage of the value among the full track width.
 * @param value
 * @param minLimit The minimum value
 * @param maxLimit The maximum value
 * @param trackWidth The track width in pixels
 */
function getFractionComplete(value, minLimit, maxLimit, trackWidth) {
    return convertValueToPixel(value, minLimit, maxLimit, trackWidth) / trackWidth;
}
/**
 * Converts a pixel value into the corresponding
 * numeric value taking into account the track
 * width (in pixels) and the min and max values.
 * @param pixelValue A value in pixels
 * @param minLimit The minimum value
 * @param maxLimit The maximum value
 * @param trackWidth The track width in pixels
 */
function convertPixelToValue(pixelValue, minLimit, maxLimit, trackWidth) {
    const range = maxLimit - minLimit;
    const preValue = pixelValue / trackWidth;
    const rounded = Math.round(range * preValue);
    return rounded + minLimit;
}
/**
 * Converts a numeric value into the corresponding
 * amount of pixels taking into account the track
 * width (in pixels) and the min and max values.
 * @param value
 * @param minLimit The minimum value
 * @param maxLimit The maximum value
 * @param trackWidth The track width in pixels
 */
function convertValueToPixel(value, minLimit, maxLimit, trackWidth) {
    if (value >= maxLimit) {
        return trackWidth;
    }
    if (value <= minLimit) {
        return 0;
    }
    return Math.round(trackWidth * ((value - minLimit) / (maxLimit - minLimit)));
}

class MbSliderRange {
    constructor(zone, cdr, sanitizer, document) {
        this.zone = zone;
        this.cdr = cdr;
        this.sanitizer = sanitizer;
        this.document = document;
        this.rangeChange = new EventEmitter();
        this.minMouseMoveHandler = this.onMinMouseMove.bind(this);
        this.maxMouseMoveHandler = this.onMaxMouseMove.bind(this);
        this.mouseUpHandler = this.onMouseUp.bind(this);
        this.minTouchStartHandler = this.onMinTouchStart.bind(this);
        this.maxTouchStartHandler = this.onMaxTouchStart.bind(this);
        this.minTouchMoveHandler = this.onMinTouchMove.bind(this);
        this.maxTouchMoveHandler = this.onMaxTouchMove.bind(this);
        this.touchEndHandler = this.onTouchEnd.bind(this);
    }
    ngOnInit() {
        if (this.min === undefined) {
            this.min = this.minLimit;
        }
        if (this.max === undefined) {
            this.max = this.maxLimit;
        }
    }
    ngAfterViewInit() {
        this.trackRect = this.track.nativeElement.getBoundingClientRect();
        this.minThumb.nativeElement.addEventListener('touchstart', this.minTouchStartHandler);
        this.maxThumb.nativeElement.addEventListener('touchstart', this.maxTouchStartHandler);
        this.cdr.detectChanges();
    }
    // Mouse events
    onMinMouseDown() {
        this.zone.runOutsideAngular(() => {
            this.document.addEventListener('mousemove', this.minMouseMoveHandler);
            this.document.addEventListener('mouseup', this.mouseUpHandler);
        });
    }
    onMaxMouseDown() {
        this.zone.runOutsideAngular(() => {
            this.document.addEventListener('mousemove', this.maxMouseMoveHandler);
            this.document.addEventListener('mouseup', this.mouseUpHandler);
        });
    }
    onMinMouseMove(event) {
        this.updateMinThumbPosition(event.clientX);
    }
    onMaxMouseMove(event) {
        this.updateMaxThumbPosition(event.clientX);
    }
    onMouseUp() {
        this.zone.run(() => {
            this.rangeChange.emit({ min: this.min, max: this.max });
        });
        this.document.removeEventListener('mousemove', this.maxMouseMoveHandler);
        this.document.removeEventListener('mousemove', this.minMouseMoveHandler);
        this.document.removeEventListener('mouseup', this.mouseUpHandler);
    }
    // Touch events
    onMinTouchStart() {
        this.zone.runOutsideAngular(() => {
            this.document.addEventListener('touchmove', this.minTouchMoveHandler);
            this.document.addEventListener('touchend', this.touchEndHandler);
        });
    }
    onMaxTouchStart() {
        this.zone.runOutsideAngular(() => {
            this.document.addEventListener('touchmove', this.maxTouchMoveHandler);
            this.document.addEventListener('touchend', this.touchEndHandler);
        });
    }
    onMinTouchMove(event) {
        this.updateMinThumbPosition(event.touches.item(event.touches.length - 1).clientX);
    }
    onMaxTouchMove(event) {
        this.updateMaxThumbPosition(event.touches.item(event.touches.length - 1).clientX);
    }
    onTouchEnd() {
        this.zone.run(() => {
            this.rangeChange.emit({ min: this.min, max: this.max });
        });
        this.document.removeEventListener('touchmove', this.minTouchMoveHandler);
        this.document.removeEventListener('touchmove', this.maxTouchMoveHandler);
        this.document.removeEventListener('touchend', this.touchEndHandler);
    }
    updateMinThumbPosition(minThumbPosition) {
        let newValue = convertPixelToValue(minThumbPosition - this.trackRect.left, this.minLimit, this.maxLimit, this.trackRect.width);
        if (newValue >= this.max) {
            return;
        }
        if (minThumbPosition - this.trackRect.left < 0) {
            newValue = this.minLimit;
        }
        this.zone.run(() => {
            this.min = newValue;
        });
    }
    updateMaxThumbPosition(maxThumbPosition) {
        let newValue = convertPixelToValue(maxThumbPosition - this.trackRect.left, this.minLimit, this.maxLimit, this.trackRect.width);
        if (newValue <= this.min) {
            return;
        }
        if (maxThumbPosition - this.trackRect.left > this.trackRect.width) {
            newValue = this.maxLimit;
        }
        this.zone.run(() => {
            this.max = newValue;
        });
    }
    get minThumbPosition() {
        if (!this.trackRect) {
            return '0%';
        }
        const minFraction = getFractionComplete(this.min, this.minLimit, this.maxLimit, this.trackRect.width);
        return `${minFraction * 100}%`;
    }
    get maxThumbPosition() {
        if (!this.trackRect) {
            return '0%';
        }
        const maxFraction = getFractionComplete(this.max, this.minLimit, this.maxLimit, this.trackRect.width);
        return `${maxFraction * 100}%`;
    }
    get fillWidth() {
        if (!this.trackRect) {
            return 'translate(0%, -50%)';
        }
        const minFraction = getFractionComplete(this.min, this.minLimit, this.maxLimit, this.trackRect.width);
        const maxFraction = getFractionComplete(this.max, this.minLimit, this.maxLimit, this.trackRect.width);
        return this.sanitizer.bypassSecurityTrustStyle(`translate(${minFraction * 100}%, -50%) scaleX(${maxFraction - minFraction})`);
    }
}
MbSliderRange.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSliderRange, deps: [{ token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: i1$1.DomSanitizer }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
MbSliderRange.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbSliderRange, selector: "mb-slider-range", inputs: { label: "label", minLimit: "minLimit", maxLimit: "maxLimit", min: "min", max: "max" }, outputs: { rangeChange: "rangeChange" }, host: { classAttribute: "mb-slider-range" }, viewQueries: [{ propertyName: "minThumb", first: true, predicate: ["minThumb"], descendants: true }, { propertyName: "maxThumb", first: true, predicate: ["maxThumb"], descendants: true }, { propertyName: "track", first: true, predicate: ["track"], descendants: true }], ngImport: i0, template: "<div class=\"mb-slider__data\">\n  <div class=\"mb-slider__value-min\">\n    <label>\n      <ng-template\n        *ngIf=\"label\"\n        [ngTemplateOutletContext]=\"{ $implicit: min }\"\n        [ngTemplateOutlet]=\"label\"\n      ></ng-template>\n      <ng-container *ngIf=\"!label\">{{ min }}</ng-container>\n    </label>\n  </div>\n  <div class=\"mb-slider__value-max\">\n    <label>\n      <ng-template\n        *ngIf=\"label\"\n        [ngTemplateOutletContext]=\"{ $implicit: max }\"\n        [ngTemplateOutlet]=\"label\"\n      ></ng-template>\n      <ng-container *ngIf=\"!label\">{{ max }}</ng-container>\n    </label>\n  </div>\n</div>\n<div class=\"mb-slider__container\">\n  <div class=\"mb-slider__slider\">\n    <div\n      #minThumb\n      role=\"slider\"\n      class=\"mb-slider__thumb\"\n      [style.left]=\"minThumbPosition\"\n      (mousedown)=\"onMinMouseDown()\"\n    ></div>\n    <div\n      #maxThumb\n      role=\"slider\"\n      class=\"mb-slider__thumb\"\n      [style.left]=\"maxThumbPosition\"\n      (mousedown)=\"onMaxMouseDown()\"\n    ></div>\n    <div #track class=\"mb-slider__track\"></div>\n    <div class=\"mb-slider__fill\" [style.transform]=\"fillWidth\"></div>\n  </div>\n</div>\n", styles: [".mb-slider__value-max,.mb-slider__value-min{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;display:flex;position:absolute;-webkit-user-select:none;user-select:none}@media only screen and (max-width: 767px){.mb-slider__value-max,.mb-slider__value-min{line-height:1.5}}.mb-slider__fill,.mb-slider__track{border-radius:2px;height:4px;pointer-events:none;position:absolute;transform:translateY(-50%);width:100%}.mb-slider:host{display:block;height:60px;margin:0;padding:0}.mb-slider__data{display:flex;height:20px;position:relative}.mb-slider__value-min{left:0}.mb-slider__value-max{right:0}.mb-slider__container{align-items:center;display:flex;height:32px;-webkit-user-select:none;user-select:none}.mb-slider__slider{border-radius:2px;margin:0 12px;position:relative;width:100%}.mb-slider__thumb{background:var(--color-foreground);border:1px solid var(--color-secondary);border-radius:50%;box-shadow:inset 0 0 0 1px transparent,inset 0 0 0 2px transparent;cursor:pointer;height:24px;outline:none;position:absolute;-webkit-tap-highlight-color:transparent;top:0;transform:translate(-50%,-50%);width:24px;z-index:3}.mb-slider__thumb:active{box-shadow:inset 0 0 1px #fff,inset 0 1px 7px #ebebeb,0 3px 6px -3px #bbb}.mb-slider__track{background:var(--color-gray-600)}.mb-slider__fill{background:var(--color-secondary);transform-origin:left;transition:background .11s cubic-bezier(.2,0,.38,.9)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSliderRange, decorators: [{
            type: Component,
            args: [{ selector: 'mb-slider-range', host: {
                        class: 'mb-slider-range',
                    }, template: "<div class=\"mb-slider__data\">\n  <div class=\"mb-slider__value-min\">\n    <label>\n      <ng-template\n        *ngIf=\"label\"\n        [ngTemplateOutletContext]=\"{ $implicit: min }\"\n        [ngTemplateOutlet]=\"label\"\n      ></ng-template>\n      <ng-container *ngIf=\"!label\">{{ min }}</ng-container>\n    </label>\n  </div>\n  <div class=\"mb-slider__value-max\">\n    <label>\n      <ng-template\n        *ngIf=\"label\"\n        [ngTemplateOutletContext]=\"{ $implicit: max }\"\n        [ngTemplateOutlet]=\"label\"\n      ></ng-template>\n      <ng-container *ngIf=\"!label\">{{ max }}</ng-container>\n    </label>\n  </div>\n</div>\n<div class=\"mb-slider__container\">\n  <div class=\"mb-slider__slider\">\n    <div\n      #minThumb\n      role=\"slider\"\n      class=\"mb-slider__thumb\"\n      [style.left]=\"minThumbPosition\"\n      (mousedown)=\"onMinMouseDown()\"\n    ></div>\n    <div\n      #maxThumb\n      role=\"slider\"\n      class=\"mb-slider__thumb\"\n      [style.left]=\"maxThumbPosition\"\n      (mousedown)=\"onMaxMouseDown()\"\n    ></div>\n    <div #track class=\"mb-slider__track\"></div>\n    <div class=\"mb-slider__fill\" [style.transform]=\"fillWidth\"></div>\n  </div>\n</div>\n", styles: [".mb-slider__value-max,.mb-slider__value-min{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;display:flex;position:absolute;-webkit-user-select:none;user-select:none}@media only screen and (max-width: 767px){.mb-slider__value-max,.mb-slider__value-min{line-height:1.5}}.mb-slider__fill,.mb-slider__track{border-radius:2px;height:4px;pointer-events:none;position:absolute;transform:translateY(-50%);width:100%}.mb-slider:host{display:block;height:60px;margin:0;padding:0}.mb-slider__data{display:flex;height:20px;position:relative}.mb-slider__value-min{left:0}.mb-slider__value-max{right:0}.mb-slider__container{align-items:center;display:flex;height:32px;-webkit-user-select:none;user-select:none}.mb-slider__slider{border-radius:2px;margin:0 12px;position:relative;width:100%}.mb-slider__thumb{background:var(--color-foreground);border:1px solid var(--color-secondary);border-radius:50%;box-shadow:inset 0 0 0 1px transparent,inset 0 0 0 2px transparent;cursor:pointer;height:24px;outline:none;position:absolute;-webkit-tap-highlight-color:transparent;top:0;transform:translate(-50%,-50%);width:24px;z-index:3}.mb-slider__thumb:active{box-shadow:inset 0 0 1px #fff,inset 0 1px 7px #ebebeb,0 3px 6px -3px #bbb}.mb-slider__track{background:var(--color-gray-600)}.mb-slider__fill{background:var(--color-secondary);transform-origin:left;transition:background .11s cubic-bezier(.2,0,.38,.9)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: i1$1.DomSanitizer }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { label: [{
                type: Input
            }], minLimit: [{
                type: Input
            }], maxLimit: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], rangeChange: [{
                type: Output
            }], minThumb: [{
                type: ViewChild,
                args: ['minThumb']
            }], maxThumb: [{
                type: ViewChild,
                args: ['maxThumb']
            }], track: [{
                type: ViewChild,
                args: ['track']
            }] } });

class MbSliderRangeModule {
}
MbSliderRangeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSliderRangeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbSliderRangeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbSliderRangeModule, declarations: [MbSliderRange], imports: [CommonModule], exports: [MbSliderRange] });
MbSliderRangeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSliderRangeModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSliderRangeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [MbSliderRange],
                    exports: [MbSliderRange]
                }]
        }] });

class MbButton {
    constructor(el) {
        this.el = el;
        this.variant = 'principal';
        this.color = 'primary';
        this.disabled = false;
        this.loading = false;
    }
    get isAnchorTag() {
        return this.el.nativeElement.tagName.toLowerCase() === 'a';
    }
    onKeyPress() {
        setTimeout(() => {
            this.el.nativeElement.blur();
        }, 0);
    }
}
MbButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbButton, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
MbButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbButton, selector: "button[mb-button], a[mb-button]", inputs: { variant: "variant", color: "color", disabled: "disabled", loading: "loading" }, host: { listeners: { "keydown.enter": "onKeyPress()" }, properties: { "class.mb-button--principal": "variant === \"principal\"", "class.mb-button--outline": "variant === \"outline\"", "class.mb-button--floating": "variant === \"floating\"", "class.mb-button--link": "variant === \"link\"", "class.mb-button--primary": "color === \"primary\"", "class.mb-button--secondary": "color === \"secondary\"", "class.mb-button--error": "color === \"critical\"", "class.mb-button--loading": "loading", "class.mb-button--disabled": "disabled", "attr.tabindex": "isAnchorTag && disabled ? -1 : null", "disabled": "disabled" }, classAttribute: "mb-button" }, queries: [{ propertyName: "icon", first: true, predicate: MbIcon, descendants: true }], ngImport: i0, template: "<span class=\"mb-button__content\" *ngIf=\"!loading; else loadingBlock\">\n  <span *ngIf=\"icon\" class=\"mb-button__icon\">\n    <ng-content select=\"mb-icon\"></ng-content>\n  </span>\n  <span>\n    <ng-content></ng-content>\n  </span>\n</span>\n<ng-template #loadingBlock>\n  <div class=\"mb-button__loader\"></div>\n</ng-template>\n", styles: [":host.mb-button.mb-button--principal.mb-button--disabled,:host.mb-button.mb-button--principal.mb-button--disabled:hover,:host.mb-button.mb-button--principal.mb-button--disabled:visited{background-color:var(--color-disabled);box-shadow:none;color:var(--color-text-disabled)}:host.mb-button.mb-button--floating.mb-button--disabled,:host.mb-button.mb-button--floating.mb-button--disabled:hover,:host.mb-button.mb-button--floating.mb-button--disabled:visited{background-color:transparent;color:var(--color-text-disabled)}:host.mb-button.mb-button--outline.mb-button--disabled,:host.mb-button.mb-button--outline.mb-button--disabled:hover,:host.mb-button.mb-button--outline.mb-button--disabled:visited{box-shadow:unset;color:var(--color-text-disabled);outline-color:var(--color-disabled)}:host.mb-button.mb-button--link.mb-button--disabled,:host.mb-button.mb-button--link.mb-button--disabled:hover,:host.mb-button.mb-button--link.mb-button--disabled:visited{color:var(--color-text-disabled)}.mb-button:host{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;-webkit-tap-highlight-color:rgba(0,0,0,0);border-radius:1.5rem;border-style:unset;cursor:pointer;display:inline-block;min-height:2.5rem;min-width:7.5rem;padding:.5rem 1rem;position:relative;transition:all .5s;vertical-align:middle;white-space:nowrap}@media only screen and (max-width: 767px){.mb-button:host{line-height:1.5}}.mb-button:host--loading{pointer-events:none}.mb-button:host--loading .mb-button__loader,.mb-button:host--loading .mb-button__loader:before,.mb-button:host--loading .mb-button__loader:after{animation:load 1.8s infinite ease-in-out;animation-fill-mode:both;background:transparent;border-radius:50%;height:1em;width:1em}.mb-button:host--loading .mb-button__loader{animation-delay:-.16s;color:var(--color-white);font-size:.625rem;left:50%;margin:calc(-.5em - 10px) 0 0 -.5em;position:absolute;top:50%;transform:translateZ(0)}.mb-button:host--loading .mb-button__loader:before,.mb-button:host--loading .mb-button__loader:after{content:\"\";position:absolute;top:0}.mb-button:host--loading .mb-button__loader:before{animation-delay:-.32s;left:-2em}.mb-button:host--loading .mb-button__loader:after{left:2em}@keyframes load{0%,80%,to{box-shadow:0 10px 0 -1.3em}40%{box-shadow:0 10px}}.mb-button:host[tabindex=\"-1\"]{pointer-events:none}.mb-button__content{display:flex;gap:.5rem;justify-content:center}.mb-button__icon{align-items:center;display:inline-flex;justify-content:center;width:1.5rem}:host.mb-button.mb-button--link.mb-button--disabled .mb-button__icon,:host.mb-button.mb-button--link.mb-button--disabled:hover .mb-button__icon,:host.mb-button.mb-button--link.mb-button--disabled:visited .mb-button__icon{color:var(--color-text-disabled)}:host.mb-button--principal.mb-button--primary{background-color:var(--color-primary-dark);color:var(--color-primary-contrast)}@media not all and (hover: none){:host.mb-button--principal.mb-button--primary:hover{background-color:var(--color-primary-light);box-shadow:0 4px 12px #0e0f2026}}:host.mb-button--principal.mb-button--primary:focus-visible{background-color:var(--color-primary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--principal.mb-button--primary .mb-button__loader{color:var(--color-primary-contrast)}:host.mb-button--floating.mb-button--primary{background-color:transparent;color:var(--color-primary-dark)}@media not all and (hover: none){:host.mb-button--floating.mb-button--primary:hover{background-color:var(--color-primary-clear);color:var(--color-primary-dark)}}:host.mb-button--floating.mb-button--primary:focus-visible{background-color:var(--color-primary-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--floating.mb-button--primary .mb-button__loader{color:var(--color-primary-dark)}:host.mb-button--outline.mb-button--primary{background-color:transparent;color:var(--color-primary-dark);outline:1px solid}@media not all and (hover: none){:host.mb-button--outline.mb-button--primary:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-primary);outline-color:var(--color-primary-light)}}:host.mb-button--outline.mb-button--primary:focus-visible{outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--outline.mb-button--primary .mb-button__loader{color:var(--color-primary-dark)}:host.mb-button--link.mb-button--primary{background-color:transparent;border-radius:.5rem;color:var(--color-primary-dark);text-decoration:underline;text-decoration-line:underline;-webkit-text-decoration-skip-ink:none;text-decoration-skip-ink:none;text-decoration-style:solid;text-decoration-thickness:auto;text-transform:unset;text-underline-offset:auto;text-underline-position:from-font}:host.mb-button--link.mb-button--primary .mb-button__icon{color:var(--color-primary-dark)}@media not all and (hover: none){:host.mb-button--link.mb-button--primary:hover{color:var(--color-primary)}}:host.mb-button--link.mb-button--primary:focus-visible{color:var(--color-primary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--link.mb-button--primary:visited{color:var(--color-notification-dark)}:host.mb-button--link.mb-button--primary:visited .mb-button__icon{color:var(--color-notification-dark)}:host.mb-button--principal.mb-button--secondary{background-color:var(--color-secondary-dark);color:var(--color-secondary-contrast)}@media not all and (hover: none){:host.mb-button--principal.mb-button--secondary:hover{background-color:var(--color-secondary-light);box-shadow:0 4px 12px #0e0f2026}}:host.mb-button--principal.mb-button--secondary:focus-visible{background-color:var(--color-secondary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--principal.mb-button--secondary .mb-button__loader{color:var(--color-secondary-contrast)}:host.mb-button--floating.mb-button--secondary{background-color:transparent;color:var(--color-secondary-dark)}@media not all and (hover: none){:host.mb-button--floating.mb-button--secondary:hover{background-color:var(--color-secondary-clear);color:var(--color-secondary-dark)}}:host.mb-button--floating.mb-button--secondary:focus-visible{background-color:var(--color-secondary-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--floating.mb-button--secondary .mb-button__loader{color:var(--color-secondary-dark)}:host.mb-button--outline.mb-button--secondary{background-color:transparent;color:var(--color-secondary-dark);outline:1px solid}@media not all and (hover: none){:host.mb-button--outline.mb-button--secondary:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-secondary);outline-color:var(--color-secondary-light)}}:host.mb-button--outline.mb-button--secondary:focus-visible{outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--outline.mb-button--secondary .mb-button__loader{color:var(--color-secondary-dark)}:host.mb-button--link.mb-button--secondary{background-color:transparent;border-radius:.5rem;color:var(--color-secondary-dark);text-decoration:underline;text-decoration-line:underline;-webkit-text-decoration-skip-ink:none;text-decoration-skip-ink:none;text-decoration-style:solid;text-decoration-thickness:auto;text-transform:unset;text-underline-offset:auto;text-underline-position:from-font}:host.mb-button--link.mb-button--secondary .mb-button__icon{color:var(--color-secondary-dark)}@media not all and (hover: none){:host.mb-button--link.mb-button--secondary:hover{color:var(--color-secondary)}}:host.mb-button--link.mb-button--secondary:focus-visible{color:var(--color-secondary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--link.mb-button--secondary:visited{color:var(--color-notification-dark)}:host.mb-button--link.mb-button--secondary:visited .mb-button__icon{color:var(--color-notification-dark)}:host.mb-button--principal.mb-button--error{background-color:var(--color-error-dark);color:var(--color-error-clear)}@media not all and (hover: none){:host.mb-button--principal.mb-button--error:hover{background-color:var(--color-error);box-shadow:0 4px 12px #0e0f2026}}:host.mb-button--principal.mb-button--error:focus-visible{background-color:var(--color-error);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--principal.mb-button--error .mb-button__loader{color:var(--color-error-clear)}:host.mb-button--floating.mb-button--error{background-color:transparent;color:var(--color-error-dark)}@media not all and (hover: none){:host.mb-button--floating.mb-button--error:hover{background-color:var(--color-error-clear);color:var(--color-error-dark)}}:host.mb-button--floating.mb-button--error:focus-visible{background-color:var(--color-error-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--floating.mb-button--error .mb-button__loader{color:var(--color-error-dark)}:host.mb-button--outline.mb-button--error{background-color:transparent;color:var(--color-error-dark);outline:1px solid}@media not all and (hover: none){:host.mb-button--outline.mb-button--error:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-error);outline-color:var(--color-error)}}:host.mb-button--outline.mb-button--error:focus-visible{outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--outline.mb-button--error .mb-button__loader{color:var(--color-error-dark)}:host.mb-button--link.mb-button--error{background-color:transparent;border-radius:.5rem;color:var(--color-error-dark);text-decoration:underline;text-decoration-line:underline;-webkit-text-decoration-skip-ink:none;text-decoration-skip-ink:none;text-decoration-style:solid;text-decoration-thickness:auto;text-transform:unset;text-underline-offset:auto;text-underline-position:from-font}:host.mb-button--link.mb-button--error .mb-button__icon{color:var(--color-error-dark)}@media not all and (hover: none){:host.mb-button--link.mb-button--error:hover{color:var(--color-error)}}:host.mb-button--link.mb-button--error:focus-visible{color:var(--color-error);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--link.mb-button--error:visited{color:var(--color-notification-dark)}:host.mb-button--link.mb-button--error:visited .mb-button__icon{color:var(--color-notification-dark)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbButton, decorators: [{
            type: Component,
            args: [{ selector: 'button[mb-button], a[mb-button]', host: {
                        'class': 'mb-button',
                        '[class.mb-button--principal]': 'variant === "principal"',
                        '[class.mb-button--outline]': 'variant === "outline"',
                        '[class.mb-button--floating]': 'variant === "floating"',
                        '[class.mb-button--link]': 'variant === "link"',
                        '[class.mb-button--primary]': 'color === "primary"',
                        '[class.mb-button--secondary]': 'color === "secondary"',
                        '[class.mb-button--error]': 'color === "critical"',
                        '[class.mb-button--loading]': 'loading',
                        '[class.mb-button--disabled]': 'disabled',
                        '[attr.tabindex]': 'isAnchorTag && disabled ? -1 : null',
                        '[disabled]': 'disabled',
                    }, template: "<span class=\"mb-button__content\" *ngIf=\"!loading; else loadingBlock\">\n  <span *ngIf=\"icon\" class=\"mb-button__icon\">\n    <ng-content select=\"mb-icon\"></ng-content>\n  </span>\n  <span>\n    <ng-content></ng-content>\n  </span>\n</span>\n<ng-template #loadingBlock>\n  <div class=\"mb-button__loader\"></div>\n</ng-template>\n", styles: [":host.mb-button.mb-button--principal.mb-button--disabled,:host.mb-button.mb-button--principal.mb-button--disabled:hover,:host.mb-button.mb-button--principal.mb-button--disabled:visited{background-color:var(--color-disabled);box-shadow:none;color:var(--color-text-disabled)}:host.mb-button.mb-button--floating.mb-button--disabled,:host.mb-button.mb-button--floating.mb-button--disabled:hover,:host.mb-button.mb-button--floating.mb-button--disabled:visited{background-color:transparent;color:var(--color-text-disabled)}:host.mb-button.mb-button--outline.mb-button--disabled,:host.mb-button.mb-button--outline.mb-button--disabled:hover,:host.mb-button.mb-button--outline.mb-button--disabled:visited{box-shadow:unset;color:var(--color-text-disabled);outline-color:var(--color-disabled)}:host.mb-button.mb-button--link.mb-button--disabled,:host.mb-button.mb-button--link.mb-button--disabled:hover,:host.mb-button.mb-button--link.mb-button--disabled:visited{color:var(--color-text-disabled)}.mb-button:host{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;-webkit-tap-highlight-color:rgba(0,0,0,0);border-radius:1.5rem;border-style:unset;cursor:pointer;display:inline-block;min-height:2.5rem;min-width:7.5rem;padding:.5rem 1rem;position:relative;transition:all .5s;vertical-align:middle;white-space:nowrap}@media only screen and (max-width: 767px){.mb-button:host{line-height:1.5}}.mb-button:host--loading{pointer-events:none}.mb-button:host--loading .mb-button__loader,.mb-button:host--loading .mb-button__loader:before,.mb-button:host--loading .mb-button__loader:after{animation:load 1.8s infinite ease-in-out;animation-fill-mode:both;background:transparent;border-radius:50%;height:1em;width:1em}.mb-button:host--loading .mb-button__loader{animation-delay:-.16s;color:var(--color-white);font-size:.625rem;left:50%;margin:calc(-.5em - 10px) 0 0 -.5em;position:absolute;top:50%;transform:translateZ(0)}.mb-button:host--loading .mb-button__loader:before,.mb-button:host--loading .mb-button__loader:after{content:\"\";position:absolute;top:0}.mb-button:host--loading .mb-button__loader:before{animation-delay:-.32s;left:-2em}.mb-button:host--loading .mb-button__loader:after{left:2em}@keyframes load{0%,80%,to{box-shadow:0 10px 0 -1.3em}40%{box-shadow:0 10px}}.mb-button:host[tabindex=\"-1\"]{pointer-events:none}.mb-button__content{display:flex;gap:.5rem;justify-content:center}.mb-button__icon{align-items:center;display:inline-flex;justify-content:center;width:1.5rem}:host.mb-button.mb-button--link.mb-button--disabled .mb-button__icon,:host.mb-button.mb-button--link.mb-button--disabled:hover .mb-button__icon,:host.mb-button.mb-button--link.mb-button--disabled:visited .mb-button__icon{color:var(--color-text-disabled)}:host.mb-button--principal.mb-button--primary{background-color:var(--color-primary-dark);color:var(--color-primary-contrast)}@media not all and (hover: none){:host.mb-button--principal.mb-button--primary:hover{background-color:var(--color-primary-light);box-shadow:0 4px 12px #0e0f2026}}:host.mb-button--principal.mb-button--primary:focus-visible{background-color:var(--color-primary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--principal.mb-button--primary .mb-button__loader{color:var(--color-primary-contrast)}:host.mb-button--floating.mb-button--primary{background-color:transparent;color:var(--color-primary-dark)}@media not all and (hover: none){:host.mb-button--floating.mb-button--primary:hover{background-color:var(--color-primary-clear);color:var(--color-primary-dark)}}:host.mb-button--floating.mb-button--primary:focus-visible{background-color:var(--color-primary-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--floating.mb-button--primary .mb-button__loader{color:var(--color-primary-dark)}:host.mb-button--outline.mb-button--primary{background-color:transparent;color:var(--color-primary-dark);outline:1px solid}@media not all and (hover: none){:host.mb-button--outline.mb-button--primary:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-primary);outline-color:var(--color-primary-light)}}:host.mb-button--outline.mb-button--primary:focus-visible{outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--outline.mb-button--primary .mb-button__loader{color:var(--color-primary-dark)}:host.mb-button--link.mb-button--primary{background-color:transparent;border-radius:.5rem;color:var(--color-primary-dark);text-decoration:underline;text-decoration-line:underline;-webkit-text-decoration-skip-ink:none;text-decoration-skip-ink:none;text-decoration-style:solid;text-decoration-thickness:auto;text-transform:unset;text-underline-offset:auto;text-underline-position:from-font}:host.mb-button--link.mb-button--primary .mb-button__icon{color:var(--color-primary-dark)}@media not all and (hover: none){:host.mb-button--link.mb-button--primary:hover{color:var(--color-primary)}}:host.mb-button--link.mb-button--primary:focus-visible{color:var(--color-primary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--link.mb-button--primary:visited{color:var(--color-notification-dark)}:host.mb-button--link.mb-button--primary:visited .mb-button__icon{color:var(--color-notification-dark)}:host.mb-button--principal.mb-button--secondary{background-color:var(--color-secondary-dark);color:var(--color-secondary-contrast)}@media not all and (hover: none){:host.mb-button--principal.mb-button--secondary:hover{background-color:var(--color-secondary-light);box-shadow:0 4px 12px #0e0f2026}}:host.mb-button--principal.mb-button--secondary:focus-visible{background-color:var(--color-secondary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--principal.mb-button--secondary .mb-button__loader{color:var(--color-secondary-contrast)}:host.mb-button--floating.mb-button--secondary{background-color:transparent;color:var(--color-secondary-dark)}@media not all and (hover: none){:host.mb-button--floating.mb-button--secondary:hover{background-color:var(--color-secondary-clear);color:var(--color-secondary-dark)}}:host.mb-button--floating.mb-button--secondary:focus-visible{background-color:var(--color-secondary-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--floating.mb-button--secondary .mb-button__loader{color:var(--color-secondary-dark)}:host.mb-button--outline.mb-button--secondary{background-color:transparent;color:var(--color-secondary-dark);outline:1px solid}@media not all and (hover: none){:host.mb-button--outline.mb-button--secondary:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-secondary);outline-color:var(--color-secondary-light)}}:host.mb-button--outline.mb-button--secondary:focus-visible{outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--outline.mb-button--secondary .mb-button__loader{color:var(--color-secondary-dark)}:host.mb-button--link.mb-button--secondary{background-color:transparent;border-radius:.5rem;color:var(--color-secondary-dark);text-decoration:underline;text-decoration-line:underline;-webkit-text-decoration-skip-ink:none;text-decoration-skip-ink:none;text-decoration-style:solid;text-decoration-thickness:auto;text-transform:unset;text-underline-offset:auto;text-underline-position:from-font}:host.mb-button--link.mb-button--secondary .mb-button__icon{color:var(--color-secondary-dark)}@media not all and (hover: none){:host.mb-button--link.mb-button--secondary:hover{color:var(--color-secondary)}}:host.mb-button--link.mb-button--secondary:focus-visible{color:var(--color-secondary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--link.mb-button--secondary:visited{color:var(--color-notification-dark)}:host.mb-button--link.mb-button--secondary:visited .mb-button__icon{color:var(--color-notification-dark)}:host.mb-button--principal.mb-button--error{background-color:var(--color-error-dark);color:var(--color-error-clear)}@media not all and (hover: none){:host.mb-button--principal.mb-button--error:hover{background-color:var(--color-error);box-shadow:0 4px 12px #0e0f2026}}:host.mb-button--principal.mb-button--error:focus-visible{background-color:var(--color-error);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--principal.mb-button--error .mb-button__loader{color:var(--color-error-clear)}:host.mb-button--floating.mb-button--error{background-color:transparent;color:var(--color-error-dark)}@media not all and (hover: none){:host.mb-button--floating.mb-button--error:hover{background-color:var(--color-error-clear);color:var(--color-error-dark)}}:host.mb-button--floating.mb-button--error:focus-visible{background-color:var(--color-error-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--floating.mb-button--error .mb-button__loader{color:var(--color-error-dark)}:host.mb-button--outline.mb-button--error{background-color:transparent;color:var(--color-error-dark);outline:1px solid}@media not all and (hover: none){:host.mb-button--outline.mb-button--error:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-error);outline-color:var(--color-error)}}:host.mb-button--outline.mb-button--error:focus-visible{outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--outline.mb-button--error .mb-button__loader{color:var(--color-error-dark)}:host.mb-button--link.mb-button--error{background-color:transparent;border-radius:.5rem;color:var(--color-error-dark);text-decoration:underline;text-decoration-line:underline;-webkit-text-decoration-skip-ink:none;text-decoration-skip-ink:none;text-decoration-style:solid;text-decoration-thickness:auto;text-transform:unset;text-underline-offset:auto;text-underline-position:from-font}:host.mb-button--link.mb-button--error .mb-button__icon{color:var(--color-error-dark)}@media not all and (hover: none){:host.mb-button--link.mb-button--error:hover{color:var(--color-error)}}:host.mb-button--link.mb-button--error:focus-visible{color:var(--color-error);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-button--link.mb-button--error:visited{color:var(--color-notification-dark)}:host.mb-button--link.mb-button--error:visited .mb-button__icon{color:var(--color-notification-dark)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { variant: [{
                type: Input
            }], color: [{
                type: Input
            }], disabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], icon: [{
                type: ContentChild,
                args: [MbIcon]
            }], onKeyPress: [{
                type: HostListener,
                args: ['keydown.enter']
            }] } });

class MbButtonModule {
}
MbButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbButtonModule, declarations: [MbButton], imports: [CommonModule], exports: [MbButton] });
MbButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbButtonModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [MbButton],
                    exports: [MbButton]
                }]
        }] });

class MbExpandableText {
    set width(value) {
        this._width = value;
        if (isDevMode()) {
            console.warn('The width input will be deprecated in following versions. Avoid using this input.');
        }
    }
    get width() {
        return this._width;
    }
    constructor(renderer, cdr) {
        this.renderer = renderer;
        this.cdr = cdr;
        this.collapse = new EventEmitter();
        this.expand = new EventEmitter();
        this.isExpanded = false;
        this.showActions = true;
        this.collapseIcon = aciArrowUp;
        this.expandIcon = aciArrowDown;
    }
    ngAfterViewInit() {
        if (this.numericHeight > this.content.nativeElement.offsetHeight) {
            this.showActions = false;
        }
        else {
            this.updateContentHeight();
        }
        this.cdr.detectChanges();
    }
    changeState() {
        this.isExpanded = !this.isExpanded;
        this.updateContentHeight();
        this.isExpanded ? this.expand.emit() : this.collapse.emit();
    }
    updateContentHeight() {
        if (this.isExpanded) {
            this.renderer.setStyle(this.content.nativeElement, 'height', 'auto');
        }
        else {
            this.renderer.setStyle(this.content.nativeElement, 'height', this.height);
        }
    }
    get numericHeight() {
        return this.height ? parseInt(this.height.replace('px', ''), 0) : 0;
    }
}
MbExpandableText.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandableText, deps: [{ token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
MbExpandableText.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbExpandableText, selector: "[mb-expandable-text]", inputs: { width: "width", height: "height" }, outputs: { collapse: "collapse", expand: "expand" }, viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true }], ngImport: i0, template: "<div #content class=\"mb-expandable-text__content\" [style.width]=\"width\">\n  <ng-content></ng-content>\n</div>\n\n<span *ngIf=\"showActions\" class=\"mb-expandable-text__button\" (click)=\"changeState()\">\n  <ng-container *ngIf=\"isExpanded; else expandTemplate\">\n    <ng-content select=\"[mb-collapse]\"></ng-content>\n    <mb-icon class=\"mb-expandable-text__button-icon\" [icon]=\"collapseIcon\"></mb-icon>\n  </ng-container>\n\n  <ng-template #expandTemplate>\n    <ng-content select=\"[mb-expand]\"></ng-content>\n    <mb-icon class=\"mb-expandable-text__button-icon\" [icon]=\"expandIcon\"></mb-icon>\n  </ng-template>\n</span>\n", styles: [".mb-expandable-text__content{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5;overflow:hidden}@media only screen and (max-width: 767px){.mb-expandable-text__content{line-height:1.5}}.mb-expandable-text__button{align-items:center;color:var(--color-secondary);cursor:pointer;display:inline-flex}.mb-expandable-text__button-icon{font-size:12px;margin-left:8px;padding-top:2px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandableText, decorators: [{
            type: Component,
            args: [{ selector: '[mb-expandable-text]', template: "<div #content class=\"mb-expandable-text__content\" [style.width]=\"width\">\n  <ng-content></ng-content>\n</div>\n\n<span *ngIf=\"showActions\" class=\"mb-expandable-text__button\" (click)=\"changeState()\">\n  <ng-container *ngIf=\"isExpanded; else expandTemplate\">\n    <ng-content select=\"[mb-collapse]\"></ng-content>\n    <mb-icon class=\"mb-expandable-text__button-icon\" [icon]=\"collapseIcon\"></mb-icon>\n  </ng-container>\n\n  <ng-template #expandTemplate>\n    <ng-content select=\"[mb-expand]\"></ng-content>\n    <mb-icon class=\"mb-expandable-text__button-icon\" [icon]=\"expandIcon\"></mb-icon>\n  </ng-template>\n</span>\n", styles: [".mb-expandable-text__content{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5;overflow:hidden}@media only screen and (max-width: 767px){.mb-expandable-text__content{line-height:1.5}}.mb-expandable-text__button{align-items:center;color:var(--color-secondary);cursor:pointer;display:inline-flex}.mb-expandable-text__button-icon{font-size:12px;margin-left:8px;padding-top:2px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { content: [{
                type: ViewChild,
                args: ['content']
            }], width: [{
                type: Input
            }], height: [{
                type: Input
            }], collapse: [{
                type: Output
            }], expand: [{
                type: Output
            }] } });

class MbExpandableButton {
}
MbExpandableButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandableButton, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MbExpandableButton.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbExpandableButton, selector: "[mb-expand], [mb-collapse]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandableButton, decorators: [{
            type: Directive,
            args: [{
                    selector: '[mb-expand], [mb-collapse]'
                }]
        }] });

class MbExpandableTextModule {
}
MbExpandableTextModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandableTextModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbExpandableTextModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbExpandableTextModule, declarations: [MbExpandableText, MbExpandableButton], imports: [CommonModule, MbIconModule, MbButtonModule], exports: [MbExpandableText, MbExpandableButton] });
MbExpandableTextModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandableTextModule, imports: [CommonModule, MbIconModule, MbButtonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandableTextModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MbIconModule, MbButtonModule],
                    declarations: [MbExpandableText, MbExpandableButton],
                    exports: [MbExpandableText, MbExpandableButton]
                }]
        }] });

class CloseEventService {
    constructor() {
        this.buttonSubject = new Subject();
        this.btnCreated = new Subject();
    }
    emitClickEvent() {
        this.buttonSubject.next(true);
    }
    getButtonObservable() {
        return this.buttonSubject.asObservable();
    }
    emitBtnCreated(isCreate) {
        setTimeout(() => {
            this.btnCreated.next(isCreate);
        }, 0);
    }
    getIsBtnCreated() {
        return this.btnCreated.asObservable();
    }
}
CloseEventService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CloseEventService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CloseEventService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CloseEventService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CloseEventService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class MbDialogHeader {
    constructor(closeEventService) {
        this.closeEventService = closeEventService;
        this.showCloseBtn = false;
        this.closeDialog = new EventEmitter();
    }
    ngOnInit() {
        this.btnClose$ = this.closeEventService.getIsBtnCreated();
    }
    closeModal() {
        this.closeEventService.emitClickEvent();
        this.closeDialog.emit(true);
    }
}
MbDialogHeader.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialogHeader, deps: [{ token: CloseEventService }], target: i0.ɵɵFactoryTarget.Component });
MbDialogHeader.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbDialogHeader, selector: "mb-dialog-header", inputs: { showCloseBtn: "showCloseBtn" }, outputs: { closeDialog: "closeDialog" }, host: { classAttribute: "mb-dialog__header" }, ngImport: i0, template: "<div class=\"header-modal\" role=\"heading\">\n  <div class=\"title-modal\">\n    <ng-content select=\"h2 , h3\"></ng-content>\n  </div>\n  <button\n    *ngIf=\"(btnClose$ | async) || showCloseBtn\"\n    class=\"closing-cross\"\n    (click)=\"closeModal()\"\n  >\n    <mb-icon icon=\"x-close\"></mb-icon>\n  </button>\n</div>\n", styles: [":host{width:100%}:host .header-modal{align-items:center;display:flex;justify-content:space-between;width:100%}:host .header-modal .closing-cross{background-color:var(--color-transparent);border:none;cursor:pointer;height:24px;outline:none;width:24px;font-size:1rem;font-weight:400;line-height:16px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialogHeader, decorators: [{
            type: Component,
            args: [{ selector: 'mb-dialog-header', host: {
                        class: 'mb-dialog__header',
                    }, template: "<div class=\"header-modal\" role=\"heading\">\n  <div class=\"title-modal\">\n    <ng-content select=\"h2 , h3\"></ng-content>\n  </div>\n  <button\n    *ngIf=\"(btnClose$ | async) || showCloseBtn\"\n    class=\"closing-cross\"\n    (click)=\"closeModal()\"\n  >\n    <mb-icon icon=\"x-close\"></mb-icon>\n  </button>\n</div>\n", styles: [":host{width:100%}:host .header-modal{align-items:center;display:flex;justify-content:space-between;width:100%}:host .header-modal .closing-cross{background-color:var(--color-transparent);border:none;cursor:pointer;height:24px;outline:none;width:24px;font-size:1rem;font-weight:400;line-height:16px}\n"] }]
        }], ctorParameters: function () { return [{ type: CloseEventService }]; }, propDecorators: { showCloseBtn: [{
                type: Input
            }], closeDialog: [{
                type: Output
            }] } });

class MbDialogBody {
    constructor(render, hostElement) {
        this.render = render;
        this.hostElement = hostElement;
    }
    ngAfterViewInit() {
        const styles = getComputedStyle(this.hostElement.nativeElement);
        if (styles && styles.height) {
            const height = parseInt(styles.height.split('px')[0], 10);
            if (height > 564) {
                this.render.setStyle(this.hostElement.nativeElement, 'max-height', '564px');
            }
        }
        if (this.supportText !== undefined) {
            this.render.addClass(this.supportText.nativeElement, 'scrolleable');
        }
    }
}
MbDialogBody.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialogBody, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
MbDialogBody.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbDialogBody, selector: "mb-dialog-body", host: { classAttribute: "mb-dialog__body" }, queries: [{ propertyName: "supportText", first: true, predicate: ["scrolleable"], descendants: true }], ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialogBody, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-dialog-body',
                    template: `
    <ng-content></ng-content>
  `,
                    host: {
                        class: 'mb-dialog__body'
                    }
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { supportText: [{
                type: ContentChild,
                args: ['scrolleable', { static: false }]
            }] } });

class MbDialogFooter {
}
MbDialogFooter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialogFooter, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbDialogFooter.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbDialogFooter, selector: "mb-dialog-footer", host: { classAttribute: "mb-dialog__footer" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialogFooter, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-dialog-footer',
                    template: '<ng-content></ng-content>',
                    host: {
                        class: 'mb-dialog__footer',
                    },
                }]
        }] });

class MbBasicDialog {
    onResize(event) {
        this.dialogResize();
    }
    constructor(hostElement, render, platform) {
        this.hostElement = hostElement;
        this.render = render;
        this.platform = platform;
        this.fullScreenMobile = false;
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.calcMaxSizeDialog();
        }, 0);
    }
    get isMobile() {
        return this.platform.IOS || this.platform.ANDROID;
    }
    maxHeightBody(maxHeightContainer) {
        return `calc(${maxHeightContainer} - 3rem)`;
    }
    calcMaxSizeDialog() {
        if (this.isMobile) {
            if (this.fullScreenMobile) {
                this.render.addClass(this.hostElement.nativeElement, 'full-dialog');
                if (this.ctnBody !== undefined) {
                    this.render.addClass(this.ctnBody.nativeElement, 'full-ctn');
                }
            }
            else {
                this.render.addClass(this.hostElement.nativeElement, 'basic-dialog');
                if (this.ctnBody !== undefined) {
                    this.render.addClass(this.ctnBody.nativeElement, 'basic-ctn');
                }
            }
            this.dialogResize();
        }
        else {
            const elementsList = document.querySelectorAll('[id^=cdk-overlay-]');
            let overtayDialog = null;
            elementsList.forEach(element => {
                if (element.classList.contains('mb-dialog-panel')) {
                    overtayDialog = element;
                }
            });
            if (overtayDialog !== null) {
                const overlayHeight = getComputedStyle(overtayDialog).maxHeight;
                this.render.setStyle(this.hostElement.nativeElement, 'max-height', overlayHeight);
                this.render.setStyle(this.ctnBody.nativeElement, 'max-height', this.maxHeightBody(overlayHeight));
            }
        }
    }
    dialogResize() {
        document.documentElement.style.setProperty('--doc-height', `${window.innerHeight}px`);
    }
}
MbBasicDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBasicDialog, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1$4.Platform }], target: i0.ɵɵFactoryTarget.Component });
MbBasicDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBasicDialog, selector: "mb-dialog ", inputs: { fullScreenMobile: "fullScreenMobile" }, host: { attributes: { "role": "dialog", "id": "modal" }, listeners: { "window:resize": "onResize($event)" }, classAttribute: "mb-dialog" }, queries: [{ propertyName: "headerDialog", first: true, predicate: MbDialogHeader, descendants: true }], viewQueries: [{ propertyName: "ctnBody", first: true, predicate: ["ctnDialog"], descendants: true, static: true }], ngImport: i0, template: `
    <div #ctnDialog class="ctn-dialog" cdkTrapFocus cdkTrapFocusAutoCapture="true">
      <div class="header">
        <ng-content select="mb-dialog-header"></ng-content>
      </div>
      <div class="body">
        <ng-content select="mb-dialog-body"></ng-content>
      </div>
      <ng-content select="mb-dialog-footer"></ng-content>
    </div>
  `, isInline: true, styles: [".mb-dialog-panel{display:block}.scrolleable{height:100%;overflow-y:auto}.mb-dialog__body{display:contents}.mb-dialog{background-color:#fff;border-radius:8px;display:block;flex-direction:column;height:max-content;max-height:96%;padding:1.5rem 2rem}.mb-dialog .ctn-dialog{display:flex;flex-direction:column;gap:1rem;justify-content:space-between}.mb-dialog .header{width:100%}.mb-dialog .header:empty{display:none}.mb-dialog .body{display:contents;height:100%;overflow:hidden}.mb-dialog__footer{display:flex;justify-content:flex-end}.mb-dialog__footer .mb-button:last-child{margin-left:8px}.modalOpen *:has(> .mb-dialog){overflow:hidden}.modalClose *:has(> .mb-dialog){overflow:initial}.cdk-overlay-pane.mb-dialog-panel::ng-deep{display:flex;justify-content:center}.basic-dialog{height:max-content;margin:1rem;max-height:46vh;max-width:560px;min-width:280px}.basic-ctn{max-height:calc(46vh - 3rem)}.full-ctn{height:100%}.full-dialog{box-sizing:border-box;height:100vh;height:var(--doc-height);max-height:100vh;max-height:var(--doc-height);width:100vw}.dialog-dp{max-height:calc(720px - 3rem)}.dialog-nt{max-height:calc(96vh - 3rem)}\n"], dependencies: [{ kind: "directive", type: i2$1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBasicDialog, decorators: [{
            type: Component,
            args: [{ selector: 'mb-dialog ', template: `
    <div #ctnDialog class="ctn-dialog" cdkTrapFocus cdkTrapFocusAutoCapture="true">
      <div class="header">
        <ng-content select="mb-dialog-header"></ng-content>
      </div>
      <div class="body">
        <ng-content select="mb-dialog-body"></ng-content>
      </div>
      <ng-content select="mb-dialog-footer"></ng-content>
    </div>
  `, encapsulation: ViewEncapsulation.None, host: {
                        class: 'mb-dialog',
                        role: 'dialog',
                        id: 'modal'
                    }, styles: [".mb-dialog-panel{display:block}.scrolleable{height:100%;overflow-y:auto}.mb-dialog__body{display:contents}.mb-dialog{background-color:#fff;border-radius:8px;display:block;flex-direction:column;height:max-content;max-height:96%;padding:1.5rem 2rem}.mb-dialog .ctn-dialog{display:flex;flex-direction:column;gap:1rem;justify-content:space-between}.mb-dialog .header{width:100%}.mb-dialog .header:empty{display:none}.mb-dialog .body{display:contents;height:100%;overflow:hidden}.mb-dialog__footer{display:flex;justify-content:flex-end}.mb-dialog__footer .mb-button:last-child{margin-left:8px}.modalOpen *:has(> .mb-dialog){overflow:hidden}.modalClose *:has(> .mb-dialog){overflow:initial}.cdk-overlay-pane.mb-dialog-panel::ng-deep{display:flex;justify-content:center}.basic-dialog{height:max-content;margin:1rem;max-height:46vh;max-width:560px;min-width:280px}.basic-ctn{max-height:calc(46vh - 3rem)}.full-ctn{height:100%}.full-dialog{box-sizing:border-box;height:100vh;height:var(--doc-height);max-height:100vh;max-height:var(--doc-height);width:100vw}.dialog-dp{max-height:calc(720px - 3rem)}.dialog-nt{max-height:calc(96vh - 3rem)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1$4.Platform }]; }, propDecorators: { fullScreenMobile: [{
                type: Input
            }], headerDialog: [{
                type: ContentChild,
                args: [MbDialogHeader, { static: false }]
            }], ctnBody: [{
                type: ViewChild,
                args: ['ctnDialog', { static: true }]
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

class MbConfirmationDialogButton {
    constructor(hostElement) {
        this.hostElement = hostElement;
    }
    get nameElement() {
        return this.hostElement.nativeElement.localName;
    }
}
MbConfirmationDialogButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbConfirmationDialogButton, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
MbConfirmationDialogButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbConfirmationDialogButton, selector: "mb-dialog-dismiss-button,mb-dialog-ok-button", inputs: { variant: "variant", color: "color" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbConfirmationDialogButton, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-dialog-dismiss-button,mb-dialog-ok-button',
                    template: '<ng-content></ng-content>',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { variant: [{
                type: Input
            }], color: [{
                type: Input
            }] } });

class MbConfirmationDialog {
    constructor(render, hostElement, platform) {
        this.render = render;
        this.hostElement = hostElement;
        this.platform = platform;
        this.fullScreenMobile = false;
        this.confirmationEvent = new EventEmitter();
        this.mbDialogDismissButton = {
            variant: 'floating',
            color: 'secondary',
        };
        this.mbDialogOkButton = {
            variant: 'principal',
            color: 'primary',
        };
    }
    ngAfterContentInit() {
        this.confirmationButtons.forEach((confirmationButton) => {
            if (confirmationButton.nameElement === 'mb-dialog-dismiss-button') {
                this.mbDialogDismissButton = {
                    color: confirmationButton.color
                        ? confirmationButton.color
                        : this.mbDialogDismissButton.color,
                    variant: confirmationButton.variant
                        ? confirmationButton.variant
                        : this.mbDialogDismissButton.variant,
                };
            }
            else {
                this.mbDialogOkButton = {
                    color: confirmationButton.color
                        ? confirmationButton.color
                        : this.mbDialogOkButton.color,
                    variant: confirmationButton.variant
                        ? confirmationButton.variant
                        : this.mbDialogOkButton.variant,
                };
            }
        });
        if (this.isMobile) {
            if (this.fullScreenMobile) {
                this.render.addClass(this.hostElement.nativeElement, 'full-dialog');
                if (this.ctnBody !== undefined) {
                    this.render.addClass(this.ctnBody.nativeElement, 'full-ctn');
                }
            }
            else {
                this.render.addClass(this.hostElement.nativeElement, 'basic-dialog');
                if (this.ctnBody !== undefined) {
                    this.render.addClass(this.ctnBody.nativeElement, 'basic-ctn');
                }
            }
        }
        else {
            if (this.ctnBody !== undefined) {
                this.render.addClass(this.ctnBody.nativeElement, 'dialog-dp');
            }
        }
    }
    get isMobile() {
        return this.platform.ANDROID || this.platform.IOS;
    }
    closeDialog(event) {
        this.confirmationEvent.emit(event);
    }
}
MbConfirmationDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbConfirmationDialog, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i1$4.Platform }], target: i0.ɵɵFactoryTarget.Component });
MbConfirmationDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbConfirmationDialog, selector: "mb-confirmation-dialog", inputs: { fullScreenMobile: "fullScreenMobile" }, outputs: { confirmationEvent: "confirmationEvent" }, host: { classAttribute: "mb-dialog" }, queries: [{ propertyName: "confirmationButtons", predicate: MbConfirmationDialogButton }], viewQueries: [{ propertyName: "ctnBody", first: true, predicate: ["ctnDialog"], descendants: true, static: true }], ngImport: i0, template: "<div #ctnDialog cdkTrapFocus cdkTrapFocusAutoCapture=\"true\" class=\"ctn-dialog\">\n  <div class=\"header\">\n    <ng-content select=\"mb-dialog-header\"></ng-content>\n  </div>\n  <div class=\"body\">\n    <ng-content select=\"mb-dialog-body\"></ng-content>\n  </div>\n  <mb-dialog-footer>\n    <button\n      mb-button\n      class=\"mb-dialog__dismiss-button\"\n      [color]=\"mbDialogDismissButton.color\"\n      [variant]=\"mbDialogDismissButton.variant\"\n      (click)=\"closeDialog(false)\"\n    >\n      <ng-content select=\"mb-dialog-dismiss-button\"></ng-content>\n    </button>\n    <button\n      mb-button\n      class=\"mb-dialog__ok-button\"\n      [color]=\"mbDialogOkButton.color\"\n      [variant]=\"mbDialogOkButton.variant\"\n      (click)=\"closeDialog(true)\"\n    >\n      <ng-content select=\"mb-dialog-ok-button\"></ng-content>\n    </button>\n  </mb-dialog-footer>\n</div>\n", styles: [".mb-dialog-panel{display:block}.scrolleable{height:100%;overflow-y:auto}.mb-dialog__body{display:contents}.mb-dialog{background-color:#fff;border-radius:8px;display:block;flex-direction:column;height:max-content;max-height:96%;padding:1.5rem 2rem}.mb-dialog .ctn-dialog{display:flex;flex-direction:column;gap:1rem;justify-content:space-between}.mb-dialog .header{width:100%}.mb-dialog .header:empty{display:none}.mb-dialog .body{display:contents;height:100%;overflow:hidden}.mb-dialog__footer{display:flex;justify-content:flex-end}.mb-dialog__footer .mb-button:last-child{margin-left:8px}.modalOpen *:has(> .mb-dialog){overflow:hidden}.modalClose *:has(> .mb-dialog){overflow:initial}.cdk-overlay-pane.mb-dialog-panel::ng-deep{display:flex;justify-content:center}.basic-dialog{height:max-content;margin:1rem;max-height:46vh;max-width:560px;min-width:280px}.basic-ctn{max-height:calc(46vh - 3rem)}.full-ctn{height:100%}.full-dialog{box-sizing:border-box;height:100vh;height:var(--doc-height);max-height:100vh;max-height:var(--doc-height);width:100vw}.dialog-dp{max-height:calc(720px - 3rem)}.dialog-nt{max-height:calc(96vh - 3rem)}\n"], dependencies: [{ kind: "component", type: MbButton, selector: "button[mb-button], a[mb-button]", inputs: ["variant", "color", "disabled", "loading"] }, { kind: "directive", type: i2$1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "component", type: MbDialogFooter, selector: "mb-dialog-footer" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbConfirmationDialog, decorators: [{
            type: Component,
            args: [{ selector: 'mb-confirmation-dialog', encapsulation: ViewEncapsulation.None, host: {
                        class: 'mb-dialog',
                    }, template: "<div #ctnDialog cdkTrapFocus cdkTrapFocusAutoCapture=\"true\" class=\"ctn-dialog\">\n  <div class=\"header\">\n    <ng-content select=\"mb-dialog-header\"></ng-content>\n  </div>\n  <div class=\"body\">\n    <ng-content select=\"mb-dialog-body\"></ng-content>\n  </div>\n  <mb-dialog-footer>\n    <button\n      mb-button\n      class=\"mb-dialog__dismiss-button\"\n      [color]=\"mbDialogDismissButton.color\"\n      [variant]=\"mbDialogDismissButton.variant\"\n      (click)=\"closeDialog(false)\"\n    >\n      <ng-content select=\"mb-dialog-dismiss-button\"></ng-content>\n    </button>\n    <button\n      mb-button\n      class=\"mb-dialog__ok-button\"\n      [color]=\"mbDialogOkButton.color\"\n      [variant]=\"mbDialogOkButton.variant\"\n      (click)=\"closeDialog(true)\"\n    >\n      <ng-content select=\"mb-dialog-ok-button\"></ng-content>\n    </button>\n  </mb-dialog-footer>\n</div>\n", styles: [".mb-dialog-panel{display:block}.scrolleable{height:100%;overflow-y:auto}.mb-dialog__body{display:contents}.mb-dialog{background-color:#fff;border-radius:8px;display:block;flex-direction:column;height:max-content;max-height:96%;padding:1.5rem 2rem}.mb-dialog .ctn-dialog{display:flex;flex-direction:column;gap:1rem;justify-content:space-between}.mb-dialog .header{width:100%}.mb-dialog .header:empty{display:none}.mb-dialog .body{display:contents;height:100%;overflow:hidden}.mb-dialog__footer{display:flex;justify-content:flex-end}.mb-dialog__footer .mb-button:last-child{margin-left:8px}.modalOpen *:has(> .mb-dialog){overflow:hidden}.modalClose *:has(> .mb-dialog){overflow:initial}.cdk-overlay-pane.mb-dialog-panel::ng-deep{display:flex;justify-content:center}.basic-dialog{height:max-content;margin:1rem;max-height:46vh;max-width:560px;min-width:280px}.basic-ctn{max-height:calc(46vh - 3rem)}.full-ctn{height:100%}.full-dialog{box-sizing:border-box;height:100vh;height:var(--doc-height);max-height:100vh;max-height:var(--doc-height);width:100vw}.dialog-dp{max-height:calc(720px - 3rem)}.dialog-nt{max-height:calc(96vh - 3rem)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i1$4.Platform }]; }, propDecorators: { ctnBody: [{
                type: ViewChild,
                args: ['ctnDialog', { static: true }]
            }], confirmationButtons: [{
                type: ContentChildren,
                args: [MbConfirmationDialogButton]
            }], fullScreenMobile: [{
                type: Input
            }], confirmationEvent: [{
                type: Output
            }] } });

class MbDialogModule {
    constructor(iconRegistry) {
        this.iconRegistry = iconRegistry;
        this.iconRegistry.addSvgIconDefinition(aciXClose);
    }
}
MbDialogModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialogModule, deps: [{ token: MbIconRegistry }], target: i0.ɵɵFactoryTarget.NgModule });
MbDialogModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbDialogModule, declarations: [MbDialogHeader,
        MbDialogBody,
        MbDialogFooter,
        MbBasicDialog,
        MbConfirmationDialog,
        MbConfirmationDialogButton], imports: [CommonModule,
        OverlayModule,
        LayoutModule,
        MbButtonModule,
        A11yModule,
        MbIconModule], exports: [MbDialogHeader,
        MbDialogBody,
        MbDialogFooter,
        MbBasicDialog,
        MbConfirmationDialog,
        MbConfirmationDialogButton] });
MbDialogModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialogModule, providers: [CloseEventService], imports: [CommonModule,
        OverlayModule,
        LayoutModule,
        MbButtonModule,
        A11yModule,
        MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        OverlayModule,
                        LayoutModule,
                        MbButtonModule,
                        A11yModule,
                        MbIconModule,
                    ],
                    declarations: [
                        MbDialogHeader,
                        MbDialogBody,
                        MbDialogFooter,
                        MbBasicDialog,
                        MbConfirmationDialog,
                        MbConfirmationDialogButton,
                    ],
                    exports: [
                        MbDialogHeader,
                        MbDialogBody,
                        MbDialogFooter,
                        MbBasicDialog,
                        MbConfirmationDialog,
                        MbConfirmationDialogButton,
                    ],
                    providers: [CloseEventService],
                }]
        }], ctorParameters: function () { return [{ type: MbIconRegistry }]; } });

class MbChips {
}
MbChips.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbChips, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbChips.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbChips, selector: "mb-chips", host: { classAttribute: "mb-chips" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{display:block;padding:8px 0}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbChips, decorators: [{
            type: Component,
            args: [{ selector: 'mb-chips', template: '<ng-content></ng-content>', host: {
                        class: 'mb-chips'
                    }, styles: [":host{display:block;padding:8px 0}\n"] }]
        }] });

let nextId$2 = 0;
class MbChip {
    constructor() {
        this.id = `mb-chip-${++nextId$2}`;
        this.value = '';
        this.dismiss = new EventEmitter();
        this.closeIcon = aciCloseCircle;
    }
}
MbChip.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbChip, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbChip.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbChip, selector: "mb-chip", inputs: { id: "id", value: "value" }, outputs: { dismiss: "dismiss" }, host: { properties: { "attr.id": "id" }, classAttribute: "mb-chip" }, ngImport: i0, template: "<div class=\"mb-chip__content\">\n  <ng-content></ng-content>\n</div>\n<div class=\"mb-chip__close\">\n  <mb-icon [icon]=\"closeIcon\" (click)=\"dismiss.emit(value)\"></mb-icon>\n</div>\n", styles: [".mb-chip:host{background-color:var(--color-secondary);border-radius:16px;color:var(--color-secondary-contrast);display:inline-flex;margin-right:8px;padding:8px}.mb-chip__content{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;align-items:center;display:flex;margin-right:4px}@media only screen and (max-width: 767px){.mb-chip__content{line-height:1.5}}.mb-chip__close{align-items:center;cursor:pointer;display:flex;justify-content:center;opacity:.6}\n"], dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbChip, decorators: [{
            type: Component,
            args: [{ selector: 'mb-chip', host: {
                        'class': 'mb-chip',
                        '[attr.id]': 'id'
                    }, template: "<div class=\"mb-chip__content\">\n  <ng-content></ng-content>\n</div>\n<div class=\"mb-chip__close\">\n  <mb-icon [icon]=\"closeIcon\" (click)=\"dismiss.emit(value)\"></mb-icon>\n</div>\n", styles: [".mb-chip:host{background-color:var(--color-secondary);border-radius:16px;color:var(--color-secondary-contrast);display:inline-flex;margin-right:8px;padding:8px}.mb-chip__content{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;align-items:center;display:flex;margin-right:4px}@media only screen and (max-width: 767px){.mb-chip__content{line-height:1.5}}.mb-chip__close{align-items:center;cursor:pointer;display:flex;justify-content:center;opacity:.6}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], value: [{
                type: Input
            }], dismiss: [{
                type: Output
            }] } });

class MbChipsModule {
}
MbChipsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbChipsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbChipsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbChipsModule, declarations: [MbChips, MbChip], imports: [MbIconModule], exports: [MbChips, MbChip] });
MbChipsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbChipsModule, imports: [MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbChipsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [MbIconModule],
                    declarations: [MbChips, MbChip],
                    exports: [MbChips, MbChip]
                }]
        }] });

class MbIconButton {
    constructor(el) {
        this.el = el;
        this.variant = 'principal';
        this.color = 'primary';
        this.disabled = false;
        this.loading = false;
    }
    get getClass() {
        const color = this.color === 'critical' ? 'error' : this.color;
        return [
            `mb-icon-button--${this.variant}-${color}`,
            this.loading ? 'mb-icon-button--loading' : '',
            this.disabled ? `mb-icon-button-disabled--${this.variant}` : '',
        ]
            .filter(Boolean)
            .join(' ');
    }
    onKeyPress() {
        setTimeout(() => {
            this.el.nativeElement.blur();
        }, 0);
    }
}
MbIconButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconButton, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
MbIconButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbIconButton, selector: "button[mb-icon-button]", inputs: { variant: "variant", color: "color", disabled: "disabled", loading: "loading" }, host: { listeners: { "keydown.enter": "onKeyPress()" }, properties: { "class": "getClass", "disabled": "disabled" }, classAttribute: "mb-icon-button" }, ngImport: i0, template: "<div class=\"mb-icon-button__container\">\n  <ng-content select=\"mb-icon\" *ngIf=\"!loading; else loadingBlock\"></ng-content>\n  <ng-template #loadingBlock>\n    <div class=\"mb-icon-button__loader\"></div>\n  </ng-template>\n</div>\n", styles: [":host.mb-icon-button.mb-icon-button-disabled--principal,:host.mb-icon-button.mb-icon-button-disabled--principal:hover{background-color:var(--color-disabled)!important;color:var(--color-text-disabled)!important}:host.mb-icon-button.mb-icon-button-disabled--floating,:host.mb-icon-button.mb-icon-button-disabled--floating:hover{background-color:transparent;color:var(--color-text-disabled)}:host.mb-icon-button.mb-icon-button-disabled--outline,:host.mb-icon-button.mb-icon-button-disabled--outline:hover{color:var(--color-text-disabled);outline-color:var(--color-disabled)}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}.mb-icon-button:host{align-items:center;border:none;border-radius:1.5rem;cursor:pointer;height:2.5rem;padding:.5rem;transition:all .5s;width:2.5rem}.mb-icon-button:host--loading{pointer-events:none}.mb-icon-button:host--loading .mb-icon-button__loader{animation:spin 1s linear infinite;border-radius:1.25rem;height:1.5rem;width:1.5rem}.mb-icon-button__container{font-size:1.5rem;height:100%;line-height:.5rem;width:100%}:host.mb-icon-button--principal-primary{background-color:var(--color-primary-dark);color:var(--color-primary-contrast)}@media not all and (hover: none){:host.mb-icon-button--principal-primary:hover{background-color:var(--color-primary-light);box-shadow:0 4px 12px #0e0f2026}}:host.mb-icon-button--principal-primary:focus-visible{background-color:var(--color-primary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--principal-primary .mb-icon-button__loader{border:.188rem solid var(--color-primary-clear);border-right-color:var(--color-primary-light)}:host.mb-icon-button--floating-primary{background-color:transparent;color:var(--color-primary-dark)}@media not all and (hover: none){:host.mb-icon-button--floating-primary:hover{background-color:var(--color-primary-clear);color:var(--color-primary-dark)}}:host.mb-icon-button--floating-primary:focus-visible{background-color:var(--color-primary-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--floating-primary .mb-icon-button__loader{border:.188rem solid var(--color-primary-clear);border-right-color:var(--color-primary)}:host.mb-icon-button--outline-primary{background-color:transparent;color:var(--color-primary-dark);outline:1px solid}@media not all and (hover: none){:host.mb-icon-button--outline-primary:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-primary);outline-color:var(--color-primary-light)}}:host.mb-icon-button--outline-primary:focus-visible{border:none;outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--outline-primary .mb-icon-button__loader{border:.188rem solid var(--color-primary-clear);border-right-color:var(--color-primary)}:host.mb-icon-button--principal-secondary{background-color:var(--color-secondary-dark);color:var(--color-secondary-contrast)}@media not all and (hover: none){:host.mb-icon-button--principal-secondary:hover{background-color:var(--color-secondary-light);box-shadow:0 4px 12px #0e0f2026}}:host.mb-icon-button--principal-secondary:focus-visible{background-color:var(--color-secondary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--principal-secondary .mb-icon-button__loader{border:.188rem solid var(--color-secondary-clear);border-right-color:var(--color-secondary-light)}:host.mb-icon-button--floating-secondary{background-color:transparent;color:var(--color-secondary-dark)}@media not all and (hover: none){:host.mb-icon-button--floating-secondary:hover{background-color:var(--color-secondary-clear);color:var(--color-secondary-dark)}}:host.mb-icon-button--floating-secondary:focus-visible{background-color:var(--color-secondary-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--floating-secondary .mb-icon-button__loader{border:.188rem solid var(--color-secondary-clear);border-right-color:var(--color-secondary)}:host.mb-icon-button--outline-secondary{background-color:transparent;color:var(--color-secondary-dark);outline:1px solid}@media not all and (hover: none){:host.mb-icon-button--outline-secondary:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-secondary);outline-color:var(--color-secondary-light)}}:host.mb-icon-button--outline-secondary:focus-visible{border:none;outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--outline-secondary .mb-icon-button__loader{border:.188rem solid var(--color-secondary-clear);border-right-color:var(--color-secondary-light)}:host.mb-icon-button--principal-error{background-color:var(--color-error-dark);color:var(--color-error-contrast)}@media not all and (hover: none){:host.mb-icon-button--principal-error:hover{background-color:var(--color-error);box-shadow:0 4px 12px #0e0f2026}}:host.mb-icon-button--principal-error:focus-visible{background-color:var(--color-error);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--principal-error .mb-icon-button__loader{border:.188rem solid var(--color-error-clear);border-right-color:var(--color-error)}:host.mb-icon-button--floating-error{background-color:transparent;color:var(--color-error-dark)}@media not all and (hover: none){:host.mb-icon-button--floating-error:hover{background-color:var(--color-error-clear);color:var(--color-error-dark)}}:host.mb-icon-button--floating-error:focus-visible{background-color:var(--color-error-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--floating-error .mb-icon-button__loader{border:.188rem solid var(--color-error-clear);border-right-color:var(--color-error)}:host.mb-icon-button--outline-error{background-color:transparent;color:var(--color-error-dark);outline:1px solid}@media not all and (hover: none){:host.mb-icon-button--outline-error:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-error);outline-color:var(--color-error-dark)}}:host.mb-icon-button--outline-error:focus-visible{border:none;outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--outline-error .mb-icon-button__loader{border:.188rem solid var(--color-error-clear);border-right-color:var(--color-error)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconButton, decorators: [{
            type: Component,
            args: [{ selector: 'button[mb-icon-button]', host: {
                        'class': 'mb-icon-button',
                        '[class]': 'getClass',
                        '[disabled]': 'disabled',
                    }, template: "<div class=\"mb-icon-button__container\">\n  <ng-content select=\"mb-icon\" *ngIf=\"!loading; else loadingBlock\"></ng-content>\n  <ng-template #loadingBlock>\n    <div class=\"mb-icon-button__loader\"></div>\n  </ng-template>\n</div>\n", styles: [":host.mb-icon-button.mb-icon-button-disabled--principal,:host.mb-icon-button.mb-icon-button-disabled--principal:hover{background-color:var(--color-disabled)!important;color:var(--color-text-disabled)!important}:host.mb-icon-button.mb-icon-button-disabled--floating,:host.mb-icon-button.mb-icon-button-disabled--floating:hover{background-color:transparent;color:var(--color-text-disabled)}:host.mb-icon-button.mb-icon-button-disabled--outline,:host.mb-icon-button.mb-icon-button-disabled--outline:hover{color:var(--color-text-disabled);outline-color:var(--color-disabled)}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}.mb-icon-button:host{align-items:center;border:none;border-radius:1.5rem;cursor:pointer;height:2.5rem;padding:.5rem;transition:all .5s;width:2.5rem}.mb-icon-button:host--loading{pointer-events:none}.mb-icon-button:host--loading .mb-icon-button__loader{animation:spin 1s linear infinite;border-radius:1.25rem;height:1.5rem;width:1.5rem}.mb-icon-button__container{font-size:1.5rem;height:100%;line-height:.5rem;width:100%}:host.mb-icon-button--principal-primary{background-color:var(--color-primary-dark);color:var(--color-primary-contrast)}@media not all and (hover: none){:host.mb-icon-button--principal-primary:hover{background-color:var(--color-primary-light);box-shadow:0 4px 12px #0e0f2026}}:host.mb-icon-button--principal-primary:focus-visible{background-color:var(--color-primary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--principal-primary .mb-icon-button__loader{border:.188rem solid var(--color-primary-clear);border-right-color:var(--color-primary-light)}:host.mb-icon-button--floating-primary{background-color:transparent;color:var(--color-primary-dark)}@media not all and (hover: none){:host.mb-icon-button--floating-primary:hover{background-color:var(--color-primary-clear);color:var(--color-primary-dark)}}:host.mb-icon-button--floating-primary:focus-visible{background-color:var(--color-primary-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--floating-primary .mb-icon-button__loader{border:.188rem solid var(--color-primary-clear);border-right-color:var(--color-primary)}:host.mb-icon-button--outline-primary{background-color:transparent;color:var(--color-primary-dark);outline:1px solid}@media not all and (hover: none){:host.mb-icon-button--outline-primary:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-primary);outline-color:var(--color-primary-light)}}:host.mb-icon-button--outline-primary:focus-visible{border:none;outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--outline-primary .mb-icon-button__loader{border:.188rem solid var(--color-primary-clear);border-right-color:var(--color-primary)}:host.mb-icon-button--principal-secondary{background-color:var(--color-secondary-dark);color:var(--color-secondary-contrast)}@media not all and (hover: none){:host.mb-icon-button--principal-secondary:hover{background-color:var(--color-secondary-light);box-shadow:0 4px 12px #0e0f2026}}:host.mb-icon-button--principal-secondary:focus-visible{background-color:var(--color-secondary);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--principal-secondary .mb-icon-button__loader{border:.188rem solid var(--color-secondary-clear);border-right-color:var(--color-secondary-light)}:host.mb-icon-button--floating-secondary{background-color:transparent;color:var(--color-secondary-dark)}@media not all and (hover: none){:host.mb-icon-button--floating-secondary:hover{background-color:var(--color-secondary-clear);color:var(--color-secondary-dark)}}:host.mb-icon-button--floating-secondary:focus-visible{background-color:var(--color-secondary-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--floating-secondary .mb-icon-button__loader{border:.188rem solid var(--color-secondary-clear);border-right-color:var(--color-secondary)}:host.mb-icon-button--outline-secondary{background-color:transparent;color:var(--color-secondary-dark);outline:1px solid}@media not all and (hover: none){:host.mb-icon-button--outline-secondary:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-secondary);outline-color:var(--color-secondary-light)}}:host.mb-icon-button--outline-secondary:focus-visible{border:none;outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--outline-secondary .mb-icon-button__loader{border:.188rem solid var(--color-secondary-clear);border-right-color:var(--color-secondary-light)}:host.mb-icon-button--principal-error{background-color:var(--color-error-dark);color:var(--color-error-contrast)}@media not all and (hover: none){:host.mb-icon-button--principal-error:hover{background-color:var(--color-error);box-shadow:0 4px 12px #0e0f2026}}:host.mb-icon-button--principal-error:focus-visible{background-color:var(--color-error);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--principal-error .mb-icon-button__loader{border:.188rem solid var(--color-error-clear);border-right-color:var(--color-error)}:host.mb-icon-button--floating-error{background-color:transparent;color:var(--color-error-dark)}@media not all and (hover: none){:host.mb-icon-button--floating-error:hover{background-color:var(--color-error-clear);color:var(--color-error-dark)}}:host.mb-icon-button--floating-error:focus-visible{background-color:var(--color-error-clear);outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--floating-error .mb-icon-button__loader{border:.188rem solid var(--color-error-clear);border-right-color:var(--color-error)}:host.mb-icon-button--outline-error{background-color:transparent;color:var(--color-error-dark);outline:1px solid}@media not all and (hover: none){:host.mb-icon-button--outline-error:hover{box-shadow:0 4px 12px #0e0f2026;color:var(--color-error);outline-color:var(--color-error-dark)}}:host.mb-icon-button--outline-error:focus-visible{border:none;outline:2px solid var(--color-notification-dark);transition:0s}:host.mb-icon-button--outline-error .mb-icon-button__loader{border:.188rem solid var(--color-error-clear);border-right-color:var(--color-error)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { variant: [{
                type: Input
            }], color: [{
                type: Input
            }], disabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], onKeyPress: [{
                type: HostListener,
                args: ['keydown.enter']
            }] } });

class MbIconButtonModule {
}
MbIconButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbIconButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbIconButtonModule, declarations: [MbIconButton], imports: [CommonModule], exports: [MbIconButton] });
MbIconButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconButtonModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [MbIconButton],
                    exports: [MbIconButton],
                }]
        }] });

class MbIconText {
}
MbIconText.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconText, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbIconText.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbIconText, selector: "mb-icon-text", ngImport: i0, template: `
    <span class="mb-icon-text__icon">
      <ng-content select="mb-icon"></ng-content>
    </span>
    <div class="mb-icon-text__text">
      <ng-content select="span"></ng-content>
    </div>
  `, isInline: true, styles: [":host{display:flex;margin:4px 0}.mb-icon-text__text{margin:0 8px}.mb-icon-text__icon{line-height:1;margin-top:.125rem}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconText, decorators: [{
            type: Component,
            args: [{ selector: 'mb-icon-text', template: `
    <span class="mb-icon-text__icon">
      <ng-content select="mb-icon"></ng-content>
    </span>
    <div class="mb-icon-text__text">
      <ng-content select="span"></ng-content>
    </div>
  `, styles: [":host{display:flex;margin:4px 0}.mb-icon-text__text{margin:0 8px}.mb-icon-text__icon{line-height:1;margin-top:.125rem}\n"] }]
        }] });

class MbDateIconText {
    constructor() {
        this.calendar = aciCalendar;
    }
}
MbDateIconText.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDateIconText, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbDateIconText.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbDateIconText, selector: "mb-date-it", ngImport: i0, template: `
    <mb-icon-text>
      <mb-icon [icon]="calendar"></mb-icon>
      <span>
        <ng-content></ng-content>
      </span>
    </mb-icon-text>
  `, isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }, { kind: "component", type: MbIconText, selector: "mb-icon-text" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDateIconText, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-date-it',
                    template: `
    <mb-icon-text>
      <mb-icon [icon]="calendar"></mb-icon>
      <span>
        <ng-content></ng-content>
      </span>
    </mb-icon-text>
  `
                }]
        }] });

class MbLocationIconText {
    constructor() {
        this.location = aciPointer;
    }
}
MbLocationIconText.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbLocationIconText, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbLocationIconText.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbLocationIconText, selector: "mb-location-it", ngImport: i0, template: `
    <mb-icon-text>
      <mb-icon [icon]="location"></mb-icon>
      <span>
        <ng-content></ng-content>
      </span>
    </mb-icon-text>
  `, isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }, { kind: "component", type: MbIconText, selector: "mb-icon-text" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbLocationIconText, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-location-it',
                    template: `
    <mb-icon-text>
      <mb-icon [icon]="location"></mb-icon>
      <span>
        <ng-content></ng-content>
      </span>
    </mb-icon-text>
  `
                }]
        }] });

class MbPassengerIconText {
    constructor() {
        this.passenger = aciLogin;
    }
}
MbPassengerIconText.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPassengerIconText, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbPassengerIconText.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbPassengerIconText, selector: "mb-passenger-it", ngImport: i0, template: `
    <mb-icon-text>
      <mb-icon [icon]="passenger"></mb-icon>
      <span>
        <ng-content></ng-content>
      </span>
    </mb-icon-text>
  `, isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }, { kind: "component", type: MbIconText, selector: "mb-icon-text" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPassengerIconText, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-passenger-it',
                    template: `
    <mb-icon-text>
      <mb-icon [icon]="passenger"></mb-icon>
      <span>
        <ng-content></ng-content>
      </span>
    </mb-icon-text>
  `
                }]
        }] });

class MbTimeIconText {
    constructor() {
        this.clock = aciClock;
    }
}
MbTimeIconText.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTimeIconText, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbTimeIconText.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTimeIconText, selector: "mb-time-it", ngImport: i0, template: `
    <mb-icon-text>
      <mb-icon [icon]="clock"></mb-icon>
      <span>
        <ng-content></ng-content>
      </span>
    </mb-icon-text>
  `, isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }, { kind: "component", type: MbIconText, selector: "mb-icon-text" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTimeIconText, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-time-it',
                    template: `
    <mb-icon-text>
      <mb-icon [icon]="clock"></mb-icon>
      <span>
        <ng-content></ng-content>
      </span>
    </mb-icon-text>
  `
                }]
        }] });

const components$1 = [
    MbIconText,
    MbDateIconText,
    MbLocationIconText,
    MbPassengerIconText,
    MbTimeIconText
];
class MbIconTextModule {
}
MbIconTextModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconTextModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbIconTextModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbIconTextModule, declarations: [MbIconText,
        MbDateIconText,
        MbLocationIconText,
        MbPassengerIconText,
        MbTimeIconText], imports: [MbIconModule], exports: [MbIconText,
        MbDateIconText,
        MbLocationIconText,
        MbPassengerIconText,
        MbTimeIconText] });
MbIconTextModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconTextModule, imports: [MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbIconTextModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [MbIconModule],
                    declarations: [...components$1],
                    exports: [...components$1]
                }]
        }] });

const expandablePanelAnimations = [
    trigger('animationState', [
        state('false', style({
            height: '0px',
            visibility: 'hidden'
        })),
        state('true', style({
            height: '*',
            visibility: 'visible',
            overflow: 'visible'
        })),
        transition('true <=> false', [animate('225ms cubic-bezier(0.4,0.0,0.2,1)')])
    ])
];

class MbExpandablePanel {
    constructor() {
        this.expanded = false;
        this.collapse = new EventEmitter();
        this.expand = new EventEmitter();
    }
    open() {
        this.expanded = true;
        this.expand.emit();
    }
    close() {
        this.expanded = false;
        this.collapse.emit();
    }
    toggle() {
        this.expanded = !this.expanded;
        this.expanded ? this.expand.emit() : this.collapse.emit();
    }
}
MbExpandablePanel.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandablePanel, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbExpandablePanel.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbExpandablePanel, selector: "mb-expandable-panel", inputs: { expanded: "expanded" }, outputs: { collapse: "collapse", expand: "expand" }, host: { properties: { "class.mb-expandable-panel--opened": "expanded", "class.mb-expandable-panel--closed": "!expanded", "@animationState": "expanded" }, classAttribute: "mb-expandable-panel" }, exportAs: ["mbExpandablePanel"], ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{box-sizing:border-box;display:block;overflow:hidden}\n"], animations: expandablePanelAnimations });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandablePanel, decorators: [{
            type: Component,
            args: [{ selector: 'mb-expandable-panel', template: '<ng-content></ng-content>', exportAs: 'mbExpandablePanel', host: {
                        'class': 'mb-expandable-panel',
                        '[class.mb-expandable-panel--opened]': 'expanded',
                        '[class.mb-expandable-panel--closed]': '!expanded',
                        '[@animationState]': 'expanded'
                    }, animations: expandablePanelAnimations, styles: [":host{box-sizing:border-box;display:block;overflow:hidden}\n"] }]
        }], propDecorators: { expanded: [{
                type: Input
            }], collapse: [{
                type: Output
            }], expand: [{
                type: Output
            }] } });

class MbExpandablePanelTrigger {
    /** @internal Handles the trigger's click event. */
    handleClick() {
        if (this.panel) {
            this.panel.toggle();
        }
    }
}
MbExpandablePanelTrigger.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandablePanelTrigger, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MbExpandablePanelTrigger.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbExpandablePanelTrigger, selector: "button[mb-expandable-panel]", inputs: { panel: ["mb-expandable-panel", "panel"] }, host: { listeners: { "click": "handleClick()" }, properties: { "class.mb-expandable-panel-trigger--open": "panel && panel.expanded" }, classAttribute: "mb-expandable-panel-trigger" }, exportAs: ["mbExpandablePanelTrigger"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandablePanelTrigger, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[mb-expandable-panel]',
                    exportAs: 'mbExpandablePanelTrigger',
                    host: {
                        'class': 'mb-expandable-panel-trigger',
                        '[class.mb-expandable-panel-trigger--open]': 'panel && panel.expanded',
                        '(click)': 'handleClick()'
                    }
                }]
        }], propDecorators: { panel: [{
                type: Input,
                args: ['mb-expandable-panel']
            }] } });

class MbExpandablePanelModule {
}
MbExpandablePanelModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandablePanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbExpandablePanelModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbExpandablePanelModule, declarations: [MbExpandablePanel, MbExpandablePanelTrigger], exports: [MbExpandablePanel, MbExpandablePanelTrigger] });
MbExpandablePanelModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandablePanelModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbExpandablePanelModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbExpandablePanel, MbExpandablePanelTrigger],
                    exports: [MbExpandablePanel, MbExpandablePanelTrigger]
                }]
        }] });

const mbPopAnimation = {
    /** Animation that transitions a tooltip in and out. */
    popState: trigger('state', [
        state('*', style({ opacity: 0, transform: 'scale(0)' })),
        state('visible', style({ transform: 'scale(1)' })),
        transition('* => visible', animate('180ms cubic-bezier(0, 0, 0.2, 1)', keyframes([
            style({ opacity: 0, transform: 'scale(0)', offset: 0 }),
            style({ opacity: 1, transform: 'scale(1)', offset: 1 })
        ]))),
        transition('* => hidden', animate('200ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 0 })))
    ])
};

const DEFAULT_DELAY = 300;
class MbTooltipContent {
    constructor(platform) {
        this.platform = platform;
        this.closeTooltip = new EventEmitter();
        this.visibility = 'hidden';
    }
    ngOnInit() {
        if (this.iconType) {
            this.icon = STATE_ICONS[this.iconType];
        }
    }
    get tooltipClasses() {
        return [`mb-tooltip--${this.position}`, `mb-tooltip--${this.color}`];
    }
    show() {
        this.visibility = 'visible';
        if (this.hideTimeoutId) {
            clearTimeout(this.hideTimeoutId);
            this.hideTimeoutId = null;
        }
        this.showTimeoutId = setTimeout(() => { }, DEFAULT_DELAY);
    }
    hide() {
        if (this.showTimeoutId) {
            clearTimeout(this.showTimeoutId);
            this.showTimeoutId = null;
        }
        this.hideTimeoutId = setTimeout(() => {
            this.visibility = 'hidden';
        }, DEFAULT_DELAY);
    }
    handleBodyInteraction() {
        if (this.visibility === 'visible' &&
            !this.platform.IOS &&
            !this.platform.ANDROID &&
            !this.dismissible) {
            this.hide();
        }
    }
    isVisible() {
        return this.visibility !== 'hidden';
    }
}
MbTooltipContent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTooltipContent, deps: [{ token: i1$4.Platform }], target: i0.ɵɵFactoryTarget.Component });
MbTooltipContent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTooltipContent, selector: "mb-tooltip", outputs: { closeTooltip: "closeTooltip" }, host: { listeners: { "body:click": "this.handleBodyInteraction()" }, classAttribute: "mb-tooltip" }, ngImport: i0, template: `
    <div
      [@state]="visibility"
      class="mb-tooltip__body"
      [ngClass]="tooltipClasses"
      role="tooltip"
    >
      <mb-icon *ngIf="icon" [icon]="icon"></mb-icon>
      <ng-container *ngIf="tooltip">
        <div class="mb-tooltip__content">
          <ng-container *ngTemplateOutlet="tooltip"></ng-container>
        </div>
      </ng-container>
      <ng-container *ngIf="!tooltip && text">
        <span>{{ text }}</span>
      </ng-container>
      <button
        class="mb-tooltip__close"
        *ngIf="dismissible"
        (click)="closeTooltip.emit(true)"
      ></button>
    </div>
  `, isInline: true, styles: ["@charset \"UTF-8\";.mb-tooltip__body{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;border-radius:4px;box-shadow:0 2px 2px #0000000d,0 2px 32px #0000001a;display:flex;gap:8px;max-width:240px;padding:8px;position:relative}@media only screen and (max-width: 767px){.mb-tooltip__body{line-height:1.5}}.mb-tooltip__body mb-icon{color:var(--color-secondary)}.mb-tooltip__close{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;align-self:flex-start}.mb-tooltip__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}.mb-tooltip__close:after{font-size:36px;font-weight:400}.mb-tooltip--light{background-color:var(--color-foreground);color:var(--color-text-dark)}.mb-tooltip--light:after{color:var(--color-foreground)}.mb-tooltip--dark{background-color:var(--color-gray-900);color:var(--color-text-light)}.mb-tooltip--dark:after{color:var(--color-gray-900)}.mb-tooltip--above,.mb-tooltip-fallback-position .mb-tooltip--below{bottom:unset;top:-8px}.mb-tooltip--above:after,.mb-tooltip-fallback-position .mb-tooltip--below:after{content:\"\";height:0;width:0;z-index:2;border-top:8px solid currentColor;border-bottom:0;border-left:8px solid transparent;border-right:8px solid transparent;bottom:-8px;left:50%;position:absolute;top:unset;margin-left:-8px}.mb-tooltip--below,.mb-tooltip-fallback-position .mb-tooltip--above{bottom:-8px;top:unset}.mb-tooltip--below:after,.mb-tooltip-fallback-position .mb-tooltip--above:after{content:\"\";height:0;width:0;z-index:2;border-bottom:8px solid currentColor;border-top:0;border-left:8px solid transparent;border-right:8px solid transparent;bottom:unset;left:50%;position:absolute;top:-8px;margin-left:-8px}.mb-tooltip--left,.mb-tooltip-fallback-position .mb-tooltip--right{left:unset;right:8px}.mb-tooltip--left:after,.mb-tooltip-fallback-position .mb-tooltip--right:after{content:\"\";height:0;width:0;z-index:2;border-left:8px solid currentColor;border-right:0;border-bottom:8px solid transparent;border-top:8px solid transparent;left:unset;position:absolute;right:-8px;top:50%;margin-top:-8px}.mb-tooltip--right,.mb-tooltip-fallback-position .mb-tooltip--left{left:8px;right:unset}.mb-tooltip--right:after,.mb-tooltip-fallback-position .mb-tooltip--left:after{content:\"\";height:0;width:0;z-index:2;border-right:8px solid currentColor;border-left:0;border-bottom:8px solid transparent;border-top:8px solid transparent;left:-8px;position:absolute;right:unset;top:50%;margin-top:-8px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }], animations: [mbPopAnimation.popState], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTooltipContent, decorators: [{
            type: Component,
            args: [{ selector: 'mb-tooltip', template: `
    <div
      [@state]="visibility"
      class="mb-tooltip__body"
      [ngClass]="tooltipClasses"
      role="tooltip"
    >
      <mb-icon *ngIf="icon" [icon]="icon"></mb-icon>
      <ng-container *ngIf="tooltip">
        <div class="mb-tooltip__content">
          <ng-container *ngTemplateOutlet="tooltip"></ng-container>
        </div>
      </ng-container>
      <ng-container *ngIf="!tooltip && text">
        <span>{{ text }}</span>
      </ng-container>
      <button
        class="mb-tooltip__close"
        *ngIf="dismissible"
        (click)="closeTooltip.emit(true)"
      ></button>
    </div>
  `, encapsulation: ViewEncapsulation.None, host: {
                        'class': 'mb-tooltip',
                        '(body:click)': 'this.handleBodyInteraction()'
                    }, animations: [mbPopAnimation.popState], styles: ["@charset \"UTF-8\";.mb-tooltip__body{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;border-radius:4px;box-shadow:0 2px 2px #0000000d,0 2px 32px #0000001a;display:flex;gap:8px;max-width:240px;padding:8px;position:relative}@media only screen and (max-width: 767px){.mb-tooltip__body{line-height:1.5}}.mb-tooltip__body mb-icon{color:var(--color-secondary)}.mb-tooltip__close{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;align-self:flex-start}.mb-tooltip__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}.mb-tooltip__close:after{font-size:36px;font-weight:400}.mb-tooltip--light{background-color:var(--color-foreground);color:var(--color-text-dark)}.mb-tooltip--light:after{color:var(--color-foreground)}.mb-tooltip--dark{background-color:var(--color-gray-900);color:var(--color-text-light)}.mb-tooltip--dark:after{color:var(--color-gray-900)}.mb-tooltip--above,.mb-tooltip-fallback-position .mb-tooltip--below{bottom:unset;top:-8px}.mb-tooltip--above:after,.mb-tooltip-fallback-position .mb-tooltip--below:after{content:\"\";height:0;width:0;z-index:2;border-top:8px solid currentColor;border-bottom:0;border-left:8px solid transparent;border-right:8px solid transparent;bottom:-8px;left:50%;position:absolute;top:unset;margin-left:-8px}.mb-tooltip--below,.mb-tooltip-fallback-position .mb-tooltip--above{bottom:-8px;top:unset}.mb-tooltip--below:after,.mb-tooltip-fallback-position .mb-tooltip--above:after{content:\"\";height:0;width:0;z-index:2;border-bottom:8px solid currentColor;border-top:0;border-left:8px solid transparent;border-right:8px solid transparent;bottom:unset;left:50%;position:absolute;top:-8px;margin-left:-8px}.mb-tooltip--left,.mb-tooltip-fallback-position .mb-tooltip--right{left:unset;right:8px}.mb-tooltip--left:after,.mb-tooltip-fallback-position .mb-tooltip--right:after{content:\"\";height:0;width:0;z-index:2;border-left:8px solid currentColor;border-right:0;border-bottom:8px solid transparent;border-top:8px solid transparent;left:unset;position:absolute;right:-8px;top:50%;margin-top:-8px}.mb-tooltip--right,.mb-tooltip-fallback-position .mb-tooltip--left{left:8px;right:unset}.mb-tooltip--right:after,.mb-tooltip-fallback-position .mb-tooltip--left:after{content:\"\";height:0;width:0;z-index:2;border-right:8px solid currentColor;border-left:0;border-bottom:8px solid transparent;border-top:8px solid transparent;left:-8px;position:absolute;right:unset;top:50%;margin-top:-8px}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$4.Platform }]; }, propDecorators: { closeTooltip: [{
                type: Output
            }] } });

/**
 * Creates an error to be thrown if the user supplied an invalid tooltip position.
 */
function getMbInvalidPositionError(position) {
    return Error(`Position "${position}" is invalid.`);
}
function getPositionWithFallback(position, fallbackPanelClass) {
    const origin = getOriginWithFallback(position);
    const overlay = getOverlayPositionWithFallback(position);
    return [
        { ...origin.main, ...overlay.main },
        {
            ...origin.fallback,
            ...overlay.fallback,
            panelClass: fallbackPanelClass
        }
    ];
}
/**
 * Returns the origin position and a fallback position based on the user's position preference.
 * The fallback position is the inverse of the origin (e.g. `'below' -> 'above'`).
 */
function getOriginWithFallback(position) {
    let originPosition;
    if (position === 'above' || position === 'below') {
        originPosition = {
            originX: 'center',
            originY: position === 'above' ? 'top' : 'bottom'
        };
    }
    else if (position === 'left') {
        originPosition = { originX: 'start', originY: 'center' };
    }
    else if (position === 'right') {
        originPosition = { originX: 'end', originY: 'center' };
    }
    else {
        throw getMbInvalidPositionError(position);
    }
    const { x, y } = invertPosition(originPosition.originX, originPosition.originY, position);
    return {
        main: originPosition,
        fallback: { originX: x, originY: y }
    };
}
/** Returns the overlay position and a fallback position based on the user's preference */
function getOverlayPositionWithFallback(position) {
    let overlayPosition;
    if (position === 'above') {
        overlayPosition = { overlayX: 'center', overlayY: 'bottom' };
    }
    else if (position === 'below') {
        overlayPosition = { overlayX: 'center', overlayY: 'top' };
    }
    else if (position === 'left') {
        overlayPosition = { overlayX: 'end', overlayY: 'center' };
    }
    else if (position === 'right') {
        overlayPosition = { overlayX: 'start', overlayY: 'center' };
    }
    else {
        throw getMbInvalidPositionError(position);
    }
    const { x, y } = invertPosition(overlayPosition.overlayX, overlayPosition.overlayY, position);
    return {
        main: overlayPosition,
        fallback: { overlayX: x, overlayY: y }
    };
}
/** Inverts an overlay position. */
function invertPosition(x, y, position) {
    if (position === 'above' || position === 'below') {
        if (y === 'top') {
            y = 'bottom';
        }
        else if (y === 'bottom') {
            y = 'top';
        }
    }
    else {
        if (x === 'end') {
            x = 'start';
        }
        else if (x === 'start') {
            x = 'end';
        }
    }
    return { x, y };
}

const INSTANCE_TOKEN = new InjectionToken('INSTANCE_TOKEN');
class MbTooltipTrigger {
    constructor(overlay, vcr, elementRef, platform) {
        this.overlay = overlay;
        this.vcr = vcr;
        this.elementRef = elementRef;
        this.platform = platform;
        /**
         * Since is possible to add a tooltip to any html element we need to "namespace"
         * their inputs to avoid collisions with other components such as mb-button, since both
         * have a color input option
         */
        this.mbTooltipPosition = 'above';
        this.mbTooltipColor = 'dark';
        this.mbTooltipDismissible = false;
        this.mbTooltipMobile = true;
        if (this.platform.IOS || this.platform.ANDROID) {
            this.isMobile = true;
        }
    }
    ngOnInit() {
        this.addHoverListeners();
        if (this.isMobile) {
            this.mbTooltipDismissible = true;
        }
    }
    addHoverListeners() {
        this.showHandler = () => this._show();
        this.hideHandler = () => this._hide();
        if (!this.isMobile) {
            this.elementRef.nativeElement.addEventListener('mouseenter', this.showHandler);
            this.elementRef.nativeElement.addEventListener('focus', this.showHandler);
            if (!this.mbTooltipDismissible) {
                this.elementRef.nativeElement.addEventListener('mouseleave', this.hideHandler);
                this.elementRef.nativeElement.addEventListener('blur', this.hideHandler);
            }
        }
        else if (this.mbTooltipMobile && this.isMobile) {
            this.elementRef.nativeElement.addEventListener('touchstart', this.showHandler);
        }
    }
    ngOnDestroy() {
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.tooltipInstance = null;
        }
        if (this.showHandler) {
            this.elementRef.nativeElement.removeEventListener('mouseenter', this.showHandler);
            this.elementRef.nativeElement.removeEventListener('touchstart', this.showHandler);
        }
        if (this.hideHandler) {
            this.elementRef.nativeElement.removeEventListener('mouseleave', this.hideHandler);
        }
    }
    _isTooltipVisible() {
        return !!this.tooltipInstance && this.tooltipInstance.isVisible();
    }
    _show() {
        if (this._isTooltipVisible()) {
            return;
        }
        const overlayRef = this.createTooltipOverlay();
        this.detach();
        this.portal = this.portal || new ComponentPortal(MbTooltipContent, this.vcr);
        this.tooltipInstance = overlayRef.attach(this.portal).instance;
        if (this.mbTooltipText) {
            this.tooltipInstance.text =
                this.mbTooltipText.length > 158
                    ? this.mbTooltipText.substring(0, 159).concat('...')
                    : this.mbTooltipText;
        }
        else {
            this.tooltipInstance.tooltip = this.tooltipTemplate;
        }
        this.tooltipInstance.color = this.mbTooltipColor;
        this.tooltipInstance.position = this.mbTooltipPosition;
        this.tooltipInstance.mobile = this.mbTooltipMobile;
        this.tooltipInstance.dismissible = this.mbTooltipDismissible;
        this.tooltipInstance.iconType = this.mbTooltipIcon;
        this.tooltipInstance.show();
        this.tooltipInstance.closeTooltip.subscribe(data => {
            this._hide();
        });
    }
    detach() {
        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
        }
        this.tooltipInstance = null;
    }
    _hide() {
        if (this._isTooltipVisible()) {
            this.detach();
        }
    }
    createTooltipOverlay() {
        if (this.overlayRef) {
            return this.overlayRef;
        }
        this.overlayRef = this.overlay.create(this.getOverlayConfig());
        this.overlayRef.keydownEvents().subscribe(event => {
            if (event.keyCode === ESCAPE) {
                event.preventDefault();
                this._hide();
            }
        });
        return this.overlayRef;
    }
    getOverlayConfig() {
        const scrollStrategy = !this.mbTooltipDismissible
            ? this.overlay.scrollStrategies.close()
            : this.overlay.scrollStrategies.reposition();
        const positions = getPositionWithFallback(this.mbTooltipPosition, 'mb-tooltip-fallback-position');
        return {
            hasBackdrop: false,
            scrollStrategy,
            positionStrategy: this.overlay
                .position()
                .flexibleConnectedTo(this.elementRef)
                .withPositions(positions)
                .withFlexibleDimensions(false)
        };
    }
}
MbTooltipTrigger.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTooltipTrigger, deps: [{ token: i1$3.Overlay }, { token: i0.ViewContainerRef }, { token: i0.ElementRef }, { token: i1$4.Platform }], target: i0.ɵɵFactoryTarget.Directive });
MbTooltipTrigger.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbTooltipTrigger, selector: "[mb-tooltip]", inputs: { tooltipTemplate: ["mb-tooltip", "tooltipTemplate"], mbTooltipPosition: "mbTooltipPosition", mbTooltipColor: "mbTooltipColor", mbTooltipText: "mbTooltipText", mbTooltipDismissible: "mbTooltipDismissible", mbTooltipMobile: "mbTooltipMobile", mbTooltipIcon: "mbTooltipIcon" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTooltipTrigger, decorators: [{
            type: Directive,
            args: [{
                    selector: '[mb-tooltip]'
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Overlay }, { type: i0.ViewContainerRef }, { type: i0.ElementRef }, { type: i1$4.Platform }]; }, propDecorators: { tooltipTemplate: [{
                type: Input,
                args: ['mb-tooltip']
            }], mbTooltipPosition: [{
                type: Input
            }], mbTooltipColor: [{
                type: Input
            }], mbTooltipText: [{
                type: Input
            }], mbTooltipDismissible: [{
                type: Input
            }], mbTooltipMobile: [{
                type: Input
            }], mbTooltipIcon: [{
                type: Input
            }] } });

class MbTooltipModule {
}
MbTooltipModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbTooltipModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbTooltipModule, declarations: [MbTooltipContent, MbTooltipTrigger], imports: [CommonModule, PlatformModule, OverlayModule, MbIconModule], exports: [MbTooltipContent, MbTooltipTrigger] });
MbTooltipModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTooltipModule, imports: [CommonModule, PlatformModule, OverlayModule, MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PlatformModule, OverlayModule, MbIconModule],
                    declarations: [MbTooltipContent, MbTooltipTrigger],
                    exports: [MbTooltipContent, MbTooltipTrigger],
                }]
        }] });

class PopoverRef {
    constructor() {
        // tslint:disable-next-line:variable-name
        this._afterHidden = new Subject();
    }
    close() {
        this._afterHidden.next();
    }
    afterHidden() {
        return this._afterHidden.asObservable();
    }
}
PopoverRef.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PopoverRef, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PopoverRef.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PopoverRef });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PopoverRef, decorators: [{
            type: Injectable
        }] });

class MbPopoverContent {
    constructor(popoverRef) {
        this.popoverRef = popoverRef;
    }
    get popoverClasses() {
        return [`mb-popover--${this.position}`, `mb-popover--${this.color}`];
    }
    show() {
        this.visibility = 'visible';
    }
    hide() {
        this.visibility = 'hidden';
    }
    isVisible() {
        return this.visibility === 'visible';
    }
    animationDone(event) {
        const toState = event.toState;
        if (toState === 'hidden' && !this.isVisible()) {
            this.popoverRef.close();
        }
    }
}
MbPopoverContent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPopoverContent, deps: [{ token: PopoverRef }], target: i0.ɵɵFactoryTarget.Component });
MbPopoverContent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbPopoverContent, selector: "mb-popover", host: { classAttribute: "mb-popover" }, ngImport: i0, template: `
    <div
      [@state]="visibility"
      (@state.done)="animationDone($event)"
      class="mb-popover__content"
      [ngClass]="popoverClasses"
    >
      <span class="mb-popover__message"
        ><ng-container *ngTemplateOutlet="popover"></ng-container
      ></span>
      <span class="mb-popover__close" (click)="hide()" *ngIf="dismissible"></span>
    </div>
  `, isInline: true, styles: ["@charset \"UTF-8\";.mb-popover{display:block}.mb-popover--above,.mb-popover-fallback-position .mb-popover--below{bottom:unset;top:-8px}.mb-popover--above:after,.mb-popover-fallback-position .mb-popover--below:after{content:\"\";height:0;width:0;z-index:2;border-top:8px solid currentColor;border-bottom:0;border-left:8px solid transparent;border-right:8px solid transparent;bottom:-8px;left:50%;position:absolute;top:unset;margin-left:-8px}.mb-popover--below,.mb-popover-fallback-position .mb-popover--above{bottom:-8px;top:unset}.mb-popover--below:after,.mb-popover-fallback-position .mb-popover--above:after{content:\"\";height:0;width:0;z-index:2;border-bottom:8px solid currentColor;border-top:0;border-left:8px solid transparent;border-right:8px solid transparent;bottom:unset;left:50%;position:absolute;top:-8px;margin-left:-8px}.mb-popover--left,.mb-popover-fallback-position .mb-popover--right{left:unset;right:8px}.mb-popover--left:after,.mb-popover-fallback-position .mb-popover--right:after{content:\"\";height:0;width:0;z-index:2;border-left:8px solid currentColor;border-right:0;border-bottom:8px solid transparent;border-top:8px solid transparent;left:unset;position:absolute;right:-8px;top:50%;margin-top:-8px}.mb-popover--right,.mb-popover-fallback-position .mb-popover--left{left:8px;right:unset}.mb-popover--right:after,.mb-popover-fallback-position .mb-popover--left:after{content:\"\";height:0;width:0;z-index:2;border-right:8px solid currentColor;border-left:0;border-bottom:8px solid transparent;border-top:8px solid transparent;left:-8px;position:absolute;right:unset;top:50%;margin-top:-8px}.mb-popover__content{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;border-radius:4px;box-shadow:0 2px 2px #0000000d,0 2px 32px #0000001a;display:flex;max-width:240px;padding:8px;position:relative}@media only screen and (max-width: 767px){.mb-popover__content{line-height:1.5}}.mb-popover__message{display:flex}.mb-popover__close{cursor:pointer;margin-left:8px}.mb-popover__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}.mb-popover--success{background-color:var(--color-success);color:var(--color-success-contrast)}.mb-popover--success:after{color:var(--color-success)}.mb-popover--notification{background-color:var(--color-notification);color:var(--color-notification-contrast)}.mb-popover--notification:after{color:var(--color-notification)}.mb-popover--light{background-color:var(--color-foreground);color:var(--color-foreground-contrast)}.mb-popover--light:after{color:var(--color-foreground)}.mb-popover--dark{background-color:var(--color-gray-900);color:var(--color-text-light)}.mb-popover--dark:after{color:var(--color-gray-900)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], animations: [mbPopAnimation.popState] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPopoverContent, decorators: [{
            type: Component,
            args: [{ selector: 'mb-popover', template: `
    <div
      [@state]="visibility"
      (@state.done)="animationDone($event)"
      class="mb-popover__content"
      [ngClass]="popoverClasses"
    >
      <span class="mb-popover__message"
        ><ng-container *ngTemplateOutlet="popover"></ng-container
      ></span>
      <span class="mb-popover__close" (click)="hide()" *ngIf="dismissible"></span>
    </div>
  `, host: {
                        class: 'mb-popover'
                    }, animations: [mbPopAnimation.popState], styles: ["@charset \"UTF-8\";.mb-popover{display:block}.mb-popover--above,.mb-popover-fallback-position .mb-popover--below{bottom:unset;top:-8px}.mb-popover--above:after,.mb-popover-fallback-position .mb-popover--below:after{content:\"\";height:0;width:0;z-index:2;border-top:8px solid currentColor;border-bottom:0;border-left:8px solid transparent;border-right:8px solid transparent;bottom:-8px;left:50%;position:absolute;top:unset;margin-left:-8px}.mb-popover--below,.mb-popover-fallback-position .mb-popover--above{bottom:-8px;top:unset}.mb-popover--below:after,.mb-popover-fallback-position .mb-popover--above:after{content:\"\";height:0;width:0;z-index:2;border-bottom:8px solid currentColor;border-top:0;border-left:8px solid transparent;border-right:8px solid transparent;bottom:unset;left:50%;position:absolute;top:-8px;margin-left:-8px}.mb-popover--left,.mb-popover-fallback-position .mb-popover--right{left:unset;right:8px}.mb-popover--left:after,.mb-popover-fallback-position .mb-popover--right:after{content:\"\";height:0;width:0;z-index:2;border-left:8px solid currentColor;border-right:0;border-bottom:8px solid transparent;border-top:8px solid transparent;left:unset;position:absolute;right:-8px;top:50%;margin-top:-8px}.mb-popover--right,.mb-popover-fallback-position .mb-popover--left{left:8px;right:unset}.mb-popover--right:after,.mb-popover-fallback-position .mb-popover--left:after{content:\"\";height:0;width:0;z-index:2;border-right:8px solid currentColor;border-left:0;border-bottom:8px solid transparent;border-top:8px solid transparent;left:-8px;position:absolute;right:unset;top:50%;margin-top:-8px}.mb-popover__content{font-size:.875rem;font-weight:var(--font-weight-regular);line-height:1.5;border-radius:4px;box-shadow:0 2px 2px #0000000d,0 2px 32px #0000001a;display:flex;max-width:240px;padding:8px;position:relative}@media only screen and (max-width: 767px){.mb-popover__content{line-height:1.5}}.mb-popover__message{display:flex}.mb-popover__close{cursor:pointer;margin-left:8px}.mb-popover__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}.mb-popover--success{background-color:var(--color-success);color:var(--color-success-contrast)}.mb-popover--success:after{color:var(--color-success)}.mb-popover--notification{background-color:var(--color-notification);color:var(--color-notification-contrast)}.mb-popover--notification:after{color:var(--color-notification)}.mb-popover--light{background-color:var(--color-foreground);color:var(--color-foreground-contrast)}.mb-popover--light:after{color:var(--color-foreground)}.mb-popover--dark{background-color:var(--color-gray-900);color:var(--color-text-light)}.mb-popover--dark:after{color:var(--color-gray-900)}\n"] }]
        }], ctorParameters: function () { return [{ type: PopoverRef }]; } });

function getBoundingClientRect(element) {
    const rect = element.getBoundingClientRect();
    return {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        x: rect.left,
        y: rect.top
    };
}

function getWindowScroll(node) {
    const win = getWindow(node);
    const scrollLeft = win.pageXOffset;
    const scrollTop = win.pageYOffset;
    return {
        scrollLeft,
        scrollTop
    };
}

function isElement(node) {
    const ownElement = getWindow(node).Element;
    return node instanceof ownElement || node instanceof Element;
}
function isHTMLElement(node) {
    const ownElement = getWindow(node).HTMLElement;
    return node instanceof ownElement || node instanceof HTMLElement;
}

function getHTMLElementScroll(element) {
    return {
        scrollLeft: element.scrollLeft,
        scrollTop: element.scrollTop
    };
}

function getNodeScroll(node) {
    if (node === getWindow(node) || !isHTMLElement(node)) {
        return getWindowScroll(node);
    }
    else {
        return getHTMLElementScroll(node);
    }
}

function getNodeName(element) {
    return element ? (element.nodeName || '').toLowerCase() : null;
}

function getDocumentElement(element) {
    return (isElement(element) ? element.ownerDocument : element.document).documentElement;
}

function getWindowScrollBarX(element) {
    // If <html> has a CSS width greater than the viewport, then this will be
    // incorrect for RTL.
    // Popper 1 is broken in this case and never had a bug report so let's assume
    // it's not an issue. I don't think anyone ever specifies width on <html>
    // anyway.
    // Browsers where the left scrollbar doesn't cause an issue report `0` for
    // this (e.g. Edge 2019, IE11, Safari)
    return (getBoundingClientRect(getDocumentElement(element)).left +
        getWindowScroll(element).scrollLeft);
}

function isScrollParent(element) {
    // Firefox wants us to check `-x` and `-y` variations as well
    const { overflow, overflowX, overflowY } = getComputedStyle(element);
    return /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX);
}

// Returns the composite rect of an element relative to its offsetParent.
// Composite means it takes into account transforms as well as layout.
function getCompositeRect(elementOrVirtualElement, offsetParent) {
    const documentElement = getDocumentElement(offsetParent);
    const rect = getBoundingClientRect(elementOrVirtualElement);
    let scroll = { scrollLeft: 0, scrollTop: 0 };
    let offsets = { x: 0, y: 0 };
    if (getNodeName(offsetParent) !== 'body' ||
        // https://github.com/popperjs/popper-core/issues/1078
        isScrollParent(documentElement)) {
        scroll = getNodeScroll(offsetParent);
    }
    if (isHTMLElement(offsetParent)) {
        offsets = getBoundingClientRect(offsetParent);
        offsets.x += offsetParent.clientLeft;
        offsets.y += offsetParent.clientTop;
    }
    else if (documentElement) {
        offsets.x = getWindowScrollBarX(documentElement);
    }
    return {
        x: rect.left + scroll.scrollLeft - offsets.x,
        y: rect.top + scroll.scrollTop - offsets.y,
        width: rect.width,
        height: rect.height
    };
}

function isTableElement(element) {
    return ['table', 'td', 'th'].indexOf(getNodeName(element)) >= 0;
}

function getTrueOffsetParent(element) {
    if (!isHTMLElement(element) || getComputedStyle(element).position === 'fixed') {
        return null;
    }
    return element.offsetParent;
}
function getOffsetParent(element) {
    const window = getWindow(element);
    let offsetParent = getTrueOffsetParent(element);
    // Find the nearest non-table offsetParent
    while (offsetParent && isTableElement(offsetParent)) {
        offsetParent = getTrueOffsetParent(offsetParent);
    }
    if (offsetParent &&
        getNodeName(offsetParent) === 'body' &&
        getComputedStyle(offsetParent).position === 'static') {
        return window;
    }
    return offsetParent || window;
}

// Returns the layout rect of an element relative to its offsetParent. Layout
// means it doesn't take into account transforms.
function getLayoutRect(element) {
    return {
        x: element.offsetLeft,
        y: element.offsetTop,
        width: element.offsetWidth,
        height: element.offsetHeight
    };
}

function getParentNode(element) {
    if (getNodeName(element) === 'html') {
        return element;
    }
    return element.parentNode || (isElement(element) && getDocumentElement(element));
}

function getScrollParent(node) {
    if (['html', 'body', '#document'].indexOf(getNodeName(node)) >= 0) {
        return node.ownerDocument.body;
    }
    if (isHTMLElement(node)) {
        // Firefox wants us to check `-x` and `-y` variations as well
        const { overflow, overflowX, overflowY } = getComputedStyle(node);
        if (/auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX)) {
            return node;
        }
    }
    return getScrollParent(getParentNode(node));
}

function listScrollParents(element, list = []) {
    const scrollParent = getScrollParent(element);
    const isBody = getNodeName(scrollParent) === 'body';
    const win = getWindow(scrollParent);
    const target = isBody ? [win].concat(win.visualViewport || []) : scrollParent;
    const updatedList = list.concat(target);
    return isBody
        ? updatedList
        : updatedList.concat(listScrollParents(getParentNode(target)));
}

class MbPopoverTrigger {
    constructor(viewContainerRef, renderer, elementRef, componentFactoryResolver, applicationRef, injector, zone, platformId) {
        this.viewContainerRef = viewContainerRef;
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.componentFactoryResolver = componentFactoryResolver;
        this.applicationRef = applicationRef;
        this.injector = injector;
        this.zone = zone;
        /**
         * Since is possible to add a popover to any html element we need to "namespace"
         * their inputs to avoid collisions with other components such as mb-button, since both
         * have a color input option
         */
        this.mbPopoverPosition = 'above';
        this.mbPopoverColor = 'dark';
        this.mbPopoverDismissible = false;
        this.mbPopoverShow = false;
        this.popoverElement = null;
        this.scrollParents = [];
        /** Emits when the component is destroyed. */
        this.destroyed = new Subject();
        this.isBrowser = platformId
            ? isPlatformBrowser(platformId)
            : typeof window === 'object' && !!window;
    }
    get nativeElement() {
        return this.elementRef.nativeElement;
    }
    ngOnInit() {
        if (this.mbPopoverShow) {
            this.show();
        }
    }
    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }
    createPopoverElement() {
        const popoverElement = this.renderer.createElement('DIV');
        this.renderer.setStyle(popoverElement, 'position', 'absolute');
        this.renderer.setStyle(popoverElement, 'z-index', '100');
        return popoverElement;
    }
    show() {
        if (this.isPopoverVisible() || !this.isBrowser) {
            return;
        }
        const nativeElement = this.nativeElement;
        this.popoverElement = this.createPopoverElement();
        this.renderer.appendChild(nativeElement.parentElement, this.popoverElement);
        const portalHost = new DomPortalOutlet(this.popoverElement, this.componentFactoryResolver, this.applicationRef, this.injector);
        const popoverRef = new PopoverRef();
        const injector = this.createInjector(popoverRef, this.injector);
        this.portal =
            this.portal ||
                new ComponentPortal(MbPopoverContent, this.viewContainerRef, injector);
        const popComponentComponentRef = portalHost.attachComponentPortal(this.portal);
        this.popoverInstance = popComponentComponentRef.instance;
        this.initPopoverInstance();
        this.popoverInstance.popoverRef
            .afterHidden()
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => portalHost.dispose());
        const passive = { passive: true };
        this.scrollParents = listScrollParents(this.nativeElement);
        this.scrollParents.forEach(scrollParent => {
            this.zone.runOutsideAngular(() => {
                scrollParent.addEventListener('scroll', () => {
                    this.update();
                }, passive);
            });
        });
        window.addEventListener('resize', () => {
            this.update();
        }, passive);
        popComponentComponentRef.changeDetectorRef.detectChanges();
        this.updatePosition();
        this.popoverInstance.show();
    }
    hide() {
        if (this.popoverInstance) {
            this.popoverInstance.hide();
        }
    }
    update() {
        this.updatePosition();
    }
    initPopoverInstance() {
        this.popoverInstance.popover = this.popoverTemplate;
        this.popoverInstance.color = this.mbPopoverColor;
        this.popoverInstance.position = this.mbPopoverPosition;
        this.popoverInstance.dismissible = this.mbPopoverDismissible;
    }
    computePopoverPosition(reference, element) {
        const commonX = reference.x + reference.width / 2 - element.width / 2;
        const commonY = reference.y + reference.height / 2 - element.height / 2;
        switch (this.mbPopoverPosition) {
            case 'above':
                return {
                    x: commonX,
                    y: reference.y - element.height
                };
            case 'below':
                return {
                    x: commonX,
                    y: reference.y + reference.height
                };
            case 'right':
                return {
                    x: reference.x + reference.width,
                    y: commonY
                };
            case 'left':
                return {
                    x: reference.x - element.width,
                    y: commonY
                };
        }
    }
    updatePosition() {
        const element = getLayoutRect(this.popoverElement);
        const reference = getCompositeRect(this.nativeElement, getOffsetParent(this.popoverElement));
        const { x, y } = this.computePopoverPosition(reference, element);
        this.renderPopoverPosition(x, y);
    }
    renderPopoverPosition(x, y) {
        this.renderer.setStyle(this.popoverElement, 'top', '0');
        this.renderer.setStyle(this.popoverElement, 'left', '0');
        this.renderer.setStyle(this.popoverElement, 'right', 'auto');
        this.renderer.setStyle(this.popoverElement, 'bottom', 'auto');
        this.renderer.setStyle(this.popoverElement, 'transform', (window.devicePixelRatio || 1) < 2
            ? `translate(${x}px, ${y}px)`
            : `translate3d(${x}px, ${y}px, 0)`);
    }
    isPopoverVisible() {
        return !!this.popoverInstance && this.popoverInstance.isVisible();
    }
    createInjector(popoverRef, injector) {
        const tokens = new WeakMap([[PopoverRef, popoverRef]]);
        return new PortalInjector(injector, tokens);
    }
}
MbPopoverTrigger.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPopoverTrigger, deps: [{ token: i0.ViewContainerRef }, { token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.ComponentFactoryResolver }, { token: i0.ApplicationRef }, { token: i0.Injector }, { token: i0.NgZone }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Directive });
MbPopoverTrigger.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbPopoverTrigger, selector: "[mb-popover]", inputs: { popoverTemplate: ["mb-popover", "popoverTemplate"], mbPopoverPosition: "mbPopoverPosition", mbPopoverColor: "mbPopoverColor", mbPopoverDismissible: "mbPopoverDismissible", mbPopoverShow: "mbPopoverShow" }, exportAs: ["mbPopover"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPopoverTrigger, decorators: [{
            type: Directive,
            args: [{
                    selector: '[mb-popover]',
                    exportAs: 'mbPopover'
                }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }, { type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }, { type: i0.Injector }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; }, propDecorators: { popoverTemplate: [{
                type: Input,
                args: ['mb-popover']
            }], mbPopoverPosition: [{
                type: Input
            }], mbPopoverColor: [{
                type: Input
            }], mbPopoverDismissible: [{
                type: Input
            }], mbPopoverShow: [{
                type: Input
            }] } });

class MbPopoverModule {
}
MbPopoverModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbPopoverModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbPopoverModule, declarations: [MbPopoverContent, MbPopoverTrigger], imports: [CommonModule, OverlayModule], exports: [MbPopoverContent, MbPopoverTrigger] });
MbPopoverModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPopoverModule, imports: [CommonModule, OverlayModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule],
                    exports: [MbPopoverContent, MbPopoverTrigger],
                    declarations: [MbPopoverContent, MbPopoverTrigger],
                }]
        }] });

class MbStarsRating {
    constructor() {
        this.starRatingIcon = aciStar;
        this.iter = [];
    }
    ngOnInit() {
        if (this.rating) {
            this.iter = new Array(Math.floor(this.rating));
        }
    }
}
MbStarsRating.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbStarsRating, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbStarsRating.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbStarsRating, selector: "mb-stars-rating", inputs: { rating: "rating" }, ngImport: i0, template: "<mb-icon [icon]=\"starRatingIcon\" class=\"stars-rating\" *ngFor=\"let i of iter\"></mb-icon>\n", styles: [":host{display:inline-block}.stars-rating{color:var(--color-warning)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbStarsRating, decorators: [{
            type: Component,
            args: [{ selector: 'mb-stars-rating', template: "<mb-icon [icon]=\"starRatingIcon\" class=\"stars-rating\" *ngFor=\"let i of iter\"></mb-icon>\n", styles: [":host{display:inline-block}.stars-rating{color:var(--color-warning)}\n"] }]
        }], propDecorators: { rating: [{
                type: Input
            }] } });

class MbStarsRatingModule {
}
MbStarsRatingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbStarsRatingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbStarsRatingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbStarsRatingModule, declarations: [MbStarsRating], imports: [CommonModule, MbIconModule], exports: [MbStarsRating] });
MbStarsRatingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbStarsRatingModule, imports: [CommonModule, MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbStarsRatingModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [MbStarsRating],
                    imports: [CommonModule, MbIconModule],
                    declarations: [MbStarsRating]
                }]
        }] });

class MbProgressSpinner {
}
MbProgressSpinner.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbProgressSpinner, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbProgressSpinner.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbProgressSpinner, selector: "mb-progress-spinner", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{animation:spin 1s linear infinite;background-color:transparent;border-bottom:4px solid var(--color-primary);border-left:4px solid var(--color-primary);border-radius:50%;border-right:4px solid var(--color-primary);border-top:4px solid var(--color-border);display:inline-block;height:40px;width:40px}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbProgressSpinner, decorators: [{
            type: Component,
            args: [{ selector: 'mb-progress-spinner', template: '<ng-content></ng-content>', styles: [":host{animation:spin 1s linear infinite;background-color:transparent;border-bottom:4px solid var(--color-primary);border-left:4px solid var(--color-primary);border-radius:50%;border-right:4px solid var(--color-primary);border-top:4px solid var(--color-border);display:inline-block;height:40px;width:40px}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"] }]
        }] });

class MbProgressSpinnerModule {
}
MbProgressSpinnerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbProgressSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbProgressSpinnerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbProgressSpinnerModule, declarations: [MbProgressSpinner], exports: [MbProgressSpinner] });
MbProgressSpinnerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbProgressSpinnerModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbProgressSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbProgressSpinner],
                    exports: [MbProgressSpinner]
                }]
        }] });

class MbBadge {
    constructor() {
        this.color = 'notification';
        this.size = 'large';
    }
}
MbBadge.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBadge, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbBadge.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBadge, selector: "mb-badge", inputs: { color: "color", size: "size" }, host: { properties: { "class.mb-badge--small": "size === \"small\"", "class.mb-badge--large": "size === \"large\"", "class.mb-badge--notification": "color === \"notification\"", "class.mb-badge--success": "color === \"success\"", "class.mb-badge--light": "color === \"light\"", "class.mb-badge--dark": "color === \"dark\"" }, classAttribute: "mb-badge" }, queries: [{ propertyName: "icon", first: true, predicate: MbIcon, descendants: true, static: true }], ngImport: i0, template: `
    <span *ngIf="icon" class="mb-badge__icon">
      <ng-content select="mb-icon"></ng-content>
    </span>
    <ng-content></ng-content>
  `, isInline: true, styles: [".mb-badge:host{align-items:center;border-radius:4px;display:inline-flex;justify-content:center}.mb-badge:host--small{height:24px;min-width:80px;padding:0 4px;font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){.mb-badge:host--small{line-height:1.5}}.mb-badge:host--large{height:32px;min-width:88px;padding:0 8px;font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){.mb-badge:host--large{line-height:1.5}}.mb-badge:host--notification{background-color:var(--color-notification-clear);color:var(--color-notification)}.mb-badge:host--success{background-color:var(--color-success-clear);color:var(--color-success)}.mb-badge:host--light{background-color:var(--color-foreground);border:1px solid currentColor;color:var(--color-text-dark)}.mb-badge:host--dark{background-color:var(--color-gray-900);color:var(--color-text-light)}.mb-badge__icon{display:flex;margin-right:8px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBadge, decorators: [{
            type: Component,
            args: [{ selector: 'mb-badge', template: `
    <span *ngIf="icon" class="mb-badge__icon">
      <ng-content select="mb-icon"></ng-content>
    </span>
    <ng-content></ng-content>
  `, host: {
                        'class': 'mb-badge',
                        '[class.mb-badge--small]': 'size === "small"',
                        '[class.mb-badge--large]': 'size === "large"',
                        '[class.mb-badge--notification]': 'color === "notification"',
                        '[class.mb-badge--success]': 'color === "success"',
                        '[class.mb-badge--light]': 'color === "light"',
                        '[class.mb-badge--dark]': 'color === "dark"'
                    }, styles: [".mb-badge:host{align-items:center;border-radius:4px;display:inline-flex;justify-content:center}.mb-badge:host--small{height:24px;min-width:80px;padding:0 4px;font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){.mb-badge:host--small{line-height:1.5}}.mb-badge:host--large{height:32px;min-width:88px;padding:0 8px;font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){.mb-badge:host--large{line-height:1.5}}.mb-badge:host--notification{background-color:var(--color-notification-clear);color:var(--color-notification)}.mb-badge:host--success{background-color:var(--color-success-clear);color:var(--color-success)}.mb-badge:host--light{background-color:var(--color-foreground);border:1px solid currentColor;color:var(--color-text-dark)}.mb-badge:host--dark{background-color:var(--color-gray-900);color:var(--color-text-light)}.mb-badge__icon{display:flex;margin-right:8px}\n"] }]
        }], propDecorators: { color: [{
                type: Input
            }], size: [{
                type: Input
            }], icon: [{
                type: ContentChild,
                args: [MbIcon, { static: true }]
            }] } });

class MbBadgeModule {
}
MbBadgeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBadgeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbBadgeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbBadgeModule, declarations: [MbBadge], imports: [CommonModule], exports: [MbBadge] });
MbBadgeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBadgeModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBadgeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbBadge],
                    imports: [CommonModule],
                    exports: [MbBadge]
                }]
        }] });

class MbCommunication {
}
MbCommunication.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunication, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunication.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunication, selector: "mb-communication", host: { classAttribute: "mb-communication" }, ngImport: i0, template: "<span class=\"mb-communication__border\"></span>\n<div class=\"mb-communication__container\">\n  <div class=\"mb-communication__icon\">\n    <ng-content\n      select=\"mb-communication-payment-icon,\n          mb-communication-info-icon,\n          mb-communication-invoice-icon,\n          mb-communication-legal-icon,\n          mb-communication-doc-icon,\n          mb-communication-alert-icon,\n          mb-communication-warning-icon\n          \"\n    ></ng-content>\n  </div>\n  <div class=\"mb-communication__content\">\n    <ng-content select=\"mb-communication-title\"></ng-content>\n    <ng-content select=\"mb-communication-message\"></ng-content>\n    <ng-content select=\"button[mb-communication-button]\"></ng-content>\n  </div>\n</div>\n", styles: [".mb-communication:host{border:1px solid var(--color-notification);border-radius:12px;display:flex;overflow:hidden}.mb-communication__border{background-color:var(--color-notification);display:inline-block;flex:0 0 8px}.mb-communication__container{background-color:var(--color-foreground);display:flex;flex-grow:1;padding:24px}@media only screen and (max-width: 767px){.mb-communication__container{padding:16px}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-communication__container{padding:16px}}.mb-communication__content{flex-grow:1;margin-left:24px}@media only screen and (max-width: 767px){.mb-communication__content{margin-left:16px}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-communication__content{margin-left:16px}}.mb-communication__icon{color:var(--color-tertiary);display:flex;font-size:44px;justify-content:center}@media only screen and (max-width: 767px){.mb-communication__icon{font-size:36px}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-communication__icon{font-size:36px}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunication, decorators: [{
            type: Component,
            args: [{ selector: 'mb-communication', host: {
                        class: 'mb-communication'
                    }, template: "<span class=\"mb-communication__border\"></span>\n<div class=\"mb-communication__container\">\n  <div class=\"mb-communication__icon\">\n    <ng-content\n      select=\"mb-communication-payment-icon,\n          mb-communication-info-icon,\n          mb-communication-invoice-icon,\n          mb-communication-legal-icon,\n          mb-communication-doc-icon,\n          mb-communication-alert-icon,\n          mb-communication-warning-icon\n          \"\n    ></ng-content>\n  </div>\n  <div class=\"mb-communication__content\">\n    <ng-content select=\"mb-communication-title\"></ng-content>\n    <ng-content select=\"mb-communication-message\"></ng-content>\n    <ng-content select=\"button[mb-communication-button]\"></ng-content>\n  </div>\n</div>\n", styles: [".mb-communication:host{border:1px solid var(--color-notification);border-radius:12px;display:flex;overflow:hidden}.mb-communication__border{background-color:var(--color-notification);display:inline-block;flex:0 0 8px}.mb-communication__container{background-color:var(--color-foreground);display:flex;flex-grow:1;padding:24px}@media only screen and (max-width: 767px){.mb-communication__container{padding:16px}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-communication__container{padding:16px}}.mb-communication__content{flex-grow:1;margin-left:24px}@media only screen and (max-width: 767px){.mb-communication__content{margin-left:16px}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-communication__content{margin-left:16px}}.mb-communication__icon{color:var(--color-tertiary);display:flex;font-size:44px;justify-content:center}@media only screen and (max-width: 767px){.mb-communication__icon{font-size:36px}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-communication__icon{font-size:36px}}\n"] }]
        }] });

class MbCommunicationTitle {
}
MbCommunicationTitle.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationTitle, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunicationTitle.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunicationTitle, selector: "mb-communication-title", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{display:block;margin-bottom:16px;font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationTitle, decorators: [{
            type: Component,
            args: [{ selector: 'mb-communication-title', template: '<ng-content></ng-content>', styles: [":host{display:block;margin-bottom:16px;font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }] });

/* tslint:disable:max-line-length */
class MbCommunicationDocIcon {
    constructor() {
        this.icon = {
            iconName: 'communication-doc',
            iconData: [
                32,
                44,
                `M25.254 25.166H6.402a.945.945 0 00-.936.954c0 .527.419.954.936.954h18.852a.945.945 0 00.936-.954.946.946 0 00-.936-.954zm0-5.086H6.402a.942.942 0 00-.936.948c0 .525.417.953.936.96h18.852a.955.955 0 00.936-.96.95.95 0 00-.275-.67.927.927 0 00-.661-.278zm0 10.49H6.402a.954.954 0 00-.936.96c0 .251.099.492.274.67a.931.931 0 00.662.278h18.852a.942.942 0 00.936-.948.955.955 0 00-.936-.96zm-8.486 5.087H6.408a.948.948 0 00-.942.948c0 .525.42.953.943.96h10.36a.959.959 0 00.667-1.63.94.94 0 00-.667-.278zM6.695 14.676a.956.956 0 00-.943.959c0 .523.422.948.943.948h18.552a.951.951 0 00.943-.948.957.957 0 00-.943-.96H6.695zm-.423-4.26a.513.513 0 111.026.002.513.513 0 01-1.026-.002zm5.12 0a.513.513 0 111.026.002.513.513 0 01-1.026-.002zm19.176 9.574a.952.952 0 00-.947.947v18.444a1.66 1.66 0 01-1.65 1.625H3.685a1.66 1.66 0 01-1.65-1.67V4.276a1.66 1.66 0 011.65-1.625h17.26v5.19c.007 1.982 1.598 3.586 3.558 3.586h5.118v4.287a.963.963 0 00.953.866c.49 0 .9-.373.954-.866v-5.29a.95.95 0 00-.28-.677L22.575.97a.952.952 0 00-.68-.282H3.685C1.725.688.135 2.292.128 4.275v35.106c.006 1.983 1.597 3.588 3.557 3.588h24.286c1.96 0 3.55-1.605 3.557-3.588V20.926a.953.953 0 00-.96-.936zM22.737 4.185l5.337 5.403h-3.713c-.88 0-1.602-.714-1.625-1.611V4.185z`
            ]
        };
    }
}
MbCommunicationDocIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationDocIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunicationDocIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunicationDocIcon, selector: "mb-communication-doc-icon", ngImport: i0, template: '<mb-icon [icon]="icon"></mb-icon>', isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationDocIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-communication-doc-icon',
                    template: '<mb-icon [icon]="icon"></mb-icon>'
                }]
        }] });

/* tslint:disable:max-line-length */
class MbCommunicationLegalIcon {
    constructor() {
        this.icon = {
            iconName: 'communication-legal',
            iconData: [
                31,
                42,
                `M23.481 14.531H4.985a.924.924 0 00-.918.93c0 .516.409.936.918.942h18.496a.935.935 0 00.918-.941.937.937 0 00-.269-.658.911.911 0 00-.65-.273zm-8.225 6.683a4.081 4.081 0 00-2.918 1.211 4.137 4.137 0 00-1.211 2.918c0 2.278 1.85 4.13 4.129 4.13a4.133 4.133 0 004.129-4.13 4.128 4.128 0 00-4.13-4.129zm2.243 6.373a3.166 3.166 0 01-4.48 0 3.164 3.164 0 010-4.48 3.163 3.163 0 014.48 0V23.1c.599.592.93 1.404.93 2.243 0 .84-.338 1.652-.93 2.244zm3.324-.757a1.94 1.94 0 000-2.98c.041-.11.076-.227.09-.351a1.942 1.942 0 00-1.59-2.237 1.94 1.94 0 00-2.58-1.487 2.658 2.658 0 00-.256-.253 1.932 1.932 0 00-2.725.254 1.934 1.934 0 00-2.574 1.487 1.83 1.83 0 00-.344.089 1.952 1.952 0 00-1.142 2.498 1.94 1.94 0 000 2.98c-.303.826-.041 1.775.695 2.257.372.24.736.172.805.358.069.193-.007.282.172.564.083.13.186.255.159.22.103.138.22.262.35.365v5.636a.485.485 0 00.083.345.48.48 0 00.675.068l2.573-2.112 2.526 2.099c.09.075.2.117.31.117h.007a.468.468 0 00.454-.475v-5.575c.227-.165.64-.715.77-1.066 0 0-.034.11 0 0 .056-.145.028-.227.07-.269.04-.041-.042.035 0 0 .02-.007.034-.034 0 0 .034-.034-.035.035 0 0 .027-.027-.097 0 0 0 .399-.013.674-.192.701-.22l.028-.02v.006-.02a1.927 1.927 0 00.743-2.278zm-3.241 8.423L15.51 33.54a.475.475 0 00-.605 0l-2.092 1.713v-4.239s.605.076.942-.103c.372.44.916.702 1.494.702a2.026 2.026 0 001.486-.702 2.079 2.079 0 00.84.117v4.225h.007zm2.298-8.946a.47.47 0 00-.227.633c.248.323.282.764.09 1.122a.984.984 0 01-.936.488.478.478 0 00-.516.44c.02.214-.014.427-.11.62a.984.984 0 01-1.26.447h-.303a.41.41 0 00-.296-.055h-.082l-.028.014-.013.014-.014.02-.014.021h-.034a.987.987 0 01-1.783 0 .484.484 0 00-.64-.234.99.99 0 01-.716.22.981.981 0 01-.894-1.066v-.09a.489.489 0 00-.523-.433.983.983 0 01-.881-1.542.48.48 0 00-.179-.709.988.988 0 01-.475-.475.978.978 0 01.475-1.307c.027-.014.055-.028.076-.048a.476.476 0 00.103-.668.986.986 0 01.901-1.535h.09a.487.487 0 00.433-.523 1 1 0 01.489-.936.973.973 0 011.053.049c.02.013.041.027.069.04.24.118.523.015.64-.22a.971.971 0 01.888-.564v-.007a.98.98 0 01.777.647c.11.241.4.344.64.234a1.042 1.042 0 011.122-.09c.33.194.523.558.489.937a.478.478 0 00.44.516c.213-.02.427.014.62.11.034.02.075.041.11.069.447.31.55.922.24 1.37a.483.483 0 00.117.667c.2.09.365.227.489.406.02.034.041.069.055.103a.998.998 0 01-.482 1.315zM4.992 9.702a.938.938 0 00-.925.941c0 .514.414.93.925.93h9.198a.923.923 0 00.925-.93.938.938 0 00-.925-.941H4.993zm25.077 9.278a.935.935 0 00-.93.93v18.096a1.63 1.63 0 01-1.62 1.594H3.693a1.63 1.63 0 01-1.62-1.638v-34.4a1.628 1.628 0 011.62-1.593h16.935V7.06c.006 1.946 1.566 3.52 3.49 3.52h5.021v4.205c.052.484.455.85.935.85.48 0 .884-.366.936-.85v-5.19c0-.25-.098-.49-.274-.664L22.225.32a.932.932 0 00-.667-.277H3.693C1.77.043.209 1.617.203 3.563v34.443c.006 1.945 1.567 3.52 3.49 3.52H27.52c1.923 0 3.484-1.575 3.49-3.52V19.899a.936.936 0 00-.941-.919zM22.383 3.474l5.237 5.302h-3.642c-.865 0-1.572-.7-1.595-1.58V3.473z`
            ]
        };
    }
}
MbCommunicationLegalIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationLegalIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunicationLegalIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunicationLegalIcon, selector: "mb-communication-legal-icon", ngImport: i0, template: '<mb-icon [icon]="icon"></mb-icon>', isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationLegalIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-communication-legal-icon',
                    template: '<mb-icon [icon]="icon"></mb-icon>'
                }]
        }] });

class MbCommunicationButton {
}
MbCommunicationButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationButton, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunicationButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunicationButton, selector: "button[mb-communication-button]", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;color:var(--color-secondary);margin-top:16px}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationButton, decorators: [{
            type: Component,
            args: [{ selector: 'button[mb-communication-button]', template: '<ng-content></ng-content>', styles: [":host{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;color:var(--color-secondary);margin-top:16px}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }] });

/* tslint:disable:max-line-length */
class MbCommunicationPaymentIcon {
    constructor() {
        this.icon = {
            iconName: 'communication-payment',
            iconData: [
                32,
                44,
                `M25.732 25.39H6.582a.96.96 0 00-.95.969.96.96 0 00.95.969h19.15a.96.96 0 00.95-.969.96.96 0 00-.95-.969zm0-5.166H6.582c-.524 0-.95.43-.95.963 0 .533.424.968.95.974h19.15a.967.967 0 00.95-.974c0-.256-.1-.5-.278-.681a.946.946 0 00-.672-.282zm0 10.656H6.582a.968.968 0 00-.95.974c0 .256.1.5.279.681.178.18.42.282.672.282h19.149c.525 0 .95-.43.95-.963a.967.967 0 00-.95-.974zm-8.619 5.166H6.59a.956.956 0 00-.957.963c0 .534.426.969.957.975h10.523a.971.971 0 00.678-1.655.957.957 0 00-.678-.283zm8.612-21.312H15.2a.971.971 0 00-.957.975.96.96 0 00.957.963h10.524a.956.956 0 00.957-.963.971.971 0 00-.957-.975zm-15.356-2.401a2.862 2.862 0 00-.687-.486 8.79 8.79 0 00-1.122-.463c-.476-.167-.814-.344-1.012-.528-.199-.187-.3-.432-.3-.742 0-.344.1-.607.301-.791.2-.184.48-.277.837-.277.361 0 .646.129.857.385.21.257.315.608.315 1.054h1.35c0-.693-.178-1.258-.53-1.691-.353-.434-.854-.695-1.5-.784v-.722a.446.446 0 10-.892 0V8c-.645.07-1.154.296-1.53.674-.376.38-.563.862-.563 1.45 0 .867.432 1.524 1.3 1.966.259.134.625.28 1.093.438.468.158.803.335 1.004.531.2.197.3.446.3.747 0 .322-.111.581-.33.776-.222.194-.545.292-.97.292-.432 0-.766-.121-1.002-.367-.236-.244-.354-.59-.353-1.036H5.578c0 .703.199 1.265.596 1.69.396.426.962.677 1.698.755v.587a.445.445 0 00.888 0v-.592c.687-.067 1.225-.286 1.61-.659.384-.372.577-.857.577-1.455 0-.32-.05-.597-.151-.835a1.891 1.891 0 00-.427-.63zm20.761 7.799a.968.968 0 00-.962.962v18.735a1.685 1.685 0 01-1.676 1.65H3.823a1.685 1.685 0 01-1.676-1.695V4.17a1.685 1.685 0 011.676-1.65h17.533v5.27c.006 2.015 1.622 3.645 3.613 3.645h5.199v4.354c.053.5.471.88.968.88a.98.98 0 00.969-.88v-5.373a.977.977 0 00-.283-.688L23.01.812a.976.976 0 00-.691-.286H3.823C1.832.526.216 2.156.21 4.17v35.66c.006 2.014 1.622 3.643 3.613 3.643h24.669c1.99 0 3.606-1.63 3.613-3.644V21.083a.968.968 0 00-.974-.951zM23.175 4.078l5.422 5.49h-3.77c-.895 0-1.628-.726-1.652-1.637V4.078z`
            ]
        };
    }
}
MbCommunicationPaymentIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationPaymentIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunicationPaymentIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunicationPaymentIcon, selector: "mb-communication-payment-icon", ngImport: i0, template: '<mb-icon [icon]="icon"></mb-icon>', isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationPaymentIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-communication-payment-icon',
                    template: '<mb-icon [icon]="icon"></mb-icon>'
                }]
        }] });

class MbCommunicationMessage {
}
MbCommunicationMessage.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationMessage, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunicationMessage.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunicationMessage, selector: "mb-communication-message", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{display:block;font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationMessage, decorators: [{
            type: Component,
            args: [{ selector: 'mb-communication-message', template: '<ng-content></ng-content>', styles: [":host{display:block;font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }] });

/* tslint:disable:max-line-length */
class MbCommunicationInfoIcon {
    constructor() {
        this.icon = {
            iconName: 'communication-info',
            svgDef: {
                attr: { viewBox: '0 0 44 44' },
                path: {
                    attr: {
                        'fill': 'none',
                        'stroke': 'currentColor',
                        'stroke-width': '1.583',
                        'd': 'M21.987.88C10.345.88.88 10.345.88 21.987s9.465 21.107 21.107 21.107 21.107-9.465 21.107-21.107S33.63.88 21.987.88zm2.355 31.639a2.37 2.37 0 01-2.355 2.355 2.371 2.371 0 01-2.355-2.355V20.743a2.37 2.37 0 012.355-2.355 2.37 2.37 0 012.355 2.355v11.776zm-2.355-17.153a3.072 3.072 0 01-3.066-3.066 3.073 3.073 0 013.066-3.066 3.072 3.072 0 013.066 3.066 3.072 3.072 0 01-3.066 3.066z'
                    }
                }
            }
        };
    }
}
MbCommunicationInfoIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationInfoIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunicationInfoIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunicationInfoIcon, selector: "mb-communication-info-icon", ngImport: i0, template: '<mb-icon [icon]="icon"></mb-icon>', isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationInfoIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-communication-info-icon',
                    template: '<mb-icon [icon]="icon"></mb-icon>'
                }]
        }] });

/* tslint:disable:max-line-length */
class MbCommunicationAlertIcon {
    constructor() {
        this.icon = {
            iconName: 'communication-alert',
            iconData: [
                41,
                43,
                `M36.917 28.864c-1.115 0-1.718-.548-2.088-1.891-.408-1.489-.423-3.654-.439-5.956 0-2.832-.037-6.042-.772-8.933a.948.948 0 00-.927-.706.974.974 0 00-.228.027c-.514.123-.83.622-.708 1.12.683 2.68.702 5.767.72 8.498 0 2.433.031 4.723.5 6.426.13.494.327.97.584 1.415H7.496a5.71 5.71 0 00.584-1.415c.473-1.706.485-3.996.5-6.423.025-3.642.05-7.772 1.582-10.902 1.714-3.472 5.087-5.16 10.363-5.16 2.912 0 5.288.524 7.06 1.557a8.244 8.244 0 013.426 3.871c.154.336.5.554.88.554.133 0 .265-.027.386-.077a.917.917 0 00.495-1.221 10.195 10.195 0 00-4.192-4.705 12.766 12.766 0 00-3.895-1.433C24.406 1.5 22.628 0 20.525 0s-3.882 1.5-4.16 3.51c-1.31.255-2.568.707-3.732 1.337A10.14 10.14 0 008.42 9.344c-1.711 3.496-1.739 7.835-1.76 11.67-.016 2.308-.032 4.467-.44 5.956-.37 1.343-.972 1.89-2.084 1.89C1.852 28.86 0 30.647 0 32.849s1.852 3.988 4.136 3.988h4.126c.53 0 .961-.415.961-.927 0-.511-.43-.926-.96-.926h-4.13c-.81.03-1.573-.37-1.987-1.043a2.068 2.068 0 010-2.18 2.234 2.234 0 011.987-1.044h32.78c1.19.043 2.133.986 2.133 2.134s-.942 2.09-2.132 2.133H10.876c-.531 0-.961.415-.961.926 0 .512.43.927.96.927h2.404c.486 3.499 3.584 6.111 7.246 6.111 3.662 0 6.76-2.612 7.246-6.111h9.146c2.284 0 4.136-1.786 4.136-3.988s-1.852-3.987-4.136-3.987v.003zM18.42 3.204c.352-.82 1.183-1.356 2.104-1.356.92 0 1.751.535 2.103 1.356a22.852 22.852 0 00-2.103-.093c-.72 0-1.424.03-2.104.093zm7.413 33.65c-.464 2.47-2.696 4.266-5.3 4.266-2.604 0-4.835-1.796-5.3-4.267h10.6zm4.823-32.321a7.99 7.99 0 012.207 2.04c1.482 1.928 2.234 4.346 2.234 7.181.03.49.449.873.956.873.508 0 .928-.383.957-.873 0-4.075-1.446-6.786-2.658-8.343a9.783 9.783 0 00-2.763-2.494.996.996 0 00-.46-.116.953.953 0 00-.93.69.913.913 0 00.457 1.042zm4.946.292c.102.063 2.525 1.584 2.525 5.45 0 .512.446.929.996.929s.997-.417.997-.93c0-4.92-3.3-6.935-3.442-7.015-.16-.099-.35-.15-.541-.147-.34 0-.657.16-.84.428-.291.434-.156 1.006.305 1.285zM4.7 14.627c.527 0 .953-.411.953-.919 0-2.823.75-5.23 2.23-7.15a8.099 8.099 0 012.198-2.03.905.905 0 00.46-1.043.95.95 0 00-.938-.684.992.992 0 00-.46.115A9.774 9.774 0 006.385 5.4c-1.21 1.55-2.653 4.25-2.653 8.308 0 .246.104.482.285.654a.975.975 0 00.683.265zm-2.822-3.423a.943.943 0 00.943-.925c0-3.887 2.287-5.403 2.387-5.472a.932.932 0 00.24-1.265.95.95 0 00-1.261-.308c-.13.084-3.254 2.101-3.254 7.045a.941.941 0 00.945.925z`
            ]
        };
    }
}
MbCommunicationAlertIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationAlertIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunicationAlertIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunicationAlertIcon, selector: "mb-communication-alert-icon", ngImport: i0, template: '<mb-icon [icon]="icon"></mb-icon>', isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationAlertIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-communication-alert-icon',
                    template: '<mb-icon [icon]="icon"></mb-icon>'
                }]
        }] });

/* tslint:disable:max-line-length */
class MbCommunicationInvoiceIcon {
    constructor() {
        this.icon = {
            iconName: 'communication-invoice',
            iconData: [
                32,
                44,
                `M1.123 43.433l3.41-1.898 3.41 1.898a.754.754 0 00.731 0l3.41-1.898 3.41 1.898a.752.752 0 00.73 0l3.411-1.898 3.41 1.898a.752.752 0 00.73 0l3.41-1.898 3.411 1.898a.752.752 0 00.747-.012.763.763 0 00.371-.653V3.42a.76.76 0 00-.378-.655L27.56.665a.752.752 0 00-.73 0l-3.411 1.897L20.003.655a.747.747 0 00-.73 0l-3.41 1.897L12.451.655a.747.747 0 00-.73 0l-3.41 1.897L4.895.655a.747.747 0 00-.73 0L.393 2.765A.761.761 0 000 3.43v39.338a.762.762 0 00.372.653c.229.136.513.14.746.012h.005zM1.516 3.86l3.023-1.68 3.41 1.898a.747.747 0 00.73 0l3.41-1.897 3.41 1.897a.747.747 0 00.73 0l3.411-1.897 3.41 1.897a.747.747 0 00.73 0l3.41-1.897 3.023 1.68V41.48l-2.654-1.477a.752.752 0 00-.73 0l-3.411 1.898-3.415-1.898a.752.752 0 00-.73 0l-3.41 1.898-3.411-1.898a.752.752 0 00-.73 0l-3.41 1.898-3.416-1.898a.752.752 0 00-.73 0L1.51 41.48l.006-37.62zm8.527 6.861a2.563 2.563 0 00-.614-.446 7.706 7.706 0 00-1.003-.424c-.426-.154-.728-.316-.905-.486-.178-.171-.268-.396-.268-.68 0-.317.09-.558.27-.728.178-.168.429-.253.747-.253.323 0 .578.118.766.353.188.236.282.558.282.968h1.207c0-.637-.159-1.156-.473-1.553-.317-.399-.764-.639-1.342-.72v-.674a.4.4 0 00-.798 0v.665c-.576.064-1.031.271-1.367.618-.336.35-.504.792-.504 1.332 0 .797.387 1.4 1.162 1.805.232.124.559.258.978.403.418.145.718.307.897.488.18.18.269.41.269.685 0 .296-.1.534-.296.713-.198.179-.487.268-.866.268-.387 0-.685-.111-.896-.337-.211-.224-.317-.541-.316-.951H5.76c0 .645.178 1.161.532 1.553.354.39.86.62 1.52.692v.55a.396.396 0 10.793 0v-.554c.614-.062 1.095-.263 1.439-.605.343-.342.516-.787.516-1.337 0-.293-.045-.548-.135-.766a1.759 1.759 0 00-.382-.579zm3.527 4.162h12.618c.227 0 .411-.23.411-.512 0-.282-.184-.511-.411-.511H13.57c-.227 0-.411.229-.411.511 0 .283.184.512.411.512zm-6.92 5.115h19.438a.512.512 0 000-1.023H6.65a.511.511 0 100 1.023zm.002 5.115H24.04a.513.513 0 100-1.023H6.652a.513.513 0 100 1.023zm.01 5.115h11.229a.518.518 0 00.524-.511.518.518 0 00-.524-.512H6.662c-.29 0-.524.23-.524.512 0 .282.235.511.524.511zm.143 5.115h19.127c.369 0 .667-.229.667-.511 0-.283-.298-.512-.667-.512H6.805c-.368 0-.667.23-.667.512 0 .282.299.511.667.511z`
            ]
        };
    }
}
MbCommunicationInvoiceIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationInvoiceIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunicationInvoiceIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunicationInvoiceIcon, selector: "mb-communication-invoice-icon", ngImport: i0, template: '<mb-icon [icon]="icon"></mb-icon>', isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationInvoiceIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-communication-invoice-icon',
                    template: '<mb-icon [icon]="icon"></mb-icon>'
                }]
        }] });

/* tslint:disable:max-line-length */
class MbCommunicationWarningIcon {
    constructor() {
        this.icon = {
            iconName: 'communication-warning',
            iconData: [
                144,
                144,
                `M141.52 108.38L94.1 24.78A20.68 20.68 0 0076 14a20.68 20.68 0 00-18.1 10.78L41.52 53.65a2.71 2.71 0 00.98 3.68c1.27.74 2.9.3 3.63-1l16.4-28.92A15.38 15.38 0 0176 19.38c5.6 0 10.75 3.08 13.46 8.03l47.45 83.63a14.6 14.6 0 011.78 6.99c0 7.94-6.38 14.4-14.22 14.4H27.53a14.33 14.33 0 01-14.22-14.4c0-2.44.62-4.85 1.78-6.98L35.54 75c.73-1.29.3-2.93-.98-3.67a2.64 2.64 0 00-3.62.99l-20.48 36.1a20.04 20.04 0 00-2.46 9.6c0 10.92 8.76 19.8 19.53 19.8h96.94c10.77 0 19.53-8.88 19.53-19.8 0-3.35-.85-6.67-2.46-9.6l-.02-.04zm-121.93 5.6a9 9 0 007.81 13.35h97.2a9 9 0 007.8-13.35L84.84 30.76a10.11 10.11 0 00-17.66 0l-47.58 83.22zm4.65 2.62l47.58-83.22a4.79 4.79 0 018.37 0l47.57 83.22a3.63 3.63 0 01-3.16 5.37H27.4a3.63 3.63 0 01-3.16-5.37zm52.28-2.91a7.87 7.87 0 000-15.74 7.87 7.87 0 000 15.74zm0-10.5a2.62 2.62 0 110 5.25 2.62 2.62 0 010-5.25zM71.3 91.65h10.46c1.45 0 2.62-1.2 2.62-2.7V56.57c0-1.49-1.17-2.7-2.62-2.7H71.3a2.66 2.66 0 00-2.61 2.7v32.38c0 1.5 1.17 2.7 2.61 2.7zm2.62-32.38h5.23v26.98H73.9V59.27zm-35.05 7.2a2.62 2.62 0 100-5.24 2.62 2.62 0 000 5.24z`
            ]
        };
    }
}
MbCommunicationWarningIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationWarningIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbCommunicationWarningIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbCommunicationWarningIcon, selector: "mb-communication-warning-icon", ngImport: i0, template: '<mb-icon [icon]="icon"></mb-icon>', isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationWarningIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-communication-warning-icon',
                    template: '<mb-icon [icon]="icon"></mb-icon>'
                }]
        }] });

const COMPONENTS$3 = [
    MbCommunication,
    MbCommunicationTitle,
    MbCommunicationMessage,
    MbCommunicationButton,
    MbCommunicationInfoIcon,
    MbCommunicationPaymentIcon,
    MbCommunicationInvoiceIcon,
    MbCommunicationLegalIcon,
    MbCommunicationDocIcon,
    MbCommunicationAlertIcon,
    MbCommunicationWarningIcon
];
class MbCommunicationModule {
}
MbCommunicationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbCommunicationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationModule, declarations: [MbCommunication,
        MbCommunicationTitle,
        MbCommunicationMessage,
        MbCommunicationButton,
        MbCommunicationInfoIcon,
        MbCommunicationPaymentIcon,
        MbCommunicationInvoiceIcon,
        MbCommunicationLegalIcon,
        MbCommunicationDocIcon,
        MbCommunicationAlertIcon,
        MbCommunicationWarningIcon], imports: [MbIconModule], exports: [MbCommunication,
        MbCommunicationTitle,
        MbCommunicationMessage,
        MbCommunicationButton,
        MbCommunicationInfoIcon,
        MbCommunicationPaymentIcon,
        MbCommunicationInvoiceIcon,
        MbCommunicationLegalIcon,
        MbCommunicationDocIcon,
        MbCommunicationAlertIcon,
        MbCommunicationWarningIcon] });
MbCommunicationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationModule, imports: [MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommunicationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [MbIconModule],
                    declarations: [...COMPONENTS$3],
                    exports: [...COMPONENTS$3]
                }]
        }] });

class MbWideNotification {
    constructor() {
        this.color = 'notification';
        this.dismiss = new EventEmitter();
    }
}
MbWideNotification.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbWideNotification, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbWideNotification.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbWideNotification, selector: "mb-wide-notification", inputs: { color: "color" }, outputs: { dismiss: "dismiss" }, ngImport: i0, template: "<div\n  class=\"mb-notification\"\n  [class.mb-notification--success]=\"color === 'success'\"\n  [class.mb-notification--notification]=\"color === 'notification'\"\n>\n  <span class=\"mb-notification__icon\">\n    <ng-content select=\"mb-icon\"></ng-content>\n  </span>\n\n  <div class=\"mb-notification__content\">\n    <ng-content select=\"mb-wide-notification-message\"></ng-content>\n    <ng-content select=\"button[mb-wide-notification-button]\"></ng-content>\n  </div>\n  <span class=\"mb-notification__close\" (click)=\"dismiss.emit()\"></span>\n</div>\n", styles: ["@charset \"UTF-8\";.mb-notification{align-items:center;display:flex;justify-content:center;min-height:48px;padding:8px 16px}.mb-notification--success{background-color:var(--color-success);color:var(--color-success-contrast)}.mb-notification--notification{background-color:var(--color-notification);color:var(--color-notification-contrast)}.mb-notification__content{align-items:center;display:flex;margin:0 16px}@media only screen and (max-width: 767px){.mb-notification__content{align-items:flex-start;flex-direction:column;flex-grow:1}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-notification__content{align-items:flex-start;flex-direction:column;flex-grow:1}}.mb-notification__close{cursor:pointer}.mb-notification__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}.mb-notification__icon{display:flex;font-size:20px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbWideNotification, decorators: [{
            type: Component,
            args: [{ selector: 'mb-wide-notification', template: "<div\n  class=\"mb-notification\"\n  [class.mb-notification--success]=\"color === 'success'\"\n  [class.mb-notification--notification]=\"color === 'notification'\"\n>\n  <span class=\"mb-notification__icon\">\n    <ng-content select=\"mb-icon\"></ng-content>\n  </span>\n\n  <div class=\"mb-notification__content\">\n    <ng-content select=\"mb-wide-notification-message\"></ng-content>\n    <ng-content select=\"button[mb-wide-notification-button]\"></ng-content>\n  </div>\n  <span class=\"mb-notification__close\" (click)=\"dismiss.emit()\"></span>\n</div>\n", styles: ["@charset \"UTF-8\";.mb-notification{align-items:center;display:flex;justify-content:center;min-height:48px;padding:8px 16px}.mb-notification--success{background-color:var(--color-success);color:var(--color-success-contrast)}.mb-notification--notification{background-color:var(--color-notification);color:var(--color-notification-contrast)}.mb-notification__content{align-items:center;display:flex;margin:0 16px}@media only screen and (max-width: 767px){.mb-notification__content{align-items:flex-start;flex-direction:column;flex-grow:1}}@media only screen and (min-width: 768px) and (max-width: 1151px){.mb-notification__content{align-items:flex-start;flex-direction:column;flex-grow:1}}.mb-notification__close{cursor:pointer}.mb-notification__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}.mb-notification__icon{display:flex;font-size:20px}\n"] }]
        }], propDecorators: { color: [{
                type: Input
            }], dismiss: [{
                type: Output
            }] } });

class MbWideNotificationMessage {
}
MbWideNotificationMessage.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbWideNotificationMessage, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbWideNotificationMessage.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbWideNotificationMessage, selector: "mb-wide-notification-message", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;display:block}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbWideNotificationMessage, decorators: [{
            type: Component,
            args: [{ selector: 'mb-wide-notification-message', template: '<ng-content></ng-content>', styles: [":host{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;display:block}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }] });

class MbWideNotificationButton {
}
MbWideNotificationButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbWideNotificationButton, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbWideNotificationButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbWideNotificationButton, selector: "button[mb-wide-notification-button]", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;margin-left:16px}@media only screen and (max-width: 767px){:host{line-height:1.5}}@media only screen and (max-width: 767px){:host{margin-left:0}}@media only screen and (min-width: 768px) and (max-width: 1151px){:host{margin-left:0}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbWideNotificationButton, decorators: [{
            type: Component,
            args: [{ selector: 'button[mb-wide-notification-button]', template: '<ng-content></ng-content>', styles: [":host{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;margin-left:16px}@media only screen and (max-width: 767px){:host{line-height:1.5}}@media only screen and (max-width: 767px){:host{margin-left:0}}@media only screen and (min-width: 768px) and (max-width: 1151px){:host{margin-left:0}}\n"] }]
        }] });

const COMPONENTS$2 = [
    MbWideNotification,
    MbWideNotificationMessage,
    MbWideNotificationButton
];
class MbWideNotificationModule {
}
MbWideNotificationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbWideNotificationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbWideNotificationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbWideNotificationModule, declarations: [MbWideNotification,
        MbWideNotificationMessage,
        MbWideNotificationButton], imports: [MbIconModule], exports: [MbWideNotification,
        MbWideNotificationMessage,
        MbWideNotificationButton] });
MbWideNotificationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbWideNotificationModule, imports: [MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbWideNotificationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [MbIconModule],
                    exports: [...COMPONENTS$2],
                    declarations: [...COMPONENTS$2]
                }]
        }] });

class MbReviewsRating {
}
MbReviewsRating.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbReviewsRating, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbReviewsRating.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbReviewsRating, selector: "mb-reviews-rating", inputs: { rating: "rating" }, host: { properties: { "class.mb-review--good": "rating >= 7", "class.mb-review--average": "rating < 7 || !rating" }, classAttribute: "mb-review" }, ngImport: i0, template: `
    <span *ngIf="rating" class="mb-review__rating-value"> {{ rating }}</span>
    <ng-content select="mb-reviews-evaluation"></ng-content>
    <ng-content select="mb-reviews-detail"></ng-content>
  `, isInline: true, styles: [".mb-review:host{display:inline-flex;min-height:26px}.mb-review:host--average .mb-review__rating-value{background-color:var(--color-warning);color:var(--color-background)}.mb-review:host--good .mb-review__rating-value{background-color:var(--color-success);color:var(--color-background)}.mb-review__rating-value{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;align-items:center;border-radius:4px;display:flex;justify-content:center;margin-right:4px;min-width:24px}@media only screen and (max-width: 767px){.mb-review__rating-value{line-height:1.5}}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbReviewsRating, decorators: [{
            type: Component,
            args: [{ selector: 'mb-reviews-rating', template: `
    <span *ngIf="rating" class="mb-review__rating-value"> {{ rating }}</span>
    <ng-content select="mb-reviews-evaluation"></ng-content>
    <ng-content select="mb-reviews-detail"></ng-content>
  `, host: {
                        'class': 'mb-review',
                        '[class.mb-review--good]': 'rating >= 7',
                        '[class.mb-review--average]': 'rating < 7 || !rating'
                    }, styles: [".mb-review:host{display:inline-flex;min-height:26px}.mb-review:host--average .mb-review__rating-value{background-color:var(--color-warning);color:var(--color-background)}.mb-review:host--good .mb-review__rating-value{background-color:var(--color-success);color:var(--color-background)}.mb-review__rating-value{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;align-items:center;border-radius:4px;display:flex;justify-content:center;margin-right:4px;min-width:24px}@media only screen and (max-width: 767px){.mb-review__rating-value{line-height:1.5}}\n"] }]
        }], propDecorators: { rating: [{
                type: Input
            }] } });

class MbReviewsEvaluation {
}
MbReviewsEvaluation.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbReviewsEvaluation, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbReviewsEvaluation.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbReviewsEvaluation, selector: "mb-reviews-evaluation", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;align-items:center;display:flex;justify-content:center;text-transform:uppercase}@media only screen and (max-width: 767px){:host{line-height:1.5}}:host-context(.mb-review--average){color:var(--color-warning)}:host-context(.mb-review--good){color:var(--color-success)}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbReviewsEvaluation, decorators: [{
            type: Component,
            args: [{ selector: 'mb-reviews-evaluation', template: '<ng-content></ng-content>', styles: [":host{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;align-items:center;display:flex;justify-content:center;text-transform:uppercase}@media only screen and (max-width: 767px){:host{line-height:1.5}}:host-context(.mb-review--average){color:var(--color-warning)}:host-context(.mb-review--good){color:var(--color-success)}\n"] }]
        }] });

class MbReviewsDetail {
}
MbReviewsDetail.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbReviewsDetail, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbReviewsDetail.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbReviewsDetail, selector: "mb-reviews-detail", ngImport: i0, template: `
    <span class="mb-review__separator"></span> <ng-content></ng-content>
  `, isInline: true, styles: [":host{font-size:.75rem;font-weight:var(--font-weight-regular);line-height:1.5;align-items:center;color:var(--color-text-dark);display:flex;margin-right:4px}@media only screen and (max-width: 767px){:host{line-height:1.5}}:host .mb-review__separator{border-left:1px solid currentColor;height:16px;margin:0 4px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbReviewsDetail, decorators: [{
            type: Component,
            args: [{ selector: 'mb-reviews-detail', template: `
    <span class="mb-review__separator"></span> <ng-content></ng-content>
  `, styles: [":host{font-size:.75rem;font-weight:var(--font-weight-regular);line-height:1.5;align-items:center;color:var(--color-text-dark);display:flex;margin-right:4px}@media only screen and (max-width: 767px){:host{line-height:1.5}}:host .mb-review__separator{border-left:1px solid currentColor;height:16px;margin:0 4px}\n"] }]
        }] });

class MbReviewsRatingModule {
}
MbReviewsRatingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbReviewsRatingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbReviewsRatingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbReviewsRatingModule, declarations: [MbReviewsRating, MbReviewsEvaluation, MbReviewsDetail], imports: [CommonModule], exports: [MbReviewsRating, MbReviewsEvaluation, MbReviewsDetail] });
MbReviewsRatingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbReviewsRatingModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbReviewsRatingModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [MbReviewsRating, MbReviewsEvaluation, MbReviewsDetail],
                    declarations: [MbReviewsRating, MbReviewsEvaluation, MbReviewsDetail],
                    imports: [CommonModule]
                }]
        }] });

var Breakpoints;
(function (Breakpoints) {
    Breakpoints["Phone"] = "(max-width: 767px)";
    Breakpoints["Tablet"] = "(max-width: 1151px)";
})(Breakpoints || (Breakpoints = {}));

const mbAcceleratorAnimations = {
    /** Animation that transitions a accelerator in and out. */
    acceleratorState: trigger('state', [
        state('hidden,void', style({ opacity: 0, bottom: 0 })),
        state('visible', style({ opacity: 1, bottom: '20px' })),
        transition('* => visible', animate('0.50s')),
        transition('visible => hidden', animate('0.50s'))
    ])
};

const ACCELERATOR_HORIZONTAL_MARGIN = '20px';
const ACCELERATOR_VERTICAL_MARGIN = '0';
class MbAccelerator {
    constructor(overlay, mediaMatcher, viewContainerRef) {
        this.overlay = overlay;
        this.mediaMatcher = mediaMatcher;
        this.viewContainerRef = viewContainerRef;
        this.onCloseSubscription = Subscription.EMPTY;
        this.color = 'notification';
        this.visibility = 'hidden';
        this.onHidden$ = new Subject();
        this._visible = false;
    }
    ngOnDestroy() {
        this.onCloseSubscription.unsubscribe();
    }
    /**
     * Close button handler
     */
    hide() {
        if (this._visible) {
            this.visibility = 'hidden';
        }
    }
    show() {
        if (!this.isVisible()) {
            const overlayRef = this.buildAcceleratorOverlay();
            const portal = this.createAcceleratorTemplatePortal();
            this.onCloseSubscription.unsubscribe();
            this.onCloseSubscription = this.onFinallyHidden().subscribe(() => {
                overlayRef.dispose();
                this._visible = false;
            });
            this.visibility = 'visible';
            overlayRef.attach(portal);
            this._visible = true;
        }
    }
    isVisible() {
        return this._visible;
    }
    animationDone($event) {
        const toState = $event.toState;
        if (toState === 'hidden') {
            this.onHidden$.next();
        }
    }
    createAcceleratorTemplatePortal() {
        return new TemplatePortal(this.acceleratorContents, this.viewContainerRef);
    }
    onFinallyHidden() {
        return this.onHidden$.asObservable();
    }
    /**
     * Builds the accelerator overlay using Angular CDK Overlay
     */
    buildAcceleratorOverlay() {
        const isPhoneLayout = this.mediaMatcher.matchMedia(Breakpoints.Phone).matches;
        let positionStrategy = this.overlay
            .position()
            .global()
            .bottom(ACCELERATOR_VERTICAL_MARGIN);
        if (isPhoneLayout) {
            positionStrategy = positionStrategy.centerHorizontally();
        }
        else {
            positionStrategy = positionStrategy.right(ACCELERATOR_HORIZONTAL_MARGIN);
        }
        return this.overlay.create({
            positionStrategy
        });
    }
}
MbAccelerator.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAccelerator, deps: [{ token: i1$3.Overlay }, { token: i2$2.MediaMatcher }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Component });
MbAccelerator.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbAccelerator, selector: "mb-accelerator", inputs: { color: "color" }, viewQueries: [{ propertyName: "acceleratorContents", first: true, predicate: ["acceleratorContent"], descendants: true, static: true }], exportAs: ["mbAccelerator"], ngImport: i0, template: "<ng-template #acceleratorContent>\n  <div\n    (@state.done)=\"animationDone($event)\"\n    [@state]=\"visibility\"\n    class=\"mb-accelerator\"\n    ngClass=\"mb-accelerator--{{ color }}\"\n  >\n    <div class=\"mb-accelerator__icon\">\n      <ng-content select=\"mb-icon\"></ng-content>\n    </div>\n    <div class=\"mb-accelerator__content\">\n      <ng-content select=\"mb-accelerator-title\"></ng-content>\n      <ng-content select=\"mb-accelerator-description\"></ng-content>\n    </div>\n    <button (click)=\"hide()\" class=\"mb-accelerator__close\"></button>\n  </div>\n</ng-template>\n", styles: ["@charset \"UTF-8\";.mb-accelerator{align-items:center;border-radius:8px;display:flex;min-width:304px;padding:8px 16px;position:relative;width:100%}.mb-accelerator--success{background:var(--color-success);color:var(--color-success-contrast)}.mb-accelerator--notification{background:var(--color-notification);color:var(--color-notification-contrast)}.mb-accelerator__icon{font-size:20px;line-height:0;margin-right:16px}.mb-accelerator__content{display:flex;flex-direction:column;flex-grow:1}.mb-accelerator__close{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;margin-left:16px}.mb-accelerator__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], animations: [mbAcceleratorAnimations.acceleratorState], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAccelerator, decorators: [{
            type: Component,
            args: [{ selector: 'mb-accelerator', exportAs: 'mbAccelerator', encapsulation: ViewEncapsulation.None, animations: [mbAcceleratorAnimations.acceleratorState], template: "<ng-template #acceleratorContent>\n  <div\n    (@state.done)=\"animationDone($event)\"\n    [@state]=\"visibility\"\n    class=\"mb-accelerator\"\n    ngClass=\"mb-accelerator--{{ color }}\"\n  >\n    <div class=\"mb-accelerator__icon\">\n      <ng-content select=\"mb-icon\"></ng-content>\n    </div>\n    <div class=\"mb-accelerator__content\">\n      <ng-content select=\"mb-accelerator-title\"></ng-content>\n      <ng-content select=\"mb-accelerator-description\"></ng-content>\n    </div>\n    <button (click)=\"hide()\" class=\"mb-accelerator__close\"></button>\n  </div>\n</ng-template>\n", styles: ["@charset \"UTF-8\";.mb-accelerator{align-items:center;border-radius:8px;display:flex;min-width:304px;padding:8px 16px;position:relative;width:100%}.mb-accelerator--success{background:var(--color-success);color:var(--color-success-contrast)}.mb-accelerator--notification{background:var(--color-notification);color:var(--color-notification-contrast)}.mb-accelerator__icon{font-size:20px;line-height:0;margin-right:16px}.mb-accelerator__content{display:flex;flex-direction:column;flex-grow:1}.mb-accelerator__close{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;margin-left:16px}.mb-accelerator__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$3.Overlay }, { type: i2$2.MediaMatcher }, { type: i0.ViewContainerRef }]; }, propDecorators: { acceleratorContents: [{
                type: ViewChild,
                args: ['acceleratorContent', { static: true }]
            }], color: [{
                type: Input
            }] } });

class MbAcceleratorTitle {
}
MbAcceleratorTitle.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAcceleratorTitle, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbAcceleratorTitle.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbAcceleratorTitle, selector: "mb-accelerator-title", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAcceleratorTitle, decorators: [{
            type: Component,
            args: [{ selector: 'mb-accelerator-title', template: '<ng-content></ng-content>', styles: [":host{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }] });

class MbAcceleratorDescription {
}
MbAcceleratorDescription.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAcceleratorDescription, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbAcceleratorDescription.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbAcceleratorDescription, selector: "mb-accelerator-description", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAcceleratorDescription, decorators: [{
            type: Component,
            args: [{ selector: 'mb-accelerator-description', template: '<ng-content></ng-content>', styles: [":host{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }] });

class MbAcceleratorModule {
}
MbAcceleratorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAcceleratorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbAcceleratorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbAcceleratorModule, declarations: [MbAccelerator, MbAcceleratorTitle, MbAcceleratorDescription], imports: [CommonModule, OverlayModule, LayoutModule], exports: [MbAccelerator, MbAcceleratorTitle, MbAcceleratorDescription] });
MbAcceleratorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAcceleratorModule, imports: [CommonModule, OverlayModule, LayoutModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAcceleratorModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [MbAccelerator, MbAcceleratorTitle, MbAcceleratorDescription],
                    imports: [CommonModule, OverlayModule, LayoutModule],
                    declarations: [MbAccelerator, MbAcceleratorTitle, MbAcceleratorDescription]
                }]
        }] });

/** Animations used by the Material bottom sheet. */
const mbSnackBarAnimations = {
    /** Animation that shows and hides a bottom sheet. */
    mbSnackBarState: trigger('state', [
        state('void, hidden', style({ transform: 'translateY(100%)' })),
        state('visible', style({ transform: 'translateY(0%)' })),
        transition('visible => void, visible => hidden', animate(`375ms cubic-bezier(0.4,0.0,1,1)`)),
        transition('void => visible', animate(`195ms cubic-bezier(0.0,0.0,0.2,1)`))
    ])
};

class MbSnackBarRef {
    constructor() {
        this.onAction$ = new Subject();
        this.onClose$ = new Subject();
    }
    dismiss() {
        this.onClose$.next();
    }
    dismissWithAction() {
        this.onAction$.next();
    }
    afterDismissed() {
        return this.onClose$.asObservable();
    }
    onAction() {
        return this.onAction$.asObservable();
    }
}

const DEFAULT_TIMEOUT_DURATION = 5000;
const defaultConfig$2 = {
    duration: DEFAULT_TIMEOUT_DURATION
};
const MB_SNACK_BAR_DEFAULT_OPTIONS = new InjectionToken('snack-bar-default-options', {
    factory() {
        return defaultConfig$2;
    }
});
class MbSnackBar {
    constructor(overlay, viewContainerRef, options) {
        this.overlay = overlay;
        this.viewContainerRef = viewContainerRef;
        this.options = options;
        this.color = 'notification';
        this.onHiddenCompleteSubscription = Subscription.EMPTY;
        this.onHiddenComplete$ = new Subject();
        this._visible = false;
        this.onAfterDismissedSubscription = Subscription.EMPTY;
        this.duration = this.options.duration;
    }
    ngOnDestroy() {
        this.onHiddenCompleteSubscription.unsubscribe();
    }
    /**
     * Shows an Snack Bar
     */
    show() {
        if (!this._visible) {
            this.icon = STATE_ICONS[this.color];
            this.ref = new MbSnackBarRef();
            const overlayRef = this.buildSnackbarOverlay();
            const snackbarPortal = this.createTemplatePortal();
            this.onHiddenCompleteSubscription.unsubscribe();
            this.onHiddenCompleteSubscription = this.onHiddenComplete().subscribe(() => {
                overlayRef.dispose();
                this._visible = false;
            });
            this.onAfterDismissedSubscription.unsubscribe();
            this.onAfterDismissedSubscription = this.ref.afterDismissed().subscribe(() => {
                this.hide();
            });
            this.visibility = 'visible';
            overlayRef.attach(snackbarPortal);
            this._visible = true;
            this._hideTimeoutID = setTimeout(() => this.dismiss(), this.duration);
        }
        return this.ref;
    }
    dismiss() {
        this.ref.dismiss();
        this.hide();
    }
    animationDone($event) {
        const toState = $event.toState;
        if (toState === 'hidden') {
            this.onHiddenComplete$.next();
            this._visible = false;
        }
    }
    actionClicked() {
        this.ref.dismissWithAction();
        this.hide();
    }
    onHiddenComplete() {
        return this.onHiddenComplete$.asObservable();
    }
    hide() {
        clearTimeout(this._hideTimeoutID);
        this.visibility = 'hidden';
    }
    createTemplatePortal() {
        return new TemplatePortal(this.snackBarContents, this.viewContainerRef);
    }
    /**
     * Builds the snack bar overlay using Angular CDK Overlay
     */
    buildSnackbarOverlay() {
        const positionStrategy = this.overlay
            .position()
            .global()
            .bottom('0')
            .centerHorizontally();
        return this.overlay.create({
            positionStrategy
        });
    }
}
MbSnackBar.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSnackBar, deps: [{ token: i1$3.Overlay }, { token: i0.ViewContainerRef }, { token: MB_SNACK_BAR_DEFAULT_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Component });
MbSnackBar.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbSnackBar, selector: "mb-snack-bar", inputs: { color: "color" }, viewQueries: [{ propertyName: "snackBarContents", first: true, predicate: ["snackbarContent"], descendants: true, static: true }], exportAs: ["mbSnackbar"], ngImport: i0, template: "<ng-template #snackbarContent>\n  <div\n    (@state.done)=\"animationDone($event)\"\n    [@state]=\"visibility\"\n    class=\"mb-snack-bar\"\n    ngClass=\"mb-snack-bar--{{ color }}\"\n  >\n    <mb-icon [icon]=\"icon\" class=\"mb-snack-bar__icon\"></mb-icon>\n    <ng-content select=\"mb-snack-bar-message\"></ng-content>\n    <ng-content select=\"[mb-snack-bar-button]\"></ng-content>\n    <button (click)=\"dismiss()\" class=\"mb-snack-bar__close\"></button>\n  </div>\n</ng-template>\n", styles: ["@charset \"UTF-8\";.mb-snack-bar{align-items:center;border-radius:4px;display:flex;padding:16px;position:relative;width:320px}.mb-snack-bar--success{background:var(--color-success);color:var(--color-success-contrast)}.mb-snack-bar--notification{background:var(--color-notification);color:var(--color-notification-contrast)}.mb-snack-bar--error{background:var(--color-error);color:var(--color-error-contrast)}.mb-snack-bar--warning{background:var(--color-warning);color:var(--color-warning-contrast)}.mb-snack-bar__icon{font-size:14px;justify-self:flex-start;line-height:0;margin-right:8px}.mb-snack-bar__close{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;justify-self:flex-end;margin-left:8px}.mb-snack-bar__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}\n"], dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }, { kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], animations: [mbSnackBarAnimations.mbSnackBarState], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSnackBar, decorators: [{
            type: Component,
            args: [{ selector: 'mb-snack-bar', exportAs: 'mbSnackbar', animations: [mbSnackBarAnimations.mbSnackBarState], encapsulation: ViewEncapsulation.None, template: "<ng-template #snackbarContent>\n  <div\n    (@state.done)=\"animationDone($event)\"\n    [@state]=\"visibility\"\n    class=\"mb-snack-bar\"\n    ngClass=\"mb-snack-bar--{{ color }}\"\n  >\n    <mb-icon [icon]=\"icon\" class=\"mb-snack-bar__icon\"></mb-icon>\n    <ng-content select=\"mb-snack-bar-message\"></ng-content>\n    <ng-content select=\"[mb-snack-bar-button]\"></ng-content>\n    <button (click)=\"dismiss()\" class=\"mb-snack-bar__close\"></button>\n  </div>\n</ng-template>\n", styles: ["@charset \"UTF-8\";.mb-snack-bar{align-items:center;border-radius:4px;display:flex;padding:16px;position:relative;width:320px}.mb-snack-bar--success{background:var(--color-success);color:var(--color-success-contrast)}.mb-snack-bar--notification{background:var(--color-notification);color:var(--color-notification-contrast)}.mb-snack-bar--error{background:var(--color-error);color:var(--color-error-contrast)}.mb-snack-bar--warning{background:var(--color-warning);color:var(--color-warning-contrast)}.mb-snack-bar__icon{font-size:14px;justify-self:flex-start;line-height:0;margin-right:8px}.mb-snack-bar__close{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;justify-self:flex-end;margin-left:8px}.mb-snack-bar__close:after{content:\"\\d7\";display:flex;font-size:30px;font-weight:700;line-height:.5}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$3.Overlay }, { type: i0.ViewContainerRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [MB_SNACK_BAR_DEFAULT_OPTIONS]
                }] }]; }, propDecorators: { snackBarContents: [{
                type: ViewChild,
                args: ['snackbarContent', { static: true }]
            }], color: [{
                type: Input
            }] } });

class MbSnackBarAction {
    constructor(snackbar) {
        this.snackbar = snackbar;
    }
    actionClicked() {
        this.snackbar.actionClicked();
    }
}
MbSnackBarAction.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSnackBarAction, deps: [{ token: MbSnackBar }], target: i0.ɵɵFactoryTarget.Component });
MbSnackBarAction.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbSnackBarAction, selector: "button[mb-snack-bar-button]", host: { listeners: { "click": "actionClicked()" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSnackBarAction, decorators: [{
            type: Component,
            args: [{ selector: 'button[mb-snack-bar-button]', template: '<ng-content></ng-content>', host: {
                        '(click)': 'actionClicked()'
                    }, styles: [":host{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }], ctorParameters: function () { return [{ type: MbSnackBar }]; } });

class MbSnackBarMessage {
}
MbSnackBarMessage.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSnackBarMessage, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbSnackBarMessage.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbSnackBarMessage, selector: "mb-snack-bar-message", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;flex-grow:1}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSnackBarMessage, decorators: [{
            type: Component,
            args: [{ selector: 'mb-snack-bar-message', template: '<ng-content></ng-content>', styles: [":host{font-size:.875rem;font-weight:var(--font-weight-semibold);line-height:1.5;flex-grow:1}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }] });

class MbSnackBarModule {
}
MbSnackBarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSnackBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbSnackBarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbSnackBarModule, declarations: [MbSnackBar, MbSnackBarMessage, MbSnackBarAction], imports: [MbIconModule, CommonModule, OverlayModule], exports: [MbSnackBar, MbSnackBarMessage, MbSnackBarAction, OverlayModule] });
MbSnackBarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSnackBarModule, imports: [MbIconModule, CommonModule, OverlayModule, OverlayModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSnackBarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [MbIconModule, CommonModule, OverlayModule],
                    exports: [MbSnackBar, MbSnackBarMessage, MbSnackBarAction, OverlayModule],
                    declarations: [MbSnackBar, MbSnackBarMessage, MbSnackBarAction]
                }]
        }] });

const MB_AUTOCOMPLETE_CONFIG = new InjectionToken('MbAutocompleteConfig', {
    factory() {
        return defaultConfig$1;
    }
});
const defaultConfig$1 = {
    highlightToken: 'mark'
};

class MbOptionGroup {
}
MbOptionGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbOptionGroup, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbOptionGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbOptionGroup, selector: "mb-option-group", ngImport: i0, template: "<div class=\"mb-option-group__container\">\n  <span class=\"mb-option-group__icon\">\n    <ng-content select=\"mb-icon\"></ng-content>\n  </span>\n  <span class=\"mb-option-group__label\">\n    <ng-content></ng-content>\n  </span>\n</div>\n<ng-content select=\"mb-option\"></ng-content>\n", styles: [".mb-option-group__container{align-items:center;border-bottom:1px solid var(--color-gray-700);display:flex;padding:4px}.mb-option-group__icon{color:var(--color-secondary);margin-right:8px}.mb-option-group__label{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;display:block;flex-grow:1}@media only screen and (max-width: 767px){.mb-option-group__label{line-height:1.5}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbOptionGroup, decorators: [{
            type: Component,
            args: [{ selector: 'mb-option-group', template: "<div class=\"mb-option-group__container\">\n  <span class=\"mb-option-group__icon\">\n    <ng-content select=\"mb-icon\"></ng-content>\n  </span>\n  <span class=\"mb-option-group__label\">\n    <ng-content></ng-content>\n  </span>\n</div>\n<ng-content select=\"mb-option\"></ng-content>\n", styles: [".mb-option-group__container{align-items:center;border-bottom:1px solid var(--color-gray-700);display:flex;padding:4px}.mb-option-group__icon{color:var(--color-secondary);margin-right:8px}.mb-option-group__label{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;display:block;flex-grow:1}@media only screen and (max-width: 767px){.mb-option-group__label{line-height:1.5}}\n"] }]
        }] });

let nextId$1 = 0;
class MbOptionSelectionChange {
    constructor(source) {
        this.source = source;
    }
}
class MbOption {
    constructor(cdr, renderer, config, group) {
        this.cdr = cdr;
        this.renderer = renderer;
        this.config = config;
        this.group = group;
        this.id = `mb-option-${++nextId$1}`;
        this.selectionChange = new EventEmitter();
        this.selected = false;
        this.active = false;
        this.checkIcon = aciCheckSimple;
        this.optionText = '';
    }
    ngAfterViewChecked() {
        const text = this.optionContent.nativeElement.textContent.trim();
        if (this.optionText !== text) {
            this.optionText = text;
            this.renderer.removeChild(this.option.nativeElement, this.optionContent.nativeElement);
            this.cdr.detectChanges();
        }
    }
    /**
     * Selects the option and emits the selection change event.
     */
    selectViaInteraction() {
        this.selected = true;
        this.emitSelectionChangeEvent();
    }
    /**
     * Ensures the option is selected when activated from the keyboard.
     */
    handleKeydown(event) {
        if (event.keyCode === ENTER) {
            this.selectViaInteraction();
            // Prevent the page from scrolling down and form submits.
            event.preventDefault();
        }
    }
    /**
     * Selects the option.
     */
    select() {
        if (!this.selected) {
            this.selected = true;
            this.emitSelectionChangeEvent();
        }
    }
    /**
     * Selects the option.
     */
    deselect() {
        if (this.selected) {
            this.selected = false;
            this.emitSelectionChangeEvent();
        }
    }
    /**
     * This method sets display styles on the option to make it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    setActiveStyles() {
        if (!this.active) {
            this.active = true;
        }
    }
    /**
     * This method removes display styles on the option that made it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    setInactiveStyles() {
        if (this.active) {
            this.active = false;
        }
    }
    /**
     * Emits the selection change output event.
     */
    emitSelectionChangeEvent() {
        this.selectionChange.emit(new MbOptionSelectionChange(this));
    }
}
MbOption.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbOption, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: MB_AUTOCOMPLETE_CONFIG }, { token: MbOptionGroup, optional: true }], target: i0.ɵɵFactoryTarget.Component });
MbOption.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbOption, selector: "mb-option", inputs: { id: "id", value: "value" }, outputs: { selectionChange: "selectionChange" }, host: { listeners: { "click": "selectViaInteraction()", "keydown": "handleKeydown($event)" }, properties: { "class.mb-option--selected": "selected", "class.mb-option--active": "active", "id": "id" }, classAttribute: "mb-option" }, viewQueries: [{ propertyName: "option", first: true, predicate: ["option"], descendants: true }, { propertyName: "optionContent", first: true, predicate: ["optionContent"], descendants: true }], ngImport: i0, template: "<span #option class=\"mb-option__label\">\n  <span *ngIf=\"selected\" class=\"mb-option__mark\">\n    <mb-icon [icon]=\"checkIcon\"></mb-icon>\n  </span>\n  <span #optionContent>\n    <ng-content></ng-content>\n  </span>\n  <span [innerHTML]=\"optionText\"></span>\n</span>\n", styles: [".mb-option{background-color:var(--color-foreground);color:var(--color-text-dark);cursor:pointer;display:flex;padding:4px}.mb-option mark{background-color:transparent;color:var(--color-secondary);font-weight:700}.mb-option__mark{color:var(--color-secondary);font-size:12px;font-weight:400;margin:0 7px}.mb-option__label{margin-left:26px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.mb-option:hover,.mb-option--active{background-color:var(--color-secondary-clear)}.mb-option--selected{font-weight:700}.mb-option--selected .mb-option__label{margin-left:0}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbOption, decorators: [{
            type: Component,
            args: [{ selector: 'mb-option', encapsulation: ViewEncapsulation.None, host: {
                        'class': 'mb-option',
                        '[class.mb-option--selected]': 'selected',
                        '[class.mb-option--active]': 'active',
                        '[id]': 'id',
                        '(click)': 'selectViaInteraction()',
                        '(keydown)': 'handleKeydown($event)',
                    }, template: "<span #option class=\"mb-option__label\">\n  <span *ngIf=\"selected\" class=\"mb-option__mark\">\n    <mb-icon [icon]=\"checkIcon\"></mb-icon>\n  </span>\n  <span #optionContent>\n    <ng-content></ng-content>\n  </span>\n  <span [innerHTML]=\"optionText\"></span>\n</span>\n", styles: [".mb-option{background-color:var(--color-foreground);color:var(--color-text-dark);cursor:pointer;display:flex;padding:4px}.mb-option mark{background-color:transparent;color:var(--color-secondary);font-weight:700}.mb-option__mark{color:var(--color-secondary);font-size:12px;font-weight:400;margin:0 7px}.mb-option__label{margin-left:26px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.mb-option:hover,.mb-option--active{background-color:var(--color-secondary-clear)}.mb-option--selected{font-weight:700}.mb-option--selected .mb-option__label{margin-left:0}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MB_AUTOCOMPLETE_CONFIG]
                }] }, { type: MbOptionGroup, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { id: [{
                type: Input
            }], value: [{
                type: Input
            }], selectionChange: [{
                type: Output
            }], option: [{
                type: ViewChild,
                args: ['option']
            }], optionContent: [{
                type: ViewChild,
                args: ['optionContent']
            }] } });

const COMPONENTS$1 = [MbOption, MbOptionGroup];
class MbOptionModule {
}
MbOptionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbOptionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbOptionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbOptionModule, declarations: [MbOption, MbOptionGroup], imports: [CommonModule, MbIconModule], exports: [MbOption, MbOptionGroup] });
MbOptionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbOptionModule, imports: [CommonModule, MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbOptionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MbIconModule],
                    declarations: [...COMPONENTS$1],
                    exports: [...COMPONENTS$1]
                }]
        }] });

/* tslint:disable unified-signatures variable-name */
class ListKeyManager {
    constructor(items) {
        this.items = items;
        this._activeItem = null;
        this._activeItemIndex = -1;
        this.wrap = false;
        /**
         * Stream that emits any time the TAB key is pressed, so components can react
         * when focus is shifted off of the list.
         */
        this.tabOut = new Subject();
        /** Stream that emits whenever the active item of the list manager changes. */
        this.change = new Subject();
        // We allow for the items to be an array because, in some cases, the consumer may
        // not have access to a QueryList of the items they want to manage (e.g. when the
        // items aren't being collected via `ViewChildren` or `ContentChildren`).
        if (items instanceof QueryList) {
            items.changes.subscribe((newItems) => {
                if (this._activeItem) {
                    const itemArray = newItems.toArray();
                    const newIndex = itemArray.indexOf(this._activeItem);
                    if (newIndex > -1 && newIndex !== this._activeItemIndex) {
                        this._activeItemIndex = newIndex;
                    }
                }
            });
        }
    }
    /** Index of the currently active item. */
    get activeItemIndex() {
        return this._activeItemIndex;
    }
    /** The active item. */
    get activeItem() {
        return this._activeItem;
    }
    /**
     * Configures wrapping mode, which determines whether the active item will wrap to
     * the other end of list when there are no more items in the given direction.
     * @param shouldWrap Whether the list should wrap when reaching the end.
     */
    withWrap(shouldWrap = true) {
        this.wrap = shouldWrap;
        return this;
    }
    setActiveItem(item) {
        const previousIndex = this._activeItemIndex;
        this.updateActiveItem(item);
        if (this._activeItemIndex !== previousIndex) {
            this.change.next(this._activeItemIndex);
        }
    }
    /**
     * Sets the active item depending on the key event passed in.
     * @param event Keyboard event to be used for determining which element should be active.
     */
    onKeydown(event) {
        const keyCode = event.keyCode;
        switch (keyCode) {
            case TAB:
                this.tabOut.next();
                return;
            case DOWN_ARROW:
                this.setNextItemActive();
                return;
            case UP_ARROW:
                this.setPreviousItemActive();
                return;
        }
        event.preventDefault();
    }
    /** Sets the active item to the first enabled item in the list. */
    setFirstItemActive() {
        this.setActiveItemByIndex(0);
    }
    /** Sets the active item to the next enabled item in the list. */
    setNextItemActive() {
        this._activeItemIndex < 0 ? this.setFirstItemActive() : this.setActiveItemByDelta(1);
    }
    /** Sets the active item to a previous enabled item in the list. */
    setPreviousItemActive() {
        this._activeItemIndex < 0 && this.wrap
            ? this.setLastItemActive()
            : this.setActiveItemByDelta(-1);
    }
    /** Sets the active item to the last enabled item in the list. */
    setLastItemActive() {
        this.setActiveItemByIndex(this.items.length - 1);
    }
    /**
     * Sets the active item properly given "wrap" mode. In other words, it will continue to move
     * down the list until it finds an item that is not disabled, and it will wrap if it
     * encounters either end of the list.
     */
    setActiveInWrapMode(delta) {
        const items = this._getItemsArray();
        const index = (this._activeItemIndex + delta + items.length) % items.length;
        this.setActiveItem(index);
    }
    /**
     * This method sets the active item, given a list of items and the delta between the
     * currently active item and the new active item. It will calculate differently
     * depending on whether wrap mode is turned on.
     */
    setActiveItemByDelta(delta) {
        this.wrap ? this.setActiveInWrapMode(delta) : this.setActiveInDefaultMode(delta);
    }
    /**
     * Sets the active item properly given the default mode. In other words, it will
     * continue to move down the list until it finds an item that is not disabled. If
     * it encounters either end of the list, it will stop and not wrap.
     */
    setActiveInDefaultMode(delta) {
        this.setActiveItemByIndex(this._activeItemIndex + delta);
    }
    /**
     * Sets the active item to the first enabled item starting at the index specified. If the
     * item is disabled, it will move in the fallbackDelta direction until it either
     * finds an enabled item or encounters the end of the list.
     */
    setActiveItemByIndex(index) {
        const items = this._getItemsArray();
        if (!items[index]) {
            return;
        }
        this.setActiveItem(index);
    }
    updateActiveItem(item) {
        const itemArray = this._getItemsArray();
        const index = typeof item === 'number' ? item : itemArray.indexOf(item);
        const activeItem = itemArray[index];
        // Explicitly check for `null` and `undefined` because other falsy values are valid.
        this._activeItem = activeItem == null ? null : activeItem;
        this._activeItemIndex = index;
    }
    /** Returns the items as an array. */
    _getItemsArray() {
        return this.items instanceof QueryList ? this.items.toArray() : this.items;
    }
}

class ActiveDescendantKeyManager extends ListKeyManager {
    setActiveItem(index) {
        if (this.activeItem) {
            this.activeItem.setInactiveStyles();
        }
        super.setActiveItem(index);
        if (this.activeItem) {
            this.activeItem.setActiveStyles();
        }
    }
}

/**
 * Counts the amount of option group labels that precede the specified option.
 * @param optionIndex Index of the option at which to start counting.
 * @param options Flat list of all of the options.
 * @param optionGroups Flat list of all of the option groups.
 * @docs-private
 */
function countGroupLabelsBeforeOption(optionIndex, options, optionGroups) {
    if (optionGroups.length) {
        let groupCounter = 0;
        for (let i = 0; i < optionIndex + 1; i++) {
            if (options[i].group && options[i].group === optionGroups[groupCounter]) {
                groupCounter++;
            }
        }
        return groupCounter;
    }
    return 0;
}
/**
 * Determines the position to which to scroll a panel in order for an option to be into view.
 * @param optionIndex Index of the option to be scrolled into the view.
 * @param optionHeight Height of the options.
 * @param currentScrollPosition Current scroll position of the panel.
 * @param panelHeight Height of the panel.
 * @docs-private
 */
function getOptionScrollPosition(optionIndex, optionHeight, currentScrollPosition, panelHeight) {
    const optionOffset = optionIndex * optionHeight;
    if (optionOffset < currentScrollPosition) {
        return optionOffset;
    }
    if (optionOffset + optionHeight > currentScrollPosition + panelHeight) {
        return Math.max(0, optionOffset - panelHeight + optionHeight);
    }
    return currentScrollPosition;
}

let nextId = 0;
/** The height of each autocomplete option. */
const AUTOCOMPLETE_OPTION_HEIGHT = 29;
/** The total height of the autocomplete panel. */
const AUTOCOMPLETE_PANEL_HEIGHT = 216;
class MbAutocomplete {
    constructor() {
        this.id = `mb-autocomplete-${++nextId}`;
        this.displayWith = null;
        this.optionSelected = new EventEmitter();
    }
    ngAfterContentInit() {
        this.keyManager = new ActiveDescendantKeyManager(this.options).withWrap();
    }
    scrollToOption() {
        const index = this.keyManager.activeItemIndex || 0;
        const labelCount = countGroupLabelsBeforeOption(index, this.options.toArray(), this.optionGroups.toArray());
        if (index === 0 && labelCount === 1) {
            this.setScrollTop(0);
        }
        else {
            const newScrollPosition = getOptionScrollPosition(index + labelCount, AUTOCOMPLETE_OPTION_HEIGHT, this.getScrollTop(), AUTOCOMPLETE_PANEL_HEIGHT);
            this.setScrollTop(newScrollPosition);
        }
    }
    emitSelectEvent(option) {
        this.optionSelected.emit({ source: this, option });
    }
    onMouseEnter() {
        // deselect the current selected element as the mouse is selecting elements now
        this.keyManager.setActiveItem(-1);
    }
    resetActiveItem() {
        if (this.keyManager) {
            this.keyManager.setActiveItem(-1);
        }
    }
    handleKeyDown(event) {
        this.keyManager.onKeydown(event);
    }
    deselectOptions() {
        this.options.forEach((opt) => opt.deselect());
    }
    hasOptions() {
        return this.options.length > 0;
    }
    getOptionsChanges() {
        return this.options.changes;
    }
    setScrollTop(scrollTop) {
        if (this.panel) {
            this.panel.nativeElement.scrollTop = scrollTop;
        }
    }
    getScrollTop() {
        return this.panel ? this.panel.nativeElement.scrollTop : 0;
    }
    get activeOption() {
        return this.keyManager ? this.keyManager.activeItem : null;
    }
    get tabOut() {
        return this.keyManager ? this.keyManager.tabOut : NEVER;
    }
}
MbAutocomplete.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAutocomplete, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbAutocomplete.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbAutocomplete, selector: "mb-autocomplete", inputs: { id: "id", displayWith: "displayWith" }, outputs: { optionSelected: "optionSelected" }, host: { listeners: { "hover": "onMouseEnter()" } }, queries: [{ propertyName: "options", predicate: MbOption, descendants: true }, { propertyName: "optionGroups", predicate: MbOptionGroup, descendants: true }], viewQueries: [{ propertyName: "template", first: true, predicate: TemplateRef, descendants: true, static: true }, { propertyName: "panel", first: true, predicate: ["panel"], descendants: true }], ngImport: i0, template: "<ng-template>\n  <div\n    class=\"mb-autocomplete-options-panel\"\n    (mouseenter)=\"onMouseEnter()\"\n    [id]=\"id\"\n    #panel\n  >\n    <ng-content></ng-content>\n  </div>\n</ng-template>\n", styles: [".mb-autocomplete-options-panel{background:var(--color-foreground);border:1px solid var(--color-gray-500);border-radius:2px;box-shadow:0 0 8px 4px #0003;max-height:216px;overflow-y:scroll;padding:4px;width:100%}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAutocomplete, decorators: [{
            type: Component,
            args: [{ selector: 'mb-autocomplete', host: {
                        '(hover)': 'onMouseEnter()',
                    }, template: "<ng-template>\n  <div\n    class=\"mb-autocomplete-options-panel\"\n    (mouseenter)=\"onMouseEnter()\"\n    [id]=\"id\"\n    #panel\n  >\n    <ng-content></ng-content>\n  </div>\n</ng-template>\n", styles: [".mb-autocomplete-options-panel{background:var(--color-foreground);border:1px solid var(--color-gray-500);border-radius:2px;box-shadow:0 0 8px 4px #0003;max-height:216px;overflow-y:scroll;padding:4px;width:100%}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], displayWith: [{
                type: Input
            }], optionSelected: [{
                type: Output
            }], template: [{
                type: ViewChild,
                args: [TemplateRef, { static: true }]
            }], panel: [{
                type: ViewChild,
                args: ['panel']
            }], options: [{
                type: ContentChildren,
                args: [MbOption, { descendants: true }]
            }], optionGroups: [{
                type: ContentChildren,
                args: [MbOptionGroup, { descendants: true }]
            }] } });

const MB_AUTOCOMPLETE_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MbAutocompleteInput),
    multi: true
};
class MbAutocompleteInput {
    constructor(elementRef, viewContainerRef, overlay, zone, changeDetectorRef, config, formField, document) {
        this.elementRef = elementRef;
        this.viewContainerRef = viewContainerRef;
        this.overlay = overlay;
        this.zone = zone;
        this.changeDetectorRef = changeDetectorRef;
        this.config = config;
        this.formField = formField;
        this.document = document;
        this.autocompleteAttr = 'off';
        this.zoneIsStable = this.zone.onStable.asObservable().pipe(take(1));
        this.closeKeyEventStream = new Subject();
        this.optionSelections = defer(() => {
            if (this.autocomplete && this.autocomplete.options) {
                return merge(...this.autocomplete.options.map(option => option.selectionChange));
            }
            // If there are any subscribers before `ngAfterViewInit`,
            // the `autocomplete` will be undefined.
            // Return a stream that we'll replace with the real one once everything is in place.
            return this.zoneIsStable.pipe(switchMap(() => this.optionSelections));
        });
    }
    get isOverlayAttached() {
        return this.overlayRef && this.overlayRef.hasAttached();
    }
    get panelCanBeOpened() {
        const element = this.elementRef.nativeElement;
        return (!this.isOverlayAttached &&
            !element.readOnly &&
            !element.disabled &&
            this.autocomplete.hasOptions());
    }
    get panelClosingActions() {
        return merge(this.optionSelections, this.autocomplete.tabOut.pipe(filter(() => this.isOverlayAttached)), this.closeKeyEventStream, this.getOutsideClickStream()).pipe(map(event => (event instanceof MbOptionSelectionChange ? event : null)));
    }
    get activeOption() {
        return this.autocomplete ? this.autocomplete.activeOption : null;
    }
    ngAfterContentInit() {
        this.autocomplete.getOptionsChanges().subscribe(queryList => {
            if (queryList.length === 0 && this.isOverlayAttached) {
                this.close();
                return;
            }
            const selectedOption = queryList.find(opt => opt.selected);
            if (queryList.length > 1 || (queryList.length === 1 && !selectedOption)) {
                this.open();
            }
        });
    }
    ngOnDestroy() {
        this.destroyPanel();
    }
    open() {
        if (!this.panelCanBeOpened) {
            return;
        }
        this.portal = new TemplatePortal(this.autocomplete.template, this.viewContainerRef);
        const overlayRef = this.createAutocompleteOverlay();
        overlayRef.attach(this.portal);
        this.closingActionsSubscription = this.subscribeToClosingActions();
    }
    close() {
        if (this.isOverlayAttached) {
            this.overlayRef.detach();
            this.closingActionsSubscription.unsubscribe();
            this.changeDetectorRef.detectChanges();
        }
    }
    handleInput(event) {
        if (this.onChange) {
            this.onChange(event.target.value);
        }
        if (event.target.value === '') {
            this.autocomplete.deselectOptions();
        }
    }
    handleKeydown(event) {
        const ARROW_KEYS = [UP_ARROW, DOWN_ARROW];
        const ALLOWED_KEYS = [ENTER, TAB, ...ARROW_KEYS];
        const keyCode = event.keyCode;
        const isArrowKey = ARROW_KEYS.includes(keyCode);
        if (!ALLOWED_KEYS.includes(keyCode) || !this.autocomplete) {
            return;
        }
        // panel is open, option is active, enter is pressed
        if (this.isOverlayAttached && this.activeOption && keyCode === ENTER) {
            this.activeOption.selectViaInteraction();
            return;
        }
        if (this.isOverlayAttached) {
            this.autocomplete.handleKeyDown(event);
            if (isArrowKey) {
                this.autocomplete.scrollToOption();
            }
        }
        else if (isArrowKey) {
            this.open();
        }
    }
    /* ControlValueAccessor methods */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.elementRef.nativeElement.disabled = isDisabled;
    }
    writeValue(value) {
        Promise.resolve().then(() => this.setTriggerValue(value));
    }
    getOutsideClickStream() {
        return merge(fromEvent(this.document, 'click'), fromEvent(this.document, 'touchend')).pipe(switchMap(event => of(event.target)), filter(clickTarget => this.isClickOutsidePanel(clickTarget)));
    }
    isClickOutsidePanel(clickTarget) {
        return (this.isOverlayAttached &&
            clickTarget !== this.elementRef.nativeElement &&
            this.isClickOutsideOverlay(clickTarget));
    }
    isClickOutsideOverlay(clickTarget) {
        if (!this.overlayRef) {
            return false;
        }
        return !this.overlayRef.overlayElement.contains(clickTarget);
    }
    subscribeToClosingActions() {
        const optionChanges = this.autocomplete.options.changes.pipe(delay(0));
        // When the zone is stable initially, and when the option list changes...
        return merge(this.zoneIsStable, optionChanges)
            .pipe(
        // create a new stream of panel closing actions, replacing any previous streams
        // that were created, and flatten it so our stream only emits closing events...
        switchMap(() => {
            this.autocomplete.resetActiveItem();
            if (this.isOverlayAttached) {
                this.overlayRef.updatePosition();
            }
            return this.panelClosingActions;
        }), take(1))
            .subscribe(event => this.setValueAndClose(event));
    }
    setValueAndClose(event) {
        if (event && event.source) {
            const value = event.source.selected ? event.source.value : '';
            if (event.source.selected) {
                this.clearPreviousSelectedOption(event.source);
                this.autocomplete.emitSelectEvent(event.source);
            }
            this.setTriggerValue(value);
            if (this.onChange) {
                this.onChange(value);
            }
            this.elementRef.nativeElement.focus();
        }
        this.close();
    }
    setTriggerValue(value) {
        const displayWith = this.autocomplete && this.autocomplete.displayWith;
        if (!displayWith && typeof value !== 'string') {
            console.warn('You should provide the autocomplete displayWith input with a function');
        }
        const valueToDisplay = displayWith ? displayWith(value) : String(value);
        if (valueToDisplay !== null && valueToDisplay !== undefined) {
            this.elementRef.nativeElement.value = valueToDisplay;
        }
    }
    clearPreviousSelectedOption(skip) {
        this.autocomplete.options.forEach(option => {
            if (option !== skip && option.selected) {
                option.deselect();
            }
        });
    }
    getHostElement() {
        return this.formField ? this.formField.getConnectedOverlayOrigin() : this.elementRef;
    }
    getOverlayConfig() {
        const scrollStrategy = this.overlay.scrollStrategies.reposition();
        const elementRef = this.getHostElement();
        return {
            width: 480,
            scrollStrategy,
            hasBackdrop: true,
            backdropClass: TRANSPARENT_BACKDROP_CLASS,
            positionStrategy: this.overlay
                .position()
                .flexibleConnectedTo(elementRef)
                .withLockedPosition()
                .withFlexibleDimensions(false)
                .withPush(false)
                .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top'
                }
            ])
        };
    }
    createAutocompleteOverlay() {
        if (this.overlayRef) {
            return this.overlayRef;
        }
        this.overlayRef = this.overlay.create(this.getOverlayConfig());
        this.overlayRef.keydownEvents().subscribe(event => {
            if (event.keyCode === ESCAPE) {
                this.autocomplete.resetActiveItem();
                this.closeKeyEventStream.next();
                event.preventDefault();
            }
        });
        return this.overlayRef;
    }
    destroyPanel() {
        if (this.overlayRef) {
            this.close();
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }
}
MbAutocompleteInput.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAutocompleteInput, deps: [{ token: i0.ElementRef }, { token: i0.ViewContainerRef }, { token: i1$3.Overlay }, { token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: MB_AUTOCOMPLETE_CONFIG }, { token: MbFormField, host: true, optional: true }, { token: DOCUMENT, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
MbAutocompleteInput.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbAutocompleteInput, selector: "input[mb-autocomplete]", inputs: { autocomplete: ["mb-autocomplete", "autocomplete"], autocompleteAttr: ["autocomplete", "autocompleteAttr"] }, host: { listeners: { "input": "handleInput($event)", "keydown": "handleKeydown($event)" }, properties: { "attr.autocomplete": "autocompleteAttr" } }, providers: [MB_AUTOCOMPLETE_VALUE_ACCESSOR], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAutocompleteInput, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[mb-autocomplete]',
                    providers: [MB_AUTOCOMPLETE_VALUE_ACCESSOR],
                    host: {
                        '[attr.autocomplete]': 'autocompleteAttr',
                        '(input)': 'handleInput($event)',
                        '(keydown)': 'handleKeydown($event)'
                    }
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ViewContainerRef }, { type: i1$3.Overlay }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MB_AUTOCOMPLETE_CONFIG]
                }] }, { type: MbFormField, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { autocomplete: [{
                type: Input,
                args: ['mb-autocomplete']
            }], autocompleteAttr: [{
                type: Input,
                args: ['autocomplete']
            }] } });

class MbHighlightPipe {
    constructor(config) {
        this.config = config;
    }
    transform(value, query) {
        if (!query) {
            return value;
        }
        const highlightToken = this.config.highlightToken;
        const queryRegExp = new RegExp(query, 'ig');
        const open = [];
        const close = [];
        let match;
        // tslint:disable-next-line
        while ((match = queryRegExp.exec(value)) != null) {
            open.push(match.index);
            close.push(match.index + query.length - 1);
        }
        return value
            .split('')
            .map((char, i) => {
            let newChar = char;
            if (open.includes(i)) {
                newChar = `<${highlightToken}>${newChar}`;
            }
            if (close.includes(i)) {
                newChar = `${newChar}</${highlightToken}>`;
            }
            return newChar;
        })
            .join('');
    }
}
MbHighlightPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbHighlightPipe, deps: [{ token: MB_AUTOCOMPLETE_CONFIG }], target: i0.ɵɵFactoryTarget.Pipe });
MbHighlightPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbHighlightPipe, name: "highlight" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbHighlightPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'highlight', pure: true }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MB_AUTOCOMPLETE_CONFIG]
                }] }]; } });

const MB_SEARCH_INPUT_CONFIG = new InjectionToken('MbSearchInputConfig', {
    factory() {
        return defaultConfig;
    }
});
const defaultConfig = {
    debounceTime: 300,
    minCharacters: 3
};
// tslint:disable:directive-selector
class MbSearchInput {
    constructor(elementRef, config) {
        this.elementRef = elementRef;
        this.config = config;
        // tslint:disable-next-line:no-output-rename
        this.searchInputChange = new EventEmitter();
    }
    ngOnInit() {
        const configDebounceTime = this.config.debounceTime || defaultConfig.debounceTime;
        const configMinCharacters = this.config.minCharacters || defaultConfig.minCharacters;
        this.inputChangeSubscription = fromEvent(this.elementRef.nativeElement, 'input')
            .pipe(map(event => event.target.value), filter(value => value.length >= configMinCharacters), debounceTime(configDebounceTime))
            .subscribe(this.searchInputChange);
    }
    ngOnDestroy() {
        this.inputChangeSubscription.unsubscribe();
    }
}
MbSearchInput.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSearchInput, deps: [{ token: i0.ElementRef }, { token: MB_SEARCH_INPUT_CONFIG }], target: i0.ɵɵFactoryTarget.Directive });
MbSearchInput.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: MbSearchInput, selector: "input[search]", outputs: { searchInputChange: "search" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSearchInput, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[search]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MB_SEARCH_INPUT_CONFIG]
                }] }]; }, propDecorators: { searchInputChange: [{
                type: Output,
                args: ['search']
            }] } });

class MbSearchInputModule {
}
MbSearchInputModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSearchInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbSearchInputModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbSearchInputModule, declarations: [MbSearchInput], exports: [MbSearchInput] });
MbSearchInputModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSearchInputModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSearchInputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MbSearchInput],
                    exports: [MbSearchInput]
                }]
        }] });

class MbAutocompleteModule {
}
MbAutocompleteModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAutocompleteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbAutocompleteModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbAutocompleteModule, declarations: [MbAutocomplete, MbAutocompleteInput, MbHighlightPipe], imports: [MbOptionModule, MbSearchInputModule], exports: [MbAutocomplete,
        MbAutocompleteInput,
        MbHighlightPipe,
        MbOptionModule,
        MbSearchInputModule] });
MbAutocompleteModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAutocompleteModule, imports: [MbOptionModule, MbSearchInputModule, MbOptionModule,
        MbSearchInputModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbAutocompleteModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [MbOptionModule, MbSearchInputModule],
                    declarations: [MbAutocomplete, MbAutocompleteInput, MbHighlightPipe],
                    exports: [
                        MbAutocomplete,
                        MbAutocompleteInput,
                        MbHighlightPipe,
                        MbOptionModule,
                        MbSearchInputModule
                    ]
                }]
        }] });

class MbTable {
}
MbTable.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTable, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbTable.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTable, selector: "mb-table", host: { classAttribute: "mb-table" }, ngImport: i0, template: "<div class=\"mb-table__container\">\n  <ng-content select=\"mb-table-title\"></ng-content>\n  <ng-content select=\"table\"></ng-content>\n</div>\n<ng-content select=\"mb-table-pagination\"></ng-content>\n", styles: [".mb-table{display:block}.mb-table__container{border:1px solid var(--color-border);border-radius:8px}.mb-table table{border-collapse:collapse;width:100%}.mb-table table tr{border-bottom:1px solid var(--color-border)}.mb-table table tbody tr:last-child{border-bottom:none}.mb-table table td,.mb-table table th{height:48px;padding:0 2rem;text-align:start}.mb-table table th{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;position:relative}@media only screen and (max-width: 767px){.mb-table table th{line-height:1.5}}.mb-table table th:before{background-color:var(--color-border);content:\"\";height:32px;left:0;position:absolute;top:8px;width:1px}.mb-table table th:first-child:before{display:none}\n"], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTable, decorators: [{
            type: Component,
            args: [{ selector: 'mb-table', encapsulation: ViewEncapsulation.None, host: {
                        class: 'mb-table'
                    }, template: "<div class=\"mb-table__container\">\n  <ng-content select=\"mb-table-title\"></ng-content>\n  <ng-content select=\"table\"></ng-content>\n</div>\n<ng-content select=\"mb-table-pagination\"></ng-content>\n", styles: [".mb-table{display:block}.mb-table__container{border:1px solid var(--color-border);border-radius:8px}.mb-table table{border-collapse:collapse;width:100%}.mb-table table tr{border-bottom:1px solid var(--color-border)}.mb-table table tbody tr:last-child{border-bottom:none}.mb-table table td,.mb-table table th{height:48px;padding:0 2rem;text-align:start}.mb-table table th{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;position:relative}@media only screen and (max-width: 767px){.mb-table table th{line-height:1.5}}.mb-table table th:before{background-color:var(--color-border);content:\"\";height:32px;left:0;position:absolute;top:8px;width:1px}.mb-table table th:first-child:before{display:none}\n"] }]
        }] });

class MbTableTitle {
}
MbTableTitle.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTableTitle, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbTableTitle.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTableTitle, selector: "mb-table-title", host: { classAttribute: "mb-table__title" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{border-bottom:1px solid var(--color-border);display:block;font-size:20px;font-weight:700;padding:1rem}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTableTitle, decorators: [{
            type: Component,
            args: [{ selector: 'mb-table-title', template: '<ng-content></ng-content>', host: {
                        class: 'mb-table__title'
                    }, styles: [":host{border-bottom:1px solid var(--color-border);display:block;font-size:20px;font-weight:700;padding:1rem}\n"] }]
        }] });

class MbTableRow {
    constructor() {
        this.selectable = false;
        this.selected = false;
        this.disabled = false;
        this.rowSelectionChange = new EventEmitter();
    }
}
MbTableRow.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTableRow, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbTableRow.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTableRow, selector: "tr[mb-tr]", inputs: { selectable: "selectable", selected: "selected", disabled: "disabled" }, outputs: { rowSelectionChange: "rowSelectionChange" }, host: { properties: { "class.selected": "selected", "class.disabled": "disabled" }, classAttribute: "mb-table-row" }, ngImport: i0, template: "<td *ngIf=\"selectable\" class=\"mb-table-row__check-cell\">\n  <mb-checkbox\n    [disabled]=\"disabled\"\n    [value]=\"selected\"\n    (valueChange)=\"rowSelectionChange.emit($event)\"\n  ></mb-checkbox>\n</td>\n<ng-content></ng-content>\n", styles: [".mb-table-row__check-cell{padding:0 0 0 16px;text-align:center}.mb-table-row:host.selected{background-color:var(--color-secondary-clear)}.mb-table-row:host.disabled{background-color:var(--color-disabled);color:var(--color-text-disabled)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MbCheckbox, selector: "mb-checkbox", inputs: ["disabled", "value", "id", "name", "tabIndex"], outputs: ["valueChange"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTableRow, decorators: [{
            type: Component,
            args: [{ selector: 'tr[mb-tr]', host: {
                        'class': 'mb-table-row',
                        '[class.selected]': 'selected',
                        '[class.disabled]': 'disabled'
                    }, template: "<td *ngIf=\"selectable\" class=\"mb-table-row__check-cell\">\n  <mb-checkbox\n    [disabled]=\"disabled\"\n    [value]=\"selected\"\n    (valueChange)=\"rowSelectionChange.emit($event)\"\n  ></mb-checkbox>\n</td>\n<ng-content></ng-content>\n", styles: [".mb-table-row__check-cell{padding:0 0 0 16px;text-align:center}.mb-table-row:host.selected{background-color:var(--color-secondary-clear)}.mb-table-row:host.disabled{background-color:var(--color-disabled);color:var(--color-text-disabled)}\n"] }]
        }], propDecorators: { selectable: [{
                type: Input
            }], selected: [{
                type: Input
            }], disabled: [{
                type: Input
            }], rowSelectionChange: [{
                type: Output
            }] } });

class MbTableButton {
    constructor(row) {
        this.row = row;
    }
    get disabled() {
        return this.row && this.row.disabled;
    }
}
MbTableButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTableButton, deps: [{ token: MbTableRow, optional: true }], target: i0.ɵɵFactoryTarget.Component });
MbTableButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTableButton, selector: "button[mb-table-button]", host: { properties: { "class.disabled": "disabled" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;color:var(--color-secondary);text-align:end;text-transform:uppercase;width:100%}@media only screen and (max-width: 767px){:host{line-height:1.5}}:host.disabled{color:var(--color-text-disabled);pointer-events:none}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTableButton, decorators: [{
            type: Component,
            args: [{ selector: 'button[mb-table-button]', template: '<ng-content></ng-content>', host: {
                        '[class.disabled]': 'disabled'
                    }, styles: [":host{-webkit-tap-highlight-color:rgba(0,0,0,0);background-color:transparent;border:none;color:inherit;cursor:pointer;font:inherit;outline:none;padding:0;font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;color:var(--color-secondary);text-align:end;text-transform:uppercase;width:100%}@media only screen and (max-width: 767px){:host{line-height:1.5}}:host.disabled{color:var(--color-text-disabled);pointer-events:none}\n"] }]
        }], ctorParameters: function () { return [{ type: MbTableRow, decorators: [{
                    type: Optional
                }] }]; } });

class MbTablePagination {
    constructor() {
        this.pageChange = new EventEmitter();
    }
}
MbTablePagination.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTablePagination, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbTablePagination.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTablePagination, selector: "mb-table-pagination", inputs: { totalEntries: "totalEntries", pageSize: "pageSize", currentPage: "currentPage" }, outputs: { pageChange: "pageChange" }, ngImport: i0, template: "<mb-paginator\n  [totalEntries]=\"totalEntries\"\n  [currentPage]=\"currentPage\"\n  [pageSize]=\"pageSize\"\n  (pageChange)=\"pageChange.emit($event)\"\n></mb-paginator>\n<div>\n  <ng-content select=\"mb-page-size-select\"></ng-content>\n  <ng-content select=\"mb-table-page-info\"></ng-content>\n</div>\n", styles: [":host{display:flex;justify-content:space-between;margin:1rem 0 0}\n"], dependencies: [{ kind: "component", type: MbPaginator, selector: "mb-paginator", inputs: ["currentPage", "totalEntries", "pageSize"], outputs: ["pageChange"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTablePagination, decorators: [{
            type: Component,
            args: [{ selector: 'mb-table-pagination', template: "<mb-paginator\n  [totalEntries]=\"totalEntries\"\n  [currentPage]=\"currentPage\"\n  [pageSize]=\"pageSize\"\n  (pageChange)=\"pageChange.emit($event)\"\n></mb-paginator>\n<div>\n  <ng-content select=\"mb-page-size-select\"></ng-content>\n  <ng-content select=\"mb-table-page-info\"></ng-content>\n</div>\n", styles: [":host{display:flex;justify-content:space-between;margin:1rem 0 0}\n"] }]
        }], propDecorators: { totalEntries: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], currentPage: [{
                type: Input
            }], pageChange: [{
                type: Output
            }] } });

class MbTablePageInfo {
    constructor(tablePagination) {
        this.tablePagination = tablePagination;
    }
    get context() {
        return {
            min: (this.tablePagination.currentPage - 1) * this.tablePagination.pageSize + 1,
            max: Math.min(this.tablePagination.currentPage * this.tablePagination.pageSize, this.tablePagination.totalEntries)
        };
    }
}
MbTablePageInfo.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTablePageInfo, deps: [{ token: MbTablePagination }], target: i0.ɵɵFactoryTarget.Component });
MbTablePageInfo.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbTablePageInfo, selector: "mb-table-page-info", inputs: { statsTemplate: "statsTemplate" }, ngImport: i0, template: "<ng-container *ngTemplateOutlet=\"statsTemplate; context: context\"></ng-container>\n", styles: [":host{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5;display:inline-block;margin-left:2rem}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"], dependencies: [{ kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTablePageInfo, decorators: [{
            type: Component,
            args: [{ selector: 'mb-table-page-info', template: "<ng-container *ngTemplateOutlet=\"statsTemplate; context: context\"></ng-container>\n", styles: [":host{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5;display:inline-block;margin-left:2rem}@media only screen and (max-width: 767px){:host{line-height:1.5}}\n"] }]
        }], ctorParameters: function () { return [{ type: MbTablePagination }]; }, propDecorators: { statsTemplate: [{
                type: Input
            }] } });

const components = [
    MbTable,
    MbTableTitle,
    MbTableRow,
    MbTableButton,
    MbTablePagination,
    MbTablePageInfo
];
class MbTableModule {
}
MbTableModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbTableModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbTableModule, declarations: [MbTable,
        MbTableTitle,
        MbTableRow,
        MbTableButton,
        MbTablePagination,
        MbTablePageInfo], imports: [CommonModule, MbCheckboxModule, MbPaginatorModule], exports: [MbTable,
        MbTableTitle,
        MbTableRow,
        MbTableButton,
        MbTablePagination,
        MbTablePageInfo] });
MbTableModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTableModule, imports: [CommonModule, MbCheckboxModule, MbPaginatorModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbTableModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MbCheckboxModule, MbPaginatorModule],
                    exports: [...components],
                    declarations: [...components]
                }]
        }] });

class MbPageSizeSelect {
    constructor() {
        this.icon = aciArrowDown;
        this.pageSizeChange = new EventEmitter();
    }
    onPageSizeChange(event) {
        const pageSize = parseInt(event.target.value, 10);
        this.pageSizeChange.emit(pageSize);
    }
}
MbPageSizeSelect.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPageSizeSelect, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbPageSizeSelect.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbPageSizeSelect, selector: "mb-page-size-select", inputs: { pageSizeOptions: "pageSizeOptions" }, outputs: { pageSizeChange: "pageSizeChange" }, host: { classAttribute: "mb-page-size-select" }, ngImport: i0, template: "<ng-content></ng-content>\n<div class=\"mb-page-size-select__container\">\n  <select class=\"mb-page-size-select__select\" (change)=\"onPageSizeChange($event)\">\n    <option *ngFor=\"let option of pageSizeOptions\" [value]=\"option\">{{ option }}</option>\n  </select>\n  <span class=\"mb-page-size-select__icon\">\n    <mb-icon [icon]=\"icon\"></mb-icon>\n  </span>\n</div>\n", styles: [".mb-page-size-select:host{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5;display:inline-block}@media only screen and (max-width: 767px){.mb-page-size-select:host{line-height:1.5}}.mb-page-size-select__container{display:inline-block;position:relative}.mb-page-size-select__select{appearance:none;background:var(--color-gray-500) none;border:none;border-radius:4px;cursor:pointer;outline:none;padding:4px 16px 4px 4px;text-align:center;width:40px}.mb-page-size-select__icon{font-size:10px;pointer-events:none;position:absolute;right:4px;top:7px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPageSizeSelect, decorators: [{
            type: Component,
            args: [{ selector: 'mb-page-size-select', host: {
                        class: 'mb-page-size-select'
                    }, template: "<ng-content></ng-content>\n<div class=\"mb-page-size-select__container\">\n  <select class=\"mb-page-size-select__select\" (change)=\"onPageSizeChange($event)\">\n    <option *ngFor=\"let option of pageSizeOptions\" [value]=\"option\">{{ option }}</option>\n  </select>\n  <span class=\"mb-page-size-select__icon\">\n    <mb-icon [icon]=\"icon\"></mb-icon>\n  </span>\n</div>\n", styles: [".mb-page-size-select:host{font-size:1rem;font-weight:var(--font-weight-regular);line-height:1.5;display:inline-block}@media only screen and (max-width: 767px){.mb-page-size-select:host{line-height:1.5}}.mb-page-size-select__container{display:inline-block;position:relative}.mb-page-size-select__select{appearance:none;background:var(--color-gray-500) none;border:none;border-radius:4px;cursor:pointer;outline:none;padding:4px 16px 4px 4px;text-align:center;width:40px}.mb-page-size-select__icon{font-size:10px;pointer-events:none;position:absolute;right:4px;top:7px}\n"] }]
        }], propDecorators: { pageSizeOptions: [{
                type: Input
            }], pageSizeChange: [{
                type: Output
            }] } });

class MbPageSizeSelectModule {
}
MbPageSizeSelectModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPageSizeSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbPageSizeSelectModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbPageSizeSelectModule, declarations: [MbPageSizeSelect], imports: [CommonModule, MbIconModule], exports: [MbPageSizeSelect] });
MbPageSizeSelectModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPageSizeSelectModule, imports: [CommonModule, MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbPageSizeSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MbIconModule],
                    exports: [MbPageSizeSelect],
                    declarations: [MbPageSizeSelect]
                }]
        }] });

class MbRecommendedCard {
    constructor() {
        this.evaluations = {
            5: 'REGULAR',
            6: 'ACEPTABLE',
            7: 'BUENO',
            8: 'MUY BUENO',
            9: 'EXCELENTE',
            10: 'FANTASTICO'
        };
    }
}
MbRecommendedCard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRecommendedCard, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbRecommendedCard.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbRecommendedCard, selector: "mb-recommended-card", inputs: { image: "image", name: "name", includedTaxes: "includedTaxes", pricePerNigth: "pricePerNigth", rating: "rating", stars: "stars", opinions: "opinions" }, ngImport: i0, template: "<div class=\"recommended-hotels__hotels-container\">\n  <mb-card class=\"recommended-hotels__hotels-container__hotel\">\n    <div style=\"width: 45%;\">\n      <ng-container>\n        <div\n          [ngStyle]=\"{ 'background-image': 'url(' + image + ')' }\"\n          class=\"recommended-hotels__hotels-container__hotel__img\"\n        ></div>\n      </ng-container>\n    </div>\n    <div class=\"recommended-hotels__hotels-container__hotel__content\">\n      <div>\n        <h2 class=\"recommended-hotels__hotels-container__hotel__content__name\">\n          {{ name }}\n        </h2>\n        <mb-stars-rating class=\"stars\" [rating]=\"stars\"></mb-stars-rating>\n        <ng-container *ngIf=\"rating; else noRating\">\n          <mb-reviews-rating class=\"reviews-rating\" *ngIf=\"rating > 4\" [rating]=\"rating\">\n            <mb-reviews-evaluation>{{ evaluations[rating] }}</mb-reviews-evaluation>\n            <mb-reviews-detail>{{ opinions }} opiniones</mb-reviews-detail>\n          </mb-reviews-rating>\n        </ng-container>\n        <ng-template #noRating>\n          <span style=\"height: 27px; display: block; margin-top: 10px;\"></span>\n        </ng-template>\n      </div>\n      <div class=\"recommended-hotels__hotels-container__hotel__content__price\">\n        <div\n          class=\"recommended-hotels__hotels-container__hotel__content__price__price-by\"\n        >\n          <ng-container *ngIf=\"pricePerNigth; else totalPrice\">\n            <p>Por noche por habitaci\u00F3n</p>\n          </ng-container>\n          <ng-template #totalPrice>\n            <p>Precio Total</p>\n          </ng-template>\n        </div>\n        <div\n          class=\"recommended-hotels__hotels-container__hotel__content__price__price-amount\"\n        >\n          <ng-content> </ng-content>\n        </div>\n        <ng-container *ngIf=\"includedTaxes; else notincluded\">\n          <span\n            class=\"recommended-hotels__hotels-container__hotel__content__price__price-amount__legal-taxes\"\n            >Impuestos incluidos</span\n          >\n        </ng-container>\n        <ng-template #notincluded>\n          <span\n            class=\"recommended-hotels__hotels-container__hotel__content__price__price-amount__legal-taxes\"\n            >Cargos e impuestos no incluidos</span\n          >\n        </ng-template>\n      </div>\n    </div>\n    <div></div>\n  </mb-card>\n</div>\n", styles: [".recommended-hotels__hotels-container{margin-right:4px;margin-top:16px}.recommended-hotels__hotels-container__hotel{cursor:pointer;display:flex}@media only screen and (max-width: 767px){.recommended-hotels__hotels-container__hotel{width:100%}.recommended-hotels__hotels-container__hotel:last-child{margin-top:16px}}.recommended-hotels__hotels-container__hotel__img{background-position:50%;background-repeat:no-repeat;background-size:cover;height:100%;width:100%}.recommended-hotels__hotels-container__hotel__content{display:flex;flex-direction:column;justify-content:space-between;padding-left:20px;width:55%}@media only screen and (max-width: 767px){.recommended-hotels__hotels-container__hotel__content{margin-left:10px}}.recommended-hotels__hotels-container__hotel__content__name{font-size:.875rem;font-weight:600;line-height:20px;color:var(--color-secondary);display:block;margin:10px 0 0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.recommended-hotels__hotels-container__hotel__content .stars{display:block}.recommended-hotels__hotels-container__hotel__content .reviews-rating{margin-top:10px}.recommended-hotels__hotels-container__hotel__content__price__price-by p{font-size:.75rem;font-weight:400;line-height:16px;margin:0}.recommended-hotels__hotels-container__hotel__content__price__price-amount__legal-taxes{font-size:.75rem;font-weight:400;line-height:16px;color:var(--color-text-disabled);display:block;margin-bottom:10px}\n"], dependencies: [{ kind: "component", type: MbCard, selector: "mb-card", inputs: ["elevation"] }, { kind: "component", type: MbStarsRating, selector: "mb-stars-rating", inputs: ["rating"] }, { kind: "component", type: MbReviewsRating, selector: "mb-reviews-rating", inputs: ["rating"] }, { kind: "component", type: MbReviewsEvaluation, selector: "mb-reviews-evaluation" }, { kind: "component", type: MbReviewsDetail, selector: "mb-reviews-detail" }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRecommendedCard, decorators: [{
            type: Component,
            args: [{ selector: 'mb-recommended-card', template: "<div class=\"recommended-hotels__hotels-container\">\n  <mb-card class=\"recommended-hotels__hotels-container__hotel\">\n    <div style=\"width: 45%;\">\n      <ng-container>\n        <div\n          [ngStyle]=\"{ 'background-image': 'url(' + image + ')' }\"\n          class=\"recommended-hotels__hotels-container__hotel__img\"\n        ></div>\n      </ng-container>\n    </div>\n    <div class=\"recommended-hotels__hotels-container__hotel__content\">\n      <div>\n        <h2 class=\"recommended-hotels__hotels-container__hotel__content__name\">\n          {{ name }}\n        </h2>\n        <mb-stars-rating class=\"stars\" [rating]=\"stars\"></mb-stars-rating>\n        <ng-container *ngIf=\"rating; else noRating\">\n          <mb-reviews-rating class=\"reviews-rating\" *ngIf=\"rating > 4\" [rating]=\"rating\">\n            <mb-reviews-evaluation>{{ evaluations[rating] }}</mb-reviews-evaluation>\n            <mb-reviews-detail>{{ opinions }} opiniones</mb-reviews-detail>\n          </mb-reviews-rating>\n        </ng-container>\n        <ng-template #noRating>\n          <span style=\"height: 27px; display: block; margin-top: 10px;\"></span>\n        </ng-template>\n      </div>\n      <div class=\"recommended-hotels__hotels-container__hotel__content__price\">\n        <div\n          class=\"recommended-hotels__hotels-container__hotel__content__price__price-by\"\n        >\n          <ng-container *ngIf=\"pricePerNigth; else totalPrice\">\n            <p>Por noche por habitaci\u00F3n</p>\n          </ng-container>\n          <ng-template #totalPrice>\n            <p>Precio Total</p>\n          </ng-template>\n        </div>\n        <div\n          class=\"recommended-hotels__hotels-container__hotel__content__price__price-amount\"\n        >\n          <ng-content> </ng-content>\n        </div>\n        <ng-container *ngIf=\"includedTaxes; else notincluded\">\n          <span\n            class=\"recommended-hotels__hotels-container__hotel__content__price__price-amount__legal-taxes\"\n            >Impuestos incluidos</span\n          >\n        </ng-container>\n        <ng-template #notincluded>\n          <span\n            class=\"recommended-hotels__hotels-container__hotel__content__price__price-amount__legal-taxes\"\n            >Cargos e impuestos no incluidos</span\n          >\n        </ng-template>\n      </div>\n    </div>\n    <div></div>\n  </mb-card>\n</div>\n", styles: [".recommended-hotels__hotels-container{margin-right:4px;margin-top:16px}.recommended-hotels__hotels-container__hotel{cursor:pointer;display:flex}@media only screen and (max-width: 767px){.recommended-hotels__hotels-container__hotel{width:100%}.recommended-hotels__hotels-container__hotel:last-child{margin-top:16px}}.recommended-hotels__hotels-container__hotel__img{background-position:50%;background-repeat:no-repeat;background-size:cover;height:100%;width:100%}.recommended-hotels__hotels-container__hotel__content{display:flex;flex-direction:column;justify-content:space-between;padding-left:20px;width:55%}@media only screen and (max-width: 767px){.recommended-hotels__hotels-container__hotel__content{margin-left:10px}}.recommended-hotels__hotels-container__hotel__content__name{font-size:.875rem;font-weight:600;line-height:20px;color:var(--color-secondary);display:block;margin:10px 0 0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.recommended-hotels__hotels-container__hotel__content .stars{display:block}.recommended-hotels__hotels-container__hotel__content .reviews-rating{margin-top:10px}.recommended-hotels__hotels-container__hotel__content__price__price-by p{font-size:.75rem;font-weight:400;line-height:16px;margin:0}.recommended-hotels__hotels-container__hotel__content__price__price-amount__legal-taxes{font-size:.75rem;font-weight:400;line-height:16px;color:var(--color-text-disabled);display:block;margin-bottom:10px}\n"] }]
        }], propDecorators: { image: [{
                type: Input
            }], name: [{
                type: Input
            }], includedTaxes: [{
                type: Input
            }], pricePerNigth: [{
                type: Input
            }], rating: [{
                type: Input
            }], stars: [{
                type: Input
            }], opinions: [{
                type: Input
            }] } });

class MbRecommendedCardModule {
}
MbRecommendedCardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRecommendedCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbRecommendedCardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbRecommendedCardModule, declarations: [MbRecommendedCard], imports: [MbCardModule, MbStarsRatingModule, MbReviewsRatingModule, CommonModule], exports: [MbRecommendedCard] });
MbRecommendedCardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRecommendedCardModule, imports: [MbCardModule, MbStarsRatingModule, MbReviewsRatingModule, CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbRecommendedCardModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [MbRecommendedCard],
                    imports: [MbCardModule, MbStarsRatingModule, MbReviewsRatingModule, CommonModule],
                    declarations: [MbRecommendedCard]
                }]
        }] });

class MbBarRating {
    getRatingPercentage() {
        return (this.rating * 10).toString() + '%';
    }
}
MbBarRating.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBarRating, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbBarRating.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBarRating, selector: "mb-bar-rating", inputs: { title: "title", rating: "rating" }, ngImport: i0, template: "<div class=\"mb-bar-rating\">\n  <div class=\"info\">\n    <span class=\"name\">{{ title }}</span>\n    <span class=\"number\">{{ rating }}</span>\n  </div>\n  <div class=\"rating-bar\">\n    <div class=\"rate\" [ngStyle]=\"{ width: getRatingPercentage() }\"></div>\n  </div>\n</div>\n", styles: [".mb-bar-rating{margin:14px 0;width:100%}.mb-bar-rating .info{display:flex;justify-content:space-between;margin-bottom:8px}.mb-bar-rating .info .name{color:var(--color-text-dark);font-size:16px;font-weight:600}.mb-bar-rating .info .number{color:var(--color-secondary);font-size:16px;font-weight:600}.mb-bar-rating .rating-bar{background-color:var(--color-disabled);border-radius:10px;height:13px;position:relative}.mb-bar-rating .rating-bar .rate{background-color:var(--color-secondary);border-radius:10px;height:13px;position:absolute}\n"], dependencies: [{ kind: "directive", type: i1$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBarRating, decorators: [{
            type: Component,
            args: [{ selector: 'mb-bar-rating', template: "<div class=\"mb-bar-rating\">\n  <div class=\"info\">\n    <span class=\"name\">{{ title }}</span>\n    <span class=\"number\">{{ rating }}</span>\n  </div>\n  <div class=\"rating-bar\">\n    <div class=\"rate\" [ngStyle]=\"{ width: getRatingPercentage() }\"></div>\n  </div>\n</div>\n", styles: [".mb-bar-rating{margin:14px 0;width:100%}.mb-bar-rating .info{display:flex;justify-content:space-between;margin-bottom:8px}.mb-bar-rating .info .name{color:var(--color-text-dark);font-size:16px;font-weight:600}.mb-bar-rating .info .number{color:var(--color-secondary);font-size:16px;font-weight:600}.mb-bar-rating .rating-bar{background-color:var(--color-disabled);border-radius:10px;height:13px;position:relative}.mb-bar-rating .rating-bar .rate{background-color:var(--color-secondary);border-radius:10px;height:13px;position:absolute}\n"] }]
        }], propDecorators: { title: [{
                type: Input
            }], rating: [{
                type: Input
            }] } });

class MbBarRatingModule {
}
MbBarRatingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBarRatingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbBarRatingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbBarRatingModule, declarations: [MbBarRating], imports: [CommonModule], exports: [MbBarRating] });
MbBarRatingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBarRatingModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBarRatingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [MbBarRating],
                    declarations: [MbBarRating]
                }]
        }] });

class MbComments {
}
MbComments.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbComments, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbComments.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbComments, selector: "mb-comments", inputs: { review: "review" }, ngImport: i0, template: "<div class=\"review\">\n  <div class=\"info-container\">\n    <div class=\"info-column\">\n      <div class=\"user\">{{ review.user }}</div>\n      <div class=\"info\">\n        {{ review.trip_type ? review.trip_type + ' |' : '' }}\n        <span>{{ review.published_date | date }}</span>\n      </div>\n    </div>\n    <mb-reviews-rating\n      class=\"mb-reviews-rating\"\n      rating=\"{{ review.rating }}\"\n      [ngClass]=\"{ 'mb-review__rating-low': review.rating < 5 }\"\n    >\n      <mb-reviews-evaluation *ngIf=\"review.rating > 4 || !review.rating\"\n        >{review.rating, select, 5 {REGULAR} 6 {ACEPTABLE} 7 {BUENO} 8 {MUY BUENO} 9\n        {EXCELENTE} 10 {FANT\u00C1STICO} other {SIN PUNTUACI\u00D3N}}</mb-reviews-evaluation\n      >\n    </mb-reviews-rating>\n  </div>\n  <div class=\"title\">{{ review.title }}</div>\n  <div class=\"text\">{{ review.text }}</div>\n</div>\n", styles: [".review{border-bottom:solid 1px var(--color-disabled);margin-bottom:20px;padding-bottom:20px}.review .info-container{border:none;display:flex;justify-content:space-between}.review .info-container .info-column .user{color:var(--color-secondary);font-size:16px;font-weight:600}.review .info-container .info-column .info{font-size:14px;margin-top:3px}.review .info-container .info-column .info span{text-transform:capitalize}.review .info-container .mb-reviews-rating{height:fit-content}.review .info-container .mb-review__rating-low .mb-review__rating-value{background-color:var(--color-error)}.review .title{font-size:16px;font-weight:600;margin-top:12px}.review .text{font-size:12px;margin-top:8px}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MbReviewsRating, selector: "mb-reviews-rating", inputs: ["rating"] }, { kind: "component", type: MbReviewsEvaluation, selector: "mb-reviews-evaluation" }, { kind: "pipe", type: i1$2.DatePipe, name: "date" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbComments, decorators: [{
            type: Component,
            args: [{ selector: 'mb-comments', template: "<div class=\"review\">\n  <div class=\"info-container\">\n    <div class=\"info-column\">\n      <div class=\"user\">{{ review.user }}</div>\n      <div class=\"info\">\n        {{ review.trip_type ? review.trip_type + ' |' : '' }}\n        <span>{{ review.published_date | date }}</span>\n      </div>\n    </div>\n    <mb-reviews-rating\n      class=\"mb-reviews-rating\"\n      rating=\"{{ review.rating }}\"\n      [ngClass]=\"{ 'mb-review__rating-low': review.rating < 5 }\"\n    >\n      <mb-reviews-evaluation *ngIf=\"review.rating > 4 || !review.rating\"\n        >{review.rating, select, 5 {REGULAR} 6 {ACEPTABLE} 7 {BUENO} 8 {MUY BUENO} 9\n        {EXCELENTE} 10 {FANT\u00C1STICO} other {SIN PUNTUACI\u00D3N}}</mb-reviews-evaluation\n      >\n    </mb-reviews-rating>\n  </div>\n  <div class=\"title\">{{ review.title }}</div>\n  <div class=\"text\">{{ review.text }}</div>\n</div>\n", styles: [".review{border-bottom:solid 1px var(--color-disabled);margin-bottom:20px;padding-bottom:20px}.review .info-container{border:none;display:flex;justify-content:space-between}.review .info-container .info-column .user{color:var(--color-secondary);font-size:16px;font-weight:600}.review .info-container .info-column .info{font-size:14px;margin-top:3px}.review .info-container .info-column .info span{text-transform:capitalize}.review .info-container .mb-reviews-rating{height:fit-content}.review .info-container .mb-review__rating-low .mb-review__rating-value{background-color:var(--color-error)}.review .title{font-size:16px;font-weight:600;margin-top:12px}.review .text{font-size:12px;margin-top:8px}\n"] }]
        }], propDecorators: { review: [{
                type: Input
            }] } });

class MbCommentsModule {
}
MbCommentsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommentsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbCommentsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbCommentsModule, declarations: [MbComments], imports: [CommonModule, MbReviewsRatingModule], exports: [MbComments] });
MbCommentsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommentsModule, imports: [CommonModule, MbReviewsRatingModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbCommentsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MbReviewsRatingModule],
                    exports: [MbComments],
                    declarations: [MbComments]
                }]
        }] });

class MbBigAlertTitle {
    constructor() { }
    ngOnInit() { }
}
MbBigAlertTitle.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertTitle, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbBigAlertTitle.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBigAlertTitle, selector: "mb-big-alert-title", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{font-size:24px;font-style:normal;font-weight:600;line-height:26px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertTitle, decorators: [{
            type: Component,
            args: [{ selector: 'mb-big-alert-title', template: '<ng-content></ng-content>', styles: [":host{font-size:24px;font-style:normal;font-weight:600;line-height:26px}\n"] }]
        }], ctorParameters: function () { return []; } });

class MbBigAlertDescription {
    constructor() { }
    ngOnInit() { }
}
MbBigAlertDescription.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertDescription, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbBigAlertDescription.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBigAlertDescription, selector: "mb-big-alert-description", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{display:block;font-size:16px;font-style:normal;font-weight:400;line-height:20px;margin-top:9px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertDescription, decorators: [{
            type: Component,
            args: [{ selector: 'mb-big-alert-description', template: '<ng-content></ng-content>', styles: [":host{display:block;font-size:16px;font-style:normal;font-weight:400;line-height:20px;margin-top:9px}\n"] }]
        }], ctorParameters: function () { return []; } });

/* tslint:disable:max-line-length */
class MbBigAlertInformationIcon {
    constructor() {
        this.icon = {
            iconName: 'big-alert-information',
            svgDef: {
                attr: {
                    with: '84',
                    height: '81',
                    viewBox: '0 0 84 81',
                    fill: 'none'
                },
                circle: {
                    attr: {
                        cx: '38.7342',
                        cy: '39.2342',
                        r: '38.7342',
                        fill: '#E4EEF8'
                    }
                },
                path: [
                    {
                        attr: {
                            'd': 'M81.3702 30.8112C82.0997 33.1955 82.6026 35.6779 82.8537 38.2335L81.8585 38.3313C81.9778 39.5443 82.0388 40.7744 82.0388 42.019C82.0388 43.2636 81.9778 44.4938 81.8585 45.7067L82.8537 45.8046C82.6026 48.3602 82.0997 50.8426 81.3702 53.2269L80.4139 52.9343C79.6875 55.3086 78.73 57.5833 77.5674 59.7326L78.447 60.2084C77.24 62.4398 75.8178 64.5392 74.2081 66.4793L73.4385 65.8408C71.8665 67.7354 70.1108 69.4741 68.1985 71.0299L68.8296 71.8056C66.8766 73.3946 64.7645 74.7977 62.5206 75.9878L62.052 75.1044C59.8948 76.2485 57.6128 77.1905 55.2314 77.9049L55.5187 78.8627C53.1317 79.5788 50.6474 80.0722 48.0904 80.3186L47.9945 79.3233C46.7807 79.4402 45.5498 79.5001 44.3046 79.5001C43.0593 79.5001 41.8284 79.4402 40.6146 79.3233L40.5187 80.3186C37.9617 80.0722 35.4774 79.5788 33.0904 78.8627L33.3777 77.9049C30.9963 77.1905 28.7143 76.2485 26.5571 75.1044L26.0885 75.9878C23.8446 74.7977 21.7325 73.3946 19.7795 71.8056L20.4106 71.0299C18.4983 69.4741 16.7426 67.7354 15.1706 65.8408L14.401 66.4793C12.7913 64.5392 11.3691 62.4398 10.1621 60.2084L11.0417 59.7326C9.8791 57.5833 8.92164 55.3086 8.19515 52.9343L7.23891 53.2269C6.50937 50.8426 6.00654 48.3602 5.75536 45.8046L6.75056 45.7067C6.63135 44.4938 6.57031 43.2636 6.57031 42.019C6.57031 40.7744 6.63135 39.5443 6.75056 38.3313L5.75536 38.2335C6.00654 35.6779 6.50937 33.1955 7.23891 30.8112L8.19515 31.1038C8.92164 28.7294 9.8791 26.4547 11.0417 24.3054L10.1621 23.8297C11.3691 21.5983 12.7913 19.4988 14.401 17.5587L15.1706 18.1973C16.7426 16.3027 18.4983 14.564 20.4106 13.0081L19.7795 12.2324C21.7325 10.6435 23.8446 9.24037 26.0885 8.05027L26.5571 8.93371C28.7143 7.78958 30.9963 6.84761 33.3777 6.13319L33.0904 5.17537C35.4774 4.45926 37.9617 3.96584 40.5187 3.71943L40.6146 4.71482C41.8284 4.59784 43.0593 4.53796 44.3046 4.53796C45.5498 4.53796 46.7807 4.59784 47.9945 4.71482L48.0904 3.71943C50.6474 3.96584 53.1317 4.45926 55.5187 5.17537L55.2314 6.13319C57.6128 6.84761 59.8948 7.78958 62.052 8.9337L62.5206 8.05027C64.7645 9.24037 66.8766 10.6435 68.8296 12.2324L68.1985 13.0081C70.1108 14.564 71.8665 16.3027 73.4385 18.1973L74.2081 17.5587C75.8178 19.4988 77.24 21.5983 78.447 23.8297L77.5674 24.3054C78.73 26.4547 79.6875 28.7294 80.4139 31.1038L81.3702 30.8112Z',
                            'stroke': '#2070C1',
                            'stroke-width': '2',
                            'stroke-dasharray': '8 8'
                        }
                    },
                    {
                        attr: {
                            'd': 'M42.9255 32.7044C42.9255 31.4981 43.9237 30.4999 45.13 30.4999C46.3362 30.4999 47.3345 31.4981 47.3345 32.7044V43.7269C47.3345 44.9332 46.3362 45.9314 45.13 45.9314C43.9237 45.9314 42.9255 44.9332 42.9255 43.7269V32.7044ZM45.13 48.7598C46.7106 48.7598 48 50.0493 48 51.6299C48 53.2104 46.7106 54.4999 45.13 54.4999C43.5494 54.4999 42.26 53.2104 42.26 51.6299C42.26 50.0493 43.5494 48.7598 45.13 48.7598Z',
                            'stroke': '#2070C1',
                            'stroke-width': '2'
                        }
                    }
                ]
            }
        };
    }
}
MbBigAlertInformationIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertInformationIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbBigAlertInformationIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBigAlertInformationIcon, selector: "mb-big-alert-information-icon", ngImport: i0, template: '<mb-icon style="display: flex !important;" [icon]="icon"></mb-icon>', isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertInformationIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-big-alert-information-icon',
                    template: '<mb-icon style="display: flex !important;" [icon]="icon"></mb-icon>'
                }]
        }] });

/* tslint:disable:max-line-length */
class MbBigAlertSuccesfulIcon {
    constructor() {
        this.icon = {
            iconName: 'big-alert-succesful',
            svgDef: {
                attr: {
                    with: '84',
                    height: '81',
                    viewBox: '0 0 84 81',
                    fill: 'none'
                },
                circle: {
                    attr: {
                        cx: '38.7342',
                        cy: '39.2342',
                        r: '38.7342',
                        fill: '#E2EEE6'
                    }
                },
                g: {
                    g: {
                        g: {
                            path: {
                                attr: {
                                    'fill-rule': 'evenodd',
                                    'clip-rule': 'evenodd',
                                    'd': 'M39.5833 52.8359L30.8566 44.1078C29.4055 42.5761 29.4377 40.1676 30.9291 38.6751C32.4204 37.1827 34.8289 37.1488 36.3616 38.5987L41.9128 44.1499L52.3096 30.2878C53.6001 28.5656 56.0424 28.2156 57.7647 29.5061C59.4869 30.7966 59.8369 33.2389 58.5464 34.9611L45.4556 52.4187C44.7778 53.3221 43.7413 53.8871 42.6147 53.9671C41.488 54.0471 40.3821 53.6344 39.5833 52.8359Z',
                                    'stroke': '#0C7130',
                                    'stroke-width': '2',
                                    'stroke-linejoin': 'round'
                                }
                            }
                        }
                    }
                },
                path: {
                    attr: {
                        'd': 'M81.3702 30.8113C82.0997 33.1956 82.6026 35.678 82.8537 38.2336L81.8585 38.3315C81.9778 39.5444 82.0388 40.7745 82.0388 42.0192C82.0388 43.2638 81.9778 44.4939 81.8585 45.7069L82.8537 45.8047C82.6026 48.3603 82.0997 50.8427 81.3702 53.227L80.4139 52.9344C79.6875 55.3088 78.73 57.5835 77.5674 59.7327L78.447 60.2085C77.24 62.4399 75.8178 64.5394 74.2081 66.4795L73.4385 65.8409C71.8665 67.7355 70.1108 69.4742 68.1985 71.0301L68.8296 71.8057C66.8766 73.3947 64.7645 74.7978 62.5206 75.9879L62.052 75.1045C59.8948 76.2486 57.6128 77.1906 55.2314 77.905L55.5187 78.8628C53.1317 79.5789 50.6474 80.0723 48.0904 80.3188L47.9945 79.3234C46.7807 79.4403 45.5498 79.5002 44.3046 79.5002C43.0593 79.5002 41.8284 79.4403 40.6146 79.3234L40.5187 80.3188C37.9617 80.0723 35.4774 79.5789 33.0904 78.8628L33.3777 77.905C30.9963 77.1906 28.7143 76.2486 26.5571 75.1045L26.0885 75.9879C23.8446 74.7978 21.7325 73.3947 19.7795 71.8057L20.4106 71.03C18.4983 69.4742 16.7426 67.7355 15.1706 65.8409L14.401 66.4795C12.7913 64.5394 11.3691 62.4399 10.1621 60.2085L11.0417 59.7327C9.8791 57.5835 8.92164 55.3088 8.19515 52.9344L7.23891 53.227C6.50937 50.8427 6.00654 48.3603 5.75536 45.8047L6.75056 45.7069C6.63135 44.4939 6.57031 43.2638 6.57031 42.0192C6.57031 40.7745 6.63135 39.5444 6.75056 38.3315L5.75536 38.2336C6.00654 35.678 6.50937 33.1956 7.23891 30.8113L8.19515 31.1039C8.92164 28.7296 9.8791 26.4549 11.0417 24.3056L10.1621 23.8298C11.3691 21.5984 12.7913 19.499 14.401 17.5589L15.1706 18.1974C16.7426 16.3028 18.4983 14.5641 20.4106 13.0083L19.7795 12.2326C21.7325 10.6436 23.8446 9.24049 26.0885 8.05039L26.5571 8.93383C28.7143 7.7897 30.9963 6.84773 33.3777 6.13331L33.0904 5.17549C35.4774 4.45938 37.9617 3.96597 40.5187 3.71955L40.6146 4.71494C41.8284 4.59797 43.0593 4.53809 44.3046 4.53809C45.5498 4.53809 46.7807 4.59797 47.9945 4.71494L48.0904 3.71955C50.6474 3.96597 53.1317 4.45939 55.5187 5.17549L55.2314 6.13332C57.6128 6.84773 59.8948 7.7897 62.052 8.93383L62.5206 8.05039C64.7645 9.24049 66.8766 10.6436 68.8296 12.2326L68.1985 13.0083C70.1108 14.5641 71.8665 16.3028 73.4385 18.1974L74.2081 17.5589C75.8178 19.499 77.24 21.5984 78.447 23.8298L77.5674 24.3056C78.73 26.4549 79.6875 28.7296 80.4139 31.1039L81.3702 30.8113Z',
                        'stroke': '#0C7130',
                        'stroke-width': '2',
                        'stroke-dasharray': '8 8'
                    }
                }
            }
        };
    }
}
MbBigAlertSuccesfulIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertSuccesfulIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbBigAlertSuccesfulIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBigAlertSuccesfulIcon, selector: "mb-big-alert-sucessful-icon", ngImport: i0, template: '<mb-icon style="display: flex !important;" [icon]="icon"></mb-icon>', isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertSuccesfulIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-big-alert-sucessful-icon',
                    template: '<mb-icon style="display: flex !important;" [icon]="icon"></mb-icon>'
                }]
        }] });

/* tslint:disable:max-line-length */
class MbBigAlertWarningIcon {
    constructor() {
        this.icon = {
            iconName: 'big-alert-warning',
            svgDef: {
                attr: {
                    with: '84',
                    height: '81',
                    viewBox: '0 0 84 81',
                    fill: 'none'
                },
                circle: {
                    attr: {
                        cx: '38.7342',
                        cy: '39.2342',
                        r: '38.7342',
                        fill: '#FBF6E6'
                    }
                },
                path: [
                    {
                        attr: {
                            'd': 'M81.3702 30.8113C82.0997 33.1956 82.6026 35.678 82.8537 38.2336L81.8585 38.3315C81.9778 39.5444 82.0388 40.7745 82.0388 42.0192C82.0388 43.2638 81.9778 44.4939 81.8585 45.7069L82.8537 45.8047C82.6026 48.3603 82.0997 50.8427 81.3702 53.227L80.4139 52.9344C79.6875 55.3088 78.73 57.5835 77.5674 59.7327L78.447 60.2085C77.24 62.4399 75.8178 64.5394 74.2081 66.4795L73.4385 65.8409C71.8665 67.7355 70.1108 69.4742 68.1985 71.0301L68.8296 71.8057C66.8766 73.3947 64.7645 74.7978 62.5206 75.9879L62.052 75.1045C59.8948 76.2486 57.6128 77.1906 55.2314 77.905L55.5187 78.8628C53.1317 79.5789 50.6474 80.0723 48.0904 80.3188L47.9945 79.3234C46.7807 79.4403 45.5498 79.5002 44.3046 79.5002C43.0593 79.5002 41.8284 79.4403 40.6146 79.3234L40.5187 80.3188C37.9617 80.0723 35.4774 79.5789 33.0904 78.8628L33.3777 77.905C30.9963 77.1906 28.7143 76.2486 26.5571 75.1045L26.0885 75.9879C23.8446 74.7978 21.7325 73.3947 19.7795 71.8057L20.4106 71.03C18.4983 69.4742 16.7426 67.7355 15.1706 65.8409L14.401 66.4795C12.7913 64.5394 11.3691 62.4399 10.1621 60.2085L11.0417 59.7327C9.8791 57.5835 8.92164 55.3088 8.19515 52.9344L7.23891 53.227C6.50937 50.8427 6.00654 48.3603 5.75536 45.8047L6.75056 45.7069C6.63135 44.4939 6.57031 43.2638 6.57031 42.0192C6.57031 40.7745 6.63135 39.5444 6.75056 38.3315L5.75536 38.2336C6.00654 35.678 6.50937 33.1956 7.23891 30.8113L8.19515 31.1039C8.92164 28.7296 9.8791 26.4549 11.0417 24.3056L10.1621 23.8298C11.3691 21.5984 12.7913 19.499 14.401 17.5589L15.1706 18.1974C16.7426 16.3028 18.4983 14.5641 20.4106 13.0083L19.7795 12.2326C21.7325 10.6436 23.8446 9.24049 26.0885 8.05039L26.5571 8.93383C28.7143 7.7897 30.9963 6.84773 33.3777 6.13331L33.0904 5.17549C35.4774 4.45938 37.9617 3.96597 40.5187 3.71955L40.6146 4.71494C41.8284 4.59797 43.0593 4.53809 44.3046 4.53809C45.5498 4.53809 46.7807 4.59797 47.9945 4.71494L48.0904 3.71955C50.6474 3.96597 53.1317 4.45939 55.5187 5.17549L55.2314 6.13332C57.6128 6.84773 59.8948 7.7897 62.052 8.93383L62.5206 8.05039C64.7645 9.24049 66.8766 10.6436 68.8296 12.2326L68.1985 13.0083C70.1108 14.5641 71.8665 16.3028 73.4385 18.1974L74.2081 17.5589C75.8178 19.499 77.24 21.5984 78.447 23.8298L77.5674 24.3056C78.73 26.4549 79.6875 28.7296 80.4139 31.1039L81.3702 30.8113Z',
                            'stroke': '#DFB032',
                            'stroke-width': '2',
                            'stroke-dasharray': '8 8'
                        }
                    },
                    {
                        attr: {
                            'd': 'M42.9255 32.2045C42.9255 30.9983 43.9237 30 45.13 30C46.3362 30 47.3345 30.9983 47.3345 32.2045V43.227C47.3345 44.4333 46.3362 45.4315 45.13 45.4315C43.9237 45.4315 42.9255 44.4333 42.9255 43.227V32.2045ZM45.13 48.26C46.7106 48.26 48 49.5494 48 51.13C48 52.7106 46.7106 54 45.13 54C43.5494 54 42.26 52.7106 42.26 51.13C42.26 49.5494 43.5494 48.26 45.13 48.26Z',
                            'stroke': '#DFB032',
                            'stroke-width': '2'
                        }
                    }
                ]
            }
        };
    }
}
MbBigAlertWarningIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertWarningIcon, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbBigAlertWarningIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBigAlertWarningIcon, selector: "mb-big-alert-warning-icon", ngImport: i0, template: '<mb-icon style="display: flex !important;" [icon]="icon"></mb-icon>', isInline: true, dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertWarningIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'mb-big-alert-warning-icon',
                    template: '<mb-icon style="display: flex !important;" [icon]="icon"></mb-icon>'
                }]
        }] });

class MbBigAlert {
    constructor() {
        this.ICON_TYPE = {
            warning: new MbBigAlertWarningIcon().icon,
            notification: new MbBigAlertInformationIcon().icon,
            success: new MbBigAlertSuccesfulIcon().icon
        };
    }
    ngOnInit() {
        this.icon = this.ICON_TYPE[this.type];
    }
}
MbBigAlert.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlert, deps: [], target: i0.ɵɵFactoryTarget.Component });
MbBigAlert.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbBigAlert, selector: "mb-big-alert", inputs: { type: "type" }, ngImport: i0, template: "<div class=\"mb-big-alert__container\">\n  <div class=\"mb-big-alert__icon-section\">\n    <mb-icon style=\"display: flex !important;\" [icon]=\"icon\"></mb-icon>\n  </div>\n  <div class=\"mb-big-alert__information-section\">\n    <div class=\"mb-big-alert__title\">\n      <ng-content select=\"mb-big-alert-title\"></ng-content>\n    </div>\n    <div class=\"mb-big-alert__description\">\n      <ng-content select=\"mb-big-alert-description\"></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [".mb-big-alert__container{display:flex;max-width:730px}.mb-big-alert__icon-section{font-size:85px}.mb-big-alert__information-section{margin-left:15px}@media (max-width: 600px){.mb-big-alert__container{flex-direction:column}.mb-big-alert__icon-section{align-self:center}.mb-big-alert__information-section{align-items:center;margin-left:0;margin-top:15px;text-align:center}}\n"], dependencies: [{ kind: "component", type: MbIcon, selector: "mb-icon", inputs: ["icon", "horizontalFlip", "verticalFlip", "spin"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlert, decorators: [{
            type: Component,
            args: [{ selector: 'mb-big-alert', template: "<div class=\"mb-big-alert__container\">\n  <div class=\"mb-big-alert__icon-section\">\n    <mb-icon style=\"display: flex !important;\" [icon]=\"icon\"></mb-icon>\n  </div>\n  <div class=\"mb-big-alert__information-section\">\n    <div class=\"mb-big-alert__title\">\n      <ng-content select=\"mb-big-alert-title\"></ng-content>\n    </div>\n    <div class=\"mb-big-alert__description\">\n      <ng-content select=\"mb-big-alert-description\"></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [".mb-big-alert__container{display:flex;max-width:730px}.mb-big-alert__icon-section{font-size:85px}.mb-big-alert__information-section{margin-left:15px}@media (max-width: 600px){.mb-big-alert__container{flex-direction:column}.mb-big-alert__icon-section{align-self:center}.mb-big-alert__information-section{align-items:center;margin-left:0;margin-top:15px;text-align:center}}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { type: [{
                type: Input
            }] } });

const COMPONENTS = [
    MbBigAlert,
    MbBigAlertTitle,
    MbBigAlertDescription,
    MbBigAlertWarningIcon,
    MbBigAlertSuccesfulIcon,
    MbBigAlertInformationIcon
];
class MbBigAlertModule {
}
MbBigAlertModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbBigAlertModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertModule, declarations: [MbBigAlert,
        MbBigAlertTitle,
        MbBigAlertDescription,
        MbBigAlertWarningIcon,
        MbBigAlertSuccesfulIcon,
        MbBigAlertInformationIcon], imports: [CommonModule, MbIconModule], exports: [MbBigAlert,
        MbBigAlertTitle,
        MbBigAlertDescription,
        MbBigAlertWarningIcon,
        MbBigAlertSuccesfulIcon,
        MbBigAlertInformationIcon] });
MbBigAlertModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertModule, imports: [CommonModule, MbIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbBigAlertModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS],
                    imports: [CommonModule, MbIconModule],
                    exports: [...COMPONENTS]
                }]
        }] });

class HammerConfig extends HammerGestureConfig {
    buildHammer(element) {
        return new Hammer(element, {
            inputClass: Hammer.TouchInput,
            touchAction: 'unset',
        });
    }
}
HammerConfig.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: HammerConfig, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
HammerConfig.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: HammerConfig });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: HammerConfig, decorators: [{
            type: Injectable
        }] });
const MODULES = [
    MbAdviceModule,
    MbProgressBarModule,
    MbBreadcrumbsModule,
    MbTabsModule,
    MbIconModule,
    MbAlertModule,
    MbCarouselModule,
    MbPaginatorModule,
    MbCheckboxModule,
    MbSwitchModule,
    MbCardModule,
    MbRadioModule,
    MbFormFieldModule,
    MbDatepickerModule,
    MbSliderRangeModule,
    MbButtonModule,
    MbExpandableTextModule,
    MbExpandablePanelModule,
    MbDialogModule,
    MbChipsModule,
    MbChipsModule,
    MbIconTextModule,
    MbIconButtonModule,
    MbTooltipModule,
    MbPopoverModule,
    MbStarsRatingModule,
    MbProgressSpinnerModule,
    MbBadgeModule,
    MbCommunicationModule,
    MbWideNotificationModule,
    MbReviewsRatingModule,
    MbAcceleratorModule,
    MbSnackBarModule,
    MbAutocompleteModule,
    MbTableModule,
    MbPageSizeSelectModule,
    MbRecommendedCardModule,
    MbBarRatingModule,
    MbCommentsModule,
    MbBigAlertModule,
    A11yModule,
];
class MoebiusUIModule {
}
MoebiusUIModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MoebiusUIModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MoebiusUIModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MoebiusUIModule, imports: [MbAdviceModule,
        MbProgressBarModule,
        MbBreadcrumbsModule,
        MbTabsModule,
        MbIconModule,
        MbAlertModule,
        MbCarouselModule,
        MbPaginatorModule,
        MbCheckboxModule,
        MbSwitchModule,
        MbCardModule,
        MbRadioModule,
        MbFormFieldModule,
        MbDatepickerModule,
        MbSliderRangeModule,
        MbButtonModule,
        MbExpandableTextModule,
        MbExpandablePanelModule,
        MbDialogModule,
        MbChipsModule,
        MbChipsModule,
        MbIconTextModule,
        MbIconButtonModule,
        MbTooltipModule,
        MbPopoverModule,
        MbStarsRatingModule,
        MbProgressSpinnerModule,
        MbBadgeModule,
        MbCommunicationModule,
        MbWideNotificationModule,
        MbReviewsRatingModule,
        MbAcceleratorModule,
        MbSnackBarModule,
        MbAutocompleteModule,
        MbTableModule,
        MbPageSizeSelectModule,
        MbRecommendedCardModule,
        MbBarRatingModule,
        MbCommentsModule,
        MbBigAlertModule,
        A11yModule], exports: [MbAdviceModule,
        MbProgressBarModule,
        MbBreadcrumbsModule,
        MbTabsModule,
        MbIconModule,
        MbAlertModule,
        MbCarouselModule,
        MbPaginatorModule,
        MbCheckboxModule,
        MbSwitchModule,
        MbCardModule,
        MbRadioModule,
        MbFormFieldModule,
        MbDatepickerModule,
        MbSliderRangeModule,
        MbButtonModule,
        MbExpandableTextModule,
        MbExpandablePanelModule,
        MbDialogModule,
        MbChipsModule,
        MbChipsModule,
        MbIconTextModule,
        MbIconButtonModule,
        MbTooltipModule,
        MbPopoverModule,
        MbStarsRatingModule,
        MbProgressSpinnerModule,
        MbBadgeModule,
        MbCommunicationModule,
        MbWideNotificationModule,
        MbReviewsRatingModule,
        MbAcceleratorModule,
        MbSnackBarModule,
        MbAutocompleteModule,
        MbTableModule,
        MbPageSizeSelectModule,
        MbRecommendedCardModule,
        MbBarRatingModule,
        MbCommentsModule,
        MbBigAlertModule,
        A11yModule] });
MoebiusUIModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MoebiusUIModule, providers: [
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: HammerConfig,
        },
    ], imports: [MODULES, MbAdviceModule,
        MbProgressBarModule,
        MbBreadcrumbsModule,
        MbTabsModule,
        MbIconModule,
        MbAlertModule,
        MbCarouselModule,
        MbPaginatorModule,
        MbCheckboxModule,
        MbSwitchModule,
        MbCardModule,
        MbRadioModule,
        MbFormFieldModule,
        MbDatepickerModule,
        MbSliderRangeModule,
        MbButtonModule,
        MbExpandableTextModule,
        MbExpandablePanelModule,
        MbDialogModule,
        MbChipsModule,
        MbChipsModule,
        MbIconTextModule,
        MbIconButtonModule,
        MbTooltipModule,
        MbPopoverModule,
        MbStarsRatingModule,
        MbProgressSpinnerModule,
        MbBadgeModule,
        MbCommunicationModule,
        MbWideNotificationModule,
        MbReviewsRatingModule,
        MbAcceleratorModule,
        MbSnackBarModule,
        MbAutocompleteModule,
        MbTableModule,
        MbPageSizeSelectModule,
        MbRecommendedCardModule,
        MbBarRatingModule,
        MbCommentsModule,
        MbBigAlertModule,
        A11yModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MoebiusUIModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES],
                    exports: [...MODULES],
                    providers: [
                        {
                            provide: HAMMER_GESTURE_CONFIG,
                            useClass: HammerConfig,
                        },
                    ],
                }]
        }] });

function toggleScroll(enable) {
    document.body.style.overflow = enable ? '' : 'hidden';
}

class MbDialogRef {
    constructor(overlay) {
        this.overlay = overlay;
        this.closeEvents = new Subject();
        /**
         * This attribute is needed in cases where the user already
         * has a component that works in a certain context, and
         * wants to reuse and render the same component as a dialog.
         * In that case, instead of adding the MB_DIALOG_DATA
         * injection token to the component, the user may use this
         * attribute to set the properties needed for the component
         * to work properly.
         */
        this.componentInstance = null;
    }
    onClose() {
        return this.closeEvents.asObservable();
    }
    close(data) {
        this.overlay.dispose();
        if (data !== null && data !== undefined) {
            this.closeEvents.next(data);
        }
        toggleScroll(true);
    }
    resize(resizeConfig) {
        this.overlay.updateSize(resizeConfig);
        this.overlay.updatePosition();
    }
}

const MB_DIALOG_DATA = new InjectionToken('MbDialogData');
class MbDialog {
    constructor(overlay, injector, breakpointObserver, platform, closeEventService) {
        this.overlay = overlay;
        this.injector = injector;
        this.breakpointObserver = breakpointObserver;
        this.platform = platform;
        this.closeEventService = closeEventService;
        this.isAnyDialogOpen = false;
        this.sizeSubscription = Subscription.EMPTY;
    }
    ngOnDestroy() {
        this.sizeSubscription.unsubscribe();
    }
    open(componentOrTemplate, options = {}) {
        if (this.isAnyDialogOpen) {
            throw new Error('A dialog instance is already open!');
        }
        if (!options.size) {
            options.size = 'md';
        }
        const overlayRef = this.createOverlay(options);
        const dialogRef = new MbDialogRef(overlayRef);
        if (componentOrTemplate instanceof TemplateRef) {
            this.attachTemplatePortal(overlayRef, dialogRef, componentOrTemplate, options);
        }
        else {
            this.attachComponentPortal(overlayRef, dialogRef, componentOrTemplate, options);
        }
        this.addSizeSubscriptions(dialogRef, options.size);
        this.addOverlayCloseSubscriptions(overlayRef, dialogRef, options);
        this.isAnyDialogOpen = true;
        toggleScroll(false);
        return dialogRef;
    }
    getOverlayWidth(size) {
        return {
            sm: '480px',
            md: '640px',
            lg: '960px',
        }[size];
    }
    getOverlayHeight(size) {
        if (this.platform.IOS || this.platform.ANDROID) {
            return '100vh';
        }
        else {
            return {
                lg: window.innerHeight > 750 ? '720px' : '96vh',
                md: window.innerHeight > 760 ? '640px' : '84vh',
                sm: window.innerHeight > 533 ? '384px' : '72vh',
            }[size];
        }
    }
    createOverlay(options) {
        const positionStrategy = this.overlay
            .position()
            .global()
            .centerHorizontally()
            .centerVertically();
        const scrollStrategy = this.overlay.scrollStrategies.noop();
        const width = this.getOverlayWidth(options.size);
        const height = this.getOverlayHeight(options.size);
        return this.overlay.create({
            panelClass: 'mb-dialog-panel',
            hasBackdrop: true,
            positionStrategy,
            scrollStrategy,
            width,
            maxHeight: height,
            ...options,
        });
    }
    createPortalInjector(dialogRef, componentData) {
        const tokens = new WeakMap();
        tokens.set(MbDialogRef, dialogRef);
        tokens.set(MB_DIALOG_DATA, componentData);
        return new PortalInjector(this.injector, tokens);
    }
    addOverlayCloseSubscriptions(overlayRef, dialogRef, options) {
        if (options.closeBtn !== undefined) {
            this.closeEventService.emitBtnCreated(options.closeBtn);
        }
        overlayRef.detachments().subscribe(() => {
            this.isAnyDialogOpen = false;
        });
        const backdropClickEvents = options.closeOnBackdropClick
            ? overlayRef.backdropClick()
            : NEVER;
        const keydownEvents = options.closeOnEscKey
            ? overlayRef.keydownEvents().pipe(filter((keyEvent) => keyEvent.keyCode === ESCAPE))
            : NEVER;
        const btnCloseClick = options.closeBtn
            ? this.closeEventService.getButtonObservable()
            : NEVER;
        merge(backdropClickEvents, keydownEvents, btnCloseClick).subscribe(() => {
            this.closeEventService.emitBtnCreated(false);
            dialogRef.close();
        });
    }
    addSizeSubscriptions(dialogRef, size) {
        this.sizeSubscription = this.breakpointObserver
            .observe([Breakpoints.Phone])
            .subscribe((state) => {
            const resizeConfig = state.matches
                ? { width: '100vw' }
                : { width: this.getOverlayWidth(size) };
            dialogRef.resize(resizeConfig);
        });
    }
    attachComponentPortal(overlayRef, dialogRef, component, options) {
        const portalInjector = this.createPortalInjector(dialogRef, options.dialogData);
        const portal = new ComponentPortal(component, null, portalInjector);
        const componentRef = overlayRef.attach(portal);
        dialogRef.componentInstance = componentRef.instance;
    }
    attachTemplatePortal(overlayRef, dialogRef, template, options) {
        const context = { $implicit: dialogRef, data: options.dialogData };
        const portal = new TemplatePortal(template, options.viewContainerRef, context);
        overlayRef.attach(portal);
    }
}
MbDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialog, deps: [{ token: i1$3.Overlay }, { token: i0.Injector }, { token: i2$2.BreakpointObserver }, { token: i1$4.Platform }, { token: CloseEventService }], target: i0.ɵɵFactoryTarget.Injectable });
MbDialog.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialog, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbDialog, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Overlay }, { type: i0.Injector }, { type: i2$2.BreakpointObserver }, { type: i1$4.Platform }, { type: CloseEventService }]; } });

class MbSlider {
    constructor(zone, cdr, sanitizer, document) {
        this.zone = zone;
        this.cdr = cdr;
        this.sanitizer = sanitizer;
        this.document = document;
        this.valueChange = new EventEmitter();
        this.mouseMoveHandler = this.onMouseMove.bind(this);
        this.mouseUpHandler = this.onMouseUp.bind(this);
        this.touchStartHandler = this.onTouchStart.bind(this);
        this.touchMoveHandler = this.onTouchMove.bind(this);
        this.touchEndHandler = this.onTouchEnd.bind(this);
    }
    ngOnInit() {
        if (this.value === undefined) {
            this.value = Math.floor((this.minLimit + this.maxLimit) / 2);
        }
    }
    ngAfterViewInit() {
        this.trackRect = this.track.nativeElement.getBoundingClientRect();
        this.thumb.nativeElement.addEventListener('touchstart', this.touchStartHandler);
        this.cdr.detectChanges();
    }
    // Mouse events
    onMouseDown() {
        this.zone.runOutsideAngular(() => {
            this.document.addEventListener('mousemove', this.mouseMoveHandler);
            this.document.addEventListener('mouseup', this.mouseUpHandler);
        });
    }
    onMouseMove(event) {
        this.updateThumbPosition(event.clientX);
    }
    onMouseUp() {
        this.zone.run(() => {
            this.valueChange.emit(this.value);
        });
        this.document.removeEventListener('mousemove', this.mouseMoveHandler);
        this.document.removeEventListener('mouseup', this.mouseUpHandler);
    }
    // Touch Events
    onTouchStart() {
        this.zone.runOutsideAngular(() => {
            this.document.addEventListener('touchmove', this.touchMoveHandler);
            this.document.addEventListener('touchend', this.touchEndHandler);
        });
    }
    onTouchMove(event) {
        this.updateThumbPosition(event.touches.item(event.touches.length - 1).clientX);
    }
    onTouchEnd() {
        this.zone.run(() => {
            this.valueChange.emit(this.value);
        });
        this.document.removeEventListener('touchmove', this.touchMoveHandler);
        this.document.removeEventListener('touchend', this.touchEndHandler);
    }
    updateThumbPosition(thumbPosition) {
        let newValue = convertPixelToValue(thumbPosition - this.trackRect.left, this.minLimit, this.maxLimit, this.trackRect.width);
        if (thumbPosition - this.trackRect.left < 0) {
            newValue = this.minLimit;
        }
        if (thumbPosition - this.trackRect.left > this.trackRect.width) {
            newValue = this.maxLimit;
        }
        this.zone.run(() => {
            this.value = newValue;
        });
    }
    get thumbPosition() {
        if (!this.trackRect) {
            return '0%';
        }
        const fraction = getFractionComplete(this.value, this.minLimit, this.maxLimit, this.trackRect.width);
        return `${fraction * 100}%`;
    }
    get fillWidth() {
        if (!this.trackRect) {
            return 'translate(0%, -50%)';
        }
        const fraction = getFractionComplete(this.value, this.minLimit, this.maxLimit, this.trackRect.width);
        return this.sanitizer.bypassSecurityTrustStyle(`translate(0%, -50%) scaleX(${fraction})`);
    }
}
MbSlider.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSlider, deps: [{ token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: i1$1.DomSanitizer }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
MbSlider.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MbSlider, selector: "mb-slider", inputs: { label: "label", minLimit: "minLimit", maxLimit: "maxLimit", value: "value" }, outputs: { valueChange: "valueChange" }, host: { classAttribute: "mb-slider" }, viewQueries: [{ propertyName: "thumb", first: true, predicate: ["thumb"], descendants: true }, { propertyName: "track", first: true, predicate: ["track"], descendants: true }], ngImport: i0, template: "<div class=\"mb-slider__data\">\n  <div class=\"mb-slider__value-min\">\n    <label>\n      <ng-template\n        *ngIf=\"label\"\n        [ngTemplateOutletContext]=\"{ $implicit: minLimit }\"\n        [ngTemplateOutlet]=\"label\"\n      ></ng-template>\n      <ng-container *ngIf=\"!label\">{{ minLimit }}</ng-container>\n    </label>\n  </div>\n  <div class=\"mb-slider__value-max\">\n    <label>\n      <ng-template\n        *ngIf=\"label\"\n        [ngTemplateOutletContext]=\"{ $implicit: value }\"\n        [ngTemplateOutlet]=\"label\"\n      ></ng-template>\n      <ng-container *ngIf=\"!label\">{{ value }}</ng-container>\n    </label>\n  </div>\n</div>\n<div class=\"mb-slider__container\">\n  <div class=\"mb-slider__slider\">\n    <div\n      #thumb\n      role=\"slider\"\n      class=\"mb-slider__thumb\"\n      [style.left]=\"thumbPosition\"\n      (mousedown)=\"onMouseDown()\"\n    ></div>\n    <div #track class=\"mb-slider__track\"></div>\n    <div class=\"mb-slider__fill\" [style.transform]=\"fillWidth\"></div>\n  </div>\n</div>\n", styles: [".mb-slider__value-max,.mb-slider__value-min{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;display:flex;position:absolute;-webkit-user-select:none;user-select:none}@media only screen and (max-width: 767px){.mb-slider__value-max,.mb-slider__value-min{line-height:1.5}}.mb-slider__fill,.mb-slider__track{border-radius:2px;height:4px;pointer-events:none;position:absolute;transform:translateY(-50%);width:100%}.mb-slider:host{display:block;height:60px;margin:0;padding:0}.mb-slider__data{display:flex;height:20px;position:relative}.mb-slider__value-min{left:0}.mb-slider__value-max{right:0}.mb-slider__container{align-items:center;display:flex;height:32px;-webkit-user-select:none;user-select:none}.mb-slider__slider{border-radius:2px;margin:0 12px;position:relative;width:100%}.mb-slider__thumb{background:var(--color-foreground);border:1px solid var(--color-secondary);border-radius:50%;box-shadow:inset 0 0 0 1px transparent,inset 0 0 0 2px transparent;cursor:pointer;height:24px;outline:none;position:absolute;-webkit-tap-highlight-color:transparent;top:0;transform:translate(-50%,-50%);width:24px;z-index:3}.mb-slider__thumb:active{box-shadow:inset 0 0 1px #fff,inset 0 1px 7px #ebebeb,0 3px 6px -3px #bbb}.mb-slider__track{background:var(--color-gray-600)}.mb-slider__fill{background:var(--color-secondary);transform-origin:left;transition:background .11s cubic-bezier(.2,0,.38,.9)}\n"], dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSlider, decorators: [{
            type: Component,
            args: [{ selector: 'mb-slider', host: {
                        class: 'mb-slider',
                    }, template: "<div class=\"mb-slider__data\">\n  <div class=\"mb-slider__value-min\">\n    <label>\n      <ng-template\n        *ngIf=\"label\"\n        [ngTemplateOutletContext]=\"{ $implicit: minLimit }\"\n        [ngTemplateOutlet]=\"label\"\n      ></ng-template>\n      <ng-container *ngIf=\"!label\">{{ minLimit }}</ng-container>\n    </label>\n  </div>\n  <div class=\"mb-slider__value-max\">\n    <label>\n      <ng-template\n        *ngIf=\"label\"\n        [ngTemplateOutletContext]=\"{ $implicit: value }\"\n        [ngTemplateOutlet]=\"label\"\n      ></ng-template>\n      <ng-container *ngIf=\"!label\">{{ value }}</ng-container>\n    </label>\n  </div>\n</div>\n<div class=\"mb-slider__container\">\n  <div class=\"mb-slider__slider\">\n    <div\n      #thumb\n      role=\"slider\"\n      class=\"mb-slider__thumb\"\n      [style.left]=\"thumbPosition\"\n      (mousedown)=\"onMouseDown()\"\n    ></div>\n    <div #track class=\"mb-slider__track\"></div>\n    <div class=\"mb-slider__fill\" [style.transform]=\"fillWidth\"></div>\n  </div>\n</div>\n", styles: [".mb-slider__value-max,.mb-slider__value-min{font-size:1rem;font-weight:var(--font-weight-semibold);line-height:1.5;display:flex;position:absolute;-webkit-user-select:none;user-select:none}@media only screen and (max-width: 767px){.mb-slider__value-max,.mb-slider__value-min{line-height:1.5}}.mb-slider__fill,.mb-slider__track{border-radius:2px;height:4px;pointer-events:none;position:absolute;transform:translateY(-50%);width:100%}.mb-slider:host{display:block;height:60px;margin:0;padding:0}.mb-slider__data{display:flex;height:20px;position:relative}.mb-slider__value-min{left:0}.mb-slider__value-max{right:0}.mb-slider__container{align-items:center;display:flex;height:32px;-webkit-user-select:none;user-select:none}.mb-slider__slider{border-radius:2px;margin:0 12px;position:relative;width:100%}.mb-slider__thumb{background:var(--color-foreground);border:1px solid var(--color-secondary);border-radius:50%;box-shadow:inset 0 0 0 1px transparent,inset 0 0 0 2px transparent;cursor:pointer;height:24px;outline:none;position:absolute;-webkit-tap-highlight-color:transparent;top:0;transform:translate(-50%,-50%);width:24px;z-index:3}.mb-slider__thumb:active{box-shadow:inset 0 0 1px #fff,inset 0 1px 7px #ebebeb,0 3px 6px -3px #bbb}.mb-slider__track{background:var(--color-gray-600)}.mb-slider__fill{background:var(--color-secondary);transform-origin:left;transition:background .11s cubic-bezier(.2,0,.38,.9)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: i1$1.DomSanitizer }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { label: [{
                type: Input
            }], minLimit: [{
                type: Input
            }], maxLimit: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], thumb: [{
                type: ViewChild,
                args: ['thumb']
            }], track: [{
                type: ViewChild,
                args: ['track']
            }] } });

class MbSliderModule {
}
MbSliderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSliderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MbSliderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: MbSliderModule, declarations: [MbSlider], imports: [CommonModule], exports: [MbSlider] });
MbSliderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSliderModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MbSliderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [MbSlider],
                    exports: [MbSlider]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AUTOCOMPLETE_OPTION_HEIGHT, AUTOCOMPLETE_PANEL_HEIGHT, CalendarCellViewModel, ChangeDateEvent, CloseEventService, HammerConfig, INSTANCE_TOKEN, InputRangeType, MB_AUTOCOMPLETE_VALUE_ACCESSOR, MB_DATEPICKER_VALIDATORS, MB_DATEPICKER_VALUE_ACCESSOR, MB_DIALOG_DATA, MB_RANGE_DATEPICKER_VALIDATORS, MB_RANGE_DATEPICKER_VALUE_ACCESSOR, MB_SEARCH_INPUT_CONFIG, MB_SNACK_BAR_DEFAULT_OPTIONS, MbAccelerator, MbAcceleratorDescription, MbAcceleratorModule, MbAcceleratorTitle, MbAdvice, MbAdviceModule, MbAlert, MbAlertDescription, MbAlertModule, MbAlertTitle, MbAutocomplete, MbAutocompleteInput, MbAutocompleteModule, MbBadge, MbBadgeModule, MbBarRating, MbBarRatingModule, MbBasicDialog, MbBigAlert, MbBigAlertDescription, MbBigAlertInformationIcon, MbBigAlertModule, MbBigAlertSuccesfulIcon, MbBigAlertTitle, MbBigAlertWarningIcon, MbBreadcrumbItem, MbBreadcrumbs, MbBreadcrumbsModule, MbButton, MbButtonModule, MbCard, MbCardModule, MbCarousel, MbCarouselCustomComponent, MbCarouselItem, MbCarouselModule, MbCheckbox, MbCheckboxModule, MbChip, MbChips, MbChipsModule, MbComments, MbCommentsModule, MbCommunication, MbCommunicationAlertIcon, MbCommunicationButton, MbCommunicationDocIcon, MbCommunicationInfoIcon, MbCommunicationInvoiceIcon, MbCommunicationLegalIcon, MbCommunicationMessage, MbCommunicationModule, MbCommunicationPaymentIcon, MbCommunicationTitle, MbCommunicationWarningIcon, MbConfirmationDialog, MbConfirmationDialogButton, MbDateIconText, MbDatepicker, MbDatepickerFooter, MbDatepickerInput, MbDatepickerModule, MbDialog, MbDialogBody, MbDialogFooter, MbDialogHeader, MbDialogModule, MbDialogRef, MbExpandableButton, MbExpandablePanel, MbExpandablePanelModule, MbExpandablePanelTrigger, MbExpandableText, MbExpandableTextModule, MbFormField, MbFormFieldError, MbFormFieldHint, MbFormFieldInput, MbFormFieldLabel, MbFormFieldLoading, MbFormFieldModule, MbFormFieldNotification, MbFormFieldPrefix, MbFormFieldSelect, MbFormFieldSuccess, MbFormFieldSuffix, MbHighlightPipe, MbIcon, MbIconButton, MbIconButtonModule, MbIconModule, MbIconRegistry, MbIconText, MbIconTextModule, MbLocationIconText, MbOption, MbOptionGroup, MbOptionModule, MbOptionSelectionChange, MbPageSizeSelect, MbPageSizeSelectModule, MbPaginator, MbPaginatorModule, MbPassengerIconText, MbPopoverContent, MbPopoverModule, MbPopoverTrigger, MbProgressBar, MbProgressBarModule, MbProgressSpinner, MbProgressSpinnerModule, MbRadioButton, MbRadioGroup, MbRadioModule, MbRangeDatepicker, MbRangeDatepickerInput, MbRecommendedCard, MbRecommendedCardModule, MbReviewsDetail, MbReviewsEvaluation, MbReviewsRating, MbReviewsRatingModule, MbSearchInput, MbSearchInputModule, MbSlider, MbSliderModule, MbSliderRange, MbSliderRangeModule, MbSnackBar, MbSnackBarAction, MbSnackBarMessage, MbSnackBarModule, MbSnackBarRef, MbStarsRating, MbStarsRatingModule, MbSwitch, MbSwitchModule, MbTab, MbTabSections, MbTable, MbTableButton, MbTableModule, MbTablePageInfo, MbTablePagination, MbTableRow, MbTableTitle, MbTabs, MbTabsModule, MbTimeIconText, MbTooltipContent, MbTooltipModule, MbTooltipTrigger, MbWideNotification, MbWideNotificationButton, MbWideNotificationMessage, MbWideNotificationModule, MoebiusUIModule, NavigationType, regexFormatDate, regexValidChars };
